import { useLanguageStore } from '@/stores/language.store';

type Lang = 'en' | 'es';

export const STRINGS = {
  // ===== Brand =====
  'brand.name': { en: 'Pathway to Better Days', es: 'Pathway to Better Days' },
  'brand.tagline': { en: 'HRSN Screening', es: 'Screening HRSN' },

  // ===== Login =====
  'login.subtitle': { en: 'Screening System — sign in to continue', es: 'Sistema de screening — inicie sesión para continuar' },
  'login.cardTitle': { en: 'Sign in', es: 'Iniciar sesión' },
  'login.cardDesc': { en: 'Use your work email and password. If your account requires it, you will be asked for a verification code.', es: 'Use su correo y contraseña de trabajo. Si su cuenta lo requiere, le pediremos un código de verificación.' },
  'login.email': { en: 'Email', es: 'Correo electrónico' },
  'login.password': { en: 'Password', es: 'Contraseña' },
  'login.mfaCode': { en: 'Verification code', es: 'Código de verificación' },
  'login.mfaHint': { en: 'Enter the 6-digit code from your authenticator app.', es: 'Ingrese el código de 6 dígitos de su app autenticadora.' },
  'login.button': { en: 'Sign in', es: 'Iniciar sesión' },
  'login.signingIn': { en: 'Signing in…', es: 'Iniciando sesión…' },
  'login.failed': { en: 'Sign-in failed', es: 'Inicio de sesión falló' },
  'login.mustEnroll': { en: 'MFA enrollment required', es: 'Requiere configurar MFA' },
  'login.mustEnrollDesc': { en: 'Your role requires multi-factor authentication. An admin must enroll you before you can sign in.', es: 'Su rol requiere autenticación multifactor. Un administrador debe configurarla antes de que pueda iniciar sesión.' },
  // -- Entrada al sistema (design spec del CEO, 2026-08-20). El castellano es
  // verbatim del spec; el inglés es su traducción.
  'login.mfaMaybe': {
    en: 'If your account requires it, we will ask you for a verification code next.',
    es: 'Si tu cuenta lo pide, después te vamos a pedir un código de verificación.',
  },
  'login.kicker': { en: 'Welcome back', es: 'Bienvenida de vuelta' },
  'login.title': {
    en: 'Sign in with your work account',
    es: 'Entrá con tu cuenta de trabajo',
  },
  'login.brandLine': {
    en: 'Every call walks with someone for a stretch of the road.',
    es: 'Cada llamada acompaña a alguien un tramo del camino.',
  },
  'login.brandSub': {
    en: 'Internal screening and navigation system for Bronx Medicaid members.',
    es: 'Sistema interno de screening y navegación para miembros de Medicaid del Bronx.',
  },
  'login.brandFoot': {
    en: 'Pathway to Better Days Inc. · Internal use',
    es: 'Pathway to Better Days Inc. · Uso interno',
  },
  'login.emailRequired': { en: 'Enter your work email.', es: 'Escribí tu correo de trabajo.' },
  'login.passwordRequired': { en: 'Enter your password.', es: 'Escribí tu contraseña.' },
  'login.show': { en: 'Show', es: 'Mostrar' },
  'login.hide': { en: 'Hide', es: 'Ocultar' },
  'login.remember': { en: 'Remember this computer', es: 'Recordar este equipo' },
  'login.forgot': { en: 'I forgot my password', es: 'Olvidé mi contraseña' },
  'login.entering': { en: 'One moment', es: 'Un momento' },
  'login.dontClose': { en: 'Do not close this tab', es: 'No cierres esta pestaña' },
  'login.offline': {
    en: 'Could not connect. Try again.',
    es: 'No se pudo conectar. Probá de nuevo.',
  },
  'login.lockedTitle': { en: 'Wait 15 minutes', es: 'Esperá 15 minutos' },
  'login.lockedBody': {
    en: 'Five failed attempts. It is a security measure — you did not lose any of your work.',
    es: 'Cinco intentos fallidos. Es una medida de seguridad, no perdiste nada de tu trabajo.',
  },
  'login.mfaEnroll': {
    en: 'Your role requires a verification code. An admin has to set it up before you can sign in.',
    es: 'Tu rol necesita código de verificación. Un administrador tiene que configurarlo antes de que puedas entrar.',
  },
  'login.back': { en: 'Back', es: 'Volver' },
  'login.codeKicker': { en: 'Verification', es: 'Verificación' },
  'login.codeTitle': { en: 'We sent you a code', es: 'Te mandamos un código' },
  'login.codeSub': {
    en: 'Six digits. It expires in 10 minutes.',
    es: 'Seis dígitos. Vence en 10 minutos.',
  },
  'login.codeRequired': { en: 'Enter the code.', es: 'Escribí el código.' },
  'login.codeSixDigits': { en: 'The code has 6 digits.', es: 'El código tiene 6 dígitos.' },
  'login.verify': { en: 'Verify and enter', es: 'Verificar y entrar' },
  'login.hipaa': {
    en: 'HIPAA-protected. Every sign-in is logged with your name and the time.',
    es: 'Protegido por HIPAA. Cada acceso queda registrado con tu nombre y la hora.',
  },
  'login.footer': { en: 'HIPAA-protected. All access is logged.', es: 'Protegido por HIPAA. Todos los accesos quedan registrados.' },

  // ===== Header =====
  'header.signOut': { en: 'Sign out', es: 'Cerrar sesión' },
  'header.settings': { en: 'Settings', es: 'Ajustes' },
  'header.myQueue': { en: 'My queue', es: 'Mi cola' },
  'header.admin': { en: 'Admin', es: 'Administración' },
  'header.role.admin': { en: 'admin', es: 'admin' },
  'header.role.supervisor': { en: 'supervisor', es: 'supervisor' },
  'header.role.agent': { en: 'agent', es: 'agente' },
  'header.patientSearch.placeholder': { en: 'Search by full CIN or full phone…', es: 'Buscar por CIN completo o teléfono completo…' },
  'header.patientSearch.hintIndexedOnly': {
    en: 'Search currently works with a COMPLETE CIN (e.g. AB12345C) or a COMPLETE 10-digit phone. Last-name search is being rebuilt — it cannot run over 474,960 encrypted records without an index.',
    es: 'La búsqueda funciona con el CIN COMPLETO (ej. AB12345C) o el teléfono COMPLETO de 10 dígitos. La búsqueda por apellido se está reconstruyendo — no puede correr sobre 474,960 registros encriptados sin índice.',
  },
  'header.patientSearch.loading': { en: 'Searching…', es: 'Buscando…' },
  'header.patientSearch.noResults': { en: 'No matches', es: 'Sin resultados' },

  // ===== Client profile =====
  'clientProfile.loading': { en: 'Loading patient profile…', es: 'Cargando perfil del paciente…' },
  'clientProfile.back': { en: 'Back', es: 'Atrás' },
  'clientProfile.status': { en: 'Status', es: 'Estado' },
  'clientProfile.bronx': { en: 'Bronx ZIP', es: 'ZIP del Bronx' },

  // v0.6.1 Fase 9 — Cluster ED Phase 1: agent edit client address + phones.
  'editClientInfo.triggerLabel': { en: 'Edit info', es: 'Editar información' },
  'editClientInfo.title': { en: 'Edit client info', es: 'Editar información del cliente' },
  'editClientInfo.close': { en: 'Close', es: 'Cerrar' },
  'editClientInfo.cancel': { en: 'Cancel', es: 'Cancelar' },
  'editClientInfo.save': { en: 'Save changes', es: 'Guardar cambios' },
  'editClientInfo.address1Label': { en: 'Address', es: 'Dirección' },
  'editClientInfo.cityLabel': { en: 'City', es: 'Ciudad' },
  'editClientInfo.postalCodeLabel': { en: 'ZIP code', es: 'Código postal' },
  'editClientInfo.postalCodeHelper': { en: 'Bronx eligibility requires a 104## ZIP.', es: 'La elegibilidad del Bronx requiere ZIP que empieza con 104.' },
  'editClientInfo.phoneCellLabel': { en: 'Cell phone', es: 'Celular' },
  'editClientInfo.phoneHomeLabel': { en: 'Home phone', es: 'Teléfono de casa' },
  'editClientInfo.phoneOtherLabel': { en: 'Other phone', es: 'Otro teléfono' },
  'editClientInfo.reasonLabel': { en: 'Reason', es: 'Motivo' },
  'editClientInfo.reasonPlaceholder': { en: 'e.g. "CSV had wrong ZIP — client confirmed new address by phone"', es: 'Ej.: "CSV tenía ZIP incorrecto — cliente confirmó nueva dirección por teléfono"' },
  'editClientInfo.reasonHelper': { en: 'Required (min 5 chars). Recorded in the audit log alongside the change.', es: 'Requerido (mín. 5 chars). Se guarda en el audit log junto al cambio.' },
  'editClientInfo.errorReasonRequired': { en: 'Reason is required (min 5 characters).', es: 'El motivo es requerido (mín. 5 caracteres).' },
  'editClientInfo.errorPostalCodeFormat': { en: 'ZIP code must be exactly 5 digits.', es: 'El código postal debe tener exactamente 5 dígitos.' },
  'editClientInfo.errorNoChanges': { en: 'No fields changed — close the modal or edit a field.', es: 'No cambiaste ningún campo — cerrá el modal o editá un campo.' },
  'editClientInfo.errorSaveTitle': { en: 'Could not save', es: 'No se pudo guardar' },
  'editClientInfo.toast.successTitle': { en: 'Client info updated', es: 'Información del cliente actualizada' },
  'editClientInfo.toast.successBody': { en: 'Changes saved + audit-logged.', es: 'Cambios guardados + registrados en audit.' },
  'editClientInfo.eligibilityBecameEligibleTitle': { en: 'Client is now eligible', es: 'Cliente ahora elegible' },
  'editClientInfo.eligibilityBecameEligibleBody': { en: 'ZIP now matches the Bronx eligibility rule — you can proceed with the call.', es: 'El ZIP ahora cumple la regla del Bronx — podés continuar con la llamada.' },
  'editClientInfo.eligibilityStillIneligibleTitle': { en: 'Still ineligible', es: 'Sigue marcado no elegible' },
  'editClientInfo.eligibilityStillIneligibleBody': { en: 'ZIP saved but still not a Bronx prefix (104##). Check with admin if this is unexpected.', es: 'ZIP guardado pero sigue sin coincidir con prefijo Bronx (104##). Consultá con admin si esto no es lo esperado.' },

  // v0.6.1 Fase 9.2 — uploader sees a notice when the agent edited the
  // client's info post-CSV-import. Avoids uploading stale data to FindHelp.
  'uploader.clientEditNotice.title': { en: 'Client info updated by agent', es: 'Información del cliente actualizada por el agente' },
  'uploader.clientEditNotice.body': { en: '{editor} ({role}) updated this client on {date}.', es: '{editor} ({role}) actualizó este cliente el {date}.' },
  'uploader.clientEditNotice.fieldsChangedLabel': { en: 'Fields changed', es: 'Campos modificados' },
  'uploader.clientEditNotice.reasonLabel': { en: 'Reason', es: 'Motivo' },
  'clientProfile.noPhone': { en: 'No phone on file', es: 'Sin teléfono registrado' },
  'clientProfile.noAddress': { en: 'No address on file', es: 'Sin dirección registrada' },
  'clientProfile.attemptCount': { en: '{n} call attempts', es: '{n} intentos de llamada' },
  'clientProfile.activeAssignment': { en: 'Active assignment', es: 'Asignación activa' },
  'clientProfile.agent': { en: 'Agent', es: 'Agente' },
  'clientProfile.assignedAt': { en: 'Assigned', es: 'Asignado' },
  'clientProfile.goToPreCall': { en: 'Open pre-call', es: 'Abrir pre-llamada' },
  'clientProfile.noActiveAssignment': { en: 'No active assignment.', es: 'Sin asignación activa.' },
  'clientProfile.bookings': { en: 'Bookings', es: 'Citas' },
  'clientProfile.noBookings': { en: 'No upcoming bookings.', es: 'Sin citas próximas.' },
  'clientProfile.recentScreenings': { en: 'Recent screenings', es: 'Screenings recientes' },
  'clientProfile.noScreenings': { en: 'No screenings yet.', es: 'Sin screenings aún.' },
  'clientProfile.submitted': { en: 'Submitted', es: 'Enviado' },
  'clientProfile.completed': { en: 'Completed', es: 'Completado' },
  'clientProfile.inProgress': { en: 'In progress', es: 'En progreso' },
  'clientProfile.readOnlyNotice': { en: 'Read-only view.', es: 'Vista de solo lectura.' },
  'clientProfile.dnc.badge': { en: 'Do Not Call', es: 'No Llamar' },
  // F5-T2-4 — chip de cola + verificacion previa (mockup Agente 04, v.5)
  'clientProfile.inYourQueue': { en: 'In your queue', es: 'En tu cola' },
  'clientProfile.precheck.okLine': { en: 'Callable: no do-not-call mark, phone on file, assigned to your queue. {n} attempts so far.', es: 'Se puede llamar: sin marca de no llamar, teléfono registrado y asignado a tu cola. {n} intentos hasta ahora.' },
  'clientProfile.precheck.blockedTitle': { en: 'Not callable right now', es: 'Ahora no se puede llamar' },
  'clientProfile.precheck.blockedDnc': { en: 'Do-not-call mark is set', es: 'Tiene marca de no llamar' },
  'clientProfile.precheck.blockedNoPhone': { en: 'No phone on file', es: 'No hay teléfono registrado' },
  'clientProfile.precheck.blockedNoAssignment': { en: 'Not assigned to any queue', es: 'No está asignado a ninguna cola' },
  'clientProfile.precheck.blockedOtherOwner': { en: 'Assigned to {name} — open it from their queue owner', es: 'Asignado a {name} — el dueño de la cola es otro' },
  'clientProfile.dnc.reason': { en: 'Reason', es: 'Motivo' },
  'clientProfile.notFound.title': { en: 'Patient not found', es: 'Paciente no encontrado' },
  'clientProfile.notFound.body': { en: 'The patient does not exist or has been removed.', es: 'El paciente no existe o fue removido.' },
  // F5-T2-5 — perfil/ajustes en tres pestañas (mockup Agente 04, v.6)
  'settings.title': { en: 'Profile & settings', es: 'Perfil y ajustes' },
  'settings.tabs.datos': { en: 'My info', es: 'Datos' },
  'settings.tabs.seguridad': { en: 'Security', es: 'Seguridad' },
  'settings.tabs.numeros': { en: 'My numbers', es: 'Mis números' },
  'settings.datos.title': { en: 'Account', es: 'Cuenta' },
  'settings.datos.desc': { en: 'What the system knows about you.', es: 'Lo que el sistema sabe de vos.' },
  'settings.datos.name': { en: 'Name', es: 'Nombre' },
  'settings.datos.email': { en: 'Email', es: 'Correo' },
  'settings.datos.emailReason': { en: 'An admin changes it', es: 'Lo cambia un admin' },
  'settings.datos.role': { en: 'Role', es: 'Rol' },
  'settings.datos.roleReason': { en: 'Assigned by your supervisor', es: 'Lo asigna tu supervisor' },
  'settings.datos.languages': { en: 'Languages', es: 'Idiomas' },
  'settings.datos.password': { en: 'Password', es: 'Contraseña' },
  'settings.datos.mustChange': { en: 'Change required', es: 'Cambio requerido' },
  'settings.security.pwTitle': { en: 'Change password', es: 'Cambiar contraseña' },
  'settings.security.pwCurrent': { en: 'Current password', es: 'Contraseña actual' },
  'settings.security.pwNew': { en: 'New password (min. 8 characters)', es: 'Contraseña nueva (mín. 8 caracteres)' },
  'settings.security.pwNew2': { en: 'Repeat new password', es: 'Repetir contraseña nueva' },
  'settings.security.pwWarning': { en: 'Changing your password signs you out everywhere else.', es: 'Cambiar la contraseña cierra tus otras sesiones.' },
  'settings.security.pwWarningAgent': { en: 'If you are about to take a call, do it afterwards.', es: 'Si estás por entrar en una llamada, hacelo después.' },
  'settings.security.pwSubmit': { en: 'Change password', es: 'Cambiar contraseña' },
  'settings.security.pwChanged': { en: 'Password changed', es: 'Contraseña cambiada' },
  'settings.security.pwFailed': { en: 'Could not change the password', es: 'No se pudo cambiar la contraseña' },
  'settings.numeros.title': { en: 'My numbers', es: 'Mis números' },
  'settings.numeros.desc': { en: 'A quick look — the full view lives in Progress.', es: 'Un vistazo rápido — la vista completa vive en Progreso.' },
  'settings.numeros.today': { en: 'Today', es: 'Hoy' },
  'settings.numeros.hit': { en: 'Target reached', es: 'Meta alcanzada' },
  'settings.numeros.month': { en: 'This month', es: 'Este mes' },
  'settings.numeros.tier': { en: 'Tier shown', es: 'Nivel mostrado' },
  'settings.numeros.streak': { en: 'Days in a row meeting the goal', es: 'Días seguidos cumpliendo la meta' },  // #162 (Edd, 23-oct): es una racha de meta, no días trabajados
  'settings.numeros.unavailable': { en: 'Numbers unavailable right now.', es: 'Números no disponibles ahora.' },
  // F5-T3 — la cáscara del admin (mockup Admin 01)
  'adminChrome.navLabel': { en: 'Admin navigation', es: 'Navegación del admin' },
  'adminChrome.railToggle': { en: 'Collapse or expand the menu', es: 'Colapsar o expandir el menú' },
  'adminChrome.group.operacion': { en: 'Operations', es: 'Operación' },
  'adminChrome.group.personas': { en: 'People', es: 'Personas' },
  'adminChrome.group.navegacion': { en: 'Navigation', es: 'Navegación' },
  'adminChrome.group.datos': { en: 'Data', es: 'Datos' },
  'adminChrome.group.sistema': { en: 'System', es: 'Sistema' },
  'adminChrome.nav.live': { en: 'Live monitoring', es: 'Monitoreo en vivo' },
  'adminChrome.nav.citas': { en: 'Missed appointments', es: 'Citas perdidas' },
  'adminChrome.nav.reasign': { en: 'Agent reassignment', es: 'Reasignación de agentes' },
  'adminChrome.nav.equipo': { en: 'Team center', es: 'Centro de equipo' },
  'adminChrome.nav.progreso': { en: 'Agent progress', es: 'Progreso de agentes' },
  'adminChrome.nav.control': { en: 'Navigation control room', es: 'Control room de navegación' },
  'adminChrome.nav.lotes': { en: 'Patient batches', es: 'Lotes de pacientes' },
  'adminChrome.nav.reportes': { en: 'Reports & exports', es: 'Reportes y exports' },
  'adminChrome.nav.config': { en: 'Settings', es: 'Configuración' },
  'adminChrome.nav.auditoria': { en: 'Audit', es: 'Auditoría' },
  'adminChrome.nav.bugs': { en: 'Bug reports', es: 'Reportes de bugs' },
  'adminChrome.role.admin': { en: 'Admin', es: 'Admin' },
  'adminChrome.role.supervisor': { en: 'Supervisor', es: 'Supervisor' },
  'adminChrome.menu.profile': { en: 'My profile', es: 'Mi perfil' },
  'adminChrome.menu.signOut': { en: 'Sign out', es: 'Cerrar sesión' },
  'adminChrome.live.ok': { en: 'Live · refreshes every 5 s', es: 'En vivo · actualiza cada 5 s' },
  'adminChrome.live.stale': { en: 'Reconnecting · data from {s} s ago', es: 'Reconectando · datos de hace {s} s' },
  'adminChrome.live.stopped': { en: 'Stopped', es: 'Detenido' },
  'adminChrome.live.retry': { en: 'Retry', es: 'Reintentar' },
  // F5-T4 — Uploader 02: el modal de revisión (una pregunta, dos caminos; F-1)
  'uploader.review.claimTitle': { en: 'You have this row claimed.', es: 'Tenés esta fila reclamada.' },
  'uploader.review.claimCloseNote': { en: 'Closing this modal releases it', es: 'Cerrar este modal la libera' },
  'uploader.review.closeRelease': { en: 'Close', es: 'Cerrar' },
  'uploader.review.closeReleaseAria': { en: 'Close and release the claimed row', es: 'Cerrar y liberar la fila reclamada' },
  'uploader.review.closeConfirm': { en: 'You are about to release the row and lose what you wrote. Close anyway?', es: 'Vas a liberar la fila y perder lo que escribiste. ¿Cerrar igual?' },
  'uploader.review.step1': { en: 'Step 1 · load it in FindHelp.', es: 'Paso 1 · cargalo en FindHelp.' },
  'uploader.review.step1Desc': { en: 'Opens in another tab. Come back here to declare the result.', es: 'Se abre en otra pestaña. Volvé acá para declarar el resultado.' },
  'uploader.review.copyAll': { en: 'Copy all', es: 'Copiar todo' },
  'uploader.review.openFindhelp': { en: 'Open FindHelp', es: 'Abrir FindHelp' },
  'uploader.review.fhNotConfigured': { en: 'FindHelp URL not configured — open it manually in another tab.', es: 'URL de FindHelp sin configurar — abrilo a mano en otra pestaña.' },
  'uploader.review.step2': { en: 'Step 2 · could you load it?', es: 'Paso 2 · ¿pudiste cargarlo?' },
  'uploader.review.yesTitle': { en: 'Yes, it is loaded', es: 'Sí, quedó cargado' },
  'uploader.review.yesConsequence': { en: 'Leaves pending and counts as processed for {agent}.', es: 'Sale de pendientes y cuenta como procesado para {agent}.' },
  'uploader.review.noTitle': { en: 'No, it could not be loaded', es: 'No se pudo cargar' },
  'uploader.review.noConsequence': { en: 'Flagged with an error: goes to the flags list for audit and does not count as processed.', es: 'Queda marcado con error: entra al listado de flags para auditoría y no cuenta como procesado.' },
  'uploader.review.yesHow': { en: 'How did it get loaded?', es: '¿Cómo quedó cargado?' },
  'uploader.review.yes.complete': { en: 'Loaded complete', es: 'Cargado completo' },
  'uploader.review.yes.completeDesc': { en: 'All 9 domains went into FindHelp as the agent sent them.', es: 'Los 9 dominios quedaron en FindHelp tal como los envió la agente.' },
  'uploader.review.yes.adapted': { en: 'Loaded with one adapted domain', es: 'Cargado con un dominio adaptado' },
  'uploader.review.yes.adaptedDesc': { en: 'FindHelp lacked the exact option and you chose the closest. Which one gets recorded.', es: 'FindHelp no tenía la opción exacta y elegiste la más cercana. Se anota cuál.' },
  'uploader.review.yes.dup': { en: 'It was already in FindHelp', es: 'Ya estaba cargado en FindHelp' },
  'uploader.review.yes.dupDesc': { en: 'Someone loaded it before. Closes without duplicating and counts once.', es: 'Alguien lo cargó antes. Se cierra sin duplicar y cuenta una sola vez.' },
  'uploader.review.adaptedPlaceholder': { en: 'Which domain did you adapt (e.g. Housing)', es: 'Qué dominio adaptaste (ej. Vivienda)' },
  'uploader.review.yesFinal.complete': { en: 'On confirm: leaves pending and counts as processed for {agent}. Recorded that you loaded it, with the time.', es: 'Al confirmar: sale de pendientes y cuenta como procesado para {agent}. Queda registrado que vos lo cargaste, con la hora.' },
  'uploader.review.yesFinal.adapted': { en: 'On confirm: counts as processed for {agent}, and the adapted domain is noted so the difference with FindHelp leaves a trace.', es: 'Al confirmar: cuenta como procesado para {agent}, y se anota qué dominio adaptaste para que quede rastro de la diferencia con FindHelp.' },
  'uploader.review.yesFinal.duplicate': { en: 'On confirm: leaves pending and counts once. {agent} does not lose the screening over a duplicate that was not theirs.', es: 'Al confirmar: sale de pendientes y cuenta una sola vez. {agent} no pierde el screening por un duplicado que no fue suyo.' },
  'uploader.review.noWhat': { en: 'Why could it not be loaded?', es: '¿Por qué no se pudo?' },
  'uploader.review.no.id': { en: 'The Medicaid ID does not exist or is not active', es: 'El ID de Medicaid no existe o no está activo' },
  'uploader.review.no.idDesc': { en: 'FindHelp rejects it. Stays flagged so the ID or coverage status gets verified.', es: 'FindHelp lo rechaza. Queda flaggeado para verificar el ID o el estado de cobertura.' },
  'uploader.review.no.datos': { en: 'Member data is missing', es: 'Faltan datos del miembro' },
  'uploader.review.no.datosDesc': { en: 'Name, date of birth or phone incomplete to create the record there.', es: 'Nombre, fecha de nacimiento o teléfono incompletos para crear el registro allá.' },
  'uploader.review.no.respuestas': { en: 'The answers do not add up', es: 'Las respuestas no cierran' },
  'uploader.review.no.respuestasDesc': { en: 'Contradictory or empty domains that cannot be transcribed as-is.', es: 'Hay dominios contradictorios o vacíos que no se pueden transcribir así.' },
  'uploader.review.no.duplicado': { en: 'It is a repeated screening from the same month', es: 'Es un screening repetido del mismo mes' },
  'uploader.review.no.duplicadoDesc': { en: 'One is already loaded for this member in the period; someone must decide which one stands.', es: 'Ya hay uno cargado para este miembro en el período; hay que decidir cuál vale.' },
  'uploader.review.noteLabel': { en: 'What you found (stays on the flag)', es: 'Qué encontraste (queda en el flag)' },
  'uploader.review.required': { en: 'required', es: 'obligatorio' },
  'uploader.review.notePlaceholder': { en: 'What is missing or wrong — recorded for the audit', es: 'Qué falta o qué está mal — queda registrado para la auditoría' },
  'uploader.review.noFinal': { en: 'On flagging: it does not count as processed, enters the flags list with your note, and supervision sees it for audit. Nothing captured is lost.', es: 'Al marcarlo: no cuenta como procesado, entra al listado de flags con tu nota, y supervisión lo ve para auditar. No se pierde nada de lo capturado.' },
  'uploader.review.confirmYes': { en: 'Mark as loaded', es: 'Marcar como cargado' },
  'uploader.review.confirmNo': { en: 'Flag with error', es: 'Marcar con error' },
  'uploader.review.whyChoosePath': { en: 'Choose whether you could load it', es: 'Elegí si pudiste cargarlo' },
  'uploader.review.whyChooseReason': { en: 'Choose what needs fixing', es: 'Elegí qué hay que corregir' },
  'uploader.review.whyNeedMessage': { en: 'The message for the agent is missing', es: 'Falta el mensaje para el agente' },
  'uploader.review.whyNeedDomain': { en: 'Say which domain you adapted', es: 'Decí qué dominio adaptaste' },
  // F5-T4/U-b — cierre por inelegibilidad (solo admin, F-1)
  'uploader.inelig.title': { en: 'Close for ineligibility (admin)', es: 'Cerrar por inelegibilidad (admin)' },
  'uploader.inelig.body': { en: 'This screening was flagged as no-Medicaid and was never uploaded. Closing disposes of it: it leaves the correction queue and never counts as processed. Irreversible.', es: 'Este screening qued\u00f3 flaggeado sin-Medicaid y nunca se subi\u00f3. Cerrarlo lo dispone: sale de la cola de correcci\u00f3n y jam\u00e1s cuenta como procesado. Irreversible.' },
  'uploader.inelig.reasonPh': { en: 'Reason (required): what was verified', es: 'Raz\u00f3n (obligatoria): qu\u00e9 se verific\u00f3' },
  'uploader.inelig.cta': { en: 'Close for ineligibility', es: 'Cerrar por inelegibilidad' },
  'uploader.inelig.savedToast': { en: 'Closed for ineligibility', es: 'Cerrado por inelegibilidad' },
  'uploader.inelig.closedLine': { en: 'Closed for ineligibility by an admin \u2014 disposed, never counts as processed.', es: 'Cerrado por inelegibilidad por un admin \u2014 dispuesto, jam\u00e1s cuenta como procesado.' },
  'uploader.review.theAgent': { en: 'the agent', es: 'la agente' },
  'clientProfile.error.title': { en: 'Could not load profile', es: 'No se pudo cargar el perfil' },
  'clientProfile.error.body': { en: 'Try again in a moment.', es: 'Inténtalo de nuevo en un momento.' },
  'admin.missedBookings.viewProfile': { en: 'View patient profile', es: 'Ver perfil del paciente' },
  'bookings.detail.viewProfile': { en: 'View profile', es: 'Ver perfil' },

  // ===== App shell =====
  'appshell.footer': { en: 'Pathway HRSN — HIPAA-protected. All access is logged.', es: 'Pathway HRSN — Protegido por HIPAA. Todos los accesos quedan registrados.' },
  'appshell.skipLink': { en: 'Skip to main content', es: 'Saltar al contenido principal' },

  // ===== Home =====
  'home.welcome': { en: 'Welcome', es: 'Bienvenido' },
  'home.welcomeWithName': { en: 'Welcome, {name}', es: 'Bienvenido, {name}' },
  'home.taglineAgent': { en: 'Your queue is waiting. Open it to take the next call.', es: 'Su cola está esperando. Ábrala para tomar la siguiente llamada.' },
  'home.taglineGeneric': { en: 'Pick a destination to get started.', es: 'Elija a dónde quiere ir para comenzar.' },
  'home.queueCardTitle': { en: 'My queue', es: 'Mi cola' },
  'home.queueCardDesc': { en: 'See pending calls and start the next screening.', es: 'Vea llamadas pendientes y comience el siguiente screening.' },
  'home.queueCardHint': { en: 'Live now — see assigned clients and take the next call.', es: 'Disponible ahora — vea clientes asignados y tome la siguiente llamada.' },
  'home.adminCardTitle': { en: 'Admin dashboard', es: 'Panel de administración' },
  'home.adminCardDesc': { en: 'Live monitoring, batches, agents, reassignments.', es: 'Monitoreo en vivo, lotes, agentes, reasignaciones.' },
  'home.adminCardHint': { en: 'Live now — agents, batches, audit, reassignments.', es: 'Disponible ahora — agentes, lotes, auditoría, reasignaciones.' },

  // ===== Queue =====
  'queue.title': { en: 'My queue', es: 'Mi cola' },
  'queue.refresh': { en: 'Refresh', es: 'Actualizar' },
  'queue.todaysTarget': { en: "Today's target", es: 'Meta de hoy' },
  'queue.pendingInQueue': { en: 'Pending in queue', es: 'Pendientes en cola' },
  'queue.thisWeek': { en: 'Lifetime', es: 'De por vida' },
  'queue.submitted': { en: 'submitted', es: 'enviados' },
  'queue.belowBronze': { en: 'Below bronze', es: 'Debajo de bronce' },
  'queue.tierMore': { en: '{n} more for {tier}', es: '{n} más para {tier}' },
  'queue.tierBronze': { en: 'Bronze', es: 'Bronce' },
  'queue.tierSilver': { en: 'Silver', es: 'Plata' },
  'queue.tierGold': { en: 'Gold', es: 'Oro' },
  'queue.pendingCallsTitle': { en: 'Pending calls', es: 'Llamadas pendientes' },
  'queue.pendingCallsDesc': { en: 'Take the next one or pick from the list.', es: 'Tome la siguiente o elija de la lista.' },
  'queue.takeNext': { en: 'Take next', es: 'Tomar siguiente' },
  'queue.claiming': { en: 'Claiming…', es: 'Asignando…' },
  'queue.couldNotClaim': { en: "Couldn't claim next", es: 'No se pudo asignar' },
  'queue.noPending': { en: 'No pending assignments. Take a break or refresh — new clients land here as the admin uploads batches.', es: 'No hay asignaciones pendientes. Tome un descanso o actualice — los nuevos clientes aparecen aquí cuando el administrador sube lotes.' },
  'queue.couldNotLoad': { en: "Couldn't load queue", es: 'No se pudo cargar la cola' },
  'queue.allClear': { en: 'All clear', es: 'Todo al día' },
  'queue.allClearDesc': { en: 'No clients pending right now. Check back after the next batch lands.', es: 'No hay clientes pendientes ahora. Vuelva a revisar después del próximo lote.' },
  'queue.col.num': { en: '#', es: '#' },
  'queue.col.client': { en: 'Client (hash)', es: 'Cliente (hash)' },
  'queue.col.lang': { en: 'Lang', es: 'Idioma' },
  'queue.col.status': { en: 'Status', es: 'Estado' },
  'queue.col.lastAttempt': { en: 'Last attempt', es: 'Último intento' },
  'queue.col.attempts': { en: 'Attempts', es: 'Intentos' },
  'queue.privacyNote': { en: 'Names and phones are revealed only when you take the next call (audit-logged).', es: 'Nombres y teléfonos se revelan solo cuando toma la siguiente llamada (con audit log).' },
  'queue.pageOf': { en: 'Page {page} of {total} · {count} total', es: 'Página {page} de {total} · {count} total' },
  'queue.prev': { en: 'Prev', es: 'Anterior' },
  'queue.next': { en: 'Next', es: 'Siguiente' },
  'queue.statusPending': { en: 'pending', es: 'pendiente' },
  'queue.statusInProgress': { en: 'in progress', es: 'en progreso' },
  'queue.toastSubmitted': { en: 'Screening submitted', es: 'Screening enviado' },
  'queue.toastSubmittedDesc': { en: 'Nice work — your queue is updated.', es: 'Buen trabajo — su cola se actualizó.' },
  // ===== Pending calls cards (Option B redesign) =====
  'queue.pending.never': { en: 'Never called', es: 'Sin llamar' },
  'queue.pending.now': { en: 'now', es: 'ahora' },
  'queue.pending.minAgo': { en: '{n}m ago', es: 'hace {n}m' },
  'queue.pending.hAgo': { en: '{n}h ago', es: 'hace {n}h' },
  'queue.pending.dAgo': { en: '{n}d ago', es: 'hace {n}d' },
  'queue.pending.take': { en: 'Take', es: 'Tomar' },

  // ===== Queue stat tiles (redesigned) =====
  'queue.tile.targetHint': { en: 'of {target} daily target', es: 'de {target} meta diaria' },
  'queue.tile.pendingTooltip': { en: '{n} clients waiting in your queue', es: '{n} clientes esperando en tu cola' },
  'queue.tile.pendingSubline': { en: 'in your queue', es: 'en tu cola' },
  'queue.tile.weekTier': { en: '{tier} tier', es: 'nivel {tier}' },
  'queue.tile.weekDefault': { en: 'this week so far', es: 'esta semana hasta ahora' },
  'queue.tile.completedToday': { en: 'Completed today', es: 'Completados hoy' },
  'queue.tile.outcomesToday': { en: 'Call Outcomes', es: 'Resultado de la llamada' },
  'queue.tile.outcomesSubline': { en: 'No-answer, refused…', es: 'Sin respuesta, rehusó…' },
  'queue.tile.clickToReview': { en: 'Click to review', es: 'Click para revisar' },
  'queue.tile.weekClickAria': { en: 'Open all-time submissions history', es: 'Abrir historial completo de envíos' },

  // ===== Agent daily flair =====
  'agent.flair.aria': { en: 'Daily progress', es: 'Progreso diario' },
  'agent.flair.target': { en: 'Target', es: 'Meta' },
  'agent.flair.tier.gold': { en: 'Gold tier', es: 'Nivel Oro' },
  'agent.flair.tier.silver': { en: 'Silver tier', es: 'Nivel Plata' },
  'agent.flair.tier.bronze': { en: 'Bronze tier', es: 'Nivel Bronce' },
  'agent.flair.tier.below': { en: 'Building up', es: 'Construyendo' },
  'agent.flair.progress.start': { en: "Let's get started!", es: '¡Vamos!' },
  'agent.flair.progress.beginning': { en: 'Off to a good start', es: 'Buen comienzo' },
  'agent.flair.progress.onWay': { en: 'On the way', es: 'En camino' },
  'agent.flair.progress.halfway': { en: 'More than halfway!', es: '¡Más de la mitad!' },
  'agent.flair.progress.almost': { en: 'Almost there!', es: '¡Casi!' },
  'agent.flair.progress.complete': { en: 'Target hit! 🎉', es: '¡Meta cumplida! 🎉' },
  'agent.flair.streak': { en: '{n} days in a row', es: '{n} días seguidos' },
  'queue.flair.noPriorAttempts': { en: 'No prior attempts', es: 'Sin intentos previos' },
  'queue.flair.priorAttempt': { en: '{n} prior attempt', es: '{n} intento previo' },
  'queue.flair.priorAttempts': { en: '{n} prior attempts', es: '{n} intentos previos' },

  'common.close': { en: 'Close', es: 'Cerrar' },

  // ===== Agent all-time submission history modal =====
  'agent.history.title': { en: 'All-time submissions', es: 'Todos los envíos' },
  'agent.history.subtitle': { en: 'Every screening you have submitted. Filter by date, status, tier, or day-of-week.', es: 'Cada screening que has enviado. Filtra por fecha, estado, nivel o día de la semana.' },
  'agent.history.filter.today': { en: 'Today', es: 'Hoy' },
  'agent.history.filter.thisWeek': { en: 'This week', es: 'Esta semana' },
  'agent.history.filter.thisMonth': { en: 'This month', es: 'Este mes' },
  'agent.history.filter.thisYear': { en: 'This year', es: 'Este año' },
  'agent.history.filter.allTime': { en: 'All time', es: 'Total' },
  'agent.history.filter.custom': { en: 'Custom', es: 'Personalizado' },
  'agent.history.filter.status': { en: 'Status', es: 'Estado' },
  'agent.history.filter.tier': { en: 'Tier', es: 'Nivel' },
  'agent.history.filter.daysOfWeek': { en: 'Days', es: 'Días' },
  'agent.history.filter.statusAll': { en: 'All', es: 'Todos' },
  'agent.history.filter.statusUploaded': { en: 'Approved', es: 'Aprobados' },
  'agent.history.filter.statusPending': { en: 'Pending', es: 'Pendientes' },
  'agent.history.filter.statusFlagged': { en: 'Rejected', es: 'Rechazados' },
  'agent.history.filter.statusNoMedicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'agent.history.filter.hh': { en: 'Type', es: 'Tipo' },
  'agent.history.filter.hhAll': { en: 'All', es: 'Todos' },
  'agent.history.filter.hhOnly': { en: 'Household only', es: 'Solo Household' },
  'agent.history.filter.hhExclude': { en: 'Primary only', es: 'Solo primarios' },
  'agent.history.row.hhBadge': { en: 'Household', es: 'Household' },
  'agent.history.filter.includeDrafts': { en: 'Include drafts', es: 'Incluir borradores' },
  'agent.history.filter.includeDraftsHint': { en: 'Show in-progress (unsubmitted) sessions too', es: 'Mostrar también sesiones en curso (sin enviar)' },
  'agent.history.filter.tierBronze': { en: 'Bronze', es: 'Bronce' },
  'agent.history.filter.tierSilver': { en: 'Silver', es: 'Plata' },
  'agent.history.filter.tierGold': { en: 'Gold', es: 'Oro' },
  'agent.history.filter.tierBelow': { en: 'Below bronze', es: 'Debajo de bronce' },
  'agent.history.filter.day.mon': { en: 'Mon', es: 'Lun' },
  'agent.history.filter.day.tue': { en: 'Tue', es: 'Mar' },
  'agent.history.filter.day.wed': { en: 'Wed', es: 'Mié' },
  'agent.history.filter.day.thu': { en: 'Thu', es: 'Jue' },
  'agent.history.filter.day.fri': { en: 'Fri', es: 'Vie' },
  'agent.history.filter.day.sat': { en: 'Sat', es: 'Sáb' },
  'agent.history.filter.day.sun': { en: 'Sun', es: 'Dom' },
  'agent.history.filter.from': { en: 'From', es: 'Desde' },
  'agent.history.filter.to': { en: 'To', es: 'Hasta' },
  'agent.history.filter.apply': { en: 'Apply', es: 'Aplicar' },
  'agent.history.filter.clear': { en: 'Clear filters', es: 'Limpiar filtros' },
  'agent.history.summary.total': { en: 'Total', es: 'Total' },
  'agent.history.summary.uploadedPercent': { en: 'Uploaded %', es: '% Subidos' },
  'agent.history.summary.avgDuration': { en: 'Avg duration', es: 'Duración prom.' },
  'agent.history.loadMore': { en: 'Load more', es: 'Cargar más' },
  'agent.history.empty': { en: 'No submissions match these filters.', es: 'Ningún envío cumple con estos filtros.' },




  // ===== Pre-call =====
  'precall.backToQueue': { en: 'Back to queue', es: 'Volver a la cola' },
  'precall.noActiveTitle': { en: 'No active client', es: 'Sin cliente activo' },
  'precall.noActiveDesc': { en: 'Take the next call from your queue to see client details here.', es: 'Tome la siguiente llamada desde su cola para ver los detalles aquí.' },
  'precall.goToQueue': { en: 'Go to my queue', es: 'Ir a mi cola' },
  'precall.phiBanner': { en: 'You are viewing protected health information. Your access is logged.', es: 'Está viendo información de salud protegida. Su acceso queda registrado.' },
  'precall.dob': { en: 'DOB', es: 'Fecha de nac.' },
  'precall.cin': { en: 'CIN', es: 'CIN' },
  'precall.eligibilityTitle': { en: 'Eligibility checks', es: 'Verificación de elegibilidad' },
  'precall.eligibilityDesc': { en: 'Auto checks run on the client. Manual checks are confirmed by you in FindHelp.', es: 'Verificaciones automáticas se hacen sobre el cliente. Las manuales las confirma usted en FindHelp.' },
  'precall.autoBronx': { en: 'Bronx ZIP (104xx)', es: 'ZIP del Bronx (104xx)' },
  'precall.autoBlackout': { en: 'No screening in last 12 months', es: 'Sin screening en los últimos 12 meses' },
  'precall.autoAttempts': { en: 'Within attempt limit', es: 'Dentro del límite de intentos' },
  'precall.autoDnc': { en: 'Not on Do-Not-Call list', es: 'No está en la lista de "No llamar"' },
  'precall.manualMedicaid': { en: 'Active Medicaid (verified in FindHelp)', es: 'Medicaid activo (verificado en FindHelp)' },
  'precall.manualNotExcluded': { en: 'Not in excluded population', es: 'No está en población excluida' },
  'precall.openFindHelp': { en: 'Open FindHelp', es: 'Abrir FindHelp' },
  'precall.priorAttempts': { en: 'Prior attempts', es: 'Intentos anteriores' },
  'precall.noAttempts': { en: 'No prior attempts on file.', es: 'No hay intentos previos en el registro.' },
  'precall.phones': { en: 'Phone numbers', es: 'Números de teléfono' },
  'precall.phoneCell': { en: 'Cell', es: 'Celular' },
  'precall.phoneAlt': { en: 'Alt', es: 'Alterno' },
  'precall.phoneHome': { en: 'Home', es: 'Casa' },
  'precall.phoneWork': { en: 'Work', es: 'Trabajo' },
  'precall.preferred': { en: 'preferred', es: 'preferido' },
  'precall.recordingNotice': { en: 'I have informed the member that the call is being recorded', es: 'He informado al miembro que la llamada está siendo grabada' },
  'precall.recordingNoticeDesc': { en: 'HIPAA + manual rule #5. Required to start the screening.', es: 'HIPAA + regla manual #5. Requerido para comenzar el screening.' },
  'precall.languageLabel': { en: 'Language', es: 'Idioma' },
  'precall.langEnglish': { en: 'English', es: 'Inglés' },
  'precall.langSpanish': { en: 'Español', es: 'Español' },
  'precall.langOther': { en: 'Other', es: 'Otro' },
  'precall.startScreening': { en: 'Start screening', es: 'Comenzar screening' },
  'precall.starting': { en: 'Starting…', es: 'Comenzando…' },
  'precall.couldNotStart': { en: "Couldn't start screening", es: 'No se pudo comenzar el screening' },
  'precall.logNoAnswer': { en: 'Log: No answer', es: 'Registrar: No contestó' },
  'precall.logVoicemail': { en: 'Log: Voicemail', es: 'Registrar: Buzón de voz' },
  'precall.toStart': { en: 'To start: {missing}', es: 'Para comenzar: {missing}' },
  'precall.missing.autoChecks': { en: 'Eligibility auto-checks not passed', es: 'Verificaciones automáticas no aprobadas' },
  'precall.missing.medicaid': { en: 'Confirm Active Medicaid (verify in FindHelp)', es: 'Confirme Medicaid activo (verifique en FindHelp)' },
  'precall.missing.notExcluded': { en: 'Confirm Not in excluded population', es: 'Confirme No está en población excluida' },
  'precall.missing.recording': { en: 'Confirm member is informed about recording', es: 'Confirme que el miembro fue informado de la grabación' },
  'precall.releasing': { en: 'Releasing…', es: 'Liberando…' },
  'precall.releaseFailed': { en: 'Could not release. Try again or use the queue link.', es: 'No se pudo liberar. Intente de nuevo o use el enlace de la cola.' },

  // ===== Screening page header =====
  'screening.backToQueue': { en: 'Back to queue', es: 'Volver a la cola' },
  'screening.backTitle': { en: 'Back to queue — discards this screening; the patient returns to the pending queue', es: 'Volver a la cola — descarta este screening; el paciente vuelve a la cola pendiente' },
  'screening.cancelDialog.title': { en: 'Leave this screening?', es: '¿Salir de este screening?' },
  'screening.cancelDialog.body': { en: 'Saved answers will be discarded and the assignment goes back to the pending queue. Time spent will not count as call time, but will be recorded as active work time for your performance metrics. This action is recorded in the audit log.', es: 'Las respuestas guardadas se descartarán y la asignación volverá a la cola pendiente. El tiempo transcurrido no contará como tiempo en llamada, pero se registrará como tiempo activo en sus métricas de desempeño. Esta acción queda registrada en el audit log.' },
  'screening.cancelDialog.confirm': { en: 'Yes, back to queue', es: 'Sí, volver a la cola' },
  'screening.cancelDialog.keep': { en: 'Keep screening', es: 'Seguir con el screening' },
  'screening.cancelDialog.error': { en: 'Could not return to queue', es: 'No se pudo volver a la cola' },

  // ===== Submit modal =====
  'submit.title': { en: 'Review & submit', es: 'Revisar y enviar' },
  'submit.close': { en: 'Close', es: 'Cerrar' },
  'submit.summaryHeading': { en: 'Domain findings', es: 'Hallazgos por dominio' },
  'submit.findingPositive': { en: 'Positive finding', es: 'Hallazgo positivo' },
  'submit.findingClear': { en: 'No finding', es: 'Sin hallazgo' },
  'submit.findingMissing': { en: 'Missing', es: 'Falta' },
  'submit.timeCategoryLabel': { en: 'Time category (declared to FindHelp)', es: 'Categoría de tiempo (declarada en FindHelp)' },
  'submit.timeUnder8': { en: 'Under 8 minutes', es: 'Menos de 8 minutos' },
  'submit.time8to22': { en: '8 to 22 minutes', es: '8 a 22 minutos' },
  'submit.time22to37': { en: '22 to 37 minutes (preferred)', es: '22 a 37 minutos (preferido)' },
  'submit.time37plus': { en: '37+ minutes', es: '37+ minutos' },
  'submit.measuredCallTime': { en: 'Measured call time: {time}', es: 'Tiempo de llamada medido: {time}' },
  'submit.nextStepNavigatorLabel': { en: 'Member is interested in Next-Step Navigator', es: 'El miembro está interesado en el Navegador del siguiente paso' },
  'submit.nextStepNavigatorYes': { en: 'Yes', es: 'Sí' },
  'submit.nextStepNavigatorNo': { en: 'No', es: 'No' },
  'submit.findhelpConfirm': { en: 'I have also submitted this screening in FindHelp', es: 'También envié este screening en FindHelp' },
  'submit.findhelpDesc': { en: 'Manual rule #6. The submission is incomplete in FindHelp without this confirmation.', es: 'Regla manual #6. El envío en FindHelp queda incompleto sin esta confirmación.' },
  'submit.findhelpIdLabel': { en: 'FindHelp screening ID (optional)', es: 'ID del screening en FindHelp (opcional)' },
  'submit.antifraudWarn': { en: 'Anti-fraud check: declared time bucket exceeds measured call duration. Lower the bucket or extend the call.', es: 'Verificación anti-fraude: la categoría declarada supera el tiempo medido de la llamada. Reduzca la categoría o extienda la llamada.' },
  'submit.missingDomains': { en: '{n} domain(s) missing answers. Go back and complete them.', es: 'Faltan respuestas en {n} dominio(s). Regrese y complételos.' },
  'submit.cancel': { en: 'Cancel', es: 'Cancelar' },
  'submit.button': { en: 'Submit screening', es: 'Enviar screening' },
  'submit.submitting': { en: 'Submitting…', es: 'Enviando…' },
  'submit.error': { en: "Couldn't submit", es: 'No se pudo enviar' },
  'submit.expiredByIdle': { en: 'This screening expired after 15 minutes without connection. The case went back to your queue — take it again from Mi cola.', es: 'Este screening venció por inactividad (15 minutos sin conexión). El caso volvió a tu cola — tomalo de nuevo desde Mi cola.' },

  // ===== 404 =====
  'notfound.title': { en: 'Page not found', es: 'Página no encontrada' },
  'notfound.desc': { en: "The page you're looking for doesn't exist or you don't have access.", es: 'La página que busca no existe o no tiene acceso.' },
  'notfound.home': { en: 'Go home', es: 'Ir al inicio' },

  // ===== Common toaster / errors =====
  'toast.dismiss': { en: 'Dismiss', es: 'Descartar' },
  'common.loading': { en: 'Loading', es: 'Cargando' },
  'common.required': { en: 'required', es: 'requerido' },
  'common.optional': { en: 'optional', es: 'opcional' },
  'common.save': { en: 'Save', es: 'Guardar' },
  'common.cancel': { en: 'Cancel', es: 'Cancelar' },
  'common.clear':                            { en: 'Clear',                                    es: 'Limpiar' },


  'completed.title': { en: 'Completed today', es: 'Completados hoy' },
  'completed.empty': { en: 'No completed calls yet today.', es: 'Aún no hay llamadas completadas hoy.' },
  'completed.viewAll': { en: 'View all', es: 'Ver todas' },
  'completed.client': { en: 'Client', es: 'Cliente' },
  'completed.duration': { en: 'Duration', es: 'Duración' },
  'completed.bucket': { en: 'Bucket', es: 'Categoría' },
  'completed.submittedAt': { en: 'Submitted', es: 'Enviado' },
  'completed.label.under_8': { en: '<8m', es: '<8m' },
  'completed.label.8_to_22': { en: '8-22m', es: '8-22m' },
  'completed.label.22_to_37': { en: '22-37m', es: '22-37m' },
  'completed.label.37_plus': { en: '37m+', es: '37m+' },

  'row.resume': { en: 'Resume screening', es: 'Reanudar screening' },
  'row.cancel': { en: 'Cancel', es: 'Cancelar' },
  'row.viewClient': { en: 'View client', es: 'Ver cliente' },
  'row.actions': { en: 'Actions', es: 'Acciones' },


  // ===== Bookings =====
  'bookings.card.title': { en: 'Bookings', es: 'Citas agendadas' },
  'bookings.card.nextToday': { en: 'Next: today {time}', es: 'Próxima: hoy {time}' },
  'bookings.card.nextTomorrow': { en: 'Next: tomorrow {time}', es: 'Próxima: mañana {time}' },
  'bookings.card.nextDate': { en: 'Next: {date} {time}', es: 'Próxima: {date} {time}' },
  'bookings.card.none': { en: 'No bookings scheduled', es: 'Sin citas agendadas' },
  'bookings.modal.title': { en: 'Bookings', es: 'Citas agendadas' },
  'bookings.modal.subtitle': { en: 'Manage your scheduled appointments', es: 'Gestiona tus citas agendadas' },
  'bookings.search.placeholder': { en: 'Search by last name or MRN', es: 'Buscar por apellido o MRN' },
  'bookings.search.clear': { en: 'Clear search', es: 'Limpiar búsqueda' },
  'bookings.search.empty': { en: "No bookings match '{query}'", es: "No se encontraron citas para '{query}'" },
  'bookings.tab.today': { en: 'Today', es: 'Hoy' },
  'bookings.tab.week': { en: 'This week', es: 'Esta semana' },
  'bookings.tab.upcoming': { en: 'Upcoming', es: 'Próximas' },
  'bookings.tab.past': { en: 'Past', es: 'Pasadas' },
  'bookings.tab.all': { en: 'All', es: 'Todas' },
  'bookings.status.scheduled': { en: 'Scheduled', es: 'Agendada' },
  'bookings.status.completed': { en: 'Completed', es: 'Completada' },
  'bookings.status.missed': { en: 'Missed', es: 'No presentado' },
  'bookings.status.rescheduled': { en: 'Rescheduled', es: 'Reagendada' },
  'bookings.status.cancelled': { en: 'Cancelled', es: 'Cancelada' },
  'bookings.empty': { en: 'No bookings in this period.', es: 'Sin citas en este período.' },
  // Inline "+ New booking" creator inside the bookings list modal (2026-05-09).
  'bookings.modal.create.button': { en: 'New booking', es: 'Nueva cita' },
  'bookings.modal.create.heading': { en: 'New booking', es: 'Nueva cita' },
  'bookings.modal.create.subtitle': { en: 'Schedule an appointment for a patient you have access to.', es: 'Agendá una cita para un paciente al que tenés acceso.' },
  'bookings.modal.create.patientLabel': { en: 'Patient', es: 'Paciente' },
  'bookings.modal.create.patientSearchPlaceholder': { en: 'Search by last name or MRN...', es: 'Buscar por apellido o MRN...' },
  'bookings.modal.create.patientSelected': { en: 'Selected patient', es: 'Paciente seleccionado' },
  'bookings.modal.create.patientChange': { en: 'Change', es: 'Cambiar' },
  'bookings.modal.create.dateLabel': { en: 'Date', es: 'Fecha' },
  'bookings.modal.create.timeLabel': { en: 'Time', es: 'Hora' },
  'bookings.modal.create.notesLabel': { en: 'Notes (optional)', es: 'Notas (opcional)' },
  'bookings.modal.create.notesPlaceholder': { en: 'Optional notes for this appointment', es: 'Notas opcionales para esta cita' },
  'bookings.modal.create.confirm': { en: 'Create booking', es: 'Crear cita' },
  'bookings.modal.create.cancel': { en: 'Cancel', es: 'Cancelar' },
  'bookings.modal.create.successToast': { en: 'Booking created', es: 'Cita creada' },
  'bookings.modal.create.searchEmpty': { en: 'No matching patients', es: 'No se encontraron pacientes' },
  'bookings.modal.create.searchHint': { en: 'Type at least 2 characters to search', es: 'Escribí al menos 2 caracteres para buscar' },
  'bookings.modal.create.errorRequired': { en: 'Pick a patient, date and time.', es: 'Elegí un paciente, una fecha y una hora.' },
  'bookings.modal.create.errorPast': { en: 'Date and time must be in the future.', es: 'La fecha y hora deben ser en el futuro.' },
  'bookings.modal.create.dedupConflict': { en: 'A booking already exists for this patient close to this time.', es: 'Ya existe una cita para este paciente cerca de este horario.' },
  'bookings.modal.create.timeConflict': { en: 'You already have another booking at this time.', es: 'Ya tenés otra cita a esta hora.' },
  // Screening detail — humanized answer view (2026-05-09).
  'screening.detail.answer.positive': { en: 'Positive', es: 'Positivo' },
  'screening.detail.answer.notes': { en: 'Agent note', es: 'Nota del agente' },
  // Admin screening detail modal (2026-05-09).
  'admin.screening.detail.title': { en: 'Screening detail', es: 'Detalle del screening' },
  'admin.screening.detail.close': { en: 'Close', es: 'Cerrar' },
  'admin.agent.recent.openDetail': { en: 'Open screening detail', es: 'Abrir detalle del screening' },
  'bookings.outcome.book': { en: 'Book appointment', es: 'Agendar cita' },
  'bookings.outcome.confirm': { en: 'Confirm booking', es: 'Confirmar cita' },
  'bookings.outcome.back': { en: 'Back', es: 'Atrás' },
  'bookings.outcome.date': { en: 'Date', es: 'Fecha' },
  'bookings.outcome.time': { en: 'Time', es: 'Hora' },
  'bookings.outcome.notes': { en: 'Notes (optional)', es: 'Notas (opcional)' },
  'bookings.outcome.charCounter': { en: '{count}/500', es: '{count}/500' },
  'bookings.outcome.errorPast': { en: 'Appointment must be in the future', es: 'La cita debe ser en el futuro' },
  'bookings.outcome.successToast': { en: 'Booked for {date} {time}', es: 'Agendada para {date} {time}' },
  'bookings.duplicate.banner.title': { en: "Couldn't book at this time", es: 'No se pudo agendar a esta hora' },
  'bookings.duplicate.banner.message': { en: 'A booking already exists for this patient at this exact time. Please choose a different date or time.', es: 'Ya existe una cita agendada para este paciente a esta misma hora. Por favor elegí otra fecha u hora.' },
  'bookings.duplicate.banner.dismiss': { en: 'Dismiss', es: 'Cerrar' },
  'bookings.agentConflict.banner.title': { en: "Couldn't book at this time", es: 'No se puede agendar a esa hora' },
  'bookings.agentConflict.banner.message': { en: 'You already have another appointment scheduled at this time. Please book this patient at a different time.', es: 'Ya tenés otra cita agendada a esa hora. Por favor agendá este paciente en otro horario.' },
  'bookings.prevalidate.duplicate': { en: 'A booking already exists for this patient at that time', es: 'Ya existe una cita para este paciente a esa hora' },
  'bookings.prevalidate.agent': { en: 'You already have another booking at that time', es: 'Ya tenés otra cita a esa hora' },
  'bookings.edit.title': { en: 'Edit booking', es: 'Editar cita' },
  'bookings.edit.save': { en: 'Save changes', es: 'Guardar cambios' },
  'bookings.edit.markCompleted': { en: 'Mark as completed', es: 'Marcar como completada' },
  'bookings.edit.markMissed': { en: 'Mark as missed', es: 'Marcar como no presentado' },
  'bookings.edit.markCancelled': { en: 'Mark as cancelled', es: 'Marcar como cancelada' },
  'bookings.edit.delete': { en: 'Delete booking', es: 'Eliminar cita' },

  // ===== Bookings detail panel (Wave 5) =====
  'bookings.detail.patientLabel': { en: 'Patient', es: 'Paciente' },
  'bookings.detail.scheduledLabel': { en: 'Scheduled for', es: 'Hora agendada' },
  'bookings.detail.statusLabel': { en: 'Status', es: 'Estado' },
  'bookings.detail.notesLabel': { en: 'Notes', es: 'Notas' },
  'bookings.detail.notesEmpty': { en: 'No notes attached', es: 'Sin notas' },
  'bookings.detail.notes.edit': { en: 'Edit', es: 'Editar' },
  'bookings.detail.notes.save': { en: 'Save', es: 'Guardar' },
  'bookings.detail.notes.cancel': { en: 'Cancel', es: 'Cancelar' },
  'bookings.detail.notes.savedToast': { en: 'Notes updated', es: 'Notas actualizadas' },
  'bookings.detail.created': { en: 'Created by', es: 'Creado por' },
  'bookings.detail.lastUpdated': { en: 'Last updated', es: 'Última actualización' },
  'bookings.detail.bookingId': { en: 'Booking ID', es: 'ID de cita' },
  'bookings.detail.relative.now': { en: 'now', es: 'ahora' },
  'bookings.detail.relative.minAgo': { en: '{n} min ago', es: 'hace {n} min' },
  'bookings.detail.relative.minIn': { en: 'in {n} min', es: 'en {n} min' },
  'bookings.detail.relative.hourAgo': { en: '{n}h ago', es: 'hace {n} h' },
  'bookings.detail.relative.hourIn': { en: 'in {n}h', es: 'en {n} h' },
  'bookings.detail.relative.dayAgo': { en: '{n}d ago', es: 'hace {n} d' },
  'bookings.detail.relative.dayIn': { en: 'in {n}d', es: 'en {n} d' },
  'bookings.detail.missedAuto': { en: 'Marked as no-show automatically — agent did not call within 15 min.', es: 'Marcado como no presentado automáticamente — no se llamó en 15 min.' },
  'bookings.detail.action.start': { en: 'Start screening', es: 'Iniciar screening' },
  'bookings.detail.action.startToast.error.finalized': { en: 'This booking is no longer active and cannot be started.', es: 'Esta cita ya no está activa y no se puede iniciar.' },
  'bookings.detail.action.startToast.error.assignedOther': { en: 'Patient is currently being attended by another agent. Please contact your supervisor.', es: 'El paciente ya está siendo atendido por otro agente. Por favor consultá con tu supervisor.' },
  'bookings.detail.action.startToast.error.generic': { en: "Couldn't start screening. Please try again.", es: 'No se pudo iniciar el screening. Intentá de nuevo.' },
  'bookings.detail.action.reschedule': { en: 'Reschedule', es: 'Reagendar' },
  'bookings.detail.action.markCompleted': { en: 'Completed', es: 'Completada' },
  'bookings.detail.action.markMissed': { en: 'No-show', es: 'No presentada' },
  'bookings.detail.action.markCancelled': { en: 'Cancel booking', es: 'Cancelar cita' },
  'bookings.detail.action.close': { en: 'Close', es: 'Cerrar' },
  'bookings.detail.reschedule.title': { en: 'Reschedule appointment', es: 'Reagendar cita' },
  'bookings.detail.reschedule.dateLabel': { en: 'New date', es: 'Nueva fecha' },
  'bookings.detail.reschedule.timeLabel': { en: 'New time', es: 'Nueva hora' },
  'bookings.detail.reschedule.notesLabel': { en: 'Notes (optional)', es: 'Notas (opcional)' },
  'bookings.detail.reschedule.errorRequired': { en: 'Date and time are required', es: 'La fecha y la hora son obligatorias' },
  'bookings.detail.reschedule.errorPast': { en: 'Appointment must be in the future', es: 'La cita debe ser en el futuro' },
  'bookings.detail.agentYou': { en: 'you', es: 'vos' },
  'bookings.detail.agentNumber': { en: 'agent #{id}', es: 'agente #{id}' },

  // Bookings — confirm dialog (terminal actions)
  'bookings.confirm.cancel.title': { en: 'Cancel this booking?', es: '¿Cancelar esta cita?' },
  'bookings.confirm.cancel.message': { en: "This booking will be marked as cancelled and will no longer count as upcoming. Continue?", es: "Esta cita pasará a estado 'Cancelada' y dejará de contar como próxima. ¿Querés continuar?" },
  'bookings.confirm.noShow.title': { en: 'Mark as no-show?', es: '¿Marcar como no presentada?' },
  'bookings.confirm.noShow.message': { en: 'This logs that the patient was not reached or did not show up at the scheduled time.', es: 'Esto deja registro de que el paciente no se presentó / no se llamó a tiempo.' },
  'bookings.confirm.completed.title': { en: 'Mark as completed?', es: '¿Marcar como completada?' },
  'bookings.confirm.completed.message': { en: "Confirm that you've already completed the call with this patient.", es: 'Confirmás que la llamada con el paciente ya se realizó.' },
  'bookings.confirm.confirmCta': { en: 'Confirm', es: 'Confirmar' },
  'bookings.confirm.cancelCta': { en: 'Back', es: 'Volver' },

  // ===== Bookings Wave 3 =====
  'bookings.tag.soon': { en: 'Booking soon', es: 'Cita pronto' },
  'bookings.tag.now': { en: 'Booking now', es: 'Cita ahora' },
  'bookings.tag.overdue': { en: 'Overdue', es: 'Vencida' },
  'bookings.tag.missed': { en: 'Missed', es: 'Perdida' },
  'bookings.alert.title': { en: "It's time to call", es: 'Es hora de llamar' },
  'bookings.alert.subtitle': { en: 'Booking scheduled for {time}', es: 'Cita agendada para las {time}' },

  // ===== Bookings Wave 4 (Missed Bookings) =====
  'bookings.missed.menu': { en: 'Missed bookings', es: 'Citas perdidas' },
  'bookings.missed.title': { en: 'Missed bookings', es: 'Citas perdidas' },
  'bookings.missed.subtitle': { en: 'Bookings the agent did not call within 15 minutes', es: 'Citas que el agente no llamó dentro de los 15 minutos' },
  'bookings.missed.tab.week': { en: 'This week', es: 'Esta semana' },
  'bookings.missed.tab.month': { en: 'This month', es: 'Este mes' },
  'bookings.missed.tab.all': { en: 'All', es: 'Todas' },
  'bookings.missed.col.patient': { en: 'Patient', es: 'Paciente' },
  'bookings.missed.col.agent': { en: 'Agent', es: 'Agente' },
  'bookings.missed.col.scheduled': { en: 'Scheduled at', es: 'Agendada el' },
  'bookings.missed.col.missedAt': { en: 'Marked missed', es: 'Marcada perdida' },
  'bookings.missed.col.actions': { en: 'Actions', es: 'Acciones' },
  'bookings.missed.action.reassign': { en: 'Reassign to me', es: 'Reasignarme' },
  'bookings.missed.action.markComplete': { en: 'Mark as completed', es: 'Marcar como completada' },
  'bookings.missed.action.reschedule': { en: 'Reschedule', es: 'Reagendar' },
  'bookings.missed.empty': { en: 'No missed bookings — nice work', es: 'Sin citas perdidas — buen trabajo' },

  // ===== Notifications =====
  'notifications.title': { en: 'Notifications', es: 'Notificaciones' },
  'notifications.markAllRead': { en: 'Mark all as read', es: 'Marcar todas como leídas' },
  'notifications.empty': { en: 'No new notifications', es: 'Sin notificaciones nuevas' },
  'notifications.type.missedBooking.title': { en: 'Booking missed', es: 'Cita perdida' },
  'notifications.type.missedBooking.subtitle': { en: '{patientName} (agent: {agentName})', es: '{patientName} (agente: {agentName})' },

  // Bug resolved notifications
  'notifications.bugResolved.title.resolved': { en: 'Your bug report was resolved', es: 'Tu reporte fue resuelto' },
  'notifications.bugResolved.title.wontfix': { en: "Bug report closed: won't be fixed", es: 'Reporte cerrado: no se corregirá' },
  'notifications.bugResolved.subtitle': { en: 'Re: {message}', es: 'Re: {message}' },
  'notifications.bugResolved.severityLabel': { en: 'Severity', es: 'Severidad' },
  'notifications.bugResolved.statusLabel': { en: 'Status', es: 'Estado' },
  'notifications.bugResolved.resolutionLabel': { en: 'Resolution note', es: 'Nota de resolución' },
  'notifications.bugResolved.resolvedByLabel': { en: 'Resolved by', es: 'Resuelto por' },
  'notifications.bugResolved.expandShow': { en: 'Show details', es: 'Ver detalles' },
  'notifications.bugResolved.expandHide': { en: 'Hide details', es: 'Ocultar detalles' },
  'notifications.bugResolved.noNote': { en: 'No resolution note provided', es: 'Sin nota de resolución' },
  'notifications.bugResolved.severity.low': { en: 'Low', es: 'Baja' },
  'notifications.bugResolved.severity.medium': { en: 'Medium', es: 'Media' },
  'notifications.bugResolved.severity.high': { en: 'High', es: 'Alta' },
  'notifications.bugResolved.severity.blocking': { en: 'Blocking', es: 'Bloqueante' },
  'notifications.bugResolved.status.resolved': { en: 'Resolved', es: 'Resuelto' },
  'notifications.bugResolved.status.wontfix': { en: "Won't fix", es: 'No se corregirá' },

  // Chat message notifications
  'notifications.chat.title': { en: 'New message from {name}', es: 'Nuevo mensaje de {name}' },
  'notifications.chat.threadLabel': { en: 'Thread', es: 'Conversación' },
  'notifications.chat.expandShow': { en: 'Show preview', es: 'Ver vista previa' },
  'notifications.chat.expandHide': { en: 'Hide preview', es: 'Ocultar vista previa' },
  'notifications.chat.goToChat': { en: 'Open chat', es: 'Abrir chat' },

  // Toast (universal new-notification pop-up)
  'notifications.toast.genericTitle': { en: 'New notification', es: 'Nueva notificación' },
  'notifications.toast.missedBooking.title': { en: 'Booking missed: {patient}', es: 'Cita perdida: {patient}' },
  'notifications.toast.bugResolved.resolved': { en: 'Your bug report was resolved', es: 'Tu reporte fue resuelto' },
  'notifications.toast.bugResolved.wontfix': { en: "Bug report closed: won't be fixed", es: 'Reporte cerrado: no se corregirá' },
  'notifications.toast.chatMessage.title': { en: 'New message', es: 'Mensaje nuevo' },

  // Bug report — submission history (in BugReportButton modal)
  'bugReport.history.title': { en: 'My submissions', es: 'Mis reportes' },
  'bugReport.history.empty': { en: "You haven't reported any bugs yet", es: 'Aún no reportaste ningún bug' },
  'bugReport.history.loadMore': { en: 'Load more', es: 'Ver más' },
  'bugReport.history.col.created': { en: 'Submitted', es: 'Enviado' },
  'bugReport.history.col.message': { en: 'Message', es: 'Mensaje' },
  'bugReport.history.col.severity': { en: 'Severity', es: 'Severidad' },
  'bugReport.history.col.status': { en: 'Status', es: 'Estado' },
  'bugReport.history.col.actions': { en: 'Actions', es: 'Acciones' },
  'bugReport.history.expand.show': { en: 'Show details', es: 'Ver detalles' },
  'bugReport.history.expand.hide': { en: 'Hide details', es: 'Ocultar detalles' },
  'bugReport.history.expand.message': { en: 'Full message', es: 'Mensaje completo' },
  'bugReport.history.expand.page': { en: 'Page reported', es: 'Página reportada' },
  'bugReport.history.expand.resolution': { en: 'Resolution note', es: 'Nota de resolución' },
  'bugReport.history.expand.resolvedBy': { en: 'Resolved by', es: 'Resuelto por' },
  'bugReport.history.expand.resolvedAt': { en: 'Resolved at', es: 'Resuelto el' },
  'bugReport.history.expand.noResolution': { en: 'No resolution note yet', es: 'Aún sin nota de resolución' },
  'bugReport.history.status.open': { en: 'Open', es: 'Abierto' },
  'bugReport.history.status.triaged': { en: 'Triaged', es: 'Clasificado' },
  'bugReport.history.status.resolved': { en: 'Resolved', es: 'Resuelto' },
  'bugReport.history.status.wontfix': { en: "Won't fix", es: 'No se corregirá' },

  // ===== Admin agent stats panel (A1) =====
  'admin.agent.stats.title': { en: 'Performance', es: 'Estadísticas' },
  'admin.agent.stats.window.today': { en: 'Today', es: 'Hoy' },
  'admin.agent.stats.window.week': { en: 'This week', es: 'Esta semana' },
  'admin.agent.stats.window.month': { en: 'This month', es: 'Este mes' },
  'admin.agent.stats.window.total': { en: 'All time', es: 'Total' },
  'admin.agent.stats.metric.screenings': { en: 'screenings', es: 'screenings' },
  'admin.agent.stats.metric.attempts': { en: '{n} attempts', es: '{n} intentos' },
  'admin.agent.stats.metric.missed': { en: '{n} missed', es: '{n} perdidas' },
  'admin.agent.stats.metric.avgCall': { en: 'avg call {dur}', es: 'llamada prom. {dur}' },
  'admin.agent.stats.metric.callTime': { en: 'Call time {dur}', es: 'Tiempo en llamada {dur}' },
  'admin.agent.stats.metric.callTimeTooltip': { en: 'Cumulative time on completed (submitted) screenings.', es: 'Tiempo acumulado en screenings completados (enviados).' },
  'admin.agent.stats.metric.activeWork': { en: 'Active work {dur}', es: 'Tiempo activo {dur}' },
  'admin.agent.stats.metric.activeWorkTooltip': { en: 'Cumulative time the agent was on a claim — completed screenings + abandoned attempts.', es: 'Tiempo acumulado del agente en una asignación — screenings completados + intentos descartados.' },
  'admin.agent.stats.metric.callTimeRatio': { en: '{pct}% on completed calls', es: '{pct}% en llamadas completadas' },
  'admin.agent.stats.now.label': { en: 'Currently', es: 'Estado ahora' },
  'admin.agent.stats.now.online': { en: 'Online', es: 'En línea' },
  'admin.agent.stats.now.idle': { en: 'Idle', es: 'Inactivo' },
  'admin.agent.stats.now.on_call': { en: 'On call', es: 'En llamada' },
  'admin.agent.stats.now.offline': { en: 'Offline', es: 'Desconectado' },
  'admin.agent.stats.now.lastSeen': { en: 'Last seen {when}', es: 'Visto {when}' },
  'admin.agent.stats.now.lastSeenNever': { en: 'Never seen', es: 'Sin actividad' },
  'admin.agent.stats.now.currentAssignment': { en: 'On assignment #{id}', es: 'En asignación #{id}' },
  'admin.agent.stats.error': { en: 'Could not load stats: {msg}', es: 'No se pudieron cargar estadísticas: {msg}' },
  'admin.agent.stats.recent.title': { en: 'Recent screenings (with notes)', es: 'Screenings recientes (con notas)' },
  'admin.agent.stats.recent.empty': { en: 'No recent screenings.', es: 'Sin screenings recientes.' },

  // ===== Notes (cross-role) =====
  'notes.label': { en: 'Notes', es: 'Notas' },
  'notes.empty': { en: 'No notes', es: 'Sin notas' },
  'screening.recentSubmissions.title': { en: 'My recent submissions', es: 'Mis envíos recientes' },
  'screening.recentSubmissions.empty': { en: 'No submissions today', es: 'Sin envíos hoy' },
  'screening.recentSubmissions.viewAll': { en: 'View all', es: 'Ver todos' },

  // Compact 'My submissions today' strip on the agent QueuePage (2026-05-09).
  'agent.submissions.compactTitle': { en: 'My submissions today', es: 'Mis envíos de hoy' },
  'agent.submissions.compactEmpty': { en: 'No submissions yet today', es: 'Sin envíos aún hoy' },
  'agent.submissions.viewAll': { en: 'View all', es: 'Ver todos' },
  'agent.submissions.openDetail': { en: 'Open detail', es: 'Ver detalle' },
  'agent.submissions.notesCount': { en: '{n} notes', es: '{n} notas' },
  'agent.submissions.statusUploaded': { en: 'Approved', es: 'Aprobado' },
  'agent.submissions.statusPending': { en: 'Pending', es: 'Pendiente' },
  'agent.submissions.statusFlagged': { en: 'Rejected', es: 'Rechazado' },
  'agent.submissions.statusNoMedicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },

  // Collapsible-section labels in UploaderScreeningDetailModal (2026-05-09).
  'screening.detail.section.patient': { en: 'Patient details', es: 'Detalles del paciente' },
  'screening.detail.section.timeline': { en: 'Timeline', es: 'Cronología' },
  'screening.detail.section.answers': { en: 'HRSN answers', es: 'Respuestas HRSN' },
  'screening.detail.section.notes': { en: 'Notes', es: 'Notas' },
  'screening.detail.section.findhelp': { en: 'FindHelp upload', es: 'Carga FindHelp' },

  // Polished detail modal additions (2026-05-09).
  'screening.detail.section.empty.notes': { en: 'No notes captured for this screening', es: 'Sin notas capturadas para este screening' },
  'screening.detail.note.capturedBy': { en: 'Captured by {agent} on {date}', es: 'Capturado por {agent} el {date}' },
  'screening.detail.findings.positive': { en: 'Positive finding', es: 'Hallazgo positivo' },
  'screening.detail.timeline.started': { en: 'Started by {agent} on {date}', es: 'Iniciado por {agent} el {date}' },
  'screening.detail.timeline.submitted': { en: 'Submitted on {date}', es: 'Enviado el {date}' },
  'screening.detail.timeline.uploaded': { en: 'Uploaded to FindHelp by {uploader} on {date}', es: 'Subido a FindHelp por {uploader} el {date}' },
  'screening.detail.timeline.flagged': { en: 'Flagged: {error}', es: 'Marcado: {error}' },
  'screening.detail.findhelp.uploaded': { en: 'Uploaded to FindHelp', es: 'Subido a FindHelp' },
  'screening.detail.findhelp.pending': { en: 'Pending upload', es: 'Pendiente de carga' },
  'screening.detail.findhelp.flagged': { en: 'Flagged: {error}', es: 'Marcado: {error}' },
  'screening.detail.findhelp.noMedicaid': { en: 'No Medicaid on file', es: 'Sin Medicaid registrado' },

  // ===== Admin audit feed (A2) =====
  'admin.audit.nav': { en: 'Audit', es: 'Auditoría' },
  'admin.audit.title': { en: 'Activity log', es: 'Registro de actividad' },
  'admin.audit.subtitle': { en: 'Every login, every PHI access, every batch import. Append-only at the database level.', es: 'Cada inicio de sesión, cada acceso a PHI, cada importación de lote. Solo añadir, a nivel de base de datos.' },
  'admin.audit.refresh': { en: 'Refresh', es: 'Actualizar' },
  'admin.audit.exportCsv': { en: 'Export CSV', es: 'Exportar CSV' },
  'admin.audit.filter.action': { en: 'Action', es: 'Acción' },
  'admin.audit.filter.actionAll': { en: 'All actions', es: 'Todas las acciones' },
  'admin.audit.filter.user': { en: 'User search', es: 'Buscar usuario' },
  'admin.audit.filter.userPlaceholder': { en: 'Name or email', es: 'Nombre o correo' },
  'admin.audit.filter.from': { en: 'From', es: 'Desde' },
  'admin.audit.filter.to': { en: 'To', es: 'Hasta' },
  'admin.audit.filter.apply': { en: 'Apply filters', es: 'Aplicar filtros' },
  'admin.audit.filter.clear': { en: 'Clear', es: 'Limpiar' },
  'admin.audit.col.time': { en: 'Time', es: 'Hora' },
  'admin.audit.col.user': { en: 'User', es: 'Usuario' },
  'admin.audit.col.action': { en: 'Action', es: 'Acción' },
  'admin.audit.col.entity': { en: 'Entity', es: 'Entidad' },
  'admin.audit.col.ip': { en: 'IP', es: 'IP' },
  'admin.audit.row.systemAnonymous': { en: 'system / anonymous', es: 'sistema / anónimo' },
  'admin.audit.row.detailsShow': { en: 'Details', es: 'Detalles' },
  'admin.audit.row.detailsHide': { en: 'Hide', es: 'Ocultar' },
  'admin.audit.empty.noFilters': { en: 'No audit entries yet', es: 'Aún no hay registros' },
  'admin.audit.empty.noFiltersDesc': { en: 'Activity will appear here as users sign in and access PHI.', es: 'La actividad aparecerá aquí cuando los usuarios inicien sesión y accedan a PHI.' },
  'admin.audit.empty.matches': { en: 'No matches', es: 'Sin coincidencias' },
  'admin.audit.empty.matchesDesc': { en: 'Try widening the date range or clearing the filters.', es: 'Pruebe ampliar el rango de fechas o limpiar los filtros.' },
  'admin.audit.error.title': { en: "Couldn't load audit log", es: 'No se pudo cargar el registro de auditoría' },
  'admin.audit.pagination.page': { en: 'Page {page} of {total} · {entries} entries', es: 'Página {page} de {total} · {entries} entradas' },
  'admin.audit.pagination.previous': { en: 'Previous', es: 'Anterior' },
  'admin.audit.pagination.next': { en: 'Next', es: 'Siguiente' },

  // ===== Admin / My profile (A3 robustness) =====
  'admin.profile.nav': { en: 'My profile', es: 'Mi perfil' },
  'admin.profile.title': { en: 'My profile', es: 'Mi perfil' },
  'admin.profile.subtitle': { en: 'Update your name, email, and password.', es: 'Actualice su nombre, correo y contraseña.' },
  'admin.profile.cardTitle': { en: 'Profile', es: 'Perfil' },
  'admin.profile.cardDesc': { en: 'These are visible to other admins on the team page.', es: 'Esto es visible para otros administradores en la página del equipo.' },
  'admin.profile.fullName': { en: 'Full name', es: 'Nombre completo' },
  'admin.profile.email': { en: 'Email', es: 'Correo electrónico' },
  'admin.profile.role': { en: 'Role', es: 'Rol' },
  'admin.profile.save': { en: 'Save changes', es: 'Guardar cambios' },
  'admin.profile.saving': { en: 'Saving…', es: 'Guardando…' },
  'admin.profile.saved': { en: 'Profile updated', es: 'Perfil actualizado' },
  'admin.profile.savedDesc': { en: 'Your changes are live.', es: 'Sus cambios están en vivo.' },
  'admin.profile.error': { en: 'Could not save profile', es: 'No se pudo guardar el perfil' },
  'admin.profile.emailInUse': { en: 'That email is already used by another account.', es: 'Ese correo ya está en uso por otra cuenta.' },
  'admin.profile.fullNameRequired': { en: 'Full name is required (1-100 characters).', es: 'El nombre completo es obligatorio (1-100 caracteres).' },
  'admin.profile.emailInvalid': { en: 'Enter a valid email address.', es: 'Ingrese un correo válido.' },

  'admin.password.cardTitle': { en: 'Change password', es: 'Cambiar contraseña' },
  'admin.password.cardDesc': { en: 'You will stay signed in after a successful change.', es: 'Permanecerá conectado tras un cambio exitoso.' },
  'admin.password.current': { en: 'Current password', es: 'Contraseña actual' },
  'admin.password.new': { en: 'New password', es: 'Contraseña nueva' },
  'admin.password.confirm': { en: 'Confirm new password', es: 'Confirmar contraseña nueva' },
  'admin.password.policy': { en: 'At least 8 characters, with one uppercase, one lowercase, and one digit.', es: 'Al menos 8 caracteres, con una mayúscula, una minúscula y un dígito.' },
  'admin.password.save': { en: 'Update password', es: 'Actualizar contraseña' },
  'admin.password.saving': { en: 'Updating…', es: 'Actualizando…' },
  'admin.password.saved': { en: 'Password updated', es: 'Contraseña actualizada' },
  'admin.password.savedDesc': { en: 'Use the new password next time you sign in.', es: 'Use la contraseña nueva la próxima vez que inicie sesión.' },
  'admin.password.mismatch': { en: 'Passwords do not match.', es: 'Las contraseñas no coinciden.' },
  'admin.password.policyFail': { en: 'Password does not meet the policy.', es: 'La contraseña no cumple la política.' },
  'admin.password.wrongCurrent': { en: 'Current password is incorrect.', es: 'La contraseña actual es incorrecta.' },
  'admin.password.error': { en: 'Could not change password', es: 'No se pudo cambiar la contraseña' },

  // ===== Admin / Settings (A4) =====
  'admin.settings.nav': { en: 'Settings', es: 'Configuración' },
  'admin.settings.title': { en: 'System settings', es: 'Configuración del sistema' },
  'admin.settings.subtitle': { en: 'Tunable values that govern screening targets, lockouts, and tier thresholds.', es: 'Valores ajustables que rigen objetivos de screening, bloqueos y umbrales de nivel.' },
  'admin.settings.refresh': { en: 'Refresh', es: 'Actualizar' },
  'admin.settings.empty': { en: 'No configuration rows', es: 'Sin filas de configuración' },
  'admin.settings.error': { en: "Couldn't load settings", es: 'No se pudo cargar la configuración' },
  'admin.settings.readonly': { en: 'Read-only', es: 'Solo lectura' },
  'admin.settings.readonlyTip': { en: 'This setting cannot be edited from the UI to prevent self-lockout.', es: 'Esta configuración no se puede editar desde la interfaz para evitar bloqueo propio.' },
  'admin.settings.unknownTag': { en: 'Custom', es: 'Personalizado' },
  'admin.settings.unknownDesc': { en: 'Custom or unrecognized key. Edit only if you know what it does.', es: 'Clave personalizada o no reconocida. Edite solo si sabe qué hace.' },
  'admin.settings.edit': { en: 'Edit', es: 'Editar' },
  'admin.settings.save': { en: 'Save', es: 'Guardar' },
  'admin.settings.cancel': { en: 'Cancel', es: 'Cancelar' },
  'admin.settings.saving': { en: 'Saving…', es: 'Guardando…' },
  'admin.settings.saved': { en: 'Setting updated', es: 'Configuración actualizada' },
  'admin.settings.savedDesc': { en: 'Change is live.', es: 'El cambio está en vivo.' },
  'admin.settings.invalidNumber': { en: 'Enter a valid number.', es: 'Ingrese un número válido.' },
  'admin.settings.invalidJson': { en: 'Enter valid JSON.', es: 'Ingrese JSON válido.' },
  'admin.settings.lastUpdated': { en: 'Last updated', es: 'Última actualización' },
  'admin.settings.never': { en: 'Never', es: 'Nunca' },
  'admin.settings.label.session_timeout_min': { en: 'Session timeout (minutes)', es: 'Tiempo de sesión (minutos)' },
  'admin.settings.label.daily_screening_target': { en: 'Daily screening target', es: 'Meta diaria de screening' },
  'admin.settings.label.weekly_thresholds': { en: 'Weekly tier thresholds', es: 'Umbrales semanales por nivel' },
  'admin.settings.label.approval_thresholds': { en: 'Approval-rate tier thresholds (%)', es: 'Umbrales de aprobación por nivel (%)' },
  'admin.settings.label.min_call_duration_sec': { en: 'Minimum call duration (sec)', es: 'Duración mínima de llamada (seg)' },
  'admin.settings.label.max_call_attempts': { en: 'Max call attempts', es: 'Máx. intentos de llamada' },
  'admin.settings.label.rescreening_blackout_days': { en: 'Re-screening blackout (days)', es: 'Bloqueo de re-screening (días)' },
  'admin.settings.label.eligible_zip_prefix': { en: 'Eligible ZIP prefix', es: 'Prefijo de ZIP elegible' },
  'admin.settings.label.phi_retention_years': { en: 'PHI retention (years)', es: 'Retención de PHI (años)' },
  'admin.settings.label.audit_retention_years': { en: 'Audit retention (years)', es: 'Retención de auditoría (años)' },
  'admin.settings.label.heartbeat_interval_sec': { en: 'Heartbeat interval (sec)', es: 'Intervalo de heartbeat (seg)' },
  'admin.settings.label.idle_threshold_sec': { en: 'Idle threshold (sec)', es: 'Umbral de inactividad (seg)' },
  'admin.settings.label.safety_score_alert_threshold': { en: 'Safety alert score threshold', es: 'Umbral de alerta de Safety' },
  'admin.settings.label.lockout_after_failed_logins': { en: 'Lockout after failed logins', es: 'Bloqueo tras inicios fallidos' },
  'admin.settings.label.lockout_duration_min': { en: 'Lockout duration (minutes)', es: 'Duración de bloqueo (minutos)' },
  'admin.settings.label.privacy_officer_user_id': { en: 'Privacy officer user ID', es: 'ID del oficial de privacidad' },
  'admin.settings.label.mfa_required_roles': { en: 'MFA required for roles', es: 'MFA obligatorio para roles' },


  // ===== Admin / Bug reports =====
  'admin.bugReports.nav': { en: 'Bug reports', es: 'Reportes' },
  'admin.bugReports.title': { en: 'Bug reports', es: 'Reportes de errores' },
  'admin.bugReports.subtitle': { en: 'Issues submitted by agents and supervisors. Track, triage, and resolve.', es: 'Problemas reportados por agentes y supervisores. Rastrear, clasificar y resolver.' },
  'admin.bugReports.refresh': { en: 'Refresh', es: 'Actualizar' },
  'admin.bugReports.totalCount': { en: '{total} total · {open} unresolved', es: '{total} en total · {open} sin resolver' },
  'admin.bugReports.filter.status': { en: 'Status', es: 'Estado' },
  'admin.bugReports.filter.statusAll': { en: 'All statuses', es: 'Todos los estados' },
  'admin.bugReports.filter.severity': { en: 'Severity', es: 'Severidad' },
  'admin.bugReports.filter.severityAll': { en: 'All severities', es: 'Todas las severidades' },
  'admin.bugReports.filter.reporter': { en: 'Reporter', es: 'Reportado por' },
  'admin.bugReports.filter.reporterAll': { en: 'All reporters', es: 'Todos' },
  'admin.bugReports.filter.from': { en: 'From', es: 'Desde' },
  'admin.bugReports.filter.to': { en: 'To', es: 'Hasta' },
  'admin.bugReports.filter.apply': { en: 'Apply filters', es: 'Aplicar filtros' },
  'admin.bugReports.filter.clear': { en: 'Clear', es: 'Limpiar' },
  'admin.bugReports.col.created': { en: 'Created', es: 'Creado' },
  'admin.bugReports.col.reporter': { en: 'Reporter', es: 'Reportado por' },
  'admin.bugReports.col.title': { en: 'Report', es: 'Reporte' },
  'admin.bugReports.col.severity': { en: 'Severity', es: 'Severidad' },
  'admin.bugReports.col.status': { en: 'Status', es: 'Estado' },
  'admin.bugReports.col.page': { en: 'Page', es: 'Página' },
  'admin.bugReports.col.actions': { en: 'Actions', es: 'Acciones' },
  'admin.bugReports.action.view': { en: 'View', es: 'Ver' },
  'admin.bugReports.status.open': { en: 'Open', es: 'Abierto' },
  'admin.bugReports.status.triaged': { en: 'In progress', es: 'En curso' },
  'admin.bugReports.status.resolved': { en: 'Resolved', es: 'Resuelto' },
  'admin.bugReports.status.wontfix': { en: 'Won’t fix', es: 'No se corregirá' },
  'admin.bugReports.severity.low': { en: 'Low', es: 'Baja' },
  'admin.bugReports.severity.medium': { en: 'Medium', es: 'Media' },
  'admin.bugReports.severity.high': { en: 'High', es: 'Alta' },
  'admin.bugReports.severity.blocking': { en: 'Blocking', es: 'Bloqueante' },
  'admin.bugReports.empty.title': { en: 'No bug reports', es: 'Sin reportes' },
  'admin.bugReports.empty.subtitle': { en: 'When agents file issues from the Report button, they will appear here.', es: 'Cuando los agentes reporten problemas desde el botón Reportar, aparecerán aquí.' },
  'admin.bugReports.empty.matchesTitle': { en: 'No matches', es: 'Sin coincidencias' },
  'admin.bugReports.empty.matchesDesc': { en: 'Try widening filters or clearing the date range.', es: 'Pruebe ampliar los filtros o limpiar el rango de fechas.' },
  'admin.bugReports.error.title': { en: 'Could not load bug reports', es: 'No se pudieron cargar los reportes' },
  'admin.bugReports.loadMore': { en: 'Load more', es: 'Cargar más' },
  'admin.bugReports.showing': { en: 'Showing {shown} of {total}', es: 'Mostrando {shown} de {total}' },
  'admin.bugReports.detail.title': { en: 'Bug report #{id}', es: 'Reporte #{id}' },
  'admin.bugReports.detail.reporter': { en: 'Reporter', es: 'Reportado por' },
  'admin.bugReports.detail.role': { en: 'Role', es: 'Rol' },
  'admin.bugReports.detail.email': { en: 'Email', es: 'Correo' },
  'admin.bugReports.detail.page': { en: 'Page reported on', es: 'Página del reporte' },
  'admin.bugReports.detail.userAgent': { en: 'Browser / device', es: 'Navegador / dispositivo' },
  'admin.bugReports.detail.description': { en: 'Description', es: 'Descripción' },
  'admin.bugReports.detail.timeline': { en: 'Timeline', es: 'Cronología' },
  'admin.bugReports.detail.created': { en: 'Created', es: 'Creado' },
  'admin.bugReports.detail.resolvedAt': { en: 'Resolved at', es: 'Resuelto el' },
  'admin.bugReports.detail.resolvedBy': { en: 'Resolved by', es: 'Resuelto por' },
  'admin.bugReports.detail.notes': { en: 'Resolution note', es: 'Nota de resolución' },
  'admin.bugReports.detail.notesPlaceholder': { en: 'What was the resolution? (visible to admins/supervisors only)', es: 'En qué consistió la resolución? (visible solo para administradores)' },
  'admin.bugReports.detail.changeStatus': { en: 'Change status', es: 'Cambiar estado' },
  'admin.bugReports.detail.save': { en: 'Save', es: 'Guardar' },
  'admin.bugReports.detail.saving': { en: 'Saving…', es: 'Guardando…' },
  'admin.bugReports.detail.savedToast': { en: 'Bug report updated', es: 'Reporte actualizado' },
  'admin.bugReports.detail.savedToastDesc': { en: 'Changes are live.', es: 'Los cambios están en vivo.' },
  'admin.bugReports.detail.errorToast': { en: 'Could not update bug report', es: 'No se pudo actualizar el reporte' },
  'admin.bugReports.detail.close': { en: 'Close', es: 'Cerrar' },
  'admin.bugReports.detail.noPage': { en: 'Not specified', es: 'Sin especificar' },
  'admin.bugReports.detail.noResolution': { en: 'No resolution note yet.', es: 'Aún sin nota de resolución.' },
  'admin.bugReports.detail.assignment': { en: 'Related assignment', es: 'Asignación relacionada' },
  'admin.bugReports.detail.screening': { en: 'Related screening', es: 'Screening relacionado' },


  // ===== Dashboard / Daily snapshot =====
  'dashboard.title': { en: 'Daily snapshot', es: 'Resumen del día' },
  'dashboard.empty': { en: 'No data yet for today', es: 'Sin datos aún para hoy' },

  'dashboard.target.label': { en: 'Target', es: 'Meta' },
  'dashboard.target.suffix': { en: 'of {target}', es: 'de {target}' },

  'dashboard.completionRate.label': { en: 'Completion', es: 'Completado' },
  'dashboard.completionRate.attempts': { en: 'attempts', es: 'intentos' },
  'dashboard.completionRate.completed': { en: 'completed', es: 'completados' },

  'dashboard.activeWork.label': { en: 'Active work', es: 'Trabajo activo' },
  'dashboard.activeWork.target': { en: 'of 8h', es: 'de 8h' },

  'dashboard.bookings.label': { en: 'Bookings', es: 'Citas' },
  'dashboard.bookings.thisWeek': { en: 'this week', es: 'esta semana' },

  'dashboard.statusBreakdown.label': { en: 'Patient status', es: 'Estado de pacientes' },
  'dashboard.statusBreakdown.total': { en: 'total', es: 'total' },
  // v0.6.1 Fase 12 — Patient status stacked-bar layout: "+N otros" pill
  // when there are more than 3 statuses.
  'dashboard.statusBreakdown.othersShort': { en: 'other', es: 'otros' },
  'dashboard.statusBreakdown.pending': { en: 'Pending', es: 'Pendiente' },
  'dashboard.statusBreakdown.assigned': { en: 'Assigned', es: 'Asignado' },
  'dashboard.statusBreakdown.in_progress': { en: 'In progress', es: 'En curso' },
  'dashboard.statusBreakdown.completed': { en: 'Completed', es: 'Completado' },
  'dashboard.statusBreakdown.missed': { en: 'Missed', es: 'Perdido' },
  'dashboard.statusBreakdown.wrong_number': { en: 'Wrong number', es: 'Número equivocado' },
  'dashboard.statusBreakdown.excluded': { en: 'Excluded', es: 'Excluido' },
  'dashboard.statusBreakdown.dnc': { en: 'Do not call', es: 'No llamar' },
  'dashboard.statusBreakdown.unreachable': { en: 'Unreachable', es: 'Inalcanzable' },

  'dashboard.drillDown.title': { en: 'Per-agent breakdown', es: 'Detalle por agente' },
  'dashboard.drillDown.perAgent': { en: 'View per-agent', es: 'Ver por agente' },
  'dashboard.drillDown.agent': { en: 'Agent', es: 'Agente' },
  'dashboard.drillDown.completed': { en: 'Done', es: 'Hechos' },
  'dashboard.drillDown.target': { en: 'Target', es: 'Meta' },
  'dashboard.drillDown.activeWork': { en: 'Active work', es: 'Trabajo' },
  'dashboard.drillDown.close': { en: 'Close', es: 'Cerrar' },


  // ===== Uploader role (Wave B) =====
  'uploader.title': { en: 'Pathway HRSN — FindHelp Upload Queue', es: 'Pathway HRSN — Cola de Carga FindHelp' },
  'uploader.subtitle': { en: 'Review submitted screenings and mark them as uploaded to FindHelp.', es: 'Revise los screenings enviados y márquelos como subidos a FindHelp.' },
  'uploader.brand': { en: 'FindHelp Upload Queue', es: 'Cola de Carga FindHelp' },
  'uploader.nav.queue': { en: 'Upload queue', es: 'Cola de carga' },

  'uploader.queue.tabs.pending': { en: 'Pending ({n})', es: 'Pendientes ({n})' },
  'uploader.queue.tabs.flagged': { en: 'Flagged ({n})', es: 'Marcados ({n})' },
  'uploader.queue.tabs.uploaded': { en: 'Uploaded to FindHelp ({n})', es: 'Subidos a FindHelp ({n})' },
  'uploader.queue.tabs.all': { en: 'All ({n})', es: 'Todos ({n})' },
  'uploader.queue.tabs.pendingShort': { en: 'Pending', es: 'Pendiente' },
  'uploader.queue.tabs.flaggedShort': { en: 'Flagged', es: 'Flagged' },
  'uploader.queue.tabs.uploadedShort': { en: 'Uploaded to FindHelp', es: 'Subido a FindHelp' },
  'uploader.queue.filter.from': { en: 'Submitted from', es: 'Enviado desde' },
  'uploader.queue.filter.to': { en: 'Submitted to', es: 'Enviado hasta' },
  'uploader.queue.filter.uploadedFrom': { en: 'Uploaded from', es: 'Subido desde' },
  'uploader.queue.filter.uploadedTo': { en: 'Uploaded to', es: 'Subido hasta' },
  'uploader.queue.filter.search': { en: 'Search by client or agent…', es: 'Buscar por cliente o agente…' },
  'uploader.queue.filter.searchLabel': { en: 'Search', es: 'Buscar' },
  'uploader.queue.filter.agent': { en: 'Submitted by agent', es: 'Sometido por agente' },
  'uploader.queue.filter.uploader': { en: 'Uploaded to FindHelp by', es: 'Subido a FindHelp por' },
  'uploader.queue.filter.anyAgent': { en: 'Any agent', es: 'Cualquier agente' },
  'uploader.queue.filter.anyUploader': { en: 'Any uploader', es: 'Cualquier uploader' },
  'uploader.queue.filter.claimed': { en: 'Claimed', es: 'Tomado' },
  'uploader.queue.filter.claimedVisible': { en: 'Available to me', es: 'Disponibles para mí' },
  'uploader.queue.filter.claimedMine': { en: 'Claimed by me', es: 'Tomados por mí' },
  'uploader.queue.filter.claimedUnclaimed': { en: 'Unclaimed', es: 'Sin tomar' },
  'uploader.queue.filter.sort': { en: 'Sort', es: 'Orden' },
  'uploader.queue.filter.sla24h': { en: 'SLA pending >24h', es: 'SLA pendientes >24h' },
  'uploader.queue.filter.clear': { en: 'Clear filters', es: 'Limpiar filtros' },
  'uploader.queue.empty.pending': { en: 'No pending screenings — you are all caught up!', es: 'No hay screenings pendientes — ¡está al día!' },
  'uploader.queue.empty.flagged': { en: 'No flagged screenings.', es: 'No hay screenings marcados con error.' },
  'uploader.queue.empty.uploaded': { en: 'No uploaded screenings yet.', es: 'Aún no hay screenings subidos.' },
  'uploader.queue.empty.all': { en: 'No screenings match these filters.', es: 'Ningún screening coincide con estos filtros.' },
  'uploader.queue.couldNotLoad': { en: 'Could not load queue', es: 'No se pudo cargar la cola' },
  'uploader.queue.refresh': { en: 'Refresh', es: 'Actualizar' },
  'uploader.queue.sort.oldest': { en: 'Oldest first', es: 'Más antiguos primero' },
  'uploader.queue.sort.newest': { en: 'Newest first', es: 'Más nuevos primero' },

  'uploader.row.client': { en: 'Client', es: 'Cliente' },
  'uploader.row.agent': { en: 'Agent', es: 'Agente' },
  'uploader.row.submittedAt': { en: 'Submitted', es: 'Enviado' },
  'uploader.row.age': { en: 'Age', es: 'Antigüedad' },
  'uploader.row.actions': { en: 'Actions', es: 'Acciones' },
  'uploader.row.uploadedAt': { en: 'Uploaded', es: 'Subido' },
  'uploader.row.flaggedAt': { en: 'Flagged', es: 'Marcado' },
  'uploader.row.findhelpId': { en: 'FindHelp ID', es: 'ID FindHelp' },
  'uploader.row.errorReason': { en: 'Error', es: 'Error' },
  'uploader.row.status': { en: 'Status', es: 'Estado' },
  'uploader.row.hoursAgo': { en: '{n}h ago', es: 'hace {n}h' },
  'uploader.row.minsAgo': { en: '{n}m ago', es: 'hace {n}m' },
  'uploader.row.daysAgo': { en: '{n}d ago', es: 'hace {n}d' },
  'uploader.row.overdue': { en: 'Overdue >24h', es: 'Atrasado >24h' },

  'uploader.action.review': { en: 'Review', es: 'Revisar' },
  'uploader.action.markUploaded': { en: 'Mark uploaded', es: 'Marcar como subido' },
  'uploader.action.flagError': { en: 'Flag error', es: 'Marcar con error' },
  'uploader.action.cancel': { en: 'Cancel', es: 'Cancelar' },
  'uploader.action.close': { en: 'Close', es: 'Cerrar' },
  'uploader.action.confirm': { en: 'Confirm', es: 'Confirmar' },

  'uploader.detail.title': { en: 'Screening detail', es: 'Detalle del screening' },
  'uploader.detail.demographics': { en: 'Client demographics', es: 'Datos del cliente' },
  'uploader.detail.answers': { en: 'HRSN answers', es: 'Respuestas HRSN' },
  'uploader.detail.notes': { en: 'Notes', es: 'Notas' },
  'uploader.detail.timeline': { en: 'Timeline', es: 'Cronología' },
  'uploader.detail.cin': { en: 'CIN', es: 'CIN' },
  'uploader.detail.dob': { en: 'Date of birth', es: 'Fecha de nacimiento' },
  'uploader.detail.address': { en: 'Address', es: 'Dirección' },
  'uploader.detail.phone': { en: 'Phone', es: 'Teléfono' },
  'uploader.detail.preferredLang': { en: 'Preferred language', es: 'Idioma preferido' },
  'uploader.detail.agent': { en: 'Agent', es: 'Agente' },
  'uploader.detail.startedAt': { en: 'Started', es: 'Iniciado' },
  'uploader.detail.submittedAt': { en: 'Submitted', es: 'Enviado' },
  'uploader.detail.duration': { en: 'Duration', es: 'Duración' },
  'uploader.detail.languageUsed': { en: 'Language used', es: 'Idioma usado' },
  'uploader.detail.timeCategory': { en: 'Time category', es: 'Categoría de tiempo' },
  'uploader.detail.positiveDomains': { en: 'Positive HRSN domains', es: 'Dominios HRSN positivos' },
  'uploader.detail.noPositive': { en: 'No positive domains', es: 'Sin dominios positivos' },
  'uploader.detail.safety': { en: 'Safety total score', es: 'Puntaje de seguridad' },
  'uploader.detail.safetyFlag': { en: 'SAFETY CONCERN', es: 'PREOCUPACIÓN DE SEGURIDAD' },
  'uploader.detail.findhelpStatus': { en: 'FindHelp status', es: 'Estado FindHelp' },
  'uploader.detail.findhelpUploaded': { en: 'Uploaded to FindHelp', es: 'Subido a FindHelp' },
  'uploader.detail.findhelpFlagged': { en: 'Flagged with error', es: 'Marcado con error' },
  'uploader.detail.findhelpPending': { en: 'Pending upload', es: 'Pendiente de subir' },
  'uploader.detail.uploadedBy': { en: 'Uploaded by', es: 'Subido por' },
  'uploader.detail.flaggedBy': { en: 'Flagged on', es: 'Marcado el' },

  'uploader.detail.markUploaded.title': { en: 'Mark as uploaded to FindHelp', es: 'Marcar como subido a FindHelp' },
  'uploader.detail.markUploaded.desc': { en: 'Confirm this screening has been entered into FindHelp manually. Optional: paste the FindHelp ID and any notes.', es: 'Confirme que este screening fue ingresado manualmente en FindHelp. Opcional: pegue el ID de FindHelp y notas.' },
  'uploader.detail.findhelpIdLabel': { en: 'FindHelp ID (optional)', es: 'ID FindHelp (opcional)' },
  'uploader.detail.findhelpIdPlaceholder': { en: 'e.g. FH-123456', es: 'p.ej. FH-123456' },
  'uploader.detail.notesLabel': { en: 'Notes (optional)', es: 'Notas (opcional)' },
  'uploader.detail.notesPlaceholder': { en: 'Anything notable about the upload', es: 'Algo destacable sobre la subida' },
  'uploader.detail.confirm': { en: 'Confirm upload', es: 'Confirmar subida' },
  'uploader.detail.confirming': { en: 'Saving…', es: 'Guardando…' },
  'uploader.detail.savedToast': { en: 'Marked as uploaded', es: 'Marcado como subido' },
  'uploader.detail.savedToastDesc': { en: 'The screening is now in the Uploaded tab.', es: 'El screening ahora está en la pestaña Subidos.' },
  'uploader.detail.errorToast': { en: 'Could not mark as uploaded', es: 'No se pudo marcar como subido' },
  'uploader.detail.markUploaded.noMedicaidLabel': { en: 'Patient has no Medicaid', es: 'Paciente sin Medicaid' },
  'uploader.detail.markUploaded.noMedicaidExplanation': { en: 'This screening will be marked as processed without a FindHelp ID.', es: 'Este screening será marcado como procesado sin ID de FindHelp.' },

  'uploader.detail.flagError.title': { en: 'Flag screening with error', es: 'Marcar screening con error' },
  'uploader.detail.flagError.desc': { en: 'Use this when the screening cannot be uploaded as-is (e.g., bad data, missing fields). Admin and the original agent will be notified.', es: 'Úselo cuando el screening no se puede subir tal cual (datos erróneos, campos faltantes). Se notificará al admin y al agente original.' },
  'uploader.detail.flagError.errorLabel': { en: 'Error reason', es: 'Razón del error' },
  'uploader.detail.flagError.errorPlaceholder': { en: 'Describe what is wrong (visible to admin and agent)', es: 'Describa qué está mal (visible para admin y agente)' },
  'uploader.detail.flagError.confirm': { en: 'Flag error', es: 'Marcar con error' },
  'uploader.detail.flagError.confirming': { en: 'Flagging…', es: 'Marcando…' },
  'uploader.detail.flagError.savedToast': { en: 'Flagged with error', es: 'Marcado con error' },
  'uploader.detail.flagError.savedToastDesc': { en: 'Admin and the agent have been notified.', es: 'Se notificó al admin y al agente.' },
  'uploader.detail.flagError.errorToast': { en: 'Could not flag screening', es: 'No se pudo marcar el screening' },

  'uploader.stats.title': { en: 'My stats', es: 'Mis estadísticas' },
  'uploader.stats.todayUploaded': { en: 'Uploaded today', es: 'Subidos hoy' },
  'uploader.stats.todayPending': { en: 'Pending now', es: 'Pendientes ahora' },
  'uploader.stats.oldestPendingHours': { en: 'Oldest pending', es: 'Más antiguo pendiente' },
  'uploader.stats.oldestUnit': { en: '{n}h', es: '{n}h' },
  'uploader.stats.avgTimeToUpload': { en: 'Avg time to upload (today)', es: 'Tiempo promedio de subida (hoy)' },
  'uploader.stats.weekUploaded': { en: 'This week', es: 'Esta semana' },
  'uploader.stats.monthUploaded': { en: 'This month', es: 'Este mes' },
  'uploader.stats.totalUploaded': { en: 'All time', es: 'Total histórico' },
  'uploader.stats.flagged': { en: 'Currently flagged', es: 'Marcados actualmente' },

  'uploader.sla.title': { en: 'Items overdue >24h', es: 'Pendientes >24h' },
  'uploader.sla.empty': { en: 'No items currently overdue.', es: 'No hay elementos atrasados.' },
  'uploader.sla.banner': { en: '{n} screening(s) overdue >24h — please prioritize these.', es: '{n} screening(s) atrasado(s) >24h — por favor priorice.' },

  // ===== Admin Team page (full user edit) =====
  'admin.team.edit.title': { en: 'Edit user', es: 'Editar usuario' },
  'admin.team.edit.subtitle': { en: 'Update profile, role, languages and access.', es: 'Actualice perfil, rol, idiomas y acceso.' },
  'admin.team.edit.emailLabel': { en: 'Email', es: 'Correo' },
  'admin.team.edit.fullNameLabel': { en: 'Full name', es: 'Nombre completo' },
  'admin.team.edit.roleLabel': { en: 'Role', es: 'Rol' },
  'admin.team.edit.languagesLabel': { en: 'Languages', es: 'Idiomas' },
  'admin.team.edit.dailyTargetLabel': { en: 'Daily target override', es: 'Objetivo diario (override)' },
  'admin.team.edit.dailyTargetHint': { en: 'Per-user override for daily screenings target.', es: 'Override individual para el objetivo diario.' },
  'admin.team.edit.activeLabel': { en: 'Active', es: 'Activo' },
  'admin.team.edit.activeHint': { en: "Inactive users can't sign in. Their open queue stays in place — reassign separately.", es: "Los usuarios inactivos no pueden iniciar sesión. Su cola queda en su lugar — reasigne aparte." },
  'admin.team.edit.useSystemDefault': { en: 'Use system default', es: 'Usar default del sistema' },
  'admin.team.edit.cantEditSelf': { en: 'Cannot change own role', es: 'No podés cambiar tu propio rol' },
  'admin.team.edit.cantDeactivateSelf': { en: 'Cannot deactivate own account', es: 'No podés desactivar tu propia cuenta' },
  'admin.team.edit.lastUpdated': { en: 'Last updated by {by} on {when}', es: 'Última actualización por {by} el {when}' },
  'admin.team.edit.error.emailInUse': { en: 'Email already in use', es: 'Correo ya en uso' },
  'admin.team.edit.error.invalidRole': { en: 'Invalid role', es: 'Rol inválido' },
  'admin.team.edit.error.invalidLanguages': { en: 'Pick at least one language (max 5).', es: 'Elija al menos un idioma (máx 5).' },
  'admin.team.edit.error.dailyTargetInvalid': { en: 'Daily target must be a non-negative integer.', es: 'El objetivo diario debe ser un entero no negativo.' },
  'admin.team.edit.role.admin': { en: 'Admin', es: 'Admin' },
  'admin.team.edit.role.supervisor': { en: 'Supervisor', es: 'Supervisor' },
  'admin.team.edit.role.agent': { en: 'Agent', es: 'Agente' },
  'admin.team.edit.role.uploader': { en: 'Uploader', es: 'Uploader' },
  'admin.team.edit.role.navigator': { en: 'Navigator', es: 'Navigator' },
  // 076 (D-3): el alcance del navigator — solo visible con ese rol.
  'admin.team.edit.scopeLabel': { en: 'Navigator scope', es: 'Alcance del navigator' },
  'admin.team.edit.scope.full': { en: 'Full — receives auto-assignment', es: 'Completo — recibe auto-asignación' },
  'admin.team.edit.scope.closer': { en: 'Closures — negative batches only', es: 'Cierres — solo tandas negativas' },
  'admin.team.edit.scope.pool': { en: 'Pool mailbox (system account)', es: 'Buzón del pool (cuenta de sistema)' },
  // E-35 (Edd, 2026-10-21, selector: «Obligar a elegir»): al CREAR un
  // navegador el tipo no viene preseleccionado — crear un closer como «full»
  // por descuido le reparte positivos y dispara un emparejamiento grande.
  'admin.team.edit.scopePlaceholder': {
    en: 'Choose a type…',
    es: 'Elige el tipo…',
  },
  'admin.team.edit.scopeHint': {
    en: 'Full receives automatic assignment. Closures can only receive FindHelp-negative batches. The pool mailbox holds unassigned work and is never a target.',
    es: 'Completo recibe asignación automática. Cierres solo puede recibir tandas negativas de FindHelp. El buzón guarda lo sin repartir y nunca es destino.',
  },
  'admin.team.edit.lang.en': { en: 'English', es: 'Inglés' },
  'admin.team.edit.lang.es': { en: 'Spanish', es: 'Español' },
  'admin.team.edit.lang.other': { en: 'Other', es: 'Otro' },
  'admin.team.edit.cancel': { en: 'Cancel', es: 'Cancelar' },
  'admin.team.edit.save': { en: 'Save changes', es: 'Guardar cambios' },
  'admin.team.edit.saving': { en: 'Saving…', es: 'Guardando…' },
  'admin.team.edit.toastUpdated': { en: 'User updated', es: 'Usuario actualizado' },
  'admin.team.edit.toastUpdatedDesc': { en: "{name}'s account was updated.", es: 'La cuenta de {name} se actualizó.' },

  // ===== Audit log redesign =====
  'admin.audit.quickFilter.label': { en: 'Quick range', es: 'Rango rápido' },
  'admin.audit.quickFilter.today': { en: 'Today', es: 'Hoy' },
  'admin.audit.quickFilter.yesterday': { en: 'Yesterday', es: 'Ayer' },
  'admin.audit.quickFilter.last7': { en: 'Last 7 days', es: 'Últimos 7 días' },
  'admin.audit.quickFilter.last30': { en: 'Last 30 days', es: 'Últimos 30 días' },
  'admin.audit.quickFilter.custom': { en: 'Custom', es: 'Personalizado' },
  'admin.audit.category.auth': { en: 'Authentication', es: 'Autenticación' },
  'admin.audit.category.bookings': { en: 'Bookings', es: 'Citas' },
  'admin.audit.category.screenings': { en: 'Screenings', es: 'Screenings' },
  'admin.audit.category.uploader': { en: 'Uploader', es: 'Uploader' },
  'admin.audit.category.household': { en: 'Household', es: 'Hogar' },
  'admin.audit.category.system': { en: 'System', es: 'Sistema' },
  'admin.audit.category.other': { en: 'Other', es: 'Otro' },
  'admin.audit.daySection.todayLabel': { en: 'Today', es: 'Hoy' },
  'admin.audit.daySection.yesterdayLabel': { en: 'Yesterday', es: 'Ayer' },
  'admin.audit.daySection.entryCount': { en: '{n} entries', es: '{n} entradas' },
  'admin.audit.detail.title': { en: 'Audit entry', es: 'Entrada de auditoría' },
  'admin.audit.detail.who': { en: 'Who', es: 'Quién' },
  'admin.audit.detail.what': { en: 'What', es: 'Qué' },
  'admin.audit.detail.when': { en: 'When', es: 'Cuándo' },
  'admin.audit.detail.metadata': { en: 'Metadata', es: 'Metadatos' },
  'admin.audit.detail.linkedEntity': { en: 'Linked entity', es: 'Entidad relacionada' },
  'admin.audit.detail.actionLabel': { en: 'Action', es: 'Acción' },
  'admin.audit.detail.entityLabel': { en: 'Entity', es: 'Entidad' },
  'admin.audit.detail.entityIdLabel': { en: 'Entity ID', es: 'ID de entidad' },
  'admin.audit.detail.ipLabel': { en: 'IP', es: 'IP' },
  'admin.audit.detail.systemActor': { en: 'System / anonymous', es: 'Sistema / anónimo' },
  'admin.audit.detail.close': { en: 'Close', es: 'Cerrar' },
  'admin.audit.view.table': { en: 'Table view', es: 'Vista tabla' },
  'admin.audit.view.timeline': { en: 'Timeline', es: 'Línea de tiempo' },

  // ===== Uploader drill-down =====
  'admin.uploaderTeam.detail.title': { en: 'Uploader detail', es: 'Detalle del uploader' },
  'admin.uploaderTeam.detail.subtitle': { en: 'Stats, recent work and audit trail for this uploader.', es: 'Estadísticas, trabajo reciente y auditoría de este uploader.' },
  'admin.uploaderTeam.tab.stats': { en: 'Stats', es: 'Estadísticas' },
  'admin.uploaderTeam.tab.recentWork': { en: 'Recent work', es: 'Trabajo reciente' },
  'admin.uploaderTeam.tab.audit': { en: 'Audit trail', es: 'Auditoría' },
  'admin.uploaderTeam.stats.uploadedToday': { en: 'Uploaded today', es: 'Subidos hoy' },
  'admin.uploaderTeam.stats.flagged': { en: 'Flagged', es: 'Marcados' },
  'admin.uploaderTeam.stats.noMedicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'admin.uploaderTeam.stats.avgTime': { en: 'Avg time-to-upload', es: 'Tiempo prom. a subir' },
  'admin.uploaderTeam.stats.window.today': { en: 'Today', es: 'Hoy' },
  'admin.uploaderTeam.stats.window.week': { en: 'This week', es: 'Esta semana' },
  'admin.uploaderTeam.stats.window.month': { en: 'This month', es: 'Este mes' },
  'admin.uploaderTeam.stats.window.total': { en: 'All time', es: 'Total' },
  'admin.uploaderTeam.recentWork.empty': { en: 'No recent work yet.', es: 'Aún no hay trabajo reciente.' },
  'admin.uploaderTeam.recentWork.col.processedAt': { en: 'Processed', es: 'Procesado' },
  'admin.uploaderTeam.recentWork.col.client': { en: 'Client', es: 'Cliente' },
  'admin.uploaderTeam.recentWork.col.status': { en: 'Status', es: 'Estado' },
  'admin.uploaderTeam.recentWork.col.findhelpId': { en: 'FindHelp ID', es: 'ID FindHelp' },
  'admin.uploaderTeam.recentWork.statusUploaded': { en: 'uploaded', es: 'subido' },
  'admin.uploaderTeam.recentWork.statusFlagged': { en: 'flagged', es: 'marcado' },
  'admin.uploaderTeam.recentWork.statusNoMedicaid': { en: 'no Medicaid', es: 'sin Medicaid' },
  'admin.uploaderTeam.audit.empty': { en: 'No audit entries for this uploader yet.', es: 'Aún no hay entradas de auditoría.' },
  'admin.uploaderTeam.detail.couldNotLoad': { en: 'Could not load uploader details', es: 'No se pudieron cargar los detalles' },
  'admin.uploaderTeam.detail.activePill': { en: 'active', es: 'activo' },
  'admin.uploaderTeam.detail.inactivePill': { en: 'inactive', es: 'inactivo' },
  'admin.uploaderTeam.detail.lastLogin': { en: 'Last login {when}', es: 'Última sesión {when}' },
  'admin.uploaderTeam.detail.lastLoginNever': { en: 'Never logged in', es: 'Nunca inició sesión' },
  'admin.uploaderTeam.detail.pendingQueueLabel': { en: 'Pending in shared queue', es: 'Pendientes en cola compartida' },
  'admin.uploaderTeam.detail.close': { en: 'Close', es: 'Cerrar' },

  // ===== Admin uploader-team page =====
  'admin.uploaderTeam.nav': { en: 'Upload queue', es: 'Cola de carga' },
  'admin.uploaderTeam.title': { en: 'FindHelp upload team', es: 'Equipo de carga FindHelp' },
  'admin.uploaderTeam.subtitle': { en: 'Read-only view of the uploader team and the FindHelp upload pipeline.', es: 'Vista de solo lectura del equipo de carga y el flujo FindHelp.' },
  'admin.uploaderTeam.totals.pending': { en: 'Total pending', es: 'Total pendientes' },
  'admin.uploaderTeam.totals.flagged': { en: 'Total flagged', es: 'Total marcados' },
  'admin.uploaderTeam.totals.uploadedToday': { en: 'Uploaded today', es: 'Subidos hoy' },
  'admin.uploaderTeam.totals.over24h': { en: 'Overdue >24h', es: 'Atrasados >24h' },
  'admin.dashboard.medicaid.breakdown': { en: 'Medicaid breakdown', es: 'Distribución de Medicaid' },
  'admin.dashboard.medicaid.withMedicaid': { en: 'With Medicaid', es: 'Con Medicaid' },
  'admin.dashboard.medicaid.withoutMedicaid': { en: 'Without Medicaid', es: 'Sin Medicaid' },
  'admin.dashboard.medicaid.processedTotal': { en: 'Processed total', es: 'Total procesados' },
  'admin.dashboard.medicaid.noMedicaidThisWeek': { en: 'No Medicaid (this week)', es: 'Sin Medicaid (esta semana)' },
  'admin.dashboard.medicaid.noMedicaidThisMonth': { en: 'No Medicaid (this month)', es: 'Sin Medicaid (este mes)' },
  'admin.agent.stats.metric.noMedicaid': { en: '{n} no Medicaid', es: '{n} sin Medicaid' },
  'admin.uploaderTeam.perUploader.title': { en: 'Per-uploader', es: 'Por uploader' },
  'admin.uploaderTeam.perUploader.empty': { en: 'No active uploaders configured.', es: 'No hay uploaders activos configurados.' },
  'admin.uploaderTeam.perUploader.todayUploaded': { en: 'Today', es: 'Hoy' },
  'admin.uploaderTeam.perUploader.weekUploaded': { en: 'Week', es: 'Semana' },
  'admin.uploaderTeam.perUploader.pending': { en: 'Pending (queue)', es: 'Pendientes (cola)' },
  'admin.uploaderTeam.perUploader.oldestHours': { en: 'Oldest pending', es: 'Más antiguo' },
  'admin.uploaderTeam.overdue.title': { en: 'Overdue >24h', es: 'Atrasados >24h' },
  'admin.uploaderTeam.overdue.empty': { en: 'Nothing overdue. Nice.', es: 'Nada atrasado. Bien.' },
  'admin.uploaderTeam.overdue.client': { en: 'Client', es: 'Cliente' },
  'admin.uploaderTeam.overdue.agent': { en: 'Agent', es: 'Agente' },
  'admin.uploaderTeam.overdue.submittedAt': { en: 'Submitted', es: 'Enviado' },
  'admin.uploaderTeam.overdue.hoursOverdue': { en: 'Overdue', es: 'Atrasado' },
  'admin.uploaderTeam.couldNotLoad': { en: 'Could not load team stats', es: 'No se pudieron cargar las estadísticas' },
  'admin.uploaderTeam.refresh': { en: 'Refresh', es: 'Actualizar' },

  // ===== Agent upload-status panel (own QueuePage) =====
  'agent.uploadStatus.title': { en: 'My uploads to FindHelp', es: 'Mis cargas a FindHelp' },
  'agent.uploadStatus.submittedToday': { en: 'Submitted today', es: 'Enviados hoy' },
  'agent.uploadStatus.uploaded': { en: 'Uploaded', es: 'Subidos' },
  'agent.uploadStatus.pending': { en: 'Pending upload', es: 'Pendientes de subir' },
  'agent.uploadStatus.overdue': { en: 'Overdue >24h', es: 'Atrasados >24h' },
  'agent.uploadStatus.overdueTooltip': { en: 'Click to message the uploader', es: 'Click para mensajear al uploader' },
  'agent.uploadStatus.chatSubject': { en: 'Need help with FindHelp uploads', es: 'Ayuda con cargas a FindHelp' },
  'agent.uploadStatus.chatPrefilledMessage': { en: 'Hi, I have {n} screening(s) waiting for upload to FindHelp. Could you help?', es: 'Hola, tengo {n} screening(s) esperando subirse a FindHelp. ¿Podés ayudar?' },

  // ===== New notification types =====
  'notifications.toast.findhelpSlaBreach.title': { en: 'FindHelp upload SLA breach', es: 'Carga FindHelp atrasada >24h' },
  'notifications.toast.screeningFlaggedByUploader.title': { en: 'Uploader flagged a screening', es: 'El uploader marcó error en un screening' },
  'notifications.toast.yourScreeningWasFlagged.title': { en: 'Your screening was flagged', es: 'Marcaron error en su screening' },
  'notifications.type.findhelpSlaBreach.title': { en: 'FindHelp SLA breach (>24h)', es: 'Carga FindHelp atrasada >24h' },
  'notifications.type.screeningFlaggedByUploader.title': { en: 'Screening flagged by uploader', es: 'Screening marcado por el uploader' },
  'notifications.type.yourScreeningWasFlagged.title': { en: 'Your screening was flagged', es: 'Su screening fue marcado' },


  // ===== Screening: household members capture =====
  'screening.household.prompt': { en: 'Are there other household members who need screening?', es: '¿Hay otras personas en este hogar que necesiten screening?' },
  'screening.household.subtitle': { en: 'You can capture them now and the uploader will follow up.', es: 'Puede capturarlos ahora y el uploader hará seguimiento.' },
  'screening.household.yes': { en: 'Yes, capture them', es: 'Sí, capturarlos' },
  'screening.household.yesHint': { en: 'Add their name, DOB, and Medicaid ID if known.', es: 'Agregue nombre, fecha de nac. y Medicaid si lo sabe.' },
  'screening.household.no': { en: 'No, skip — continue to review', es: 'No, omitir — continuar a revisión' },
  'screening.household.noHint': { en: 'Goes straight to the submit summary.', es: 'Va directo al resumen de envío.' },
  'screening.household.addMember': { en: 'Add member', es: 'Agregar miembro' },
  'screening.household.addAnother': { en: 'Add another', es: 'Agregar otro' },
  'screening.household.fullNameLabel': { en: 'Full name', es: 'Nombre completo' },
  'screening.household.fullNamePlaceholder': { en: 'First and last name', es: 'Nombre y apellido' },
  'screening.household.dobLabel': { en: 'Date of birth', es: 'Fecha de nacimiento' },
  'screening.household.medicaidIdLabel': { en: 'Medicaid ID', es: 'Medicaid ID' },
  'screening.household.medicaidIdPlaceholder': { en: 'Optional', es: 'Opcional' },
  'screening.household.capturedTitle': { en: 'Household members', es: 'Miembros del hogar' },
  'screening.household.capturedCount': { en: '{n} captured', es: '{n} capturado(s)' },
  'screening.household.continue': { en: 'Continue to review', es: 'Continuar a revisión' },
  'screening.household.back': { en: 'Back', es: 'Atrás' },
  'screening.household.stepLabel': { en: 'Household members - step before review', es: 'Miembros del hogar - paso antes de revisión' },
  'screening.household.errorRequired': { en: 'Full name and date of birth are required.', es: 'Se requiere nombre completo y fecha de nacimiento.' },
  'screening.household.errorInvalidDate': { en: 'Date of birth must be a valid past date.', es: 'La fecha de nacimiento debe ser una fecha pasada válida.' },
  'screening.household.errorAlreadyAdded': { en: 'This person is already in the list.', es: 'Esta persona ya está en la lista.' },

  // v0.6.1 Fase 4 — relationship to source client (capture form + display)
  'screening.household.relationshipLabel': { en: 'Relationship', es: 'Relación' },
  'screening.household.relationshipOptional': { en: 'optional', es: 'opcional' },
  'screening.household.relationshipPlaceholder': { en: '— select —', es: '— seleccionar —' },
  'screening.household.relationship.madre': { en: 'Mother', es: 'Madre' },
  'screening.household.relationship.padre': { en: 'Father', es: 'Padre' },
  'screening.household.relationship.hermano': { en: 'Brother', es: 'Hermano' },
  'screening.household.relationship.hermana': { en: 'Sister', es: 'Hermana' },
  'screening.household.relationship.hijo': { en: 'Son', es: 'Hijo' },
  'screening.household.relationship.hija': { en: 'Daughter', es: 'Hija' },
  'screening.household.relationship.esposo': { en: 'Husband', es: 'Esposo' },
  'screening.household.relationship.esposa': { en: 'Wife', es: 'Esposa' },
  'screening.household.relationship.primo': { en: 'Cousin (m)', es: 'Primo' },
  'screening.household.relationship.prima': { en: 'Cousin (f)', es: 'Prima' },
  'screening.household.relationship.tio': { en: 'Uncle', es: 'Tío' },
  'screening.household.relationship.tia': { en: 'Aunt', es: 'Tía' },
  'screening.household.relationship.sobrino': { en: 'Nephew', es: 'Sobrino' },
  'screening.household.relationship.sobrina': { en: 'Niece', es: 'Sobrina' },
  'screening.household.relationship.abuelo': { en: 'Grandfather', es: 'Abuelo' },
  'screening.household.relationship.abuela': { en: 'Grandmother', es: 'Abuela' },
  'screening.household.relationship.nieto': { en: 'Grandson', es: 'Nieto' },
  'screening.household.relationship.nieta': { en: 'Granddaughter', es: 'Nieta' },
  'screening.household.relationship.otro': { en: 'Other', es: 'Otro' },

  // ===== Uploader: household members tab =====
  'uploader.household.tab': { en: 'Household members', es: 'Miembros del hogar' },
  'uploader.household.empty': { en: 'No household members captured', es: 'Sin miembros capturados' },
  'uploader.household.col.member': { en: 'Member', es: 'Miembro' },
  'uploader.household.col.dob': { en: 'Birth year', es: 'Año de nac.' },
  'uploader.household.col.medicaid': { en: 'Medicaid', es: 'Medicaid' },
  'uploader.household.col.status': { en: 'Status', es: 'Estado' },
  'uploader.household.col.capturedBy': { en: 'Captured by', es: 'Capturado por' },
  'uploader.household.col.capturedAt': { en: 'Captured', es: 'Capturado' },
  'uploader.household.detail.title': { en: 'Household member', es: 'Miembro del hogar' },
  'uploader.household.editTitle': { en: 'Edit household member', es: 'Editar miembro del hogar' },
  'uploader.household.notesLabel': { en: 'Uploader notes', es: 'Notas del uploader' },
  'uploader.household.hasMedicaidLabel': { en: 'Has Medicaid (verified)', es: 'Tiene Medicaid (verificado)' },
  'uploader.household.statusLabel': { en: 'Status', es: 'Estado' },
  'uploader.household.save': { en: 'Save', es: 'Guardar' },
  'uploader.household.saved': { en: 'Saved', es: 'Guardado' },
  'uploader.household.status.pending': { en: 'Pending', es: 'Pendiente' },
  'uploader.household.status.contacted': { en: 'Contacted', es: 'Contactado' },
  'uploader.household.status.scheduled': { en: 'Scheduled', es: 'Agendado' },
  'uploader.household.status.screened': { en: 'Screened', es: 'Screening hecho' },
  'uploader.household.status.declined': { en: 'Declined', es: 'Rechazó' },
  'uploader.household.status.unreachable': { en: 'Unreachable', es: 'No localizable' },
  'uploader.household.medicaid.yes': { en: 'Yes', es: 'Sí' },
  'uploader.household.medicaid.no': { en: 'No', es: 'No' },
  'uploader.household.medicaid.unknown': { en: 'Not verified', es: 'No verificado' },

  // ===== Admin: household captures metric =====
  'admin.householdMetrics.title': { en: 'Household captures', es: 'Capturas de miembros del hogar' },
  'admin.householdMetrics.todayCount': { en: 'Today', es: 'Hoy' },
  'admin.householdMetrics.weekCount': { en: 'This week', es: 'Esta semana' },
  'admin.householdMetrics.monthCount': { en: 'This month', es: 'Este mes' },
  'admin.householdMetrics.captureRate': { en: 'Capture rate (per submission)', es: 'Tasa de captura (por envío)' },
  'admin.householdMetrics.medicaidRate': { en: 'Medicaid confirmed', es: 'Medicaid confirmado' },
  'admin.householdMetrics.totalCount': { en: 'Total captured', es: 'Total capturado' },


  // ===== Admin: Live Monitoring - All submitted screenings (global list) =====
  'admin.live.allScreenings.title': { en: 'All submitted screenings', es: 'Todos los screenings sometidos' },
  'admin.live.allScreenings.subtitle': { en: 'Global view across every agent. Click a row to open the read-only detail.', es: 'Vista global de todos los agentes. Haga clic en una fila para abrir el detalle de solo lectura.' },
  'admin.live.allScreenings.filter.agent': { en: 'Agent', es: 'Agente' },
  'admin.live.allScreenings.filter.agentAll': { en: 'All agents', es: 'Todos los agentes' },
  'admin.live.allScreenings.filter.status': { en: 'Status', es: 'Estado' },
  'admin.live.allScreenings.filter.statusSubmitted': { en: 'Submitted only', es: 'Solo sometidos' },
  'admin.live.allScreenings.filter.statusAll': { en: 'All', es: 'Todos' },
  'admin.live.allScreenings.filter.from': { en: 'From', es: 'Desde' },
  'admin.live.allScreenings.filter.to': { en: 'To', es: 'Hasta' },
  'admin.live.allScreenings.filter.apply': { en: 'Apply', es: 'Aplicar' },
  'admin.live.allScreenings.filter.clear': { en: 'Clear', es: 'Limpiar' },
  'admin.live.allScreenings.col.submittedAt': { en: 'Submitted', es: 'Sometido' },
  'admin.live.allScreenings.col.agent': { en: 'Agent', es: 'Agente' },
  'admin.live.allScreenings.col.client': { en: 'Client', es: 'Cliente' },
  'admin.live.allScreenings.col.duration': { en: 'Duration', es: 'Duración' },
  'admin.live.allScreenings.col.status': { en: 'FindHelp', es: 'FindHelp' },
  'admin.live.allScreenings.col.notes': { en: 'Notes', es: 'Notas' },
  'admin.live.allScreenings.col.actions': { en: 'Open', es: 'Abrir' },
  'admin.live.allScreenings.loadMore': { en: 'Load more', es: 'Cargar más' },
  'admin.live.allScreenings.loading': { en: 'Loading…', es: 'Cargando…' },
  'admin.live.allScreenings.empty': { en: 'No screenings match your filters.', es: 'Ningún screening coincide con sus filtros.' },
  'admin.live.allScreenings.error': { en: "Couldn't load the screenings list.", es: 'No se pudo cargar la lista de screenings.' },
  'admin.live.allScreenings.shownOf': { en: 'Showing {shown} of {total}', es: 'Mostrando {shown} de {total}' },
  'admin.live.allScreenings.open': { en: 'Open', es: 'Abrir' },
  'admin.live.allScreenings.notSubmitted': { en: 'In progress', es: 'En progreso' },
  'admin.live.allScreenings.notesCount': { en: '{n} notes', es: '{n} notas' },
  'admin.live.allScreenings.noneNotes': { en: 'No notes', es: 'Sin notas' },
  'admin.live.allScreenings.status.uploaded': { en: 'Uploaded', es: 'Subido' },
  'admin.live.allScreenings.status.pending': { en: 'Pending upload', es: 'Pendiente' },
  'admin.live.allScreenings.status.flagged': { en: 'Flagged', es: 'Con error' },
  'admin.live.allScreenings.status.noMedicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },

  // SUB-K3 (v0.4.1) — Uploaders activos glance widget on LiveMonitoringPage.
  'admin.live.uploaders.title':          { en: 'Active uploaders',           es: 'Uploaders activos' },
  'admin.live.uploaders.activos':        { en: 'Active',                     es: 'Activos' },
  'admin.live.uploaders.claimedNow':     { en: 'Claimed now',                es: 'Claimed ahora' },
  'admin.live.uploaders.pending':        { en: 'Total pending',              es: 'Total pendientes' },
  'admin.live.uploaders.topClaims':      { en: 'Top 3 by claims',            es: 'Top 3 con más claims' },
  'admin.live.uploaders.claimedSuffix':  { en: '{count} claimed',            es: '{count} claimed' },
  'admin.live.uploaders.viewDetail':     { en: 'View full activity',         es: 'Ver actividad completa' },

  // SUB-K1 (v0.4.1) — Admin uploaders activity page (/admin/uploaders-activity).
  'admin.uploadersActivity.nav':              { en: 'Uploaders activity',                                  es: 'Actividad de uploaders' },
  'admin.uploadersActivity.title':            { en: 'Uploaders activity',                                  es: 'Actividad de uploaders' },
  'admin.uploadersActivity.subtitle':         { en: 'Per-uploader today snapshot + global pending count.', es: 'Snapshot del día por uploader + total pendientes global.' },
  'admin.uploadersActivity.refresh':          { en: 'Refresh',                                             es: 'Actualizar' },
  'admin.uploadersActivity.errorTitle':       { en: "Couldn't load uploaders activity",                   es: 'No se pudo cargar la actividad de uploaders' },
  'admin.uploadersActivity.empty':            { en: 'No active uploaders.',                                es: 'Sin uploaders activos.' },
  'admin.uploadersActivity.globalPending':    { en: 'Total pending',                                       es: 'Total pendientes' },
  'admin.uploadersActivity.globalFlagged':    { en: 'Total flagged',                                       es: 'Total con error' },
  'admin.uploadersActivity.uploadersTotal':   { en: 'Active uploaders',                                    es: 'Uploaders activos' },
  'admin.uploadersActivity.col.uploader':         { en: 'Uploader',                                        es: 'Uploader' },
  'admin.uploadersActivity.col.uploadedToday':    { en: 'Uploaded today',                                  es: 'Subidos hoy' },
  'admin.uploadersActivity.col.noMedicaidToday':  { en: 'No Medicaid today',                               es: 'Sin Medicaid hoy' },
  'admin.uploadersActivity.col.flaggedToday':     { en: 'Flagged today',                                   es: 'Con error hoy' },
  'admin.uploadersActivity.col.claimActive':      { en: 'Claim active',                                    es: 'Claim activo' },
  'admin.uploadersActivity.pollNote':          { en: 'Auto-refreshes every 30 seconds.',                   es: 'Se actualiza automáticamente cada 30 segundos.' },

  // Cluster M v0.5.0 — Agent motivation & progress.
  'progress.openModalAria':    { en: 'Open my progress',                       es: 'Abrir mi progreso' },
  'progress.todayLabel':       { en: 'Today',                                  es: 'Hoy' },

  // v0.6.0 Cluster N — uploader-progress sidebar card (mirror progress.* keys)
  'uploaderProgress.openModalAria':         { en: 'Open my upload progress',              es: 'Abrir mi progreso de subidas' },
  'uploaderProgress.todayLabel':            { en: 'Today',                                 es: 'Hoy' },
  'uploaderProgress.atTopTier':             { en: 'Top tier reached — Diamante!',          es: 'Máximo nivel — \u00a1Diamante!' },
  'uploaderProgress.toNextTier':            { en: '{count} more to next tier',             es: '{count} m\u00e1s para el siguiente nivel' },
  'uploaderProgress.motivationalDisclaimer':{ en: 'Motivational only — does not affect pay', es: 'Solo motivacional \u2014 no afecta el pago' },

  // v0.6.0 Cluster N — SystemConfigPage (N-Q6 safeguards UX)
  'admin.nav.systemConfig':                  { en: 'System config',                          es: 'Config sistema' },
  'systemConfig.title':                      { en: 'System configuration',                   es: 'Configuraci\u00f3n del sistema' },
  'systemConfig.subtitle':                   { en: 'Edit runtime configuration keys. Payroll-affecting keys require reason + effective date.', es: 'Editar claves de configuraci\u00f3n en tiempo de ejecuci\u00f3n. Las claves que afectan payroll requieren raz\u00f3n + fecha efectiva.' },
  'systemConfig.banner':                     { en: 'Keys marked \u201cPayroll\u201d affect Cluster P pay calculations. Coordinate with your accountant before editing.', es: 'Las claves marcadas \u201cPayroll\u201d afectan los c\u00e1lculos de pago de Cluster P. Coord\u00ednate con tu contador antes de editar.' },
  'systemConfig.col.key':                    { en: 'Key',                                    es: 'Clave' },
  'systemConfig.col.value':                  { en: 'Value',                                  es: 'Valor' },
  'systemConfig.col.lastUpdated':            { en: 'Last updated',                           es: '\u00daltima edici\u00f3n' },
  'systemConfig.col.actions':                { en: 'Actions',                                es: 'Acciones' },
  'systemConfig.row.payrollAffecting':       { en: 'Payroll',                                es: 'Payroll' },
  'systemConfig.row.readonly':               { en: 'Read-only',                              es: 'Solo lectura' },
  'systemConfig.row.editTracked':            { en: 'Edit (with safeguards)',                 es: 'Editar (con salvaguardas)' },
  'systemConfig.row.edit':                   { en: 'Edit',                                   es: 'Editar' },
  'systemConfig.row.editComingSoon':         { en: 'Basic edit modal lands in v0.6.0 follow-up', es: 'Modal de edici\u00f3n b\u00e1sica llega en seguimiento v0.6.0' },
  'systemConfig.row.editOnSettingsPage':       { en: 'Edit in Settings',                       es: 'Editar en Configuración' },

  // v0.6.0 Cluster L — admin bulk reassign tool
  'bulkReassign.button':                     { en: 'Bulk reassign',                            es: 'Reasignar masivo' },
  'bulkReassign.title':                      { en: 'Bulk reassign',                            es: 'Reasignación masiva' },
  'bulkReassign.audience.agent':             { en: 'Move pending assignments between agents',  es: 'Mover asignaciones pendientes entre agentes' },
  'bulkReassign.audience.uploader':          { en: 'Move pending uploads between uploaders',   es: 'Mover subidas pendientes entre uploaders' },
  'bulkReassign.audience.choose':            { en: 'Pick audience (agents or uploaders) below', es: 'Elegí público (agentes o uploaders) abajo' },
  'bulkReassign.audienceLabel':              { en: 'Audience',                                 es: 'Público' },
  'bulkReassign.audienceAgent':              { en: 'Agents',                                   es: 'Agentes' },
  'bulkReassign.audienceUploader':           { en: 'Uploaders',                                es: 'Uploaders' },

  // v0.6.0 Fase 11.10 — Sub-screening Option C Hybrid stepper
  'subScreeningStepper.ariaNav':             { en: 'Screening domains',                       es: 'Dominios del screening' },
  'subScreeningStepper.stepAria':            { en: 'Step {step}: {title} ({status})',          es: 'Paso {step}: {title} ({status})' },
  'subScreeningStepper.completedOfTotal':    { en: '{count} of {total} completed',             es: '{count} de {total} completadas' },
  'subScreeningStepper.completedStrip.title':{ en: '{count} sections completed',               es: '{count} secciones completadas' },
  'subScreeningStepper.finding.noFinding':   { en: 'No finding',                               es: 'Sin hallazgo' },
  'subScreeningStepper.finding.positive':    { en: 'Positive finding',                         es: 'Hallazgo positivo' },
  'subScreeningStepper.finding.alert':       { en: 'Safety concern flagged',                   es: 'Alerta de seguridad' },
  'subScreeningStepper.pregunta.label':      { en: 'QUESTION {n}',                             es: 'PREGUNTA {n}' },
  'subScreeningStepper.pregunta.enProgreso': { en: 'In progress',                              es: 'En progreso' },

  // v0.6.0 Fase 11.12 — IncompleteHmModal (block submit con HM incompletos)
  'incompleteHm.title':                      { en: 'Household members pending',                es: 'Miembros del hogar pendientes' },
  'incompleteHm.body':                       { en: '{count} member(s) not yet completed or declined. The parent screening cannot be submitted until each member is finalized.', es: '{count} miembro(s) sin completar o declinar. El screening padre no puede enviarse hasta que cada miembro esté finalizado.' },
  'incompleteHm.status.pending':             { en: 'Pending capture',                         es: 'Pendiente de captura' },
  'incompleteHm.status.inProgress':          { en: 'In progress (mini-screening unfinished)', es: 'En curso (mini-screening sin finalizar)' },
  'incompleteHm.completar':                  { en: 'Complete members',                        es: 'Completar miembros' },
  'incompleteHm.declineAll':                 { en: 'Decline all pending',                     es: 'Declinar todos pendientes' },
  'incompleteHm.toast.declineSuccess':       { en: '{count} member(s) declined; retrying submit', es: '{count} miembro(s) declinado(s); reintentando envío' },
  'bulkReassign.body':                       { en: 'Select source and target users, the count of items to move, and a reason for the audit log.', es: 'Selecciona los usuarios de origen y destino, la cantidad de items a mover, y una razón para el log de auditoría.' },
  'bulkReassign.source':                     { en: 'Source user',                              es: 'Usuario origen' },
  'bulkReassign.target':                     { en: 'Target user',                              es: 'Usuario destino' },
  'bulkReassign.pickUser':                   { en: '— pick a user —',                          es: '— elegir usuario —' },
  'bulkReassign.count':                      { en: 'Count',                                    es: 'Cantidad' },
  'bulkReassign.countHint':                  { en: 'Positive integer. Max 1000 per call. If count > available, only available items move (partial result reported).', es: 'Entero positivo. Máximo 1000 por llamada. Si count > disponible, solo se mueven los disponibles (resultado parcial reportado).' },
  'bulkReassign.reason':                     { en: 'Reason',                                   es: 'Razón' },
  'bulkReassign.reasonPlaceholder':          { en: 'e.g. Agent2 leaving for vacation — redistributing pending queue', es: 'p.ej. Agente2 sale de vacaciones — redistribuyendo cola pendiente' },
  'bulkReassign.reasonHint':                 { en: 'Minimum 5 characters. Recorded permanently in audit log.', es: 'Mínimo 5 caracteres. Se registra permanentemente en el log de auditoría.' },
  'bulkReassign.submit':                     { en: 'Move now',                                 es: 'Mover ahora' },
  'bulkReassign.toast.success':              { en: 'Bulk reassign complete',                   es: 'Reasignación masiva completa' },
  'bulkReassign.toast.successDesc':          { en: '{count} items moved',                      es: '{count} items movidos' },
  'bulkReassign.toast.partial':              { en: 'Partial reassign',                         es: 'Reasignación parcial' },
  'bulkReassign.toast.partialDesc':          { en: '{actual} of {requested} items moved (source had fewer available)', es: '{actual} de {requested} items movidos (el origen tenía menos disponibles)' },
  'systemConfig.row.history':                { en: 'History',                                es: 'Historial' },
  'systemConfig.edit.title':                 { en: 'Edit',                                   es: 'Editar' },
  'systemConfig.edit.warningTitle':          { en: 'This edit affects payroll',              es: 'Esta edici\u00f3n afecta payroll' },
  'systemConfig.edit.warningBody':           { en: 'Changing this value affects Cluster P payroll calculations. Coordinate with your accountant before saving. The change is recorded with an effective date so retroactive payroll uses the historical value for each screening.', es: 'Cambiar este valor afecta los c\u00e1lculos de payroll (Cluster P). Coord\u00ednate con tu contador antes de guardar. El cambio se registra con fecha efectiva para que los c\u00e1lculos retroactivos usen el valor hist\u00f3rico de cada screening.' },
  'systemConfig.edit.valueLabelJson':        { en: 'New value (JSON)',                       es: 'Nuevo valor (JSON)' },
  'systemConfig.edit.valueLabelInt':        { en: 'New value (positive integer)',           es: 'Nuevo valor (entero positivo)' },
  'systemConfig.edit.reasonLabel':           { en: 'Reason for change',                      es: 'Raz\u00f3n del cambio' },
  'systemConfig.edit.reasonPlaceholder':    { en: 'e.g. Updating thresholds for Q3 ramp per accountant guidance', es: 'p.ej. Actualizando umbrales para ramp Q3 seg\u00fan recomendaci\u00f3n del contador' },
  'systemConfig.edit.reasonHint':            { en: 'Minimum 5 characters. Recorded in audit log permanently.', es: 'M\u00ednimo 5 caracteres. Se registra permanentemente en el log de auditor\u00eda.' },
  'systemConfig.edit.effectiveDateLabel':    { en: 'Effective date',                         es: 'Fecha efectiva' },
  'systemConfig.edit.effectiveDateHint':     { en: 'Today or past: applies NOW. A FUTURE date schedules the change \u2014 the live value does not move until that day.', es: 'Hoy o pasada: aplica AHORA. Una fecha FUTURA deja el cambio PROGRAMADO \u2014 el valor vivo no se mueve hasta ese d\u00eda.' },
  'systemConfig.editScheduled':              { en: 'Change scheduled \u2014 it will apply on its effective date', es: 'Cambio programado \u2014 aplicar\u00e1 en su fecha de vigencia' },
  'systemConfig.edit.ack1':                  { en: 'I am sure I want to change this payroll-affecting value', es: 'Estoy seguro de cambiar este valor que afecta payroll' },
  'systemConfig.edit.ack2':                  { en: 'I understand this edit affects pay calculations', es: 'Entiendo que esta edici\u00f3n afecta los c\u00e1lculos de pago' },
  'systemConfig.edit.submit':                { en: 'Save with safeguards',                   es: 'Guardar con salvaguardas' },
  'systemConfig.editSuccess':                { en: 'System config updated. Audit row recorded.', es: 'Configuraci\u00f3n actualizada. Registro de auditor\u00eda creado.' },
  'systemConfig.history.title':              { en: 'Edit history for',                       es: 'Historial de ediciones de' },
  'systemConfig.history.empty':              { en: 'No edits recorded yet.',                 es: 'Sin ediciones registradas a\u00fan.' },
  'systemConfig.history.effectiveDate':      { en: 'Effective date',                         es: 'Fecha efectiva' },
  'systemConfig.history.reason':             { en: 'Reason',                                 es: 'Raz\u00f3n' },
  'systemConfig.history.before':             { en: 'Before',                                 es: 'Antes' },
  'systemConfig.history.after':              { en: 'After',                                  es: 'Despu\u00e9s' },

  // v0.6.0 Cluster N — CIN late-capture modal (uploader profile view)
  'cinCapture.cta':                          { en: 'CIN not captured \u2014 add now',         es: 'CIN no capturado \u2014 agregar' },
  'cinCapture.modal.title':                  { en: 'Add Medicaid ID',                          es: 'Agregar Medicaid ID' },
  'cinCapture.modal.body':                   { en: 'This client was promoted from a household member without a captured Medicaid ID. Add it now so the FindHelp upload uses the real CIN instead of the HH placeholder.', es: 'Este cliente se promovi\u00f3 desde un miembro del hogar sin Medicaid ID capturado. Agregalo ahora para que la subida a FindHelp use el CIN real en vez del placeholder HH.' },
  'cinCapture.modal.label':                  { en: 'Medicaid ID',                              es: 'Medicaid ID' },
  'cinCapture.modal.placeholder':            { en: 'e.g. AB12345C',                            es: 'p.ej. AB12345C' },
  'cinCapture.modal.hint':                   { en: '6\u201330 characters. Saved encrypted; mirrored to both household_members and clients in one transaction.', es: '6\u201330 caracteres. Se guarda encriptado; se replica en household_members y clients en una sola transacci\u00f3n.' },
  'cinCapture.modal.submit':                 { en: 'Save Medicaid ID',                         es: 'Guardar Medicaid ID' },
  'cinCapture.toast.success':                { en: 'Medicaid ID captured \u2714',             es: 'Medicaid ID capturado \u2714' },
  'cinCapture.error.duplicate':              { en: 'Another client already has this Medicaid ID on file. Contact admin to investigate.', es: 'Otro cliente ya tiene este Medicaid ID registrado. Contactar a admin para investigar.' },

  // v0.6.0 Cluster N — /admin/uploaders-activity deep refactor
  'uploadersActivity.title':                 { en: 'Uploaders activity',                       es: 'Actividad de uploaders' },
  'uploadersActivity.subtitle':              { en: 'Per-uploader stats with drill-down, filters, and export. Polls every 30s.', es: 'Estad\u00edsticas por uploader con drill-down, filtros y exportaci\u00f3n. Refresca cada 30s.' },
  'uploadersActivity.exportButton':          { en: 'Export',                                   es: 'Exportar' },
  'uploadersActivity.export.comingSoon':     { en: 'Export endpoint coming soon (CSV / XLSX)', es: 'Endpoint de exportaci\u00f3n pr\u00f3ximamente (CSV / XLSX)' },
  'uploadersActivity.tierDistribution':      { en: 'Tier distribution',                        es: 'Distribuci\u00f3n por nivel' },
  'uploadersActivity.filters.title':         { en: 'Filters',                                  es: 'Filtros' },
  'uploadersActivity.filters.searchLabel':   { en: 'Search by name or email',                  es: 'Buscar por nombre o correo' },
  'uploadersActivity.filters.searchPlaceholder': { en: 'Type to filter\u2026',                es: 'Escrib\u00ed para filtrar\u2026' },
  'uploadersActivity.filters.tierLabel':     { en: 'Tier',                                     es: 'Nivel' },
  'uploadersActivity.filters.activeOnly':    { en: 'Active only',                              es: 'Solo activos' },
  'uploadersActivity.empty.noUploaders':     { en: 'No uploaders found.',                      es: 'No hay uploaders.' },
  'uploadersActivity.empty.noMatchingFilters': { en: 'No uploaders match the current filters.', es: 'Ning\u00fan uploader coincide con los filtros actuales.' },
  'uploadersActivity.col.uploader':          { en: 'Uploader',                                 es: 'Uploader' },
  'uploadersActivity.col.tier':              { en: 'Tier',                                     es: 'Nivel' },
  'uploadersActivity.col.uploadsToday':      { en: 'Today',                                    es: 'Hoy' },
  'uploadersActivity.col.uploadsMonth':      { en: 'This month',                               es: 'Este mes' },
  'uploadersActivity.col.subMonth':          { en: 'Sub',                                      es: 'Sub' },
  'uploadersActivity.col.noMedicaidMonth':   { en: 'No-Med',                                   es: 'Sin-Med' },
  'uploadersActivity.col.actions':           { en: 'Open',                                     es: 'Abrir' },

  // v0.6.0 Cluster N — LiveMonitoring uploader widgets
  'admin.uploadersProgress.distribution.title':      { en: 'Uploader tier distribution',          es: 'Distribuci\u00f3n de niveles (uploaders)' },
  'admin.uploadersProgress.distribution.columnAria': { en: 'Filter to {tier}: {count} uploaders',  es: 'Filtrar a {tier}: {count} uploaders' },
  'admin.uploadersProgress.topPerformer.title':      { en: 'Today\u2019s top uploaders',          es: 'Top uploaders de hoy' },
  'admin.uploadersProgress.topPerformer.empty':      { en: 'No uploads yet today.',                es: 'A\u00fan sin subidas hoy.' },

  // v0.6.0 Cluster N — uploaders-activity export (N-Q8 B FUNCIONAL)
  'uploadersActivity.export.success':        { en: '{count} rows exported',                    es: '{count} filas exportadas' },
  'uploadersActivity.export.failed':         { en: 'Export failed',                            es: 'Falló la exportación' },

  // v0.6.0 Fase 11.5 — Tiers & Metas page restructure + per-user overrides
  'admin.nav.tiersAndTargets':               { en: 'Tiers & Targets',                          es: 'Niveles y metas' },
  'systemConfig.v2.title':                   { en: 'Tiers, Targets & Overrides',               es: 'Niveles, metas y excepciones' },
  'systemConfig.v2.subtitle':                { en: 'Manage agent + uploader tier thresholds, daily targets, and per-user overrides. Legacy system keys (MFA / retention / lockout) live in Settings.', es: 'Administra los umbrales de niveles + metas diarias de agentes y uploaders, más las excepciones individuales. Las claves heredadas (MFA / retención / lockout) viven en Configuración.' },
  'systemConfig.v2.banner':                  { en: 'Tier threshold edits affect payroll (Cluster P) and require reason + effective date. Per-user overrides bypass the global daily target for that one user.', es: 'Los cambios a umbrales de niveles afectan el payroll (Cluster P) y requieren razón + fecha efectiva. Las excepciones individuales saltan la meta global para ese usuario.' },
  'systemConfig.v2.section.agents':          { en: 'Agents',                                   es: 'Agentes' },
  'systemConfig.v2.section.agentsDesc':      { en: 'Daily screening target + monthly tier thresholds for agents (drives Cluster P payroll).', es: 'Meta diaria de screenings + umbrales mensuales por nivel para agentes (alimenta el payroll Cluster P).' },
  'systemConfig.v2.section.uploaders':       { en: 'Uploaders',                                es: 'Uploaders' },
  'systemConfig.v2.section.uploadersDesc':   { en: 'Daily uploads target + monthly tier thresholds for uploaders. Motivational only — does NOT affect uploader pay.', es: 'Meta diaria de subidas + umbrales mensuales para uploaders. Motivacional — NO afecta el pago.' },
  'systemConfig.v2.section.system':          { en: 'System (advanced)',                        es: 'Sistema (avanzado)' },
  'systemConfig.v2.section.systemDesc':      { en: 'Internal caching + runtime tunables.',     es: 'Caché interno + ajustes en tiempo de ejecución.' },
  'systemConfig.v2.keyLabel.agent_daily_target':       { en: 'Daily screening target',         es: 'Meta diaria de screenings' },
  'systemConfig.v2.keyLabel.agent_tier_thresholds':    { en: 'Monthly tier thresholds',        es: 'Umbrales mensuales por nivel' },
  'systemConfig.v2.keyLabel.uploader_daily_target':    { en: 'Daily uploads target',           es: 'Meta diaria de subidas' },
  'systemConfig.v2.keyLabel.uploader_tier_thresholds': { en: 'Monthly tier thresholds',        es: 'Umbrales mensuales por nivel' },
  'systemConfig.v2.keyLabel.config_cache_ttl_sec':     { en: 'Config cache TTL',               es: 'TTL del caché de configuración' },
  'systemConfig.unit.perDay':                { en: 'per day',                                  es: 'por día' },
  'systemConfig.unit.seconds':               { en: 'seconds',                                  es: 'segundos' },
  'systemConfig.edit.tier.label':            { en: 'Monthly threshold per tier',               es: 'Umbral mensual por nivel' },
  'systemConfig.edit.tier.monotoneHint':     { en: 'Each tier must be strictly greater than the previous: Bronce < Plata < Oro < Diamante.', es: 'Cada nivel debe ser estrictamente mayor al anterior: Bronce < Plata < Oro < Diamante.' },
  'systemConfig.edit.tier.invalidNumber':    { en: 'All four values must be non-negative integers.', es: 'Los cuatro valores deben ser enteros no negativos.' },
  'systemConfig.edit.tier.monotoneError':    { en: 'Tier thresholds must be strictly increasing.', es: 'Los umbrales deben ser estrictamente crecientes.' },
  'systemConfig.edit.dailyTarget.label':     { en: 'New daily target',                         es: 'Nueva meta diaria' },
  'systemConfig.edit.dailyTarget.unit':      { en: 'per day',                                  es: 'por día' },
  'systemConfig.overrides.title':            { en: 'Individual overrides',                     es: 'Excepciones individuales' },
  'systemConfig.overrides.subtitle':         { en: 'Set a per-user daily target that bypasses the global value for that user. Useful for trainees, ramping reps, or part-time staff.', es: 'Define una meta diaria por usuario que sobreescribe el valor global para esa persona. Útil para entrenamiento, ramp, o personal part-time.' },
  'systemConfig.overrides.add':              { en: 'Add override',                             es: 'Agregar excepción' },
  'systemConfig.overrides.empty':            { en: 'No individual overrides yet — all users follow the global daily target.', es: 'No hay excepciones individuales — todos los usuarios siguen la meta global.' },
  'systemConfig.overrides.col.user':         { en: 'User',                                     es: 'Usuario' },
  'systemConfig.overrides.col.role':         { en: 'Role',                                     es: 'Rol' },
  'systemConfig.overrides.col.override':     { en: 'Daily target',                             es: 'Meta diaria' },
  'systemConfig.overrides.picker.title':     { en: 'Pick a user',                              es: 'Elegir usuario' },
  'systemConfig.overrides.picker.searchPlaceholder': { en: 'Search by name or email…',    es: 'Buscar por nombre o correo…' },
  'systemConfig.overrides.picker.empty':     { en: 'No eligible users found.',                 es: 'No hay usuarios disponibles.' },
  'systemConfig.overrides.modal.title':      { en: 'Set daily target override',                es: 'Definir excepción de meta diaria' },
  'systemConfig.overrides.modal.valueLabel': { en: 'Daily target for this user',               es: 'Meta diaria para este usuario' },
  'systemConfig.overrides.modal.valueHint':  { en: 'Positive integer. Overrides the global daily target for THIS user only.', es: 'Entero positivo. Sobreescribe la meta diaria global solo para ESTE usuario.' },
  'systemConfig.overrides.modal.clearToggle':{ en: 'Clear override (use global instead)',       es: 'Borrar excepción (volver al global)' },
  'systemConfig.overrides.modal.clearHint':  { en: 'This user will revert to the global daily target.', es: 'Este usuario vuelve a la meta diaria global.' },
  'systemConfig.overrides.modal.reasonLabel':{ en: 'Reason',                                   es: 'Razón' },
  'systemConfig.overrides.modal.reasonPlaceholder': { en: 'e.g. Trainee — ramp to full target by end of month', es: 'p.ej. Entrenamiento — subir a meta completa a fin de mes' },
  'systemConfig.overrides.modal.reasonHint': { en: 'Minimum 5 characters. Recorded in audit log.', es: 'Mínimo 5 caracteres. Se registra en auditoría.' },
  'systemConfig.overrides.modal.submit':     { en: 'Save override',                            es: 'Guardar excepción' },
  'systemConfig.overrides.modal.submitClear':{ en: 'Clear override',                           es: 'Borrar excepción' },
  'systemConfig.overrides.modal.saveSuccess':{ en: 'Override saved',                           es: 'Excepción guardada' },
  'systemConfig.overrides.modal.invalidValue': { en: 'Value must be a positive integer.',       es: 'El valor debe ser un entero positivo.' },
  'progress.tierThisMonth':    { en: 'This month tier',                        es: 'Nivel este mes' },
  'progress.toTargetToday':    { en: '{count} to go',                          es: 'Faltan {count}' },
  'progress.toNextTier':       { en: '{count} to next tier',                   es: 'Faltan {count} para subir' },
  'progress.atTopTier':        { en: 'Diamond reached!',                       es: 'Diamante alcanzado!' },
  'progress.pendingValidation.badge':   { en: '+{count} pending uploader',                                                          es: '+{count} pendientes uploader' },
  'progress.pendingValidation.tooltip': { en: 'Sub-screenings from household members count when uploader validates insurance.', es: 'Sub-screenings de household members cuentan cuando uploader valida insurance.' },
  'progress.today.validatedNote':       { en: '{validated} validated of {submitted} submitted',                                  es: '{validated} validated de {submitted} submitted' },
  'progress.tierJourney.pendingHint':   { en: '+{count} pending — may count when uploader validates this month',                 es: '+{count} pendientes — pueden contar si uploader las valida este mes' },
  'progress.last7.avg':              { en: 'Avg: {count}',                          es: 'Promedio: {count}' },
  'progress.last7.barAria':          { en: '{dow}: {count} screenings',             es: '{dow}: {count} screenings' },
  'progress.last7.tooltipCount':     { en: '{count} screenings validated',          es: '{count} screenings validated' },
  'progress.last7.tooltipTarget':    { en: 'Daily target: {count}',                 es: 'Meta diaria: {count}' },
  'progress.last7.tooltipMet':       { en: 'Target reached',                        es: 'Meta alcanzada' },
  'progress.last7.tooltipMissing':   { en: 'Missing: {count}',                      es: 'Faltan: {count}' },

  // v0.5.1 BUG-2/4 — IneligibilityModal (PRE-CALL doesn't meet requirements flow)
  'ineligibility.button':                          { en: 'Does not meet requirements',  es: 'No cumple requisitos' },
  'ineligibility.modal.title':                     { en: 'Mark client as ineligible',   es: 'Marcar como inelegible' },
  'ineligibility.modal.body':                      { en: 'Select the reason this client is not eligible for screening.', es: 'Seleccione el motivo por el cual este cliente no cumple los requisitos.' },
  'ineligibility.modal.reasonLegend':              { en: 'Ineligibility reason',        es: 'Motivo de inelegibilidad' },
  'ineligibility.modal.submit':                    { en: 'Confirm',                     es: 'Confirmar' },
  'ineligibility.reason.moved_out_of_ny':          { en: 'Moved out of NY',             es: 'Se mudó fuera de NY' },
  'ineligibility.reason.moved_out_of_area':        { en: 'Moved out of coverage area',  es: 'Se mudó del área de cobertura' },
  'ineligibility.reason.changed_insurance_plan':   { en: 'Changed insurance plan',      es: 'Cambió de plan/seguro' },
  'ineligibility.reason.deceased':                 { en: 'Deceased',                    es: 'Paciente fallecido' },
  'ineligibility.reason.wrong_contact_info':       { en: 'Wrong contact info',          es: 'Datos de contacto incorrectos' },
  'ineligibility.toast.success':                   { en: 'Client marked as ineligible', es: 'Paciente marcado como inelegible' },
  'ineligibility.toast.failed':                    { en: 'Could not mark as ineligible', es: 'No se pudo marcar como inelegible' },
  'progress.bestMonth':        { en: 'Best month: {count} screenings',         es: 'Mejor mes: {count} screenings' },
  'progress.modal.title':      { en: 'My Progress',                            es: 'Mi Progreso' },
  'progress.section.today':         { en: 'Today',          es: 'Hoy' },
  'progress.section.tierJourney':   { en: 'Tier Journey',   es: 'Recorrido de niveles' },
  'progress.section.achievements':  { en: 'Achievements',   es: 'Logros' },
  'progress.section.last7Days':     { en: 'Last 7 days',    es: 'Últimos 7 días' },
  'progress.tierJourney.empty':     { en: 'No closed months yet — your tier history will populate after the first month rollover.', es: 'Aún no hay meses cerrados — tu historial aparecerá tras el primer cierre mensual.' },
  // Tier names (canonical Spanish + English)
  'tier.caminoBronce':         { en: 'Path to Bronze',     es: 'Camino a Bronce' },
  'tier.bronce':               { en: 'Bronze',             es: 'Bronce' },
  'tier.plata':                { en: 'Silver',             es: 'Plata' },
  'tier.oro':                  { en: 'Gold',               es: 'Oro' },
  'tier.diamante':             { en: 'Diamond',            es: 'Diamante' },
  // Streak
  'streak.daysWorking':        { en: '{count} day streak',                          es: '{count} días seguidos' },
  'streak.flameTooltip':       { en: '{count} day streak!',                         es: '{count} días seguidos!' },
  'streak.badge.diamanteDeConstancia': { en: 'Diamond of Consistency',              es: 'Diamante de Constancia' },
  'streak.badge.tooltip':      { en: 'Awarded at {count} consecutive working days', es: 'Se obtiene a los {count} días hábiles consecutivos' },
  // Admin agents-progress
  'admin.nav.agentsProgress':                { en: 'Agents progress',                      es: 'Progreso de agentes' },
  'admin.agentsProgress.title':              { en: 'Agents progress',                      es: 'Progreso de agentes' },
  'admin.agentsProgress.subtitle':           { en: 'Tier, streak and monthly trend per agent.', es: 'Nivel, racha y tendencia mensual por agente.' },
  'admin.agentsProgress.searchPlaceholder':  { en: 'Search by name…',                      es: 'Buscar por nombre…' },
  'admin.agentsProgress.filter.label':       { en: 'Filter by tier',                       es: 'Filtrar por nivel' },
  'admin.agentsProgress.filter.all':         { en: 'All tiers',                            es: 'Todos los niveles' },
  'admin.agentsProgress.empty':              { en: 'No agents match the current filter.',  es: 'No hay agentes que coincidan con el filtro.' },
  'admin.agentsProgress.col.agent':          { en: 'Agent',                                es: 'Agente' },
  'admin.agentsProgress.col.today':          { en: 'Today',                                es: 'Hoy' },
  'admin.agentsProgress.col.thisMonth':      { en: 'This month',                           es: 'Este mes' },
  'admin.agentsProgress.col.tier':           { en: 'Tier',                                 es: 'Nivel' },
  'admin.agentsProgress.col.streak':         { en: 'Streak',                               es: 'Racha' },
  'admin.agentsProgress.col.trend':          { en: 'Trend',                                es: 'Tendencia' },
  'admin.agentsProgress.distribution.title':       { en: 'Tier distribution',              es: 'Distribución de niveles' },
  'admin.agentsProgress.distribution.columnAria':  { en: 'Filter by {tier} tier ({count} agents)', es: 'Filtrar por nivel {tier} ({count} agentes)' },
  // Generic
  'common.error':              { en: 'Error',              es: 'Error' },
  'common.refresh':            { en: 'Refresh',            es: 'Actualizar' },


  // ===== Admin Settings - extended polish (units + structured inputs) =====
  'admin.settings.unit.screeningsPerDay': { en: 'screenings/day', es: 'screenings/día' },
  'admin.settings.unit.seconds': { en: 'seconds', es: 'segundos' },
  'admin.settings.unit.minutes': { en: 'minutes', es: 'minutos' },
  'admin.settings.unit.attempts': { en: 'attempts', es: 'intentos' },
  'admin.settings.unit.points': { en: 'points (max 16)', es: 'puntos (máx. 16)' },
  'admin.settings.unit.days': { en: 'days', es: 'días' },
  'admin.settings.unit.years': { en: 'years', es: 'años' },
  'admin.settings.unit.percent': { en: '%', es: '%' },
  'admin.settings.unit.screenings': { en: 'screenings', es: 'screenings' },
  'admin.settings.field.bronze': { en: 'Bronze', es: 'Bronce' },
  'admin.settings.field.silver': { en: 'Silver', es: 'Plata' },
  'admin.settings.field.gold': { en: 'Gold', es: 'Oro' },
  'admin.settings.confirmEdit.title': { en: 'Confirm change', es: 'Confirmar cambio' },
  'admin.settings.confirmEdit.body': { en: 'Apply this change live?', es: '¿Aplicar este cambio en vivo?' },
  'admin.settings.confirmEdit.cancel': { en: 'Cancel', es: 'Cancelar' },
  'admin.settings.confirmEdit.save': { en: 'Save', es: 'Guardar' },
  'admin.settings.user.unknown': { en: 'Unknown user', es: 'Usuario desconocido' },
  'admin.settings.user.loadFailed': { en: 'Could not load users', es: 'No se pudieron cargar los usuarios' },
  'admin.settings.invalid.notInteger': { en: 'Must be a whole number.', es: 'Debe ser un número entero.' },
  'admin.settings.invalid.outOfRange': { en: 'Out of range.', es: 'Fuera de rango.' },
  'admin.settings.invalid.tierOrder': { en: 'Bronze must be ≤ Silver ≤ Gold.', es: 'Bronce debe ser ≤ Plata ≤ Oro.' },
  'admin.settings.tierExplain': { en: 'Bronze ≤ Silver ≤ Gold', es: 'Bronce ≤ Plata ≤ Oro' },
  'admin.settings.label.weekly_thresholds.desc': { en: 'Number of submitted screenings per week to reach each tier.', es: 'Número de screenings sometidos por semana para alcanzar cada nivel.' },

  // ===== Admin profile - polish (avatar + password req visualization) =====
  'admin.profile.avatar.label': { en: 'User avatar with initials', es: 'Avatar de usuario con iniciales' },
  'admin.profile.lastLogin': { en: 'Last sign-in', es: 'Último ingreso' },
  'admin.profile.lastLoginNever': { en: 'Never', es: 'Nunca' },
  'admin.profile.editToggle': { en: 'Edit', es: 'Editar' },
  'admin.profile.cancelEdit': { en: 'Cancel', es: 'Cancelar' },
  'admin.password.requirements.title': { en: 'Password must:', es: 'La contraseña debe:' },
  'admin.password.requirements.minLength': { en: 'Be at least 8 characters', es: 'Tener al menos 8 caracteres' },
  'admin.password.requirements.upperLower': { en: 'Include upper and lowercase letters', es: 'Incluir mayúsculas y minúsculas' },
  'admin.password.requirements.digit': { en: 'Include a number', es: 'Incluir un número' },

  'admin.settings.label.approval_thresholds.desc': { en: 'Approval percentage required to reach each tier.', es: 'Porcentaje de aprobación requerido para alcanzar cada nivel.' },
  'admin.settings.label.unknown.desc': { en: 'Custom or unrecognized value.', es: 'Valor personalizado o no reconocido.' },

  // ===== Audit: humanized action labels (one per AUDIT_ACTIONS entry) =====
  'admin.audit.action.LOGIN': { en: 'Login', es: 'Inicio de sesión' },
  'admin.audit.action.LOGIN_FAIL': { en: 'Login failed', es: 'Inicio de sesión fallido' },
  'admin.audit.action.LOGIN_LOCKED': { en: 'Account locked', es: 'Cuenta bloqueada' },
  'admin.audit.action.LOGOUT': { en: 'Logout', es: 'Cierre de sesión' },
  'admin.audit.action.AUTO_LOGOUT': { en: 'Auto logout', es: 'Cierre automático' },
  'admin.audit.action.PASSWORD_RESET': { en: 'Password reset', es: 'Restablecer contraseña' },
  'admin.audit.action.PASSWORD_CHANGE': { en: 'Password change', es: 'Cambio de contraseña' },
  'admin.audit.action.PASSWORD_CHANGED': { en: 'Password changed', es: 'Contraseña cambiada' },
  'admin.audit.action.MFA_ENABLE': { en: 'MFA enabled', es: 'MFA activado' },
  'admin.audit.action.MFA_DISABLE': { en: 'MFA disabled', es: 'MFA desactivado' },
  'admin.audit.action.MFA_VERIFY': { en: 'MFA verified', es: 'MFA verificado' },
  'admin.audit.action.MFA_VERIFY_FAIL': { en: 'MFA verification failed', es: 'Verificación MFA fallida' },
  'admin.audit.action.REFRESH_TOKEN': { en: 'Token refreshed', es: 'Token renovado' },
  'admin.audit.action.VIEW_CLIENT': { en: 'Patient viewed', es: 'Paciente visto' },
  'admin.audit.action.EDIT_CLIENT': { en: 'Patient edited', es: 'Paciente editado' },
  'admin.audit.action.VIEW_SCREENING': { en: 'Screening viewed', es: 'Screening visto' },
  'admin.audit.action.EDIT_SCREENING': { en: 'Screening edited', es: 'Screening editado' },
  'admin.audit.action.START_SCREENING': { en: 'Screening started', es: 'Screening iniciado' },
  'admin.audit.action.SUBMIT_SCREENING': { en: 'Screening submitted', es: 'Screening enviado' },
  'admin.audit.action.REVOKE_SUBMIT': { en: 'Submission revoked', es: 'Envío revertido' },
  'admin.audit.action.ASSIGNMENT_ABANDONED': { en: 'Assignment abandoned', es: 'Asignación abandonada' },
  'admin.audit.action.PREVIEW_BATCH': { en: 'Batch previewed', es: 'Vista previa de lote' },
  'admin.audit.action.UPLOAD_BATCH': { en: 'Batch uploaded', es: 'Lote subido' },
  'admin.audit.action.ASSIGN': { en: 'Assignment created', es: 'Asignación creada' },
  'admin.audit.action.REASSIGN': { en: 'Assignment reassigned', es: 'Asignación reasignada' },
  'admin.audit.action.MARK_DNC': { en: 'Marked do-not-call', es: 'Marcado como no llamar' },
  'admin.audit.action.EXPORT_REPORT': { en: 'Report exported', es: 'Reporte exportado' },
  'admin.audit.action.EXPORT_CSV': { en: 'CSV exported', es: 'CSV exportado' },
  'admin.audit.action.USER_CREATE': { en: 'User created', es: 'Usuario creado' },
  'admin.audit.action.USER_UPDATE': { en: 'User updated', es: 'Usuario actualizado' },
  'admin.audit.action.USER_DEACTIVATE': { en: 'User deactivated', es: 'Usuario desactivado' },
  'admin.audit.action.PROFILE_UPDATED': { en: 'Profile updated', es: 'Perfil actualizado' },
  'admin.audit.action.BOOKING_CREATED': { en: 'Booking created', es: 'Cita creada' },
  'admin.audit.action.BOOKING_STATUS_CHANGED': { en: 'Booking status changed', es: 'Estado de cita cambiado' },
  'admin.audit.action.BOOKING_RESCHEDULED': { en: 'Booking rescheduled', es: 'Cita reprogramada' },
  'admin.audit.action.BOOKING_NOTES_EDITED': { en: 'Booking notes edited', es: 'Notas de cita editadas' },
  'admin.audit.action.BOOKING_DELETED': { en: 'Booking deleted', es: 'Cita eliminada' },
  'admin.audit.action.CONFIG_CHANGE': { en: 'Configuration change', es: 'Cambio de configuración' },
  'admin.audit.action.SYSTEM_CONFIG_UPDATED': { en: 'System setting updated', es: 'Ajuste del sistema actualizado' },
  'admin.audit.action.BUG_REPORT_RESOLVED': { en: 'Bug report resolved', es: 'Reporte de error resuelto' },
  'admin.audit.action.BUG_REPORT_REOPENED': { en: 'Bug report reopened', es: 'Reporte de error reabierto' },
  'admin.audit.action.SCREENING_UPLOADED_TO_FINDHELP': { en: 'Screening uploaded to FindHelp', es: 'Screening subido a FindHelp' },
  'admin.audit.action.SCREENING_FLAGGED_BY_UPLOADER': { en: 'Screening flagged by uploader', es: 'Screening marcado por uploader' },
  'admin.audit.action.FINDHELP_SLA_BREACH': { en: 'FindHelp SLA breach', es: 'Incumplimiento de SLA FindHelp' },
  'admin.audit.action.HOUSEHOLD_MEMBER_CAPTURED': { en: 'Household member captured', es: 'Miembro del hogar capturado' },
  'admin.audit.action.HOUSEHOLD_MEMBER_UPDATED': { en: 'Household member updated', es: 'Miembro del hogar actualizado' },

  // ===== Audit: humanized entity-type labels =====
  'admin.audit.entity.user': { en: 'User', es: 'Usuario' },
  'admin.audit.entity.client': { en: 'Patient', es: 'Paciente' },
  'admin.audit.entity.screening': { en: 'Screening', es: 'Screening' },
  'admin.audit.entity.booking': { en: 'Booking', es: 'Cita' },
  'admin.audit.entity.batch': { en: 'Batch', es: 'Lote' },
  'admin.audit.entity.assignment': { en: 'Assignment', es: 'Asignación' },
  'admin.audit.entity.household_member': { en: 'Household member', es: 'Miembro del hogar' },
  'admin.audit.entity.bug_report': { en: 'Bug report', es: 'Reporte de error' },
  'admin.audit.entity.system_config': { en: 'System setting', es: 'Ajuste del sistema' },
  'admin.audit.entity.audit_log': { en: 'Audit log', es: 'Registro de auditoría' },
  'admin.audit.entity.report': { en: 'Report', es: 'Reporte' },
  'admin.audit.entity.session': { en: 'Session', es: 'Sesión' },

  // ===== Audit detail modal: extra copy =====
  'admin.audit.detail.rawAction': { en: 'Raw event', es: 'Evento crudo' },
  'admin.audit.detail.entityIdShort': { en: 'ID', es: 'ID' },
  'admin.audit.detail.additionalDetails': { en: 'Additional details', es: 'Detalles adicionales' },
  'admin.audit.detail.rawJson': { en: 'Raw JSON', es: 'JSON crudo' },

  // ===== Uploader detail modal v2 — enriched copy =====
  'admin.uploaderTeam.detail.role.uploader': { en: 'uploader', es: 'uploader' },
  'admin.uploaderTeam.detail.role.admin': { en: 'admin', es: 'admin' },
  'admin.uploaderTeam.detail.role.supervisor': { en: 'supervisor', es: 'supervisor' },
  'admin.uploaderTeam.detail.role.agent': { en: 'agent', es: 'agente' },
  'admin.uploaderTeam.recentWork.col.timeDelta': { en: 'Submit → upload', es: 'Envío → carga' },
  'admin.uploaderTeam.recentWork.col.notes': { en: 'Notes / error', es: 'Notas / error' },
  'admin.uploaderTeam.recentWork.col.agent': { en: 'Agent', es: 'Agente' },
  'admin.uploaderTeam.recentWork.noteLabel': { en: 'Note', es: 'Nota' },
  'admin.uploaderTeam.recentWork.errorLabel': { en: 'Error', es: 'Error' },
  'admin.uploaderTeam.recentWork.noMedicaidBadge': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'admin.uploaderTeam.recentWork.noDelta': { en: '—', es: '—' },
  'admin.uploaderTeam.stats.hourlyTitle': { en: 'Uploads per hour today', es: 'Cargas por hora hoy' },
  'admin.uploaderTeam.stats.hourlyEmpty': { en: 'No uploads yet today', es: 'Aún sin cargas hoy' },
  'admin.uploaderTeam.stats.longestPending': { en: 'Longest pending in shared queue', es: 'Más antiguo en cola compartida' },
  'admin.uploaderTeam.stats.teamAvg': { en: 'Team avg today', es: 'Prom. equipo hoy' },
  'admin.uploaderTeam.stats.vsTeam.faster': { en: '{n}h faster than team', es: '{n}h más rápido que el equipo' },
  'admin.uploaderTeam.stats.vsTeam.slower': { en: '{n}h slower than team', es: '{n}h más lento que el equipo' },
  'admin.uploaderTeam.stats.vsTeam.equal': { en: 'On pace with team', es: 'Al ritmo del equipo' },
  'admin.uploaderTeam.audit.entryCount': { en: '{n} entries', es: '{n} entradas' },
  'admin.uploaderTeam.audit.dayToday': { en: 'Today', es: 'Hoy' },
  'admin.uploaderTeam.audit.dayYesterday': { en: 'Yesterday', es: 'Ayer' },
  'admin.uploaderTeam.audit.opens': { en: 'Open detail', es: 'Abrir detalle' },
  'admin.uploaderTeam.audit.relativeNow': { en: 'just now', es: 'justo ahora' },
  'admin.uploaderTeam.audit.relativeMin': { en: '{n}m ago', es: 'hace {n}m' },
  'admin.uploaderTeam.audit.relativeHr': { en: '{n}h ago', es: 'hace {n}h' },
  'admin.uploaderTeam.audit.relativeDay': { en: '{n}d ago', es: 'hace {n}d' },
  'admin.uploaderTeam.detail.avatarLabel': { en: 'User avatar with initials', es: 'Avatar de usuario con iniciales' },
  'admin.uploaderTeam.detail.entityIdShort': { en: 'ID', es: 'ID' },

  // ===== Admin: Reports (S7) =====
  'admin.reports.nav': { en: 'Reports', es: 'Reportes' },
  'admin.reports.title': { en: 'Reports & analytics', es: 'Reportes y analítica' },
  'admin.reports.subtitle': { en: 'Operations overview', es: 'Resumen operativo' },
  'admin.reports.period.last7': { en: 'Last 7 days', es: 'Últimos 7 días' },
  'admin.reports.period.last30': { en: 'Last 30 days', es: 'Últimos 30 días' },
  'admin.reports.period.last90': { en: 'Last 90 days', es: 'Últimos 90 días' },
  'admin.reports.period.custom': { en: 'Custom range', es: 'Rango personalizado' },
  'admin.reports.kpi.screeningsSubmitted': { en: 'Screenings submitted', es: 'Screenings enviados' },
  'admin.reports.kpi.uploadedSub': { en: 'uploaded', es: 'cargados' },
  'admin.reports.kpi.bookingsCompleted': { en: 'Bookings completed', es: 'Citas completadas' },
  'admin.reports.kpi.missedSub': { en: 'missed', es: 'perdidas' },
  'admin.reports.kpi.householdCaptured': { en: 'Household members', es: 'Miembros del hogar' },
  'admin.reports.kpi.householdMedSub': { en: 'with Medicaid', es: 'con Medicaid' },
  'admin.reports.kpi.activeAgents': { en: 'Active agents', es: 'Agentes activos' },
  'admin.reports.kpi.agentAvgSub': { en: 'avg / agent', es: 'prom / agente' },
  'admin.reports.kpi.uploadSLA': { en: 'Upload SLA breaches (24h)', es: 'Incumplimientos SLA (24h)' },
  'admin.reports.kpi.slaSub': { en: 'pending > 24h', es: 'pendientes > 24h' },
  'admin.reports.kpi.callAttempts': { en: 'Call attempts', es: 'Intentos de llamada' },
  'admin.reports.kpi.callsSuccessSub': { en: 'successful', es: 'exitosos' },
  'admin.reports.trends.title': { en: 'Daily trends', es: 'Tendencias diarias' },
  'admin.reports.trends.metricScreenings': { en: 'Submitted', es: 'Enviados' },
  'admin.reports.trends.metricUploads': { en: 'Uploaded', es: 'Cargados' },
  'admin.reports.trends.metricBookings': { en: 'Bookings', es: 'Citas' },
  'admin.reports.byAgent.title': { en: 'By agent', es: 'Por agente' },
  'admin.reports.byAgent.col.agent': { en: 'Agent', es: 'Agente' },
  'admin.reports.byAgent.submitted': { en: 'Submitted', es: 'Enviados' },
  'admin.reports.byAgent.uploaded': { en: 'Uploaded', es: 'Cargados' },
  'admin.reports.byAgent.uploadRate': { en: 'Upload rate', es: 'Tasa de carga' },
  'admin.reports.byAgent.calls': { en: 'Calls', es: 'Llamadas' },
  'admin.reports.byAgent.avgCall': { en: 'Avg call', es: 'Llam. prom.' },
  'admin.reports.byAgent.missed': { en: 'Missed', es: 'Perdidas' },
  'admin.reports.byAgent.household': { en: 'Household', es: 'Hogar' },
  'admin.reports.byAgent.activeWork': { en: 'Active work', es: 'Trabajo activo' },
  'admin.reports.byAgent.callTime': { en: 'Call time', es: 'Tiempo en llamada' },
  'admin.reports.byAgent.tier': { en: 'Tier', es: 'Nivel' },
  'admin.reports.byUploader.title': { en: 'By uploader', es: 'Por uploader' },
  'admin.reports.byUploader.col.uploader': { en: 'Uploader', es: 'Uploader' },
  'admin.reports.byUploader.uploaded': { en: 'Uploaded', es: 'Cargados' },
  'admin.reports.byUploader.flagged': { en: 'Flagged', es: 'Marcados' },
  'admin.reports.byUploader.noMedicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'admin.reports.byUploader.avgTime': { en: 'Avg time-to-upload', es: 'Tiempo prom. de carga' },
  'admin.reports.byUploader.slaBreaches': { en: 'SLA breaches', es: 'Incumpl. SLA' },
  'admin.reports.outcomes.title': { en: 'Call outcomes', es: 'Resultados de llamadas' },
  'admin.reports.outcomes.no_answer': { en: 'No answer', es: 'Sin respuesta' },
  'admin.reports.outcomes.voicemail': { en: 'Voicemail', es: 'Buzón de voz' },
  'admin.reports.outcomes.refused': { en: 'Refused', es: 'Rechazada' },
  'admin.reports.outcomes.language_barrier': { en: 'Language barrier', es: 'Barrera idiomática' },
  'admin.reports.outcomes.answered_completed': { en: 'Completed', es: 'Completada' },
  'admin.reports.outcomes.answered_partial': { en: 'Partial', es: 'Parcial' },
  'admin.reports.outcomes.callback_requested': { en: 'Callback requested', es: 'Devolver llamada' },
  'admin.reports.outcomes.wrong_number': { en: 'Wrong number', es: 'Número equivocado' },
  'admin.reports.outcomes.disconnected': { en: 'Disconnected', es: 'Desconectado' },
  'admin.reports.outcomes.deceased': { en: 'Deceased', es: 'Fallecido' },
  'admin.reports.outcomes.completed': { en: 'Completed', es: 'Completada' },
  'admin.reports.outcomes.other': { en: 'Other', es: 'Otro' },
  'admin.reports.tier.bronze': { en: 'Bronze', es: 'Bronce' },
  'admin.reports.tier.silver': { en: 'Silver', es: 'Plata' },
  'admin.reports.tier.gold': { en: 'Gold', es: 'Oro' },
  'admin.reports.tier.below': { en: 'Below', es: 'Bajo' },
  'admin.reports.export.csv': { en: 'Export CSV', es: 'Exportar CSV' },
  'admin.reports.export.byAgent': { en: 'By agent CSV', es: 'CSV por agente' },
  'admin.reports.export.byUploader': { en: 'By uploader CSV', es: 'CSV por uploader' },
  'admin.reports.empty.title': { en: 'No data for this period yet', es: 'Sin datos en este período' },
  'admin.reports.empty.subtitle': { en: 'Try a wider date range or check back later.', es: 'Pruebe con un rango mayor o revise más tarde.' },
  'admin.reports.error.title': { en: 'Could not load reports', es: 'No se pudieron cargar los reportes' },

  'admin.reports.outcomesExport.title': { en: 'Call outcomes export', es: 'Exportar resultados de llamada' },
  'admin.reports.outcomesExport.subtitle': { en: 'CORE (bad data), REACH (voicemail / no answer), and EXHAUSTED (at max attempts). Last 7 days by default. Phone last-4 only — open the profile by client id to call.', es: 'CORE (datos malos), REACH (buzón / no contesta) y EXHAUSTED (agotó intentos). Últimos 7 días por defecto. Solo últimos 4 del teléfono: abrí el perfil por client id para llamar.' },
  'admin.reports.outcomesExport.preview': { en: 'Preview', es: 'Vista previa' },
  'admin.reports.outcomesExport.download': { en: 'Download CSV', es: 'Descargar CSV' },
  'admin.reports.outcomesExport.core': { en: 'CORE — update data', es: 'CORE — actualizar datos' },
  'admin.reports.outcomesExport.reach': { en: 'REACH — recycled queue', es: 'REACH — reciclaban cola' },
  'admin.reports.outcomesExport.exhausted': { en: 'EXHAUSTED — max attempts', es: 'EXHAUSTED — intentos agotados' },
  'admin.reports.outcomesExport.coreHint': { en: 'wrong_number, disconnected, wrong_contact_info', es: 'wrong_number, disconnected, wrong_contact_info' },
  'admin.reports.outcomesExport.reachHint': { en: 'voicemail, no_answer', es: 'voicemail, no_answer' },
  'admin.reports.outcomesExport.exhaustedHint': { en: 'attempt_count ≥ max_call_attempts (same filter as the queue)', es: 'attempt_count ≥ max_call_attempts (el mismo filtro que la cola)' },
  'admin.reports.outcomesExport.total': { en: 'Showing {shown} of {total}', es: 'Mostrando {shown} de {total}' },
  'admin.reports.outcomesExport.truncated': { en: 'truncated at cap', es: 'truncado al tope' },
  'admin.reports.outcomesExport.empty': { en: 'No matching attempts in this range.', es: 'No hay intentos en este rango.' },
  'admin.reports.outcomesExport.col.clientId': { en: 'client_id', es: 'client_id' },
  'admin.reports.outcomesExport.col.outcome': { en: 'outcome', es: 'outcome' },
  'admin.reports.outcomesExport.col.attemptAt': { en: 'attempt_at', es: 'attempt_at' },
  'admin.reports.outcomesExport.col.agent': { en: 'agent', es: 'agente' },
  'admin.reports.outcomesExport.col.assignmentId': { en: 'assignment_id', es: 'assignment_id' },
  'admin.reports.outcomesExport.col.attemptCount': { en: 'attempts', es: 'intentos' },
  'admin.reports.outcomesExport.col.phoneLast4': { en: 'phone last-4', es: 'tel. last-4' },
  'admin.reports.outcomesExport.col.clientStatus': { en: 'client_status', es: 'client_status' },
  'admin.reports.outcomesExport.error': { en: 'Could not export outcomes', es: 'No se pudo exportar resultados' },

  // ===== Admin reassignment (Sprint 7) =====
  'admin.reassign.nav': { en: 'Reassignment', es: 'Reasignación' },
  'admin.reassign.title': { en: 'Reassignment', es: 'Reasignación' },
  'admin.reassign.subtitle': { en: 'Balance the team queue. Auto-rebalance, drag-drop, or bulk-move.', es: 'Equilibre la cola del equipo. Auto-balance, arrastrar y soltar, o mover en bloque.' },
  'admin.reassign.refresh': { en: 'Refresh', es: 'Actualizar' },
  'admin.reassign.errors.dashboardLoad': { en: "Couldn't load reassignment dashboard", es: 'No se pudo cargar el panel de reasignación' },

  'admin.reassign.dashboard.totalPending': { en: 'Total pending', es: 'Total pendientes' },
  'admin.reassign.dashboard.totalInProgress': { en: 'In progress', es: 'En curso' },
  'admin.reassign.dashboard.activeAgents': { en: 'Active agents', es: 'Agentes activos' },
  'admin.reassign.dashboard.idealPerAgent': { en: 'Ideal per agent', es: 'Ideal por agente' },

  'admin.reassign.agent.pendingCount': { en: 'Pending', es: 'Pendientes' },
  'admin.reassign.agent.inProgress': { en: 'in progress', es: 'en curso' },
  'admin.reassign.agent.dailyTarget': { en: 'Daily target', es: 'Meta diaria' },
  'admin.reassign.agent.completedToday': { en: 'Completed today', es: 'Completadas hoy' },
  'admin.reassign.agent.targetPercent': { en: 'Target %', es: '% meta' },
  'admin.reassign.agent.statusLabel.online': { en: 'Online', es: 'En línea' },
  'admin.reassign.agent.statusLabel.idle': { en: 'Idle', es: 'Inactivo' },
  'admin.reassign.agent.statusLabel.on_call': { en: 'On call', es: 'En llamada' },
  'admin.reassign.agent.statusLabel.offline': { en: 'Offline', es: 'Desconectado' },
  'admin.reassign.agent.inactive': { en: 'Inactive', es: 'Inactivo' },
  'admin.reassign.agent.overloaded': { en: 'Overloaded', es: 'Sobrecargado' },
  'admin.reassign.agent.balanced': { en: 'Balanced', es: 'Equilibrado' },
  'admin.reassign.agent.underloaded': { en: 'Underloaded', es: 'Subcargado' },

  'admin.reassign.auto.title': { en: 'Auto-rebalance', es: 'Auto-balance' },
  'admin.reassign.auto.button': { en: 'Auto-rebalance now', es: 'Auto-balancear ahora' },
  'admin.reassign.auto.explanation': { en: 'Calculates the ideal queue size per active agent (total pending / active agents) and shifts clients from overloaded (>120%) to underloaded (<50%) agents until everyone is within 10% of ideal.', es: 'Calcula el tamaño ideal de cola por agente activo (total pendientes / agentes activos) y mueve clientes de los sobrecargados (>120%) a los subcargados (<50%) hasta que todos estén a 10% del ideal.' },
  'admin.reassign.auto.respectLanguage': { en: 'Respect language preference (Spanish clients to ES-speaking agents)', es: 'Respetar idioma preferido (clientes en español a agentes que hablen ES)' },
  'admin.reassign.auto.computing': { en: 'Computing plan…', es: 'Calculando plan…' },

  'admin.reassign.manual.title': { en: 'Manual reassignment', es: 'Reasignación manual' },
  'admin.reassign.manual.show': { en: 'Show manual tools', es: 'Mostrar herramientas manuales' },
  'admin.reassign.manual.hide': { en: 'Hide manual tools', es: 'Ocultar herramientas manuales' },
  'admin.reassign.manual.fromLabel': { en: 'From agent', es: 'Agente origen' },
  'admin.reassign.manual.toLabel': { en: 'To agent', es: 'Agente destino' },
  'admin.reassign.manual.dragHint': { en: 'Pick a source on the left, a destination on the right. Drag clients across or use the bulk action.', es: 'Elija un origen a la izquierda y un destino a la derecha. Arrastre clientes o use la acción en bloque.' },
  'admin.reassign.manual.moveSelected': { en: 'Move {count} selected', es: 'Mover {count} seleccionados' },
  'admin.reassign.manual.dropHere': { en: 'Drop clients here to reassign', es: 'Suelte clientes aquí para reasignar' },
  'admin.reassign.manual.fromEmpty': { en: 'No matching clients in this agent queue', es: 'No hay clientes que coincidan en la cola del agente' },
  'admin.reassign.manual.toEmpty': { en: 'No clients yet — drop some here', es: 'Aún sin clientes — arrastre aquí' },
  'admin.reassign.manual.loading': { en: 'Loading…', es: 'Cargando…' },
  'admin.reassign.manual.clients': { en: 'clients', es: 'clientes' },
  'admin.reassign.manual.selectAll': { en: 'Select all', es: 'Seleccionar todo' },
  'admin.reassign.manual.selectedCount': { en: '{count} selected', es: '{count} seleccionados' },
  'admin.reassign.manual.noSelection': { en: 'No clients selected', es: 'No hay clientes seleccionados' },
  'admin.reassign.manual.sameAgent': { en: 'Source and destination must be different agents', es: 'El origen y el destino deben ser agentes distintos' },

  'admin.reassign.filter.language': { en: 'Language', es: 'Idioma' },
  'admin.reassign.filter.languageAny': { en: 'Any', es: 'Cualquiera' },
  'admin.reassign.filter.priority': { en: 'Priority', es: 'Prioridad' },
  'admin.reassign.filter.status': { en: 'Status', es: 'Estado' },
  'admin.reassign.filter.statusPending': { en: 'Pending', es: 'Pendiente' },
  'admin.reassign.filter.statusInProgress': { en: 'In progress', es: 'En curso' },
  'admin.reassign.filter.statusAll': { en: 'All open', es: 'Todos abiertos' },

  'admin.reassign.preview.title': { en: 'Review the plan', es: 'Revise el plan' },
  'admin.reassign.preview.summary': { en: 'About to move {count} clients ({method}).', es: 'A punto de mover {count} clientes ({method}).' },
  'admin.reassign.preview.apply': { en: 'Apply', es: 'Aplicar' },
  'admin.reassign.preview.applying': { en: 'Applying…', es: 'Aplicando…' },
  'admin.reassign.preview.cancel': { en: 'Cancel', es: 'Cancelar' },
  'admin.reassign.preview.edit': { en: 'Edit', es: 'Editar' },
  'admin.reassign.preview.empty.title': { en: 'Nothing to do', es: 'Nada que hacer' },
  'admin.reassign.preview.empty.body': { en: 'Queues are already balanced enough. No reassignment needed right now.', es: 'Las colas ya están suficientemente equilibradas. No se requiere reasignación ahora.' },

  'admin.reassign.result.applied': { en: 'Reassigned {count} clients', es: 'Se reasignaron {count} clientes' },
  'admin.reassign.result.error': { en: "Reassignment couldn't be applied", es: 'No se pudo aplicar la reasignación' },

  // ===== Admin missed bookings — extra keys for reassign-to-other-agent =====
  'admin.missedBookings.assignToMe': { en: 'Assign to me', es: 'Asignarme' },
  'admin.missedBookings.reassignTo': { en: 'Reassign to…', es: 'Reasignar a…' },
  'admin.missedBookings.reassignTo.placeholder': { en: 'Choose agent', es: 'Elegí agente' },
  'admin.missedBookings.reassignTo.confirm': { en: 'Reassign?', es: '¿Reasignar?' },
  'admin.missedBookings.reassignTo.successToast': { en: 'Booking reassigned to {name}', es: 'Cita reasignada a {name}' },
  'admin.missedBookings.reassignTo.errorToast': { en: "Couldn't reassign booking", es: 'No se pudo reasignar la cita' },

  // ===== Cluster A followup (2026-05-13) — A-1/A-2/A-3/A-4 =====
  // Client profile — request reassign / assignment (A-1)
  'clientProfile.requestReassign.action': { en: 'Request reassignment from admin', es: 'Solicitar reasignación al admin' },
  'clientProfile.requestReassign.successTitle': { en: 'Request sent', es: 'Solicitud enviada' },
  'clientProfile.requestReassign.successBody': { en: 'An admin will follow up.', es: 'Un admin va a hacer seguimiento.' },
  'clientProfile.requestReassign.errorTitle': { en: 'Could not send request', es: 'No se pudo enviar la solicitud' },
  'clientProfile.requestAssignment.action': { en: 'Request assignment from admin', es: 'Solicitar asignación al admin' },
  // S5 (2026-09-19) — el miembro llama después de un «número equivocado».
  // Antes, sin asignación activa el único botón era pedirle al admin, con el
  // miembro esperando en la línea.
  'clientProfile.claimInbound.action': { en: 'The member is calling — take now', es: 'El miembro está llamando — atender ahora' },
  'clientProfile.claimInbound.hint': { en: 'Type the number the member is calling from. Creates the assignment for you and opens pre-call. Admin is notified by the audit trail.', es: 'Escribí el número desde el que está llamando el miembro. Crea la asignación a tu nombre y abre la pre-llamada. El admin se entera por la auditoría.' },
  // H5 (2026-09-19, decision CEO opcion C) — tope diario + evidencia de la
  // llamada entrante. El numero se pide acá porque es el unico momento en que
  // alguien lo sabe: el miembro está en la línea.
  'clientProfile.claimInbound.phoneLabel': { en: 'Number the member is calling from', es: 'Número desde el que llama el miembro' },
  'clientProfile.claimInbound.phonePlaceholder': { en: '(718) 555-0142', es: '(718) 555-0142' },
  'clientProfile.claimInbound.phoneMismatch': { en: 'That number is not on this member’s record. Check it with them, or ask an admin to assign the member.', es: 'Ese número no figura en el registro de este miembro. Verificalo con él, o pedile al admin que lo asigne.' },
  'clientProfile.claimInbound.dailyCap': { en: 'You have reached today’s limit of inbound claims. Ask an admin to assign this member.', es: 'Llegaste al tope de reclamos entrantes de hoy. Pedile al admin que asigne a este miembro.' },
  'clientProfile.claimInbound.errorTitle': { en: 'Could not take this member', es: 'No se pudo atender a este miembro' },
  'clientProfile.claimInbound.alreadyAssigned': { en: 'Someone already has this member open. Ask them to take the call.', es: 'Alguien ya tiene abierto a este miembro. Pedile que atienda la llamada.' },
  'clientProfile.claimInbound.ineligible': { en: 'This member was marked ineligible. Only an admin can revert it.', es: 'Este miembro está marcado como no elegible. Sólo un admin lo revierte.' },
  'clientProfile.claimInbound.notEligible': { en: 'This member does not pass the queue checks (do-not-call, ZIP, attempt cap or recent screening).', es: 'Este miembro no pasa los chequeos de la cola (no llamar, ZIP, tope de intentos o screening reciente).' },
  'clientProfile.claimInbound.notDropped': { en: 'This door is only for members whose call we dropped (wrong number, disconnected, no answer, voicemail).', es: 'Esta puerta es sólo para miembros a los que se les cayó la llamada (número equivocado, desconectado, no contestó, buzón).' },
  'clientProfile.adminReassign': { en: 'Open reassignment page', es: 'Abrir reasignación' },
  'clientProfile.adminAssign': { en: 'Open reassignment page', es: 'Abrir reasignación' },
  // Client profile — preflight on "Open pre-call" (A-2)
  'clientProfile.goToPreCall.staleTitle': { en: 'This assignment is no longer active', es: 'Esta asignación ya no está activa' },
  'clientProfile.goToPreCall.staleBody': { en: 'The session was closed (timeout or reassignment). Pick the next client from your queue.', es: 'La sesión se cerró (timeout o reasignación). Tomá el siguiente cliente de tu cola.' },
  'clientProfile.goToPreCall.errorTitle': { en: 'Could not open pre-call', es: 'No se pudo abrir la pre-llamada' },
  // Client profile — admin reset attempts (A-3)
  'clientProfile.resetAttempts.action': { en: 'Reset attempt counter (admin)', es: 'Resetear contador (admin)' },
  'clientProfile.resetAttempts.reasonLabel': { en: 'Reason (required, min 3 chars)', es: 'Motivo (requerido, mín. 3 caracteres)' },
  'clientProfile.resetAttempts.reasonPlaceholder': { en: 'e.g. wrong-number cleanup, new contact info found…', es: 'Ej: limpieza de wrong-number, contacto nuevo encontrado…' },
  'clientProfile.resetAttempts.confirm': { en: 'Reset to 0', es: 'Resetear a 0' },
  'clientProfile.resetAttempts.cancel': { en: 'Cancel', es: 'Cancelar' },
  'clientProfile.resetAttempts.successTitle': { en: 'Attempt counter reset', es: 'Contador de intentos reseteado' },
  'clientProfile.resetAttempts.successBody': { en: 'The client is back in the queue. Last attempting agent was notified.', es: 'El cliente vuelve a la cola. El último agente que lo intentó fue notificado.' },
  'clientProfile.resetAttempts.errorTitle': { en: 'Could not reset attempts', es: 'No se pudo resetear el contador' },
  // Pre-call — stale (A-2 frontend message)
  'precall.stale.title': { en: 'This assignment is no longer active', es: 'Esta asignación ya no está activa' },
  'precall.stale.body': { en: 'The session was closed (timeout or reassignment). Go back to your queue and take the next call.', es: 'La sesión se cerró (timeout o reasignación). Volvé a tu cola y tomá la siguiente llamada.' },
  'precall.stale.backToQueue': { en: 'Back to my queue', es: 'Volver a mi cola' },
  // Pre-call — max attempts UX (A-3)
  'precall.maxAttempts.title': { en: 'Maximum call attempts reached', es: 'Cliente alcanzó el máximo de intentos' },
  'precall.maxAttempts.body': { en: 'This client cannot be screened until an admin resets the counter or the attempt is excluded.', es: 'Este cliente no se puede screenear hasta que un admin resetee el contador o se excluya el intento.' },
  'precall.maxAttempts.markExcluded': { en: 'Mark as excluded (max attempts)', es: 'Marcar excluido (max intentos)' },
  'precall.maxAttempts.adminReset': { en: 'Reset counter (admin only)', es: 'Resetear contador (admin)' },
  'precall.maxAttempts.markExcludedSuccess': { en: 'Marked as excluded. Returning to queue.', es: 'Marcado como excluido. Volviendo a la cola.' },
  // Pre-call — mismatched assignment with 3 actions (A-4)
  'precall.mismatch.title': { en: 'You already have an active call', es: 'Ya tenés una llamada activa' },
  'precall.mismatch.body': { en: "You're on a call with {name}. This URL points to a different client. What do you want to do?", es: 'Estás en llamada con {name}. Esta URL apunta a otro cliente. ¿Qué querés hacer?' },
  'precall.mismatch.backToCurrent': { en: 'Back to my current call', es: 'Volver a mi llamada actual' },
  'precall.mismatch.cancelAndOpen': { en: 'Cancel current and open this one', es: 'Cancelar la actual y abrir esta' },
  'precall.mismatch.backToQueue': { en: 'Back to queue', es: 'Volver a la cola' },

  // ===== Cluster A-followup-v2 (2026-05-13) — A-11/A-10/A-5/A-6/A-7 =====
  // A-11: AGENT_HAS_ACTIVE_CALL conflict in BookingsListModal startScreening
  'bookings.detail.conflict.title': { en: 'You already have an active call', es: 'Ya tenés una llamada activa' },
  'bookings.detail.conflict.body': { en: "You're already on a call with another client. End or release that call before starting this booking, or return to your current call.", es: 'Ya estás en una llamada con otro cliente. Terminá o liberá esa llamada antes de empezar esta cita, o volvé a tu llamada actual.' },
  'bookings.detail.conflict.backToCurrent': { en: 'Back to my current call', es: 'Volver a mi llamada actual' },
  'bookings.detail.conflict.cancelAndOpen': { en: 'Release current and start this booking', es: 'Liberar la actual y empezar esta cita' },
  'bookings.detail.conflict.dismiss': { en: 'Dismiss', es: 'Descartar' },
  // A-5: per-reason CTA when recent_screening is the blocker (Bug-User-04 closes from root)
  'precall.recentScreening.title': { en: 'Client already screened in the last 6 months', es: 'Cliente ya screened en los últimos 6 meses' },
  'precall.recentScreening.body': { en: 'This client received a screening within the rescreening blackout window. Mark as already-screened to close the assignment cleanly.', es: 'Este cliente recibió un screening dentro de la ventana de blackout. Marcalo como already-screened para cerrar la asignación limpio.' },
  'precall.recentScreening.markAlready': { en: 'Mark as already screened (6mo)', es: 'Marcar como already_screened (6mo)' },

  // ===== A-14 (2026-05-14) — booking completion lifecycle + KPI =====
  'bookings.history.show': { en: 'Show history', es: 'Mostrar historial' },
  'bookings.history.hide': { en: 'Hide history', es: 'Ocultar historial' },
  // Admin KPI widget
  'admin.kpi.bookings.title': { en: 'Booking completion (30d)', es: 'Completitud de citas (30d)' },
  'admin.kpi.bookings.completed': { en: 'Completed', es: 'Completadas' },
  'admin.kpi.bookings.missed': { en: 'Missed', es: 'No show' },
  'admin.kpi.bookings.cancelled': { en: 'Cancelled', es: 'Canceladas' },
  'admin.kpi.bookings.scheduled': { en: 'Scheduled (open)', es: 'Agendadas (abiertas)' },
  'admin.kpi.bookings.completionRate': { en: 'Completion rate', es: 'Tasa de completitud' },
  'admin.kpi.bookings.noShowRate': { en: 'No-show rate', es: 'Tasa de no show' },
  'admin.kpi.bookings.empty': { en: 'No bookings in the last 30 days yet.', es: 'Sin citas en los últimos 30 días.' },
  'admin.kpi.bookings.errorTitle': { en: "Couldn't load booking KPIs", es: 'No se pudo cargar el KPI' },

  // ===== Cluster H v2 — Household screening inline panel (2026-05-17) =====
  // Mechanical/internal subScreening.* keys below are still in use until the
  // i18n rename cleanup (deferred to post-Fase-6). New panel-only keys go here.
  'householdScreening.header.title': { en: 'Household screening', es: 'Screening del hogar' },

  // ===== Cluster H v2 Fase 2.7 — Bidirectional referral chain (2026-05-17) =====
  // Inbound: this client was promoted from a parent client's household screening.
  'referralChain.label':         { en: 'Referred from', es: 'Referido de' },
  'referralChain.fromHousehold': { en: 'Household of', es: 'Hogar de' },
  'referralChain.promotedBy':    { en: 'Agent: {name}', es: 'Agente: {name}' },
  // Outbound: this client\'s screening captured + promoted N household members.
  'linkedPromotions.title':      { en: 'Household members promoted ({n})', es: 'Miembros del hogar promovidos ({n})' },
  // ===== Cluster H v2 Fase 3 — ScreeningSummary post-submit (2026-05-17) =====
  'screeningSummary.heading':         { en: 'Screening completed', es: 'Screening completado' },
  'screeningSummary.loading':         { en: 'Loading summary…', es: 'Cargando resumen…' },
  'screeningSummary.error':           { en: "Couldn't load summary", es: 'No se pudo cargar el resumen' },
  'screeningSummary.parentLabel':     { en: 'Main screening', es: 'Screening principal' },
  'screeningSummary.parentSubmitted': { en: '✓ Submitted • {time}', es: '✓ Enviado • {time}' },
  'screeningSummary.parentBonusNote': { en: '+1 to your daily counter', es: '+1 en tu meta diaria' },
  'screeningSummary.householdSection':{ en: 'Household members ({n})', es: 'Miembros del hogar ({n})' },
  // [SUB-J v2] Removed orphaned screeningSummary.hint* keys (6 entries) — privacy
  // debt cleanup. The legacy hint* set inlined a compensation amount into
  // the English/Spanish strings; the ScreeningSummary refactor (commit a2c9385)
  // dropped the hint UI and replaced it with neutral memberState* labels below.
  // Cluster H v2 privacy refactor — household members card (names + state).
  'screeningSummary.householdMembersTitle':  { en: 'Household members ({n})', es: 'Miembros del hogar ({n})' },
  'screeningSummary.memberStatePending':     { en: 'Not started', es: 'No iniciado' },
  'screeningSummary.memberStateInProgress':  { en: 'In progress', es: 'En curso' },
  'screeningSummary.memberStateCompleted':   { en: '✓ Awaiting uploader validation', es: '✓ Pendiente validación uploader' },
  'screeningSummary.memberStateValidated':   { en: '✓ Validated by uploader', es: '✓ Validado por uploader' },
  'screeningSummary.memberStateDeclined':    { en: 'Declined by agent', es: 'Declinado por agente' },
  'screeningSummary.memberStateRejected':    { en: 'Rejected by uploader', es: 'Rechazado por uploader' },
  // Monthly progress card — tier labels (NO dollar amounts).
  'screeningSummary.progressTitle':   { en: 'Your progress this month', es: 'Tu progreso este mes' },
  'screeningSummary.tier.below_floor':{ en: 'Below floor', es: 'Bajo el piso' },
  'screeningSummary.tier.base':       { en: 'Base', es: 'Base' },
  'screeningSummary.tier.t1':         { en: 'T1', es: 'T1' },
  'screeningSummary.tier.t2':         { en: 'T2', es: 'T2' },
  'screeningSummary.tier.t3':         { en: 'T3', es: 'T3' },
  'screeningSummary.toNext':          { en: '{n} more to reach {tierLabel}', es: 'Te faltan {n} para {tierLabel}' },
  'screeningSummary.atTopTier':       { en: "🎉 You're at T3 — outstanding effort! Every screening is stretch.", es: '🎉 Estás en T3 — súper esfuerzo. Cada screening adicional cuenta como stretch.' },
  'screeningSummary.btnQueue':        { en: 'Back to queue', es: 'Volver al queue' },
  'screeningSummary.btnDashboard':    { en: 'View my dashboard', es: 'Ver mi dashboard' },

  // ===== Cluster H v2 SUB-G — agent pending uploader-validation section =====
  'queue.pendingValidations.title': { en: 'Awaiting uploader validation ({n})', es: 'Pendientes validación uploader ({n})' },
  'queue.pendingValidations.show':  { en: 'Show', es: 'Mostrar' },
  'queue.pendingValidations.hide':  { en: 'Hide', es: 'Ocultar' },
  'queue.pendingValidations.row':   { en: 'Household of {parent} (…{cin}) · submitted {hours}h ago', es: 'Hogar de {parent} (…{cin}) · enviada hace {hours}h' },
  'queue.pendingValidations.awaitingBadge': { en: 'Awaiting', es: 'Pendiente' },

  // Modal overlay (ClientProfileModal) — preview of a related client.
  'profileModal.title':            { en: 'Client preview', es: 'Vista rápida del cliente' },
  'profileModal.openFull':         { en: 'Open full profile', es: 'Abrir perfil completo' },
  'profileModal.close':            { en: 'Close', es: 'Cerrar' },
  'profileModal.loading':          { en: 'Loading…', es: 'Cargando…' },
  'profileModal.error':            { en: "Couldn't load profile", es: 'No se pudo cargar el perfil' },
  'profileModal.recentScreenings': { en: 'Recent screenings', es: 'Screenings recientes' },
  'profileModal.noScreenings':     { en: 'No screenings yet', es: 'Sin screenings' },

  // 6 derived-state badge labels (deriveHouseholdScreeningState).
  // 'pending' reuses the existing key (already defined further down in the
  // householdStep.* block); only the 5 new states are added here.
  'householdStep.statusBadge.in_progress': { en: 'In progress', es: 'En curso' },
  'householdStep.statusBadge.completed': { en: 'Completed', es: 'Completada' },
  'householdStep.statusBadge.validated': { en: 'Validated', es: 'Validada' },
  'householdStep.statusBadge.declined': { en: 'Declined', es: 'Declinado' },
  'householdStep.statusBadge.rejected_by_uploader': { en: 'Rejected by uploader', es: 'Rechazada por uploader' },

  // Panel toggle + decline action labels (LD-H2-07/08).
  'householdStep.actions.screen': { en: 'Screen', es: 'Screening' },
  'householdStep.actions.collapseScreen': { en: 'Close', es: 'Cerrar' },
  'householdStep.actions.decline': { en: 'Decline', es: 'Declinar' },
  'householdStep.actions.declineConfirm': { en: 'Confirm decline', es: 'Confirmar' },
  'householdStep.actions.declineCancel': { en: 'Cancel', es: 'Cancelar' },
  'householdStep.actions.declineSuccess': { en: 'Household member declined', es: 'Miembro declinado' },
  'householdStep.actions.declineFailed': { en: "Couldn't decline", es: 'No se pudo declinar' },
  // v0.6.0 Issue 6 — declined-section toggle (HouseholdMembersStep)
  'householdStep.declined.section.toggleShow':  { en: 'Show declined ({count})', es: 'Ver declinados ({count})' },
  'householdStep.declined.section.toggleHide':  { en: 'Hide declined',           es: 'Ocultar declinados' },
  // v0.6.0 Issue 5 — advancement guard for in_progress sub-screenings
  'householdStep.guard.banner.title':           { en: 'Unsubmitted sub-screening',        es: 'Mini-screening sin enviar' },
  'householdStep.guard.banner.body':            { en: 'You have {count} household sub-screening(s) started but not submitted. Finish or decline them before advancing.', es: 'Tenés {count} mini-screening(s) iniciados pero sin enviar. Completalos o declinalos antes de avanzar.' },
  'householdStep.guard.banner.cta':             { en: 'Go to first unsubmitted',          es: 'Ir al primero sin enviar' },
  'householdStep.guard.continueBlocked':        { en: 'Finish or decline all in-progress sub-screenings first', es: 'Completá o decliná los mini-screenings en curso primero' },
  // v0.6.1 Fase 2.5 — block-Continuar UX when ANY HM is in pending OR in_progress state.
  'householdStep.continueBlockedCount':         { en: '{count} pending member(s)', es: 'Hay {count} miembros pendientes' },
  'householdStep.continueBlockedHelper':        { en: 'Complete or decline pending members before continuing.', es: 'Completá o declinalá los miembros pendientes antes de continuar.' },
  'householdStep.incompleteRow.badge':          { en: 'Pending', es: 'Pendiente' },

  // ===== Cluster H v2 SUB-E — DeclineModal + reasons (LD-H2-12/13) =====
  'declineModal.title':                       { en: 'Decline {name}', es: 'Declinar {name}' },
  'declineModal.close':                       { en: 'Close', es: 'Cerrar' },
  'declineModal.prompt':                      { en: 'Choose a reason to confirm declining this household member. This action is terminal.', es: 'Elegí un motivo para confirmar la declinación. Acción terminal.' },
  'declineModal.reasonLegend':                { en: 'Reason for declining', es: 'Motivo de la declinación' },
  'declineModal.reason.already_has_plan':     { en: 'Already has a plan', es: 'Ya tiene plan activo' },
  'declineModal.reason.moved_out_state':      { en: 'Moved out of state', es: 'Se mudó del estado' },
  'declineModal.reason.refused':              { en: 'Refused to participate', es: 'No quiere participar' },
  'declineModal.reason.no_contact':           { en: 'No contact / no response', es: 'Sin contacto / no responde' },
  'declineModal.reason.other':                { en: 'Other (free text)', es: 'Otro (texto libre)' },
  'declineModal.otherTextLabel':              { en: 'Describe the reason', es: 'Describí el motivo' },
  'declineModal.otherTextPlaceholder':        { en: 'Brief explanation (min 2 chars)', es: 'Explicación breve (mín 2 caracteres)' },
  'declineModal.otherTextHint':               { en: '{n}/1000', es: '{n}/1000' },
  'declineModal.cancel':                      { en: 'Cancel', es: 'Cancelar' },
  'declineModal.confirm':                     { en: 'Confirm decline', es: 'Confirmar declinación' },
  'declineModal.success':                     { en: 'Household member declined', es: 'Miembro declinado' },
  'declineModal.failed':                      { en: "Couldn't decline", es: 'No se pudo declinar' },

  // ===== Cluster H v2 SUB-F.1 — uploader claim UI (LD-H2-11) =====
  'uploader.queue.workingBy': { en: 'Working: {name}', es: 'Trabajando: {name}' },
  'uploader.queue.claim409':  { en: "Already claimed", es: 'Ya tomado' },
  'uploader.queue.claim409.knownOwner':   { en: '{name} is already working on this screening. It will be available again in a few minutes.', es: '{name} ya está trabajando en este screening. Volverá a estar disponible en unos minutos.' },
  'uploader.queue.claim409.unknownOwner': { en: 'Another uploader is already working on this screening. Try again shortly.', es: 'Otro uploader ya está trabajando en este screening. Volvé a intentar en un momento.' },
  // SUB-F.3 — sub-screening linkage row badge.
  'uploader.queue.householdBadge':      { en: 'Household of {parent} (…{cin})', es: 'Hogar de {parent} (…{cin})' },
  'uploader.queue.householdBadgeHint':  { en: 'Click to view parent profile', es: 'Click para ver el perfil del padre' },

  // ===== Cluster H v2 SUB-F.2 — FlagErrorModal (LD-H2-14/15) =====
  'flagErrorModal.title':                       { en: 'Flag error — {name} (…{cin})', es: 'Marcar error — {name} (…{cin})' },
  'flagErrorModal.close':                       { en: 'Close', es: 'Cerrar' },
  'flagErrorModal.prompt':                      { en: 'Choose a reason. The agent and admins will be notified.', es: 'Elegí un motivo. Se notifica al agente y al admin.' },
  'flagErrorModal.reasonLegend':                { en: 'Flag reason', es: 'Motivo del flag' },
  'flagErrorModal.reason.no_medicaid':          { en: 'No Medicaid', es: 'No tiene Medicaid' },
  'flagErrorModal.reason.data_inconsistent':    { en: 'Inconsistent data (DOB / name)', es: 'Datos inconsistentes (DOB / nombre)' },
  'flagErrorModal.reason.cin_invalid':          { en: 'Invalid CIN', es: 'CIN inválido' },
  'flagErrorModal.reason.duplicate':            { en: 'Duplicate', es: 'Duplicado' },
  'flagErrorModal.reason.other':                { en: 'Other (free text)', es: 'Otro (texto libre)' },
  'flagErrorModal.noMedicaidNote':              { en: 'Counts as no-Medicaid (agent receives no bonus credit)', es: 'Cuenta como sin-Medicaid (el agente no recibe bonus)' },
  'flagErrorModal.otherTextLabel':              { en: 'Describe the reason', es: 'Describí el motivo' },
  'flagErrorModal.otherTextPlaceholder':        { en: 'Brief explanation (min 2 chars)', es: 'Explicación breve (mín 2 caracteres)' },
  'flagErrorModal.otherTextHint':               { en: '{n}/1000', es: '{n}/1000' },
  'flagErrorModal.cancel':                      { en: 'Cancel', es: 'Cancelar' },
  'flagErrorModal.confirm':                     { en: 'Confirm flag', es: 'Confirmar flag' },
  'flagErrorModal.success':                     { en: 'Screening flagged', es: 'Screening marcado' },
  'flagErrorModal.failed':                      { en: "Couldn't flag screening", es: 'No se pudo marcar el screening' },

  // ===== Cluster H — Sub-screening drawer (2026-05-17) — deprecated namespace =====
  'subScreening.header.title': { en: 'Mini-screening', es: 'Mini-screening' },
  'subScreening.header.referredBy': { en: 'Referred by {parent}', es: 'Referido por {parent}' },
  'subScreening.header.householdLabel': { en: 'Household member', es: 'Miembro del hogar' },

  'subScreening.relationship.daughter': { en: 'Daughter', es: 'Hija' },
  'subScreening.relationship.son': { en: 'Son', es: 'Hijo' },
  'subScreening.relationship.spouse': { en: 'Spouse', es: 'Esposo(a)' },
  'subScreening.relationship.parent': { en: 'Parent', es: 'Padre/Madre' },
  'subScreening.relationship.sibling': { en: 'Sibling', es: 'Hermano(a)' },
  'subScreening.relationship.other': { en: 'Other', es: 'Otro' },

  'subScreening.consent.proxyLabel': { en: 'Proxy consent', es: 'Consentimiento del proxy' },
  'subScreening.consent.verbalLabel': { en: 'Verbal consent', es: 'Consentimiento verbal' },
  'subScreening.consent.banner.proxy.text': { en: 'This member is under 18. The parent guardian must verbally consent before we screen this minor.', es: 'Este miembro es menor de 18 años. El tutor debe dar consentimiento verbal antes de hacer el screening del menor.' },
  'subScreening.consent.banner.verbal.text': { en: 'Please confirm verbal consent from {name} before starting.', es: 'Confirme el consentimiento verbal de {name} antes de comenzar.' },
  'subScreening.consent.confirmButton': { en: 'Consent given — start', es: 'Consentimiento dado — comenzar' },

  'subScreening.timer.elapsed': { en: 'Elapsed', es: 'Transcurrido' },
  'subScreening.save.saving': { en: 'Saving…', es: 'Guardando…' },
  'subScreening.save.saved': { en: 'Saved', es: 'Guardado' },
  'subScreening.save.error': { en: "Couldn't save", es: 'No se pudo guardar' },

  'subScreening.actions.back': { en: 'Back', es: 'Atrás' },
  'subScreening.actions.next': { en: 'Next', es: 'Siguiente' },
  'subScreening.actions.submit': { en: 'Submit', es: 'Enviar' },
  'subScreening.actions.close': { en: 'Close', es: 'Cerrar' },

  'subScreening.submit.confirmTitle': { en: 'Submit mini-screening?', es: '¿Enviar mini-screening?' },
  'subScreening.submit.confirmBody': { en: 'All {count} domains are answered. This will finalize the screening for {name}.', es: 'Las {count} áreas están respondidas. Esto finalizará el screening de {name}.' },
  'subScreening.submit.submitButton': { en: 'Submit screening', es: 'Enviar screening' },
  'subScreening.submit.cancelButton': { en: 'Continue editing', es: 'Continuar editando' },
  'subScreening.submit.success': { en: 'Mini-screening submitted', es: 'Mini-screening enviado' },

  'subScreening.abandon.confirmTitle': { en: 'Abandon mini-screening?', es: '¿Abandonar mini-screening?' },
  'subScreening.abandon.confirmBody': { en: 'Unsaved progress for {name} will be lost. The session has been counted but cannot be resumed later.', es: 'El progreso no guardado para {name} se perderá. La sesión queda contada pero no se puede reanudar después.' },
  'subScreening.abandon.unsavedWarning': { en: 'You have unsaved answers.', es: 'Tiene respuestas sin guardar.' },
  'subScreening.abandon.confirmButton': { en: 'Yes, abandon', es: 'Sí, abandonar' },
  'subScreening.abandon.cancelButton': { en: 'Cancel', es: 'Cancelar' },

  'subScreening.close.parentDisabledHint': { en: 'Close the mini-screening to return to the parent.', es: 'Cierre el mini-screening para volver al padre.' },

  'subScreening.errors.openFailed': { en: "Couldn't open the mini-screening. Try again.", es: 'No se pudo abrir el mini-screening. Intente de nuevo.' },
  'subScreening.errors.submitFailed': { en: "Couldn't submit. Check connection and try again.", es: 'No se pudo enviar. Revise la conexión e intente de nuevo.' },
  'subScreening.errors.alreadyPromoted': { en: 'This member has already been promoted.', es: 'Este miembro ya fue promovido.' },
  'subScreening.errors.parentNotActive': { en: 'The parent screening was already submitted; cannot open a sub.', es: 'El screening padre ya fue enviado; no se puede abrir un sub.' },

  'subScreening.status.promoting': { en: 'Setting up the mini-screening…', es: 'Preparando el mini-screening…' },
  'subScreening.status.submitting': { en: 'Submitting…', es: 'Enviando…' },
  // ===== Cluster C — Household member Edit/Delete (2026-05-17 Agent C) =====
  'householdStep.statusBadge.pending': { en: 'Pending', es: 'Pendiente' },
  'householdStep.statusBadge.processed': { en: 'Processed', es: 'Procesado' },
  'householdStep.statusBadge.promoted': { en: 'Promoted', es: 'Promovido' },
  'householdStep.actions.miniScreening': { en: 'Mini-screening', es: 'Mini-screening' },
  'householdStep.actions.edit': { en: 'Edit', es: 'Editar' },
  'householdStep.actions.delete': { en: 'Delete', es: 'Borrar' },
  'householdStep.edit.title': { en: 'Edit household member', es: 'Editar miembro del hogar' },
  'householdStep.edit.fullNameLabel': { en: 'Full name', es: 'Nombre completo' },
  'householdStep.edit.dobLabel': { en: 'Date of birth', es: 'Fecha de nacimiento' },
  'householdStep.edit.medicaidIdLabel': { en: 'Medicaid ID', es: 'Medicaid ID' },
  'householdStep.edit.relationshipLabel': { en: 'Relationship', es: 'Parentesco' },
  'householdStep.edit.saveButton': { en: 'Save', es: 'Guardar' },
  'householdStep.edit.cancelButton': { en: 'Cancel', es: 'Cancelar' },
  'householdStep.delete.title': { en: 'Delete household member', es: 'Borrar miembro del hogar' },
  'householdStep.delete.body': { en: 'Delete {name}? This action cannot be undone.', es: '¿Borrar {name}? Esta acción es irreversible.' },
  'householdStep.delete.reasonLabel': { en: 'Reason (optional)', es: 'Razón (opcional)' },
  'householdStep.delete.confirmButton': { en: 'Delete', es: 'Borrar' },
  'householdStep.delete.cancelButton': { en: 'Cancel', es: 'Cancelar' },
  // ===== Cluster H Agent D — AdminDashboard / Reports / ClientProfile / LiveMonitoring =====
  'admin.householdPromotion.dashboard.backlogTitle': { en: 'Promotion backlog', es: 'Backlog de promoción' },
  'admin.householdPromotion.dashboard.backlogSub': { en: '{rate}% promotion rate', es: '{rate}% tasa de promoción' },
  'admin.householdPromotion.dashboard.subSubmittedTitle': { en: 'Mini-screenings (30d)', es: 'Mini-screenings (30d)' },
  'admin.householdPromotion.dashboard.subSubmittedSub': { en: '{opened} opened · {abandoned} abandoned', es: '{opened} abiertos · {abandoned} abandonados' },
  'admin.householdPromotion.dashboard.revenueTitle': { en: 'Sub revenue (30d est.)', es: 'Ingresos de subs (30d est.)' },
  'admin.householdPromotion.dashboard.revenueSub': { en: '${rate}/sub', es: '${rate}/sub' },
  'admin.householdPromotion.reports.section': { en: 'Household promotion', es: 'Promoción de hogar' },
  'admin.householdPromotion.reports.trendTitle': { en: 'Sub-screenings submitted (30d)', es: 'Mini-screenings enviados (30d)' },
  'admin.householdPromotion.reports.byAgentTitle': { en: 'Top agents by promotion', es: 'Top agentes por promoción' },
  'admin.householdPromotion.reports.colEmail': { en: 'Agent', es: 'Agente' },
  'admin.householdPromotion.reports.colPromoted': { en: 'Promoted', es: 'Promovidos' },
  'admin.householdPromotion.reports.colSubSubmitted': { en: 'Subs submitted', es: 'Subs enviados' },
  'admin.householdPromotion.reports.colAbandoned': { en: 'Subs abandoned', es: 'Subs abandonados' },
  'admin.householdPromotion.reports.colAvgDuration': { en: 'Avg duration', es: 'Duración prom.' },
  'admin.householdPromotion.reports.colRevenue': { en: 'Revenue (est.)', es: 'Ingresos (est.)' },
  'admin.householdPromotion.reports.empty': { en: 'No promotions in window.', es: 'Sin promociones en la ventana.' },
  'admin.householdPromotion.reports.exportPending': { en: 'Export CSV (coming soon)', es: 'Exportar CSV (próximamente)' },

  'clientProfile.promotedBanner.title': { en: 'Promoted from household member', es: 'Promovido desde miembro del hogar' },
  'clientProfile.promotedBanner.body': { en: 'Promoted from {parent} by {agent} on {date}.', es: 'Promovido desde {parent} por {agent} el {date}.' },
  'clientProfile.promotedBanner.bodyNoParent': { en: 'Promoted by {agent} on {date}.', es: 'Promovido por {agent} el {date}.' },
  'clientProfile.promotedBanner.viewParentLink': { en: 'View original screening', es: 'Ver screening original' },
  // v0.6.1 Fase 4 — relationship line below body.
  'clientProfile.promotedBanner.relationshipLabel': { en: 'Relationship', es: 'Relación' },

  'liveMonitoring.subScreenings.widgetTitle': { en: 'Active mini-screenings', es: 'Mini-screenings activos' },
  'liveMonitoring.subScreenings.empty': { en: 'No active mini-screenings.', es: 'Sin mini-screenings activos.' },
  'liveMonitoring.subScreenings.rowAgent': { en: 'Agent {agent}', es: 'Agente {agent}' },
  'liveMonitoring.subScreenings.rowParent': { en: 'parent screening #{parentId}', es: 'screening padre #{parentId}' },

  // ===== Cluster NAV — navigator surface (Wave 6) =====
  'navigator.brand': { en: 'Navigation', es: 'Navegación' },
  'navigator.roleBadge': { en: 'navigator', es: 'navegador' },
  'navigator.menuLabel': { en: 'Navigator menu', es: 'Menú de navegación' },
  'navigator.language': { en: 'Language', es: 'Idioma' },
  'navigator.common.yes': { en: 'Yes', es: 'Sí' },
  'navigator.common.no': { en: 'No', es: 'No' },
  'navigator.common.cancel': { en: 'Cancel', es: 'Cancelar' },
  'navigator.common.save': { en: 'Save', es: 'Guardar' },
  'navigator.common.close': { en: 'Close', es: 'Cerrar' },

  'navigator.queue.title': { en: 'My navigation queue', es: 'Mi cola de navegación' },
  'navigator.queue.subtitle': {
    en: 'Cases the system opened from positive screenings, plus the ones you took. Urgent first; ⭐ means the member asked for a navigator.',
    es: 'Casos que el sistema abrió desde screenings positivos, más los que ya tomaste. Urgentes primero; ⭐ = el miembro pidió navegador.',
  },
  'navigator.queue.loading': { en: 'Loading queue…', es: 'Cargando la cola…' },
  'navigator.queue.loadFailed': { en: 'The queue could not be loaded.', es: 'No se pudo cargar la cola.' },
  'navigator.queue.empty': { en: 'Nothing in the queue right now.', es: 'No hay casos en la cola por ahora.' },
  'navigator.queue.slaBanner': {
    en: '{n} of your cases reach their outreach deadline today or tomorrow.',
    es: '{n} de tus casos llegan a su fecha límite de contacto hoy o mañana.',
  },
  'navigator.queue.truncated': {
    en: 'Showing {shown} of {total} cases. Work the top of the list — it is ordered by priority.',
    es: 'Mostrando {shown} de {total} casos. Trabajá desde arriba — la lista está ordenada por prioridad.',
  },
  'navigator.queue.claimFailed': { en: 'Could not take the case', es: 'No se pudo tomar el caso' },
  'navigator.queue.claimTaken': {
    en: 'Another navigator took it first. The queue has been refreshed.',
    es: 'Otro navegador lo tomó primero. La cola se actualizó.',
  },

  'navigator.stats.urgent': { en: 'Urgent', es: 'Urgentes' },
  'navigator.stats.available': { en: 'Available', es: 'Disponibles' },
  'navigator.stats.mine': { en: 'Mine, in progress', es: 'Míos en progreso' },
  'navigator.stats.unitsToday': { en: 'Units today', es: 'Unidades hoy' },

  'navigator.sla.today': { en: 'Deadline: today', es: 'Fecha límite: hoy' },
  'navigator.sla.daysLeft': { en: '{n} days left', es: 'quedan {n} días' },
  'navigator.sla.overdue': { en: '{n} days overdue', es: 'vencido hace {n} días' },

  'navigator.badge.urgent': { en: 'URGENT', es: 'URGENTE' },
  'navigator.badge.askedForNavigator': { en: 'asked for a navigator', es: 'pidió navegador' },
  'navigator.badge.somos': { en: 'SOMOS list', es: 'lista SOMOS' },
  'navigator.badge.pendingSync': {
    en: '{n} to mirror to findhelp',
    es: '{n} pendientes de pasar a findhelp',
  },

  'navigator.card.attempts': { en: '{n} outreach attempts', es: '{n} intentos de contacto' },
  'navigator.card.take': { en: 'Take case', es: 'Tomar caso' },
  'navigator.card.open': { en: 'Open', es: 'Abrir' },

  'navigator.case.loading': { en: 'Loading case…', es: 'Cargando el caso…' },
  'navigator.case.loadFailed': { en: 'The case could not be loaded.', es: 'No se pudo cargar el caso.' },
  'navigator.case.backToQueue': { en: 'Back to queue', es: 'Volver a la cola' },
  'navigator.case.heading': { en: 'Case #{id}', es: 'Caso #{id}' },
  'navigator.case.attemptsOf': { en: 'Attempts {n} of {required}', es: 'Intentos {n} de {required}' },
  'navigator.case.claimed': { en: 'Case taken', es: 'Caso tomado' },
  'navigator.case.heldBy': {
    en: 'This case is held by {name}. You can read it, but not work it.',
    es: 'Este caso lo tiene {name}. Podés verlo, pero no trabajarlo.',
  },
  'navigator.case.unclaimed': {
    en: 'Nobody is holding this case. Take it to log attempts and sessions.',
    es: 'Nadie tiene este caso. Tomalo para registrar intentos y sesiones.',
  },
  'navigator.case.closedNotice': { en: 'Case closed as {status}.', es: 'Caso cerrado como {status}.' },
  'navigator.case.memberPanel': { en: 'Member', es: 'Miembro' },
  'navigator.case.cin': { en: 'Medicaid CIN', es: 'CIN de Medicaid' },
  'navigator.case.copy': { en: 'copy', es: 'copiar' },
  'navigator.case.copied': { en: 'copied', es: 'copiado' },
  'navigator.case.copyFailed': { en: 'Could not copy the CIN', es: 'No se pudo copiar el CIN' },
  'navigator.case.phone': { en: 'Phone', es: 'Teléfono' },
  'navigator.case.noPhone': { en: 'no phone on file', es: 'sin teléfono registrado' },
  'navigator.case.domains': { en: 'Positive domains', es: 'Dominios positivos' },
  'navigator.case.screening': { en: 'Screening', es: 'Screening' },
  'navigator.case.noScreening': { en: 'no linked screening', es: 'sin screening vinculado' },
  'navigator.case.askedForNavigator': { en: 'Asked for a navigator?', es: '¿Pidió navegador?' },
  'navigator.case.timeline': { en: 'Case timeline', es: 'Historial del caso' },
  'navigator.case.timelineEmpty': { en: 'Nothing recorded yet.', es: 'Todavía no hay nada registrado.' },
  'navigator.case.unitsPanel': { en: 'Units and limits', es: 'Unidades y topes' },

  'navigator.systemOfRecordNotice': {
    en: 'Dispatch is the primary record. Remember to mirror what is pending into findhelp — without findhelp it does not get billed to the State.',
    es: 'Dispatch es el registro principal. Recordá espejar a findhelp lo pendiente — sin findhelp no se factura ante el Estado.',
  },

  'navigator.action.logOutreach': { en: 'Log attempt', es: 'Registrar intento' },
  'navigator.action.logSession': { en: 'Log session', es: 'Registrar sesión' },
  'navigator.action.changeStatus': { en: 'Change status', es: 'Cambiar estado' },

  'navigator.caps.l2Month': { en: 'L2 this month (member)', es: 'L2 este mes (miembro)' },
  'navigator.caps.l1Event': { en: 'L1 this case', es: 'L1 en este caso' },
  'navigator.caps.l1Year': { en: 'L1 last 12 months (member)', es: 'L1 últimos 12 meses (miembro)' },
  'navigator.caps.usedOf': { en: '{used} of {cap}', es: '{used} de {cap}' },
  'navigator.caps.serverEnforced': {
    en: 'The system will not record units above a limit.',
    es: 'El sistema no permite registrar unidades por encima del tope.',
  },

  'navigator.progress.title': { en: 'My progress', es: 'Mi progreso' },
  'navigator.progress.unitsToday': { en: 'Units today', es: 'Unidades hoy' },
  'navigator.progress.unitsMonth': { en: 'Units this month', es: 'Unidades del mes' },
  'navigator.progress.activeCases': { en: 'Active cases', es: 'Casos activos' },
  'navigator.progress.slaDueSoon': { en: 'Deadlines within a day', es: 'Vencen en un día' },

  'navigator.error.claimLostTitle': { en: 'You no longer hold this case', es: 'Ya no tenés este caso' },
  'navigator.error.released': {
    en: 'This case was released by a supervisor. Check your queue: it may be available to take again.',
    es: 'Este caso fue liberado por un supervisor. Revisa tu cola: puede estar disponible para re-tomar.',
  },
  'navigator.error.heldByOther': {
    en: 'Another navigator is holding this case now.',
    es: 'Otro navegador tiene este caso ahora.',
  },
  'navigator.error.generic': { en: 'The action could not be completed', es: 'No se pudo completar la acción' },

  'navigator.timeline.attempt': { en: 'Attempt: {type}, {outcome}', es: 'Intento: {type}, {outcome}' },
  'navigator.timeline.session': {
    en: 'Session: {activity}, {minutes} min → {units} {level} unit(s)',
    es: 'Sesión: {activity}, {minutes} min → {units} unidad(es) {level}',
  },
  'navigator.timeline.mirrored': { en: 'mirrored into findhelp', es: 'espejado en findhelp' },
  'navigator.timeline.notMirrored': { en: 'not yet in findhelp', es: 'todavía no está en findhelp' },
  'navigator.timeline.note': { en: 'Note added', es: 'Nota agregada' },
  'navigator.timeline.statusChanged': { en: 'Status: {from} → {to}', es: 'Estado: {from} → {to}' },
  'navigator.timeline.createdAuto': {
    en: 'Case opened automatically from a positive screening ({domains})',
    es: 'Caso abierto automático desde un screening positivo ({domains})',
  },
  'navigator.lifecycle.created': { en: 'Case opened automatically', es: 'Caso abierto automático' },
  'navigator.lifecycle.claimed': { en: 'Case taken by a navigator', es: 'Caso tomado por un navegador' },
  'navigator.lifecycle.statusChanged': { en: 'Status changed', es: 'Cambio de estado' },
  'navigator.lifecycle.override': { en: 'Adjusted by a supervisor', es: 'Ajustado por un supervisor' },

  'navigator.status.suggested': { en: 'Suggested', es: 'Sugerido' },
  'navigator.status.flagged': { en: 'Flagged', es: 'Marcado' },
  'navigator.status.in_progress': { en: 'In progress', es: 'En progreso' },
  'navigator.status.completed': { en: 'Completed', es: 'Completado' },
  'navigator.status.declined_by_patient': { en: 'Declined by member', es: 'Declinado por el miembro' },
  'navigator.status.not_required': { en: 'Not required', es: 'No requiere' },

  'navigator.attemptType.call': { en: 'Call', es: 'Llamada' },
  'navigator.attemptType.text': { en: 'Text', es: 'Mensaje de texto' },
  'navigator.attemptType.email': { en: 'Email', es: 'Correo' },
  'navigator.attemptType.other': { en: 'Other', es: 'Otro' },

  'navigator.outcome.no_answer': { en: 'No answer', es: 'No contestó' },
  'navigator.outcome.voicemail': { en: 'Voicemail', es: 'Buzón de voz' },
  'navigator.outcome.wrong_number': { en: 'Wrong number', es: 'Número equivocado' },
  'navigator.outcome.reached': { en: 'Reached', es: 'Contactado' },
  'navigator.outcome.refused': { en: 'Refused', es: 'Rechazó' },
  'navigator.outcome.callback_scheduled': { en: 'Callback scheduled', es: 'Agendó devolución' },
  'navigator.outcome.other': { en: 'Other', es: 'Otro' },

  'navigator.activity.eligibility_assessment': { en: 'Eligibility Assessment', es: 'Evaluación de elegibilidad' },
  'navigator.activity.care_management': { en: 'Care management', es: 'Gestión de cuidado' },
  'navigator.activity.follow_up': { en: 'Follow-up', es: 'Seguimiento' },
  'navigator.activity.referral_coordination': { en: 'Referral coordination', es: 'Coordinación de referral' },

  'navigator.noteType.general': { en: 'General', es: 'General' },
  'navigator.noteType.ea': { en: 'Eligibility Assessment', es: 'Evaluación de elegibilidad' },
  'navigator.noteType.outreach': { en: 'Outreach', es: 'Contacto' },
  'navigator.noteType.referral': { en: 'Referral', es: 'Referral' },
  'navigator.noteType.follow_up': { en: 'Follow-up', es: 'Seguimiento' },

  'navigator.outreach.title': { en: 'Log an outreach attempt', es: 'Registrar intento de contacto' },
  'navigator.outreach.type': { en: 'Type', es: 'Tipo' },
  'navigator.outreach.outcome': { en: 'Outcome', es: 'Resultado' },
  'navigator.outreach.note': { en: 'Short note', es: 'Nota breve' },
  'navigator.outreach.notePlaceholder': { en: 'e.g. try again in the afternoon', es: 'ej: volver a intentar en la tarde' },
  'navigator.outreach.noPhiHint': {
    en: 'No member details here — this note is read by people who were not on the call.',
    es: 'Sin datos del paciente acá — esta nota la lee gente que no estuvo en la llamada.',
  },
  'navigator.outreach.slaHint': {
    en: 'Attempt {n} of {required} · outreach deadline: {deadline}. Attempts are not billable, but they prove compliance.',
    es: 'Intento {n} de {required} · fecha límite de contacto: {deadline}. Los intentos no se facturan, pero prueban el cumplimiento.',
  },
  'navigator.outreach.reachedHint': {
    en: 'Choose Contacted if the member answered. That stamps first contact, starts the work session, and unlocks the Eligibility Assessment. Any other outcome keeps EA locked.',
    es: 'Elegí Contactado si el miembro contestó. Eso sella el primer contacto, arranca la sesión de trabajo y desbloquea el Eligibility Assessment. Cualquier otro resultado deja el EA bloqueado.',
  },
  'navigator.outreach.save': { en: 'Save attempt', es: 'Guardar intento' },
  'navigator.outreach.saved': { en: 'Attempt recorded', es: 'Intento registrado' },

  'navigator.session.title': { en: 'Log a navigation session', es: 'Registrar sesión de navegación' },
  'navigator.session.activity': { en: 'Activity', es: 'Actividad' },
  'navigator.session.level': { en: 'Level', es: 'Nivel' },
  'navigator.session.chooseLevel': { en: 'Choose L1 or L2', es: 'Elegí L1 o L2' },
  'navigator.session.chooseActivity': { en: 'Choose activity', es: 'Elegí la actividad' },
  'navigator.session.chooseForm': { en: 'Choose the Reimbursement Tracker', es: 'Elegí el Reimbursement Tracker' },
  'navigator.session.levelRequired': {
    en: 'Choose L1 or L2 to match the Eligibility Assessment. Billing never defaults to L2.',
    es: 'Elegí L1 o L2 para coincidir con el Eligibility Assessment. El cobro nunca asume L2.',
  },
  'navigator.session.reimbForm': {
    en: 'Reimbursement Tracker evidence',
    es: 'Evidencia del Reimbursement Tracker',
  },
  'navigator.session.noReimbForm': {
    en: 'Submit the Navigation Reimbursement Tracker first. Units are created from that evidence, not from this form alone.',
    es: 'Primero registrá el Navigation Reimbursement Tracker. Las unidades salen de esa evidencia, no de este formulario solo.',
  },
  'navigator.session.minutes': { en: 'Minutes', es: 'Minutos' },
  'navigator.session.ruleHint': {
    en: 'Under {min} minutes bills no units. The system calculates the units when you save.',
    es: 'Menos de {min} minutos no genera unidades. El sistema calcula las unidades al guardar.',
  },
  'navigator.session.remainingHint': {
    en: '{level} remaining for this member: {remaining}',
    es: 'Remanente {level} de este miembro: {remaining}',
  },
  'navigator.session.memberFacing': { en: 'Time spent with the member', es: 'Tiempo con el miembro' },
  'navigator.session.documented': { en: 'Already mirrored into findhelp', es: 'Ya espejado en findhelp' },
  'navigator.session.documentedHint': {
    en: 'Tick it once the session is in findhelp. Without findhelp it does not get billed.',
    es: 'Marcalo cuando la sesión ya esté en findhelp. Sin findhelp no se factura.',
  },
  'navigator.session.save': { en: 'Save session', es: 'Guardar sesión' },
  'navigator.session.saved': { en: 'Session recorded', es: 'Sesión registrada' },
  'navigator.session.savedDetail': {
    en: '{units} {level} unit(s) · remaining: {remaining}',
    es: '{units} unidad(es) {level} · remanente: {remaining}',
  },
  'navigator.session.rejected': { en: 'The session was not recorded.', es: 'La sesión no se registró.' },

  'navigator.status.title': { en: 'Change case status', es: 'Cambiar estado del caso' },
  'navigator.status.newStatus': { en: 'New status', es: 'Nuevo estado' },
  'navigator.status.reasonRequired': { en: 'Reason (required to close)', es: 'Razón (obligatoria al cerrar)' },
  'navigator.status.reasonOptional': { en: 'Reason (optional)', es: 'Razón (opcional)' },
  'navigator.status.reasonPlaceholder': {
    en: 'e.g. EA completed, two referrals accepted, services under way',
    es: 'ej: EA completado, dos referrals aceptados, servicios en curso',
  },
  'navigator.status.terminal': {
    en: 'This case is closed. There is no status left to move it to.',
    es: 'Este caso está cerrado. No hay estados a los que moverlo.',
  },
  'navigator.status.saved': { en: 'Status updated', es: 'Estado actualizado' },
  'navigator.status.savedDetail': { en: '{from} → {to}', es: '{from} → {to}' },
  'navigator.status.rejected': { en: 'The status was not changed.', es: 'El estado no se cambió.' },

  'navigator.notes.title': { en: 'Case notes', es: 'Notas del caso' },
  'navigator.notes.add': { en: 'Add note', es: 'Agregar nota' },
  'navigator.notes.type': { en: 'Type', es: 'Tipo' },
  'navigator.notes.body': { en: 'Note', es: 'Nota' },
  'navigator.notes.bodyPlaceholder': {
    en: 'What happened, what was agreed, what comes next',
    es: 'Qué pasó, qué se acordó, qué sigue',
  },
  'navigator.notes.empty': { en: 'No notes on this case yet.', es: 'Este caso todavía no tiene notas.' },
  'navigator.notes.added': { en: 'Note saved', es: 'Nota guardada' },
  'navigator.notes.synced': { en: 'mirrored', es: 'espejada' },
  'navigator.notes.notSynced': { en: 'pending', es: 'pendiente' },
  'navigator.notes.markSynced': { en: 'mark as mirrored', es: 'marcar como espejada' },
  'navigator.notes.markedSynced': { en: 'Marked as mirrored in findhelp', es: 'Marcada como espejada en findhelp' },

  // ===== Cluster NAV Wave 6B — pre-call, timer, checklist, screening notes =====
  'navigator.common.saving': { en: 'Saving…', es: 'Guardando…' },

  // M0b -- la jornada del caso en cuatro etapas.
  'navigator.journey.railLabel': { en: 'Member and journey', es: 'Miembro y jornada' },
  'navigator.journey.navLabel': { en: 'Case stages', es: 'Etapas del caso' },
  'navigator.journey.precall': { en: 'Before the call', es: 'Antes de llamar' },
  'navigator.journey.call': { en: 'On the call', es: 'En la llamada' },
  'navigator.journey.mirror': { en: 'Mirror to findhelp', es: 'Espejo a findhelp' },
  // El rail nombra la ETAPA; el boton de adentro es la ACCION. Las dos decian
  // "Cerrar el caso" y quedaban a la vista al mismo tiempo. La etapa se llama
  // por lo que es -- las otras tres tambien son sustantivos -- y "Cerrar el
  // caso" queda para el unico control que efectivamente cierra.
  'navigator.journey.close': { en: 'Close', es: 'Cierre' },
  // M1 -- el seguro, en castellano de todos los dias.
  'navigator.precall.coverageTitle': { en: 'Active coverage today', es: 'Cobertura activa hoy' },
  'navigator.precall.coverageHint': { en: 'I checked it in MEVS with today’s date', es: 'Lo verifiqué en MEVS con fecha de hoy' },
  'navigator.precall.stateListTitle': { en: 'I reviewed what the state already knows', es: 'Revisé lo que el estado ya sabe' },
  'navigator.precall.stateListHint': { en: 'The state list in findhelp (ESMF)', es: 'La lista del estado en findhelp (ESMF)' },
  'navigator.precall.privacyTitle': { en: 'I am somewhere private and I do not know this person', es: 'Estoy en un lugar privado y no conozco a esta persona' },
  'navigator.precall.insuranceTitle': { en: 'What insurance they have', es: 'Qué seguro tiene' },
  'navigator.precall.insuranceHint': { en: 'This is where it is decided whether the assessment runs in full or short', es: 'De acá sale si la evaluación va completa o corta' },
  'navigator.precall.carrier': { en: 'Insurer', es: 'Aseguradora' },
  'navigator.precall.carrierOther': { en: 'Another one…', es: 'Otra…' },
  'navigator.precall.planType': { en: 'Plan type', es: 'Tipo de plan' },
  'navigator.precall.planUnknown': { en: 'I do not know yet', es: 'No sé todavía' },
  'navigator.precall.insuranceSaved': { en: 'Insurance recorded', es: 'Seguro registrado' },
  'navigator.precall.capturedBy': { en: 'Recorded by {name}', es: 'Anotado por {name}' },
  'navigator.precall.ffsWarn': { en: 'Fee-for-service: the assessment skips the health block and the result will be Standard. Better to know now than at the end.', es: 'Fee-for-service: la evaluación salta el bloque de salud y el resultado va a ser Standard. Mejor saberlo ahora que al final.' },
  'navigator.precall.title': { en: 'Before you dial', es: 'Antes de marcar' },
  'navigator.precall.completeToday': { en: 'done today', es: 'hecho hoy' },
  'navigator.precall.notVerifiedNotice': {
    en: 'Dispatch cannot check any of this for you — it has no feed from MEVS or the state roster. Ticking a box records that you confirmed it today, with your name.',
    es: 'Dispatch no puede verificar nada de esto por vos — no tiene conexión con MEVS ni con el roster del estado. Marcar registra que vos lo confirmaste hoy, con tu nombre.',
  },
  'navigator.precall.choose': { en: 'Choose…', es: 'Elegir…' },
  'navigator.precall.managedCare': { en: 'Managed Care', es: 'Managed Care' },
  'navigator.precall.ffs': { en: 'FFS (fee-for-service)', es: 'FFS (fee-for-service)' },
  'navigator.precall.ffsBadge': { en: 'FFS — assessment will be Standard', es: 'FFS — la evaluación será Standard' },
  'navigator.precall.save': { en: 'Save pre-call', es: 'Guardar pre-call' },
  'navigator.precall.saved': { en: 'Pre-call recorded', es: 'Pre-call registrado' },

  'navigator.timer.start': { en: 'Start', es: 'Iniciar' },
  // D-04: 'navigator.timer.pause' se elimina con el control manual del reloj
  // (TIMER v2 / T3-2). Su unico consumidor era el bloque «Cuando cuelgues».

  'navigator.checklist.title': { en: 'Steps to close the case', es: 'Pasos para cerrar el caso' },
  'navigator.checklist.step1': { en: 'Eligibility Assessment done', es: 'Se hace el EA' },
  'navigator.checklist.step2': { en: 'Goal created in findhelp', es: 'Se crea el Goal en findhelp' },
  'navigator.checklist.step3': { en: 'Referral sent', es: 'Se hace el referido' },
  'navigator.checklist.step4': { en: 'Copies emailed to the member', es: 'Se le envían copias al cliente por email' },
  'navigator.checklist.step5': { en: 'Outreach documented — the call happened', es: 'Outreach documentado — la llamada se hizo' },
  'navigator.checklist.step6': { en: 'Not reached, and documented anyway', es: 'No se contactó, y se documentó igual' },
  'navigator.checklist.step7': { en: 'Reimbursement tracked (session logged)', es: 'Reimbursement registrado (sesión cargada)' },
  'navigator.checklist.step8': { en: 'Data mirrored into findhelp', es: 'Data espejada a findhelp' },
  'navigator.checklist.autoHint': {
    en: 'The system marks this one off the work already recorded.',
    es: 'El sistema marca este solo, desde el trabajo ya registrado.',
  },
  'navigator.checklist.doneAuto': { en: 'done automatically', es: 'marcado automático' },
  'navigator.checklist.pendingAuto': { en: 'marks itself when the work is recorded', es: 'se marca solo cuando se registra el trabajo' },
  'navigator.checklist.gateHint': {
    en: 'All eight are required to complete a case. Declining or "not required" do not need them.',
    es: 'Los ocho son obligatorios para completar un caso. Declinado o "no requiere" no los necesitan.',
  },

  // ---------------------------------------------------------------- REORG-3
  // El espejo a findhelp, conducido. Los ocho pasos del checklist agrupados en
  // cinco tarjetas, en el orden real del trabajo: objetivo → referidos → lo que
  // se le manda y documenta al miembro → tiempo → espejado.
  // D8: acá no hay un solo monto del fee. El único monto que aparece en esta
  // etapa es el umbral de cobertura del PROGRAMA, y vive en el aviso de preauth.

  'navigator.mirror.rosterTitle': { en: 'Confirm against the roster', es: 'Confirmar contra el roster' },
  'navigator.mirror.rosterHint': {
    en: 'These are not questions for the member: they are what the state roster says, and you check them with findhelp open. They used to be asked mid-call, between one question and the next.',
    es: 'Estas no son preguntas al miembro: son lo que dice el roster del estado, y se verifican con findhelp abierto. Antes se preguntaban en medio de la llamada, entre una pregunta y la siguiente.',
  },
  'navigator.mirror.rosterNone': { en: 'No roster field applies to this case.', es: 'Ningún campo de roster aplica a este caso.' },
  'navigator.mirror.memberSaid': { en: 'The member said', es: 'El miembro dijo' },
  'navigator.mirror.recapTitle': { en: 'Every answer, in findhelp order', es: 'Todas las respuestas, en orden findhelp' },
  'navigator.mirror.recapHint': {
    en: 'The transcription source: fill the findhelp assessment from here. It follows the form’s 22 sections, not the order of the conversation.',
    es: 'La fuente de transcripción: de acá se llena el EA de findhelp. Sigue las 22 secciones del formulario, no el orden de la conversación.',
  },
  'navigator.mirror.title': { en: 'Mirror into findhelp', es: 'Espejo a findhelp' },
  'navigator.mirror.hint': {
    en: 'Each step asks for the piece of findhelp that proves it. None of them is completed by saying so.',
    es: 'Cada paso pide el pedazo de findhelp que lo prueba. Ninguno se completa diciendo que se hizo.',
  },
  'navigator.mirror.progress': { en: '{done} of {total}', es: '{done} de {total}' },
  'navigator.mirror.card1': { en: '1. Goal in the care plan', es: '1. Objetivo en el plan de cuidado' },
  'navigator.mirror.card2': { en: '2. Referrals — one per service', es: '2. Referidos — uno por servicio' },
  'navigator.mirror.card3': { en: '3. Sent and documented with the member', es: '3. Enviado y documentado con el miembro' },
  'navigator.mirror.card4': { en: '4. Log the time', es: '4. Registrar el tiempo' },
  'navigator.mirror.card5': { en: '5. Everything mirrored', es: '5. Todo espejado' },
  'navigator.mirror.goalPrompt': { en: 'Paste the goal ID from findhelp', es: 'Pegá el ID del objetivo en findhelp' },
  'navigator.mirror.goalPlaceholder': { en: 'e.g. SCP-4471', es: 'ej. SCP-4471' },
  'navigator.mirror.emailPrompt': {
    en: 'Transcribe recipient, subject and time',
    es: 'Transcribí destinatario, asunto y hora',
  },
  'navigator.mirror.emailPlaceholder': {
    en: 'e.g. maria@… · Your resources · 15:40',
    es: 'ej. maria@… · Sus recursos · 15:40',
  },
  'navigator.mirror.declaredWhy': {
    en: 'The email leaves your mailbox, not Dispatch: there is no evidence to capture. It is recorded as declared, not as proved — and it is shown that way.',
    es: 'El correo sale de tu casilla, no de Dispatch: no hay evidencia que capturar. Queda como declarado, no como probado — y se muestra así.',
  },
  'navigator.mirror.declared': { en: 'declared', es: 'declarado' },
  'navigator.mirror.proved': { en: 'proved', es: 'probado' },
  'navigator.mirror.auto': { en: 'automatic', es: 'automático' },
  'navigator.mirror.oneRefPerService': {
    en: 'The manual requires one referral per service (§V.A.1).',
    es: 'El manual manda un referido por cada servicio (§V.A.1).',
  },
  'navigator.mirror.noTracks': {
    en: 'No service is being tracked on this case yet. Open one in Documents and forms.',
    es: 'Este caso todavía no tiene ningún servicio en seguimiento. Abrí uno en Documentos y formularios.',
  },
  'navigator.mirror.doRefer': { en: 'Refer', es: 'Referir' },
  'navigator.mirror.dontRefer': { en: 'Do not refer', es: 'No referir' },
  'navigator.mirror.pickReason': { en: 'Why not?', es: '¿Por qué no?' },
  'navigator.mirror.notReferred': { en: 'Not referred', es: 'No referido' },
  'navigator.mirror.refPlaceholder': { en: 'Referral ID in findhelp', es: 'ID del referido en findhelp' },
  'navigator.mirror.attestationSoftBlock': {
    en: 'This service needs the provider attestation before referring. You can refer anyway, with a justification — it is recorded and audited.',
    es: 'Este servicio necesita la atestación del médico antes de referir. Podés referir igual, con justificación — queda auditado.',
  },
  'navigator.mirror.justificationPlaceholder': {
    en: 'Why refer without the attestation',
    es: 'Por qué se refiere sin la atestación',
  },
  'navigator.mirror.notePlaceholder': { en: 'Note (optional)', es: 'Nota (opcional)' },
  'navigator.mirror.duplicationNote': {
    en: 'Referring a service the member already receives is a claim denied for duplication — work done that is not paid. Careful: SNAP and WIC are NOT a duplication of a food box; the manual says so explicitly.',
    es: 'Referir un servicio que el miembro ya recibe es un claim denegado por duplicación — trabajo hecho que no se cobra. Ojo: SNAP y WIC NO son duplicación de una caja de comida; el manual lo dice explícito.',
  },
  'navigator.mirror.mech.1': {
    en: 'sealed when the assessment is submitted',
    es: 'se sella al enviar la evaluación',
  },
  'navigator.mirror.mech.3': {
    en: 'derived from the referrals: every service with its findhelp ID or its reason for not referring',
    es: 'se deriva de los referidos: cada servicio con su ID de findhelp o su razón de no referir',
  },
  'navigator.mirror.mech.5': {
    en: 'sealed by the attempt where the member picked up',
    es: 'se sella con el intento de contacto alcanzado',
  },
  'navigator.mirror.mech.6': {
    en: 'sealed by the documented attempts',
    es: 'se sella con los intentos documentados',
  },
  'navigator.mirror.mech.7': {
    en: 'derived from the logged session, with the minutes from the clock',
    es: 'se deriva de la sesión registrada, con los minutos del reloj',
  },
  'navigator.mirror.mech.8': {
    en: 'sealed when there is nothing left to mirror',
    es: 'se sella cuando no queda nada por espejar',
  },
  'navigator.notReferred.ya_lo_recibe': { en: 'Already receives it', es: 'Ya lo recibe' },
  'navigator.notReferred.no_consintio': { en: 'Did not consent', es: 'No consintió' },
  'navigator.notReferred.miembro_se_retiro': { en: 'Member withdrew', es: 'El miembro se retiró' },
  'navigator.notReferred.fuera_de_cobertura': { en: 'Outside coverage', es: 'Fuera de cobertura' },
  'navigator.notReferred.otro': { en: 'Other', es: 'Otro' },
  // D-17 (encargo del CEO, 2026-08-21): «otro» lleva nota obligatoria.
  'navigator.notReferred.noteLabel': {
    en: 'Why not? (required)', es: '¿Por qué no? (obligatorio)',
  },
  'navigator.notReferred.notePlaceholder': {
    en: 'In one line, so the next person understands',
    es: 'En una línea, para que el que venga después entienda',
  },
  'navigator.notReferred.noteRequired': {
    en: 'With «Other» you have to write why.',
    es: 'Con «Otro» hace falta escribir por qué.',
  },

  // REORG-3, punto 2 — las salidas que no llegan al espejo. Cada una dice qué
  // se hace, en qué orden, y qué exige el sistema antes de dejar cerrar.
  'navigator.outcome.deceased_reported': { en: 'Death reported', es: 'Fallecimiento reportado' },
  // Lo escribe el server al marcar «No llegó» en una cita (§04): la lista de
  // intentos del caso lo LEE; el modal de intentos no lo ofrece.
  'navigator.outcome.no_show': { en: 'Did not show up', es: 'No llegó a la cita' },
  'navigator.terminal.title': {
    en: 'The exits that do not reach this point',
    es: 'Las salidas que no llegan hasta acá',
  },
  'navigator.terminal.hint': {
    en: 'When the case does not end in the mirror. Each one asks for what the manual asks before letting you close.',
    es: 'Cuando el caso no termina en el espejo. Cada una pide lo que el manual pide antes de dejar cerrar.',
  },
  'navigator.terminal.closeWith': { en: 'Close the case with this reason', es: 'Cerrar el caso con este motivo' },
  'navigator.terminal.noConsent': { en: 'Did not consent', es: 'No consintió' },
  'navigator.terminal.withdrew': { en: 'Withdraws after consenting', es: 'Se retira después de haber consentido' },
  'navigator.terminal.unreachable': { en: 'Could not be reached', es: 'No se lo pudo ubicar' },
  'navigator.terminal.deceased': { en: 'Died', es: 'Falleció' },
  'navigator.terminal.noConsentScript': {
    en: 'I completely understand, and it is your right. If you change your mind, you can call us any time.',
    es: 'Entiendo perfectamente, y está en su derecho. Si cambia de opinión, puede llamarnos cuando quiera.',
  },
  'navigator.terminal.noConsentDont': {
    en: 'Do not insist, do not argue, and do not call again about the same case.',
    es: 'No insista, no argumente, y no vuelva a llamar por el mismo caso.',
  },
  'navigator.terminal.noConsentStep1': { en: 'First, the contact in findhelp.', es: 'Primero, el contacto en findhelp.' },
  'navigator.terminal.noConsentStep2': {
    en: 'Then the assessment, marking "Member does not consent", and submit it right there.',
    es: 'Después la evaluación, marcando «Member does not consent», y enviarla ahí mismo.',
  },
  'navigator.terminal.noConsentAuto': {
    en: 'Submitting the assessment that way closes the case on its own, as declined by the patient.',
    es: 'Enviar la evaluación así cierra el caso solo, como declinado por el paciente.',
  },
  'navigator.terminal.withdrewAsk': { en: 'First, the question nobody asks today:', es: 'Primero, la pregunta que hoy nadie hace:' },
  'navigator.terminal.withdrewFully': { en: 'Are they withdrawing entirely?', es: '¿Se retira del todo?' },
  'navigator.terminal.withdrewPrefers': {
    en: 'Or do they prefer resources they already have?',
    es: '¿O prefiere recursos que ya existen?',
  },
  'navigator.terminal.openReferrals': { en: 'Open services on this case', es: 'Servicios abiertos en este caso' },
  'navigator.terminal.noOpenReferrals': { en: 'No open services on this case.', es: 'No hay servicios abiertos en este caso.' },
  'navigator.terminal.notYetReferred': { en: 'not referred yet', es: 'sin referir' },
  'navigator.terminal.willCancel': {
    en: 'Closing cancels {n} service(s) of this case. The server does it — it does not depend on remembering.',
    es: 'Al cerrar se cancelan {n} servicio(s) de este caso. Lo hace el servidor — no depende de acordarse.',
  },
  'navigator.terminal.tellProviders': {
    en: '{n} already has a referral in findhelp: tell the providers that already started.',
    es: '{n} ya tiene referido en findhelp: avisales a los proveedores que ya empezaron.',
  },
  'navigator.terminal.supplant': {
    en: 'From the moment it closes, no units can be logged with a later date. A live referral for someone who withdrew is exactly what an audit calls supplant.',
    es: 'Desde el cierre no se pueden registrar unidades con fecha posterior. Un referido vivo de alguien que se retiró es exactamente lo que una auditoría llama supplant.',
  },
  'navigator.terminal.tracksCancelledTitle': { en: 'Services cancelled', es: 'Servicios cancelados' },
  'navigator.terminal.tracksCancelled': {
    en: '{n} open service(s) were cancelled with the case.',
    es: 'Se cancelaron {n} servicio(s) abiertos junto con el caso.',
  },
  'navigator.terminal.unreachableRule': {
    en: '{n} attempts in 5 business days — and before letting you close, what SOMOS requires is checked.',
    es: '{n} intentos en 5 días hábiles — y antes de dejarte cerrar se valida lo que SOMOS exige.',
  },
  'navigator.terminal.reqAttempts': { en: '{have} of {need} attempts', es: '{have} de {need} intentos' },
  'navigator.terminal.reqTypes': {
    en: 'Two different channels — call and message ({have} used)',
    es: 'Dos vías distintas — llamada y mensaje ({have} usada/s)',
  },
  'navigator.terminal.reqBands': {
    en: 'Two different time bands ({have}: {covered})',
    es: 'Dos franjas distintas ({have}: {covered})',
  },
  'navigator.terminal.outOfHours': {
    en: '{n} attempt(s) fell outside every band: they are recorded, but they do not prove coverage.',
    es: '{n} intento(s) quedaron fuera de toda franja: se registraron, pero no prueban cobertura.',
  },
  'navigator.terminal.unreachableBlocked': {
    en: 'This cannot be closed as unreachable yet. What is missing is listed above.',
    es: 'Todavía no se puede cerrar por inubicable. Lo que falta está arriba.',
  },
  'navigator.terminal.bandsProvisional': {
    en: 'Bands (New York time): {bands}. These cuts are our own default, provisional until SOMOS gives theirs.',
    es: 'Franjas (hora de New York): {bands}. Los cortes son un default nuestro, provisional hasta que SOMOS precise los suyos.',
  },
  'navigator.terminal.band.manana': { en: 'morning', es: 'mañana' },
  'navigator.terminal.band.tarde': { en: 'afternoon', es: 'tarde' },
  'navigator.terminal.band.noche': { en: 'evening', es: 'noche' },
  'navigator.terminal.band.fin_de_semana': { en: 'weekend', es: 'fin de semana' },
  'navigator.terminal.band.otra': { en: 'other band', es: 'otra franja' },

  // REORG-3, punto 3 — las respuestas de SOMOS, en el lugar donde hacen falta.
  'navigator.docs.deniedTitle': { en: 'Preauthorization denied', es: 'Preautorización denegada' },
  'navigator.docs.deniedHow': {
    en: 'There is no formal appeal: SOMOS emails the reason, you correct it and submit it again.',
    es: 'No hay apelación formal: SOMOS manda el motivo por correo, se corrige y se vuelve a someter.',
  },
  'navigator.docs.resubmit': { en: 'Correct and resubmit', es: 'Corregir y re-someter' },
  'navigator.docs.resubmitted': { en: 'Resubmitted', es: 'Re-sometida' },
  'navigator.docs.resubmitCount': { en: 'resubmitted {n} time(s)', es: 're-sometida {n} vez/veces' },
  'navigator.docs.attestationRejected': {
    en: 'The provider rejected the attestation, or is not responding. Escalate to your supervisor or administration — they take it to SOMOS. The L2 level is NOT recalculated: the attestation gets pushed, the case is not downgraded.',
    es: 'El médico rechazó la atestación, o no responde. Escalá a tu supervisor o a administración — ellos lo llevan a SOMOS. El nivel L2 NO se recalcula: se empuja la atestación, no se baja el caso.',
  },
  'navigator.terminal.deceasedRule': {
    en: 'The SOMOS norm is to document it in the notes AND record it in the outreach tracker.',
    es: 'La norma de SOMOS es documentarlo en las notas Y registrarlo en el outreach tracker.',
  },
  'navigator.terminal.deceasedStep1': {
    en: 'Log the attempt with the outcome "Death reported". Without it the case cannot be closed as deceased.',
    es: 'Registrá el intento con el resultado «Fallecimiento reportado». Sin eso el caso no cierra como fallecido.',
  },
  'navigator.terminal.deceasedStep2': {
    en: 'Write the note with what you were told and who told you.',
    es: 'Escribí la nota con lo que te dijeron y quién te lo dijo.',
  },
  'navigator.terminal.deceasedSource': {
    en: 'Source: a SOMOS answer relayed by the CEO (2026-08-10). There is no written programme guidance for this situation.',
    es: 'Fuente: respuesta de SOMOS relayada por el CEO (2026-08-10). No hay guía escrita del programa para esta situación.',
  },

  // ---------------------------------------------------------------- Wave 11
  // Los dos formularios de SOMOS, sus estados y los documentos del caso.
  // D8: el umbral de $2,000 y las coberturas del PROGRAMA se muestran; las
  // tarifas del fee no aparecen en ningún string de esta sección.

  'navigator.checklist.attestationWarning': {
    en: 'The SOMOS form requires the provider attestation BEFORE referring to Enhanced Services. One is still pending or out with the provider. You can mark the referral anyway — the decision is recorded.',
    es: 'El formulario de SOMOS exige la atestación del proveedor ANTES de referir a Enhanced. Hay una pendiente o todavía en manos del médico. Podés marcar el referido igual — la decisión queda registrada.',
  },
  'navigator.checklist.markAnyway': { en: 'Mark it anyway', es: 'Marcar igual' },
  'navigator.checklist.preauthWarning': {
    en: 'This case has a service that needs a SIGNED preauthorization BEFORE delivery — without it the service is not paid. Do not promise the member an amount for their case before it is authorized. The programme covers up to $2,000 per request; at or above that, three quotes or a SOMOS preferred contractor are required.',
    es: 'Este caso tiene un servicio que necesita preautorización FIRMADA ANTES del servicio — sin ella el servicio no se paga. No le prometas al miembro un monto para su caso antes de que esté autorizada. El programa cubre hasta $2,000 por solicitud; de ahí en adelante hacen falta tres cotizaciones o un contratista preferido de SOMOS.',
  },

  'navigator.common.loading': { en: 'Loading…', es: 'Cargando…' },

  'navigator.docs.title': { en: 'Documents and forms', es: 'Documentos y formularios' },
  'navigator.docs.perService': { en: 'one track per service', es: 'un seguimiento por servicio' },
  'navigator.docs.empty': {
    en: 'No service is being tracked yet. Add the one you are about to refer.',
    es: 'Todavía no hay ningún servicio en seguimiento. Agregá el que vas a referir.',
  },
  'navigator.docs.addService': { en: 'Add a service', es: 'Agregar un servicio' },
  'navigator.docs.choose': { en: 'Choose…', es: 'Elegí…' },
  'navigator.docs.add': { en: 'Add', es: 'Agregar' },
  'navigator.docs.attestation': { en: 'Attestation', es: 'Atestación' },
  'navigator.docs.preauth': { en: 'Preauthorization', es: 'Preautorización' },
  'navigator.docs.generate': { en: 'Upload the completed form — Pathway does not generate it', es: 'Subí el formulario completo — Pathway no lo genera' },
  'navigator.docs.generated': { en: 'Evidence stored', es: 'Evidencia guardada' },
  'navigator.docs.cannotGenerate': { en: 'Pathway does not generate this form. Upload the completed file from FindHelp or the provider.', es: 'Pathway no genera este formulario. Subí el archivo completo desde FindHelp o el proveedor.' },
  'navigator.docs.since': { en: 'since', es: 'desde' },
  'navigator.docs.reasonPlaceholder': {
    en: 'Why the service is being requested',
    es: 'Por qué se solicita el servicio',
  },
  'navigator.docs.preauthRequiredBadge': { en: 'needs preauthorization', es: 'exige preautorización' },
  'navigator.docs.preauthRule': {
    en: 'This service needs a SIGNED preauthorization BEFORE it is delivered — without it, it is not paid. Do not promise the member an amount for their case before it is authorized. The programme covers up to $2,000 per request; at or above that, three quotes or a SOMOS preferred contractor are required. Utilities also need proof of necessity (bill, lease or title).',
    es: 'Este servicio necesita preautorización FIRMADA ANTES de entregarse — sin ella no se paga. No le prometas al miembro un monto para su caso antes de que esté autorizada. El programa cubre hasta $2,000 por solicitud; de ahí en adelante hacen falta tres cotizaciones o un contratista preferido de SOMOS. Los servicios básicos además piden prueba de necesidad (factura, contrato o título).',
  },
  'navigator.docs.signoffHint': {
    en: 'You can take it up to "in review". Authorizing or denying is the Navigator Supervisor or the Program Manager.',
    es: 'Vos podés llevarla hasta "en revisión". Autorizar o denegar es del Navigator Supervisor o del Program Manager.',
  },

  'navigator.docs.att.no_requerida': { en: 'not required', es: 'no requerida' },
  'navigator.docs.att.pendiente': { en: 'pending', es: 'pendiente' },
  'navigator.docs.att.enviada': { en: 'sent to provider', es: 'enviada al proveedor' },
  'navigator.docs.att.recibida': { en: 'received', es: 'recibida' },
  'navigator.docs.att.rechazada': { en: 'refused', es: 'rechazada' },

  'navigator.docs.pre.no_requerida': { en: 'not required', es: 'no requerida' },
  'navigator.docs.pre.pendiente': { en: 'pending', es: 'pendiente' },
  'navigator.docs.pre.en_revision': { en: 'in review', es: 'en revisión' },
  'navigator.docs.pre.autorizada': { en: 'authorized', es: 'autorizada' },
  'navigator.docs.pre.denegada': { en: 'denied', es: 'denegada' },

  'navigator.docs.attachments': { en: 'Attachments', es: 'Adjuntos' },
  'navigator.docs.noFiles': { en: 'Nothing attached yet.', es: 'Todavía no hay nada adjunto.' },
  'navigator.docs.download': { en: 'Download', es: 'Descargar' },
  'navigator.docs.kindLabel': { en: 'Type', es: 'Tipo' },
  'navigator.docs.forService': { en: 'For the service', es: 'Para el servicio' },
  'navigator.docs.wholeCase': { en: 'The whole case', es: 'Todo el caso' },
  'navigator.docs.file': { en: 'File', es: 'Archivo' },
  'navigator.docs.uploaded': { en: 'Attached', es: 'Adjuntado' },
  'navigator.docs.uploadRejected': { en: 'File rejected', es: 'Archivo rechazado' },
  'navigator.docs.uploadHint': {
    en: 'PDF, PNG, JPG, DOCX or TXT, up to 20 MB. Every download is recorded.',
    es: 'PDF, PNG, JPG, DOCX o TXT, hasta 20 MB. Cada descarga queda registrada.',
  },
  'navigator.docs.kind.photo': { en: 'photo', es: 'foto' },
  'navigator.docs.kind.quote': { en: 'quote', es: 'cotización' },
  'navigator.docs.kind.proof': { en: 'proof', es: 'prueba' },
  'navigator.docs.kind.other': { en: 'other', es: 'otro' },

  // Las siete razones de cierre catalogadas. Cada una viene del manual SOMOS o
  // del mapeo de ramas terminales; el texto libre sigue existiendo al lado para
  // contar el caso concreto.
  'navigator.status.reasonCode': { en: 'Reason for closing', es: 'Motivo del cierre' },
  'navigator.status.reasonCodeChoose': { en: 'Choose a reason…', es: 'Elegí un motivo…' },
  'navigator.status.reasonCodeHint': {
    en: 'The catalogued reason is what can be counted. The text below tells this case.',
    es: 'El motivo catalogado es lo que se puede contar. El texto de abajo cuenta este caso.',
  },
  'navigator.closeReason.completado': { en: 'Completed', es: 'Completado' },
  'navigator.close.title': { en: 'Why the case closes', es: 'Por qué se cierra el caso' },
  'navigator.close.hint': {
    en: 'The code says what kind of closure it is — so it can be counted. The free text tells this case. Both, not one or the other.',
    es: 'El código dice de qué clase de cierre se trata — para poder contarlos. El texto cuenta este caso. Los dos, no uno u otro.',
  },
  'navigator.closeReason.completado_pre_modulo': {
    en: 'Completed before the module (declared)',
    es: 'Completado antes del módulo (declarado)',
  },
  'navigator.closeReason.preModuleHint': {
    en: 'The work was done in findhelp before Dispatch existed. Three of the eight steps are events that never happened here, so this closure is DECLARED, not proved — and it is counted apart from a normal completion.',
    es: 'El trabajo se hizo en findhelp antes de que Dispatch existiera. Tres de los ocho pasos son eventos que nunca ocurrieron acá, así que este cierre es DECLARADO, no probado — y se cuenta aparte de un completado normal.',
  },
  'navigator.closeReason.no_consent_ea': {
    en: 'Did not consent to the assessment',
    es: 'No consintió la evaluación',
  },
  'navigator.closeReason.withdrew_after_consent': {
    en: 'Withdrew after consenting',
    es: 'Se retiró después de consentir',
  },
  'navigator.closeReason.unreachable_3_attempts': {
    en: 'Unreachable — 3 documented attempts',
    es: 'Inubicable — 3 intentos documentados',
  },
  'navigator.closeReason.lost_medicaid': {
    en: 'No longer eligible / lost coverage',
    es: 'Ya no es elegible / perdió la cobertura',
  },
  'navigator.closeReason.moved_out_of_region': {
    en: 'Moved out of the region',
    es: 'Se mudó fuera de la región',
  },
  'navigator.closeReason.deceased': { en: 'Deceased', es: 'Falleció' },
  'navigator.closeReason.services_already_in_place': {
    en: 'Already receiving what they need',
    es: 'Ya recibe lo que necesita',
  },
  // E-19 (D-2): etiqueta HONESTA — jamás dice «no consintió». La marca de
  // no-consent vive solo en FindHelp porque allá es la única mecánica de cierre.
  'navigator.closeReason.negative_no_needs': {
    en: 'Negative — no needs',
    es: 'Negativo — sin necesidades',
  },
  'navigator.status.closed_external': { en: 'Closed — external cause', es: 'Cerrado — causa externa' },

  // ---------------------------------------------------------------- Wave 13.1
  // Citas, piscina de agendamiento y los seguimientos que manda el SOP.
  'navigator.appt.title': { en: 'My appointments', es: 'Mis citas' },
  'navigator.nav.queue': { en: 'Queue', es: 'Cola' },
  'navigator.appt.intro': {
    en: 'Your navigation appointments. Opening this starts no clock — the timer only follows you into a case when you open its assessment.',
    es: 'Tus citas de navegación. Abrir esto no arranca ningún reloj — el timer sólo te sigue a un caso cuando abrís su evaluación.',
  },
  'navigator.appt.today': { en: 'Today', es: 'Hoy' },
  'navigator.appt.week': { en: 'This week', es: 'Esta semana' },
  'navigator.appt.empty': { en: 'Nothing scheduled.', es: 'Nada agendado.' },
  'navigator.appt.openCase': { en: 'Open case', es: 'Abrir caso' },
  'navigator.appt.panelTitle': { en: 'Appointments and follow-ups', es: 'Citas y seguimientos' },
  'navigator.appt.panelHint': {
    en: 'What you agreed with the member, and what the SOP says to check back on.',
    es: 'Lo que acordaste con el miembro, y lo que el SOP dice que hay que volver a mirar.',
  },
  'navigator.appt.when': { en: 'Date and time', es: 'Fecha y hora' },
  'navigator.appt.type': { en: 'Type', es: 'Tipo' },
  'navigator.appt.schedule': { en: 'Schedule', es: 'Agendar' },
  'navigator.appt.created': { en: 'Appointment scheduled', es: 'Cita agendada' },
  'navigator.appt.type.ea_call': { en: 'Assessment call', es: 'Llamada de evaluación' },
  'navigator.appt.type.follow_up': { en: 'Follow-up', es: 'Seguimiento' },
  'navigator.appt.type.entrega_servicio': { en: 'Service delivery', es: 'Entrega de servicio' },
  'navigator.appt.type.otro': { en: 'Other', es: 'Otro' },
  'navigator.appt.poolHint': {
    en: 'Cannot reach them to agree on a time? Send them to the scheduling pool and someone will call.',
    es: '¿No lográs ubicarlo para acordar horario? Mandalo a la piscina y alguien lo llama.',
  },
  'navigator.appt.enqueue': { en: 'Send to the pool', es: 'Mandar a la piscina' },
  'navigator.appt.poolQueued': { en: 'Sent to the pool', es: 'Mandado a la piscina' },
  'navigator.appt.poolAlready': { en: 'It was already in the pool', es: 'Ya estaba en la piscina' },
  'navigator.appt.noVoicemail': { en: 'No voicemail', es: 'No dejar mensaje' },
  'navigator.appt.noVoicemailHint': {
    en: 'Leaving a message could put this person at risk. Call, do not leave voicemail.',
    es: 'Dejar un mensaje puede ponerla en riesgo. Llamá, no dejes mensaje.',
  },

  'navigator.followup.banner': {
    en: '{n} follow-up(s) the SOP says to schedule',
    es: '{n} seguimiento(s) que el SOP dice que hay que agendar',
  },
  'navigator.followup.accept': { en: 'Schedule it', es: 'Agendar' },
  'navigator.followup.accepted': { en: 'Scheduled', es: 'Agendado' },

  // ── Rediseño NAV (reparación 2026-08-17): razones de próxima-acción,
  //    códigos de EA, resultados de servicio, estados de cita, motivos del
  //    reloj, no-voicemail y acciones de admin. Merge-only sobre HEAD.
  'navigator.workspace.reason.call_or_schedule': {
    en: 'Call now or schedule the member. The timer and billable work begin only when the member answers.',
    es: 'Llamá ahora o agendá al miembro. El reloj y el trabajo facturable arrancan sólo cuando contesta.',
  },
  'navigator.workspace.reason.verify_prior_navigation': {
    en: 'FindHelp shows prior navigation. Verify it with the member and in FindHelp before doing new work.',
    es: 'FindHelp muestra navegación previa. Verificala con el miembro y en FindHelp antes de trabajar de nuevo.',
  },
  'navigator.workspace.reason.complete_ea': {
    en: 'Open the Pathway wizard. When it is done, continue with services — do not paste a FindHelp ID.',
    es: 'Abrí el wizard de Pathway. Al terminar, seguí con los servicios — sin pegar IDs de FindHelp.',
  },
  'navigator.workspace.reason.review_needs': {
    en: 'Review the needs from the EA and add only the services that are still missing.',
    es: 'Revisá las necesidades del EA y agregá sólo los servicios que sigan faltando.',
  },
  'navigator.workspace.reason.record_goal': {
    en: 'Mark the Goal done for {service} after you create it in FindHelp. No ID paste.',
    es: 'Marcá el Goal de {service} como hecho después de crearlo en FindHelp. Sin pegar ID.',
  },
  'navigator.workspace.reason.confirm_referral_consent': {
    en: 'General consent is not repeated; referral consent is recorded once for this service.',
    es: 'El consentimiento general no se repite; el del referido se registra una sola vez para este servicio.',
  },
  'navigator.workspace.reason.record_referral': {
    en: 'Mark the referral done after you create or reuse it in FindHelp. No ID paste.',
    es: 'Marcá el referido como hecho después de crearlo o reusarlo en FindHelp. Sin pegar ID.',
  },
  'navigator.workspace.reason.complete_followup': {
    en: 'Log the outcome of this follow-up. FindHelp IDs are not required.',
    es: 'Registrá el resultado de este follow-up. No hacen falta IDs de FindHelp.',
  },
  'navigator.workspace.reason.continue_service_followup': {
    en: 'Follow the real outcome until every service is terminal; units are the ceiling, not the goal.',
    es: 'Seguí el resultado real hasta que cada servicio tenga salida terminal; las unidades son el techo, no la meta.',
  },
  'navigator.workspace.reason.close_episode': {
    en: 'All services are terminal. Close when the work is done.',
    es: 'Todos los servicios tienen salida terminal. Cerrá cuando el trabajo esté hecho.',
  },
  'navigator.workspace.reason.attempts_exhausted': {
    en: 'Five outreach attempts did not reach the member. Unblock if they call back — do not close as unreachable from here.',
    es: 'Cinco intentos de contacto no alcanzaron al miembro. Desbloqueá si devuelve el llamado — no lo cierres como inubicable desde acá.',
  },
  'navigator.workspace.reason.closed': {
    en: 'This episode is complete and read-only.',
    es: 'Este episodio está completo y es de solo lectura.',
  },
  'navigator.workspace.eaReason.none': {
    en: 'No completed Eligibility Assessment is on file.',
    es: 'No hay ninguna Eligibility Assessment completada registrada.',
  },
  'navigator.workspace.eaReason.material_change': {
    en: 'A material change requires a new Eligibility Assessment.',
    es: 'Un cambio material exige una nueva Eligibility Assessment.',
  },
  'navigator.workspace.eaReason.expired': {
    en: 'The Eligibility Assessment is expired.',
    es: 'La Eligibility Assessment está vencida.',
  },
  'navigator.workspace.eaReason.reusable': {
    en: 'The existing Eligibility Assessment is valid and may be reused.',
    es: 'La Eligibility Assessment existente es válida y puede reutilizarse.',
  },
  'navigator.close.requiresEa': {
    en: 'You cannot close as completed without a completed Eligibility Assessment.',
    es: 'No se puede cerrar como completado sin una Eligibility Assessment completada.',
  },
  'navigator.close.requiresTracks': {
    en: 'You cannot close as completed without any evaluated service. Add the needs from the EA, or close it for the reason that applies.',
    es: 'No se puede cerrar como completado sin ningún servicio evaluado. Agregá las necesidades del EA, o cerralo por el motivo que corresponda.',
  },
  'navigator.workspace.mirrorTitle': {
    en: 'Mirror to FindHelp (EA answers and roster)',
    es: 'Espejo a FindHelp (respuestas del EA y roster)',
  },
  'navigator.workspace.celebrationTitle': { en: 'Episode completed!', es: '¡Episodio completado!' },
  'navigator.workspace.notReferredSaved': { en: 'Recorded: not referred', es: 'Registrado: no referido' },
  'navigator.workspace.notReferredWhy': { en: 'Why is it not referred?', es: '¿Por qué no se refiere?' },
  'navigator.workspace.serviceOutcome': { en: 'Service outcome', es: 'Resultado del servicio' },
  'navigator.workspace.recordOutcome': { en: 'Record outcome', es: 'Registrar resultado' },
  'navigator.workspace.outcomeSaved': { en: 'Outcome recorded', es: 'Resultado registrado' },
  'navigator.workspace.providerOutcomeHint': {
    en: 'Provider rejected or did not respond: re-refer or escalate — never mark it completed.',
    es: 'El proveedor rechazó o no respondió: re-referí o escalá — nunca lo marques completado.',
  },
  'navigator.workspace.attestJustification': {
    en: 'Justification (attestation still pending)',
    es: 'Justificación (la atestación sigue pendiente)',
  },
  'navigator.workspace.attestJustificationHint': {
    en: 'The referral goes out anyway; why it cannot wait is written down and audited.',
    es: 'El referido sale igual; por qué no puede esperar queda escrito y auditado.',
  },
  'navigator.noVm.saved': { en: 'Contact flag updated', es: 'Marca de contacto actualizada' },
  'navigator.noVm.confirmOn': {
    en: 'Mark this member as DO NOT leave voicemail? Everyone who dials will see it.',
    es: '¿Marcar a este miembro como NO dejar mensaje de voz? Lo verá cualquiera que vaya a marcar.',
  },
  'navigator.noVm.confirmOff': {
    en: 'Remove the do-not-leave-voicemail flag?',
    es: '¿Quitar la marca de no dejar mensaje de voz?',
  },
  'navigator.noVm.turnOn': { en: 'Mark: no voicemail', es: 'Marcar: sin voicemail' },
  'navigator.noVm.turnOff': { en: 'Remove no-voicemail flag', es: 'Quitar marca sin voicemail' },
  'navigator.lifecycle.adjustedByNavigator': { en: 'Case adjusted', es: 'Ajuste del caso' },
  'navigator.svcStatus.identified': { en: 'Identified', es: 'Identificado' },
  'navigator.svcStatus.goal_pending': { en: 'Goal pending', es: 'Goal pendiente' },
  'navigator.svcStatus.referral_pending': { en: 'Referral pending', es: 'Referido pendiente' },
  'navigator.svcStatus.provider_pending': { en: 'Waiting on provider', es: 'Esperando al proveedor' },
  'navigator.svcStatus.service_pending': { en: 'Service pending', es: 'Servicio pendiente' },
  'navigator.svcStatus.service_active': { en: 'Service active', es: 'Servicio activo' },
  'navigator.svcStatus.followup_due': { en: 'Follow-up due', es: 'Seguimiento vencido' },
  'navigator.svcStatus.blocked': { en: 'Blocked', es: 'Bloqueado' },
  'navigator.svcResult.received': { en: 'Received', es: 'Recibido' },
  'navigator.svcResult.active': { en: 'Active', es: 'Activo' },
  'navigator.svcResult.need_resolved': { en: 'Need resolved', es: 'Necesidad resuelta' },
  'navigator.svcResult.member_declined': { en: 'Member declined', es: 'El miembro declinó' },
  'navigator.svcResult.provider_denied': { en: 'Provider denied', es: 'El proveedor denegó' },
  'navigator.svcResult.provider_no_response': { en: 'Provider did not respond', es: 'El proveedor no respondió' },
  'navigator.svcResult.not_eligible': { en: 'Not eligible', es: 'No elegible' },
  'navigator.svcResult.duplicate_service': { en: 'Duplicate service', es: 'Servicio duplicado' },
  'navigator.svcResult.moved_out_of_region': { en: 'Moved out of region', es: 'Se mudó de la región' },
  'navigator.svcResult.deceased': { en: 'Deceased', es: 'Fallecido' },
  'navigator.svcResult.other': { en: 'Other', es: 'Otro' },
  'navigator.appt.status.scheduled': { en: 'Scheduled', es: 'Programada' },
  'navigator.appt.status.rescheduled': { en: 'Rescheduled', es: 'Reprogramada' },
  'navigator.appt.status.cancelled': { en: 'Cancelled', es: 'Cancelada' },
  'navigator.appt.status.completed': { en: 'Completed', es: 'Completada' },
  'navigator.appt.status.missed': { en: 'No-show', es: 'No se presentó' },
  'navigator.appt.status.no_show': { en: 'No-show', es: 'No se presentó' },
  'navigator.endReason.manual': { en: 'paused by hand', es: 'pausado a mano' },
  'navigator.endReason.switch': { en: 'switched case', es: 'cambio de caso' },
  'navigator.endReason.route_exit': { en: 'left the case', es: 'salió del caso' },
  'navigator.endReason.idle': { en: 'idle (closed at last touch)', es: 'inactividad (cerrado en el último toque)' },
  'navigator.endReason.logout': { en: 'signed out', es: 'cerró sesión' },
  'navigator.endReason.max_duration': { en: 'max duration', es: 'duración máxima' },
  'navigator.endReason.reassigned': { en: 'reassigned', es: 'reasignado' },
  'navigator.endReason.case_closed': { en: 'case closed', es: 'caso cerrado' },
  'navigator.endReason.owner_lost': { en: 'owner changed', es: 'cambió el dueño' },
  'navigator.followup.rule.post_initial_reimbursement_10d': {
    en: 'General check ~10 days after the first billable contact (SOP).',
    es: 'Control general ~10 días después del primer contacto facturable (SOP).',
  },
  'navigator.followup.rule.generic': { en: 'Scheduled follow-up', es: 'Seguimiento programado' },
  'admin.nav.flag.parked': { en: 'attempts exhausted (parked)', es: 'intentos agotados (parkeado)' },
  'admin.nav.actions.title': { en: 'Admin actions', es: 'Acciones de admin' },
  'admin.nav.unblock': { en: 'Unblock outreach', es: 'Desbloquear outreach' },
  'admin.nav.unblockHint': {
    en: 'Lifts the outreach parking so the navigator can keep trying. The server refuses if the case is not parked.',
    es: 'Levanta el parking de outreach para que el navigator pueda seguir intentando. El servidor lo rechaza si el caso no está parkeado.',
  },
  'admin.nav.unblockDone': { en: 'Outreach unblocked', es: 'Outreach desbloqueado' },
  'admin.nav.unblockFailed': { en: 'Could not unblock', es: 'No se pudo desbloquear' },
  'admin.nav.waiveTitle': { en: 'Waive a checklist step', es: 'Dispensar un paso del checklist' },
  'admin.nav.waiveHint': {
    en: 'The waived step and its reason land on the record and the audit log. Since the next-action engine replaced the checklist gate, this corrects the record — it does not unlock the close.',
    es: 'El paso dispensado y su motivo quedan en el expediente y en el audit. Desde que el motor de próxima acción reemplazó al checklist, esto corrige el registro — no destraba el cierre.',
  },
  'admin.nav.waiveStep': { en: 'Step', es: 'Paso' },
  'admin.nav.waiveReason': { en: 'Reason', es: 'Motivo' },
  'admin.nav.waiveApply': { en: 'Waive step', es: 'Dispensar paso' },
  'admin.nav.waiveDone': { en: 'Step waived', es: 'Paso dispensado' },
  'admin.nav.waiveFailed': { en: 'Could not waive the step', es: 'No se pudo dispensar el paso' },
  'admin.nav.tracksTitle': { en: 'Service tracks', es: 'Seguimientos por servicio' },
  'admin.nav.tracksEmpty': { en: 'No services on this case yet.', es: 'Este caso todavía no tiene servicios.' },
  'admin.nav.preauthAuthorize': { en: 'Authorize', es: 'Autorizar' },
  'admin.nav.preauthDeny': { en: 'Deny', es: 'Denegar' },
  'admin.nav.preauthDenyReason': { en: 'Denial reason (from SOMOS)', es: 'Motivo de la denegación (de SOMOS)' },
  'admin.nav.preauthDone': { en: 'Preauthorization updated', es: 'Preautorización actualizada' },
  'admin.nav.preauthFailed': { en: 'Could not update the preauthorization', es: 'No se pudo actualizar la preautorización' },
  'admin.nav.downloadFailed': { en: 'Download failed', es: 'La descarga falló' },
  'navigator.followup.dismissed': { en: 'Dismissed', es: 'Descartado' },
  'navigator.followup.rule.post_completion_5bd': {
    en: 'Within 5 business days after the service ends (Ops Manual v8)',
    es: 'Dentro de los 5 días hábiles de terminado el servicio (Ops Manual v8)',
  },
  'navigator.followup.rule.pre_completion_60d': {
    en: '60 days before the service ends (Ops Manual v8)',
    es: '60 días antes de que termine el servicio (Ops Manual v8)',
  },
  'navigator.followup.rule.pre_completion_10d': {
    en: '10 days before the service ends (Ops Manual v8, nutrition)',
    es: '10 días antes de que termine el servicio (Ops Manual v8, nutrición)',
  },
  'navigator.followup.rule.pre_completion_14d': {
    en: '14 days before the service ends (Ops Manual v8, medical respite)',
    es: '14 días antes de que termine el servicio (Ops Manual v8, respiro médico)',
  },
  'navigator.followup.rule.referral_no_response': {
    en: 'Referral with no provider response — provisional window, pending SOMOS',
    es: 'Referido sin respuesta del proveedor — plazo provisional, pendiente de SOMOS',
  },

  'navigator.pool.title': { en: 'Scheduling pool', es: 'Piscina de agendamiento' },
  'navigator.pool.intro': {
    en: 'People to call so a navigator can be given a time that works for them.',
    es: 'Gente a la que hay que llamar para poder darle a un navigator un horario que le sirva.',
  },
  'navigator.pool.notBillable': {
    en: 'This work is not billable: chasing someone to agree on a time is not paid. Billing starts when the member takes the navigator call, on their case.',
    es: 'Este trabajo no es facturable: conseguir que alguien atienda para acordar horario no se cobra. Lo facturable arranca cuando el miembro toma la llamada del navigator, en su caso.',
  },
  'navigator.pool.empty': { en: 'Nobody waiting.', es: 'Nadie esperando.' },
  'navigator.pool.take': { en: 'Take and call', es: 'Tomar y llamar' },
  'navigator.pool.logAttempt': { en: 'Log attempt', es: 'Registrar intento' },
  'navigator.pool.convert': { en: 'Turn into appointment', es: 'Convertir en cita' },
  'navigator.pool.when': { en: 'Date and time', es: 'Fecha y hora' },
  'navigator.pool.outcome': { en: 'Outcome', es: 'Resultado' },
  'navigator.pool.attempts': { en: '{n} attempt(s)', es: '{n} intento(s)' },
  'navigator.pool.attemptsSoFar': { en: '{n} of 3 attempts', es: '{n} de 3 intentos' },
  'navigator.pool.attemptSaved': { en: 'Attempt logged', es: 'Intento registrado' },
  'navigator.pool.nowExhausted': { en: 'Three attempts reached — exhausted.', es: 'Tres intentos alcanzados — agotado.' },
  'navigator.pool.nowOptout': { en: 'They asked not to be called again.', es: 'Pidió que no lo llamemos más.' },
  'navigator.pool.scheduled': { en: 'Appointment created', es: 'Cita creada' },
  'navigator.pool.urgent': { en: 'Urgent', es: 'Urgente' },
  'navigator.pool.error': { en: 'Could not do it', es: 'No se pudo' },
  'navigator.pool.status.pending': { en: 'waiting', es: 'esperando' },
  'navigator.pool.status.claimed': { en: 'being called', es: 'en llamada' },
  'navigator.pool.status.scheduled': { en: 'scheduled', es: 'agendada' },
  'navigator.pool.status.exhausted': { en: 'exhausted', es: 'agotado' },
  'navigator.pool.status.optout': { en: 'opted out', es: 'no llamar' },
  'navigator.pool.outcome.no_answer': { en: 'No answer', es: 'No contestó' },
  'navigator.pool.outcome.voicemail': { en: 'Voicemail', es: 'Buzón' },
  'navigator.pool.outcome.wrong_number': { en: 'Wrong number', es: 'Número equivocado' },
  'navigator.pool.outcome.reached': { en: 'Reached', es: 'Contactado' },
  'navigator.pool.outcome.refused': { en: 'Refused', es: 'Se negó' },
  'navigator.pool.outcome.optout': { en: 'Do not call again', es: 'No llamar más' },

  // ---------------------------------------------------------------- Wave 12
  'navigator.household.title': { en: 'Household member', es: 'Miembro del hogar' },
  'navigator.household.intro': {
    en: 'Someone else in the household needs help too? Screen them now, on this same call — they do not go to the uploader queue and the case comes to you.',
    es: '¿Hay otra persona del hogar que también necesita ayuda? Screeneala ahora, en esta misma llamada — no pasa por la cola de uploaders y el caso queda tuyo.',
  },
  'navigator.household.captured': { en: 'Captured in the screening', es: 'Capturados en el screening' },
  'navigator.household.start': { en: 'Screen this person', es: 'Screenear a esta persona' },
  'navigator.household.started': { en: 'Screening open', es: 'Screening abierto' },
  'navigator.household.reused': {
    en: 'This person was already a patient — their file was reused.',
    es: 'Esta persona ya era paciente nuestra — se reusó su ficha.',
  },
  'navigator.household.none': {
    en: 'The original screening did not capture anyone else in the household.',
    es: 'El screening original no capturó a nadie más del hogar.',
  },
  'navigator.household.billableHint': {
    en: 'They are already on the phone: the new case opens with first contact sealed, so it is billable from the first minute.',
    es: 'Ya está al teléfono: el caso nuevo abre con el primer contacto sellado, así que es facturable desde el primer minuto.',
  },
  'navigator.household.close': { en: 'Back to the case', es: 'Volver al caso' },
  // REORG-2 punto 4 — la fila sabe en que estado esta cada familiar, asi que el
  // boton de screenear no reaparece despues de recargar.
  'navigator.household.windowTitle': {
    en: 'Screening the household member', es: 'Screening del miembro del hogar',
  },
  'navigator.household.resume': { en: 'Continue', es: 'Continuar' },
  'navigator.household.resumed': {
    en: 'Screening already open — reopened', es: 'El screening ya estaba abierto — se retomo',
  },
  'navigator.household.submitted': {
    en: 'Screening submitted', es: 'Screening enviado',
  },
  'navigator.household.alreadySubmitted': {
    en: 'This person was already screened', es: 'A esta persona ya se le hizo el screening',
  },
  'navigator.household.promotedBadge': { en: 'Screened', es: 'Ya screeneado' },
  'navigator.household.inProgressBadge': { en: 'Screening open', es: 'Screening en curso' },
  'navigator.household.alreadyPatient': {
    en: 'Already a patient', es: 'Ya es paciente nuestro',
  },
  'navigator.household.openCase': { en: 'Open the case', es: 'Abrir el caso' },
  'navigator.household.noCaseYet': {
    en: 'No case: no positive domains', es: 'Sin caso: no dio dominios positivos',
  },

  'navigator.status.checklistInfo': {
    en: 'Checklist progress: {done} of {total}. Outstanding: {pending}. Informational — the server decides the close by EA + terminal services.',
    es: 'Progreso del checklist: {done} de {total}. Faltan: {pending}. Informativo — el cierre lo decide el servidor por EA + servicios terminales.',
  },

  'navigator.firstContact.pending': {
    en: 'This member has not taken the call yet. Work is not billable until they do — log a reached attempt first.',
    es: 'Este miembro todavía no ha tomado la llamada. El trabajo no es facturable hasta que la tome — registrá primero un intento con contacto.',
  },
  'navigator.firstContact.blocked': {
    en: 'Nothing is billable before first contact. Log a reached outreach attempt, then record the session.',
    es: 'Nada es facturable antes del primer contacto. Registrá un intento con resultado "contactado" y después la sesión.',
  },


  // REORG-5 P1 — el panel dejó de ser sólo las notas: ahora trae el screening
  // entero, con lo que el miembro contestó en cada dominio.
  'navigator.screeningNotes.title': { en: 'The screening', es: 'El screening' },
  'navigator.screeningNotes.originalLabel': { en: 'Answered with the agent — read only', es: 'Contestado con el agente — solo lectura' },
  'navigator.screeningNotes.appendsLabel': { en: 'Added afterwards', es: 'Agregado después' },
  'navigator.screeningNotes.add': { en: 'Add detail', es: 'Agregar detalle' },
  'navigator.screeningNotes.domain': { en: 'Domain (optional)', es: 'Dominio (opcional)' },
  'navigator.screeningNotes.noDomain': { en: 'No particular domain', es: 'Ningún dominio en particular' },
  'navigator.screeningNotes.body': { en: 'What to add', es: 'Qué agregar' },
  'navigator.screeningNotes.bodyPlaceholder': {
    en: 'New detail the member gave you',
    es: 'Detalle nuevo que te dio el miembro',
  },
  'navigator.screeningNotes.appendHint': {
    en: 'This is added below the original — the agent\'s note is never overwritten.',
    es: 'Esto se agrega debajo del original — la nota del agente nunca se sobrescribe.',
  },
  'navigator.screeningNotes.empty': { en: 'This case has no screening on file.', es: 'Este caso no tiene screening cargado.' },
  'navigator.screeningNotes.appended': { en: 'Detail added', es: 'Detalle agregado' },

  // ===== Cluster NAV Wave 6C — history already in findhelp =====
  'navigator.fh.title': { en: 'History in findhelp', es: 'Historia en findhelp' },
  'navigator.fh.status': { en: 'What findhelp already has for this case', es: 'Qué tiene findhelp ya de este caso' },
  'navigator.fh.none': { en: 'Not worked there', es: 'No trabajado allá' },
  'navigator.fh.incomplete': { en: 'Started, incomplete', es: 'Empezado, incompleto' },
  'navigator.fh.complete': { en: 'Completed there', es: 'Completado allá' },
  'navigator.fh.chipIncomplete': { en: 'findhelp: incomplete', es: 'findhelp: incompleto' },
  'navigator.fh.chipComplete': { en: 'findhelp: complete', es: 'findhelp: completo' },
  'navigator.fh.saved': { en: 'History updated', es: 'Historia actualizada' },

  'navigator.fh.importNotes': { en: 'Paste notes from findhelp', es: 'Pegar notas desde findhelp' },
  'navigator.fh.importNotesHint': {
    en: 'These arrive already mirrored -- they came out of findhelp, so they do not count as pending to copy back.',
    es: 'Estas llegan ya espejadas — salieron de findhelp, así que no cuentan como pendientes de copiar de vuelta.',
  },
  'navigator.fh.originalDate': { en: 'Original date (optional)', es: 'Fecha original (opcional)' },
  'navigator.fh.notePlaceholder': {
    en: 'Paste the note exactly as it reads in findhelp',
    es: 'Pegá la nota tal como está en findhelp',
  },
  'navigator.fh.importNote': { en: 'Import note', es: 'Importar nota' },
  'navigator.fh.noteImported': { en: 'Note imported', es: 'Nota importada' },

  'navigator.fh.stepsTitle': { en: 'Steps already done in findhelp', es: 'Pasos ya hechos en findhelp' },
  'navigator.fh.markStep': { en: 'done in findhelp', es: 'hecho en findhelp' },
  'navigator.fh.stepDone': { en: 'done', es: 'hecho' },
  'navigator.fh.stepImported': { en: 'Step recorded as done in findhelp', es: 'Paso registrado como hecho en findhelp' },

  'navigator.fh.contactTitle': { en: 'Contact already made', es: 'Contacto ya realizado' },
  'navigator.fh.contactCheck': {
    en: 'The member was actually reached in findhelp',
    es: 'El miembro sí fue contactado en findhelp',
  },
  'navigator.fh.contactHint': {
    en: 'Recorded as a reached outreach attempt, which is what unlocks billing for this case.',
    es: 'Se registra como intento contactado, que es lo que habilita facturar este caso.',
  },
  'navigator.fh.contactDate': { en: 'When (optional)', es: 'Cuándo (opcional)' },
  'navigator.fh.contactSave': { en: 'Record contact', es: 'Registrar contacto' },
  'navigator.fh.contactImported': { en: 'Contact recorded', es: 'Contacto registrado' },
  'navigator.fh.contactUnlocked': {
    en: 'Sessions can now be billed for this member.',
    es: 'Ahora se pueden facturar sesiones de este miembro.',
  },
  'navigator.fh.contactAlreadyHad': {
    en: 'This case already had a first contact; the earlier date stands.',
    es: 'Este caso ya tenía primer contacto; queda la fecha anterior.',
  },
  'navigator.fh.contactAlready': { en: 'First contact: {date}', es: 'Primer contacto: {date}' },

  'navigator.session.serviceDate': { en: 'Service date (leave empty for today)', es: 'Fecha del servicio (vacío = hoy)' },
  'navigator.session.serviceDateHint': {
    en: 'For work already done in findhelp, use the real date -- the monthly limit is measured against that month.',
    es: 'Para trabajo ya hecho en findhelp, poné la fecha real — el tope mensual se mide contra ese mes.',
  },

  // ===== Cluster NAV Wave 7 — /admin/navigation control room =====
  'adminNav.nav': { en: 'Navigation', es: 'Navegación' },
  'adminNav.title': { en: 'Navigation — control room', es: 'Navegación — centro de control' },
  'adminNav.subtitle': {
    en: 'The whole module: what is queued, who is working it, and what is still waiting to reach findhelp.',
    es: 'Todo el módulo: qué hay en cola, quién lo trabaja, y qué falta pasar a findhelp.',
  },
  'adminNav.loading': { en: 'Loading…', es: 'Cargando…' },
  'adminNav.loadFailed': { en: 'Could not load.', es: 'No se pudo cargar.' },

  'adminNav.tab.summary': { en: 'Live', es: 'Resumen' },
  'adminNav.tab.byNavigator': { en: 'By navigator', es: 'Por navigator' },
  'adminNav.tab.cases': { en: 'Cases', es: 'Casos' },
  'adminNav.tab.kb': { en: 'Knowledge base', es: 'Base de conocimiento' },
  'adminNav.tab.config': { en: 'Config', es: 'Config' },
  'adminNav.tab.priorityList': { en: 'Priority list', es: 'Lista prioritaria' },
  'adminNav.tab.reassign': { en: 'Reassignment', es: 'Reasignación' },

  'adminNav.stat.open': { en: 'Open cases', es: 'Casos abiertos' },
  'adminNav.stat.unclaimed': { en: 'Unclaimed', es: 'Sin tomar' },
  'adminNav.stat.urgent': { en: 'Urgent', es: 'Urgentes' },
  'adminNav.stat.slaOverdue': { en: 'Deadline passed', es: 'SLA vencido' },
  'adminNav.stat.slaDueSoon': { en: 'Due within a day', es: 'Vence en un día' },
  'adminNav.stat.pendingMirror': { en: 'To mirror to findhelp', es: 'Pendiente de espejar' },

  'adminNav.awaitingFirstContact': {
    en: '{n} claimed case(s) where the member has never answered — nothing on them is billable yet.',
    es: '{n} caso(s) tomados donde el miembro nunca atendió — todavía no son facturables.',
  },
  'adminNav.byStatus': { en: 'By status', es: 'Por estado' },
  'adminNav.byFindhelp': { en: 'Already in findhelp', es: 'Ya en findhelp' },
  'adminNav.byFindhelpHint': {
    en: 'How much of the backlog was already worked over there during the manual phase.',
    es: 'Cuánto del backlog ya venía trabajado de allá en la fase manual.',
  },
  'adminNav.pendingMirrorTitle': { en: 'Waiting to reach findhelp', es: 'Esperando llegar a findhelp' },
  'adminNav.notBuilt': { en: 'Not built yet: {list}', es: 'Todavía no existe: {list}' },

  'adminNav.fh.none': { en: 'Not worked there', es: 'No trabajado allá' },
  'adminNav.fh.incomplete': { en: 'Started, incomplete', es: 'Empezado, incompleto' },
  'adminNav.fh.complete': { en: 'Completed there', es: 'Completado allá' },

  'adminNav.window': { en: 'Window', es: 'Ventana' },
  'adminNav.window.today': { en: 'Today', es: 'Hoy' },
  'adminNav.window.week': { en: 'Last 7 days', es: 'Últimos 7 días' },
  'adminNav.window.month': { en: 'This month', es: 'Este mes' },
  'adminNav.noNavigators': { en: 'No active navigators.', es: 'No hay navigators activos.' },

  'adminNav.col.navigator': { en: 'Navigator', es: 'Navigator' },
  'adminNav.col.cases': { en: 'Cases', es: 'Casos' },
  'adminNav.col.open': { en: 'Open', es: 'Abiertos' },
  'adminNav.col.attempts': { en: 'Attempts', es: 'Intentos' },
  'adminNav.col.sessions': { en: 'Sessions', es: 'Sesiones' },
  'adminNav.col.units': { en: 'Units', es: 'Unidades' },
  'adminNav.col.delta': { en: 'Timer gap', es: 'Delta timer' },
  'adminNav.col.checklist': { en: 'Checklist', es: 'Checklist' },
  'adminNav.col.pending': { en: 'To mirror', es: 'A espejar' },
  'adminNav.col.member': { en: 'Member', es: 'Miembro' },
  'adminNav.col.status': { en: 'Status', es: 'Estado' },
  'adminNav.col.findhelp': { en: 'findhelp', es: 'findhelp' },
  'adminNav.col.sla': { en: 'Deadline', es: 'Fecha límite' },
  'adminNav.col.actions': { en: 'Actions', es: 'Acciones' },

  'adminNav.deltaNone': {
    en: 'No session in this window ran the timer.',
    es: 'Ninguna sesión de esta ventana usó el timer.',
  },
  'adminNav.deltaHint': { en: 'Across {n} timed session(s).', es: 'Sobre {n} sesión(es) cronometradas.' },
  'adminNav.deltaFooter': {
    en: 'The gap compares reported minutes against measured ones, and only for sessions that ran a timer. It is a signal to ask about, not a verdict.',
    es: 'El delta compara minutos reportados contra medidos, y solo en sesiones que usaron timer. Es una señal para preguntar, no un veredicto.',
  },

  'adminNav.filter.status': { en: 'Status', es: 'Estado' },
  'adminNav.filter.findhelp': { en: 'Already in findhelp', es: 'Ya en findhelp' },
  // GC-3 #3 — filtro por razón de cierre + export del listado.
  'adminNav.filter.closedReason': { en: 'Closed reason', es: 'Razón de cierre' },
  'adminNav.export.download': { en: 'Download CSV', es: 'Descargar CSV' },
  'adminNav.export.working': { en: 'Preparing…', es: 'Preparando…' },
  'adminNav.export.failed': { en: 'Export failed', es: 'La exportación falló' },
  'adminNav.filter.search': { en: 'CIN hash (last chars)', es: 'Hash del CIN (últimos)' },
  'adminNav.filter.apply': { en: 'Search', es: 'Buscar' },
  'adminNav.filter.any': { en: 'Any', es: 'Cualquiera' },
  'adminNav.filter.urgent': { en: 'Urgent only', es: 'Solo urgentes' },
  'adminNav.filter.somos': { en: 'SOMOS list', es: 'Lista SOMOS' },
  'adminNav.filter.overdue': { en: 'Deadline passed', es: 'SLA vencido' },

  'adminNav.priorityList.title': {
    en: 'Upload priority list',
    es: 'Cargar lista prioritaria',
  },
  'adminNav.priorityList.hint': {
    en: 'Upload a CIN list (xlsx/csv). Matching is by Medicaid CIN. Navigators do not see a special label — only queue order changes. Admin sees the SOMOS chip on Cases.',
    es: 'Subí una lista de CINs (xlsx/csv). El match es por Medicaid CIN. El navigator no ve etiqueta especial — solo cambia el orden de la cola. Admin ve el chip SOMOS en Casos.',
  },
  'adminNav.priorityList.file': { en: 'File', es: 'Archivo' },
  'adminNav.priorityList.preview': { en: 'Dry-run preview', es: 'Vista previa (dry-run)' },
  'adminNav.priorityList.apply': { en: 'Apply priority', es: 'Aplicar prioridad' },
  'adminNav.priorityList.createCases': {
    en: 'Create cases for matched eligible members without an open episode',
    es: 'Crear casos para matched elegibles sin episodio abierto',
  },
  'adminNav.priorityList.navigator': {
    en: 'Assign to navigator (optional)',
    es: 'Asignar a navigator (opcional)',
  },
  'adminNav.priorityList.navigatorNone': { en: 'Keep / auto', es: 'Mantener / auto' },
  'adminNav.priorityList.previewDone': { en: 'Preview ready', es: 'Vista previa lista' },
  'adminNav.priorityList.applyDone': {
    en: 'Applied: {priority} priority · {created} cases created · {reassigned} reassigned',
    es: 'Aplicado: {priority} prioridad · {created} casos creados · {reassigned} reasignados',
  },
  'adminNav.priorityList.failed': { en: 'Import failed', es: 'El import falló' },
  'adminNav.priorityList.needFile': { en: 'Choose a file first', es: 'Elegí un archivo primero' },
  'adminNav.priorityList.needPreview': {
    en: 'Run a dry-run preview before applying',
    es: 'Corré la vista previa antes de aplicar',
  },
  'adminNav.priorityList.stat.unique': { en: 'Unique CINs', es: 'CINs únicos' },
  'adminNav.priorityList.stat.matched': { en: 'Matched', es: 'Encontrados' },
  'adminNav.priorityList.stat.notFound': { en: 'Not found', es: 'No encontrados' },
  'adminNav.priorityList.stat.open': { en: 'Open cases', es: 'Con caso abierto' },
  'adminNav.priorityList.stat.eligible': { en: 'Eligible to create', es: 'Elegibles a crear' },
  'adminNav.priorityList.stat.already': { en: 'Already priority', es: 'Ya prioritarios' },
  'adminNav.priorityList.notFoundSample': { en: 'Sample not found', es: 'Muestra no encontrados' },
  'adminNav.priorityList.adminOnly': {
    en: 'Apply is admin-only. Supervisors can preview.',
    es: 'Aplicar es solo admin. Supervisores pueden previsualizar.',
  },

  // E-36 (Edd, 2026-10-21, propuesta B): las opciones dicen QUÉ casos son,
  // con las palabras de la pantalla. El servidor no cambia: los valores
  // (`findhelp_positives`, …) siguen siendo los mismos.
  'adminNav.bulkReassign.filter.findhelp_positives': {
    en: 'Positives ready to deal',
    es: 'Positivos listos para repartir',
  },
  'adminNav.bulkReassign.filter.findhelp_negatives': {
    en: 'Negatives with Medicaid',
    es: 'Negativos con Medicaid',
  },
  // POOL-B2 (D-1): «En el buzón» reemplaza a «Sin asignar» — el CHECK del dueño
  // hace imposible un caso abierto sin dueño (medido: siempre 0).
  // F5-P3 - se llamaba «En el buzón» y el CEO lo cruzó con las cuatro
  // vistas: eran el MISMO conjunto con dos nombres en la misma pantalla
  // (16 arriba, 16 abajo). Ahora el nombre dice que es el total.
  // E-36: «Todo el buzón» (la pestaña ya se llama «Del buzón»). Solo existe en
  // esa pestaña: para una persona el server da siempre 0 (exige dueño 'pool').
  'adminNav.bulkReassign.filter.in_pool': { en: 'Everything in the pool', es: 'Todo el buzón' },
  'adminNav.bulkReassign.filter.somos': { en: 'SOMOS priority list', es: 'Lista prioritaria SOMOS' },
  // E-36: `open` SOLO se ofrece en «De una persona», donde quiere decir todos
  // los abiertos de esa persona. En el buzón era lo mismo que «Todo el buzón»
  // con otro nombre (los dos decían 13) y parecía «todo el sistema»: sale de
  // esa pestaña. El server lo sigue aceptando igual.
  'adminNav.bulkReassign.filter.open': { en: 'Everything this person has', es: 'Todo lo de esta persona' },
  'adminNav.bulkReassign.navigatorLoading': { en: 'Loading navigators…', es: 'Cargando navigators…' },
  'adminNav.bulkReassign.navigatorEmpty': {
    en: 'No active navigators',
    es: 'No hay navigators activos',
  },
  'adminNav.bulkReassign.navigatorError': {
    en: 'Could not load navigators.',
    es: 'No se pudieron cargar los navigators.',
  },
  'adminNav.bulkReassign.retry': { en: 'Retry', es: 'Reintentar' },
  'adminNav.bulkReassign.applyNothingToAssign': {
    en: 'Preview found nothing to assign with this filter.',
    es: 'La vista previa no encontró nada para asignar con este filtro.',
  },
  // El cero mas confuso de la pantalla: hay casos en el filtro, pero YA
  // son del destino elegido. Antes quedaba mudo y parecia que el numero
  // de arriba estaba viejo.
  'f5p2.re.allAlreadyTarget': {
    en: 'Nothing moves: all {n} already belong to {name}. Pick another destination.',
    es: 'No se mueve nada: los {n} ya son de {name}. Elegí otro destino.',
  },
  'adminNav.bulkReassign.previewDone': { en: 'Preview ready', es: 'Vista previa lista' },
  'adminNav.bulkReassign.previewFailed': {
    en: 'Preview failed:',
    es: 'Falló la vista previa:',
  },
  'adminNav.bulkReassign.previewFailedGeneric': {
    en: 'Could not load preview. Check your connection and try again.',
    es: 'No se pudo cargar la vista previa. Revisá la conexión e intentá de nuevo.',
  },
  'adminNav.bulkReassign.applyDone': {
    en: 'Assigned {count} episode(s)',
    es: 'Asignados {count} episodio(s)',
  },
  'adminNav.bulkReassign.failed': { en: 'Bulk reassignment failed', es: 'Falló la reasignación en lote' },
  'adminNav.bulkReassign.confirm': {
    en: 'Assign {count} episode(s) to the selected navigator?',
    es: '¿Asignar {count} episodio(s) al navigator elegido?',
  },
  'adminNav.bulkReassign.skippedSessions': {
    en: '{n} skipped (open work session — not moved).',
    es: '{n} omitidos (sesión de trabajo abierta — no se mueven).',
  },
  'adminNav.bulkReassign.negativesInfo': {
    en: '{n} FindHelp-negative screening(s) have no open case yet — creating those episodes is a separate arco (navigator_scope / N-plan). This tool only moves existing cases.',
    es: '{n} screening(s) negativo(s) FindHelp aún sin caso abierto — crear esos episodios es otro arco (navigator_scope / plan N). Esta herramienta solo mueve casos existentes.',
  },
  'adminNav.bulkReassign.appliedLine': {
    en: 'Applied: {count} episode(s) reassigned.',
    es: 'Aplicado: {count} episodio(s) reasignados.',
  },

  'adminNav.showing': { en: 'Showing {n} of {total}', es: 'Mostrando {n} de {total}' },
  'adminNav.pager.prev': { en: '← Previous', es: '← Anterior' },
  'adminNav.pager.next': { en: 'Next →', es: 'Siguiente →' },
  'adminNav.pager.page': { en: 'Page {page} of {pages}', es: 'Página {page} de {pages}' },

  'adminNav.action.release': { en: 'Release claim', es: 'Soltar caso' },
  'adminNav.action.urgent': { en: 'Mark urgent', es: 'Marcar urgente' },
  'adminNav.overrideDone': { en: 'Case updated', es: 'Caso actualizado' },
  'adminNav.overrideFailed': { en: 'Could not update the case', es: 'No se pudo actualizar el caso' },

  'adminNav.kb.stub': {
    en: 'The knowledge base editor arrives with Wave 10.',
    es: 'El editor de la base de conocimiento llega con la Wave 10.',
  },
  'adminNav.kb.stubHint': {
    en: 'What gets loaded there will show up for navigators during a call.',
    es: 'Lo que se cargue ahí va a aparecerle al navigator durante la llamada.',
  },

  'adminNav.config.hint': {
    en: 'These are the navigation module\'s live parameters. They take effect immediately, without a deploy, and every change is audited.',
    es: 'Estos son los parámetros vivos del módulo de navegación. Aplican al instante, sin deploy, y cada cambio queda auditado.',
  },
  'adminNav.config.saved': { en: '{key} saved', es: '{key} guardado' },
  'adminNav.config.failed': { en: 'Could not save', es: 'No se pudo guardar' },
  'adminNav.config.badJson': { en: '{key}: the value is not valid JSON', es: '{key}: el valor no es JSON válido' },

  // ===== Cluster NAV Wave 6D — timer por segmentos =====
  'navigator.timer.activityLabel': { en: 'What this stretch was', es: 'Qué fue este tramo' },
  'navigator.timer.activityTitle': {
    en: 'Correct it if the guess is wrong. It is saved when you pause.',
    es: 'Corregilo si la sugerencia está mal. Se guarda al pausar.',
  },
  'navigator.timer.act.precall': { en: 'Pre-call', es: 'Pre-call' },
  'navigator.timer.act.ea': { en: 'Eligibility Assessment', es: 'Evaluación (EA)' },
  'navigator.timer.act.espejo': { en: 'Mirroring to findhelp', es: 'Espejo a findhelp' },
  'navigator.timer.act.documentacion': { en: 'Documentation', es: 'Documentación' },
  'navigator.timer.act.otro': { en: 'Other', es: 'Otro' },
  'navigator.timer.segments': { en: 'stretches: {n}', es: 'tramos: {n}' },
  'navigator.timer.total': { en: 'TOTAL', es: 'TOTAL' },
  // ===== Navigator — EA wizard, unit layout (W9.1) =====
  'navigator.ea.tipTitle': { en: 'What this question is for', es: 'Qué busca esta pregunta' },
  'navigator.ea.tipSource': { en: 'Source', es: 'Fuente' },
  'navigator.ea.tipNone': {
    en: '(no guidance in source — pending CEO). Nothing is written here that a document does not say.',
    es: '(sin guía en fuente — pendiente CEO). Acá no se escribe nada que no diga un documento.',
  },
  'navigator.ea.questionNote': { en: 'Note on this question', es: 'Nota de esta pregunta' },
  'navigator.ea.questionNotePlaceholder': {
    en: 'What the member said right here.',
    es: 'Lo que el miembro dijo justo acá.',
  },
  'navigator.ea.unitOf': { en: 'Part {n} of {total}', es: 'Parte {n} de {total}' },
  'navigator.ea.unitDone': { en: 'complete', es: 'completa' },
  // REORG-2 punto 1 — una unidad abierta a la vez.
  'navigator.ea.unitOpen': { en: 'open', es: 'abrir' },
  'navigator.ea.unitPending': { en: 'not answered yet', es: 'sin contestar' },
  'navigator.ea.unitNoFields': {
    en: 'Opening line — it fills no field.',
    es: 'Apertura — no llena ningún campo.',
  },
  'navigator.ea.sharedField': {
    en: 'Both sentences above fill the SAME field. Mark them on the one list.',
    es: 'Las dos preguntas de arriba llenan EL MISMO campo. Se marcan sobre la misma lista.',
  },
  'navigator.ea.otherFields': { en: 'Other fields in this section', es: 'Otros campos de esta sección' },
  'navigator.ea.otherFieldsHint': {
    en: 'The Guion does not script these one by one — there is no sentence to put on top of them.',
    es: 'El Guion no los guiona uno por uno — no hay una frase que ponerles encima.',
  },
  // ===== Navigator — briefing + knowledge base (Wave 10) =====
  'navigator.briefing.title': { en: 'Closing note', es: 'Nota de cierre' },
  'navigator.briefing.hint': {
    en: 'Assembled here from the assessment, the checklist and every note of the call. Copy it into findhelp.',
    es: 'Se arma acá con la evaluación, el checklist y todas las notas de la llamada. Se copia a findhelp.',
  },
  'navigator.briefing.generate': { en: 'Build the note', es: 'Generar la nota' },
  'navigator.briefing.generated': { en: 'Note built', es: 'Nota generada' },
  'navigator.briefing.regenerated': { en: 'Note rebuilt', es: 'Nota regenerada' },
  'navigator.briefing.failed': { en: 'The note could not be built', es: 'No se pudo generar la nota' },
  'navigator.briefing.copy': { en: 'Copy', es: 'Copiar' },
  'navigator.briefing.copied': { en: 'Copied', es: 'Copiada' },
  'navigator.briefing.thenMark': {
    en: 'Then tick it as mirrored in the notes panel.',
    es: 'Después márquela como espejada en el panel de notas.',
  },
  'navigator.kb.open': { en: 'Knowledge base', es: 'Base de conocimiento' },
  'navigator.kb.title': { en: 'Knowledge base', es: 'Base de conocimiento' },
  'navigator.kb.placeholder': { en: 'Search: scam, Medicaid, $2,000, utilities…', es: 'Buscar: estafa, Medicaid, $2,000, utilities…' },
  'navigator.kb.noResults': { en: 'Nothing matches that', es: 'No hay nada con eso' },
  'navigator.kb.source': { en: 'Source', es: 'Fuente' },
  'navigator.kb.catAll': { en: 'All', es: 'Todo' },
  'navigator.kb.catFaq': { en: 'What they ask', es: 'Lo que preguntan' },
  'navigator.kb.catMomentos': { en: 'Hard moments', es: 'Momentos difíciles' },
  'navigator.kb.catOperativo': { en: 'Rules', es: 'Reglas' },
  'navigator.kb.catReferencia': { en: 'Reference', es: 'Referencia' },
  'adminNav.kb.intro': {
    en: 'What you write here reaches the navigators on their next search — no deploy.',
    es: 'Lo que cargue acá les llega a los navigators en su próxima búsqueda — sin deploy.',
  },
  'adminNav.kb.new': { en: 'New article', es: 'Artículo nuevo' },
  'adminNav.kb.slug': { en: 'slug (lowercase-with-dashes)', es: 'slug (minúsculas-con-guiones)' },
  'adminNav.kb.titulo': { en: 'Title', es: 'Título' },
  'adminNav.kb.bodyEs': { en: 'Body in Spanish (required)', es: 'Cuerpo en español (obligatorio)' },
  'adminNav.kb.bodyEn': { en: 'Body in English (optional)', es: 'Cuerpo en inglés (opcional)' },
  'adminNav.kb.keywords': { en: 'Keywords for search', es: 'Palabras clave para la búsqueda' },
  'adminNav.kb.fuente': { en: 'Source (which document this comes from)', es: 'Fuente (de qué documento sale)' },
  'adminNav.kb.moneyNote': {
    en: 'Programme amounts belong here (the $2,000 threshold, coverage limits, the Member Cap). The fee schedule — what SOMOS pays Pathway — never does.',
    es: 'Los montos del programa van acá (el umbral de $2,000, los límites de cobertura, el Member Cap). El fee schedule — lo que SOMOS le paga a Pathway — nunca.',
  },
  'adminNav.kb.created': { en: 'Article created', es: 'Artículo creado' },
  'adminNav.kb.saved': { en: 'Article saved', es: 'Artículo guardado' },
  'adminNav.kb.failed': { en: 'Could not save', es: 'No se pudo guardar' },
  'adminNav.kb.inactive': { en: 'off', es: 'inactivo' },
  'adminNav.kb.deactivate': { en: 'Deactivate', es: 'Desactivar' },
  'adminNav.kb.reactivate': { en: 'Reactivate', es: 'Reactivar' },
  'common.edit': { en: 'Edit', es: 'Editar' },
  // ===== Navigator — EA wizard (Wave 9) =====
  'navigator.ea.start': { en: 'Start Eligibility Assessment (EA)', es: 'Iniciar Evaluación (EA)' },
  'navigator.ea.startHint': { en: 'Twelve guided blocks, 25-35 minutes. You can pause and resume.', es: 'Doce bloques guiados, 25-35 minutos. Puede pausar y retomar.' },
  'navigator.ea.resume': { en: 'Resume assessment', es: 'Retomar evaluación' },
  'navigator.ea.viewCompleted': { en: 'View completed assessment', es: 'Ver evaluación completada' },
  'navigator.ea.backToCase': { en: 'Back to case', es: 'Volver al caso' },
  'navigator.ea.progress': { en: 'Assessment progress', es: 'Avance de la evaluación' },
  'navigator.ea.prev': { en: 'Previous', es: 'Anterior' },
  'navigator.ea.next': { en: 'Next block', es: 'Siguiente bloque' },
  'navigator.ea.complete': { en: 'Finish assessment', es: 'Completar evaluación' },
  'navigator.ea.saving': { en: 'Saving…', es: 'Guardando…' },
  'navigator.ea.saved': { en: 'Saved', es: 'Guardado' },
  'navigator.ea.saveFailed': { en: 'Could not save', es: 'No se pudo guardar' },
  'navigator.ea.saveFailedShort': { en: 'Not saved', es: 'Sin guardar' },
  'navigator.ea.cannotOpen': { en: 'The assessment could not be opened', es: 'No se pudo abrir la evaluación' },
  'navigator.ea.cannotComplete': { en: 'The assessment cannot be closed yet', es: 'Todavía no se puede cerrar la evaluación' },
  'navigator.ea.copyField': { en: 'Copy this answer', es: 'Copiar esta respuesta' },
  'navigator.ea.copySection': { en: 'Copy section', es: 'Copiar sección' },
  'navigator.ea.copy': { en: 'Copy', es: 'Copiar' },
  'navigator.ea.rosterSays': { en: 'What findhelp / the state roster says', es: 'Lo que dice findhelp / el roster del Estado' },
  'navigator.ea.rosterHint': {
    en: 'Transcribe what you see on the other screen. "N" does not mean it does not apply — it means the state has no record, which is the provider-attestation route.',
    es: 'Transcriba lo que ve en la otra pantalla. "N" no significa que no aplique: significa que el Estado no tiene el dato, que es la vía de atestación del proveedor.',
  },
  'navigator.ea.minorRoute': {
    en: 'The member is under 18. The wizard is on the minor route: guardian consent, child questions, and scripts addressed to the parent or guardian.',
    es: 'El miembro es menor de 18. El wizard va por la ruta de menor: consentimiento del tutor, preguntas de niño, y los scripts dirigidos al padre, madre o tutor.',
  },
  'navigator.ea.ffsBanner': {
    en: 'Fee-for-service, from the pre-call. Block 4 is skipped and the result is Standard (L1) by state rule.',
    es: 'Fee-for-service, según el pre-call. El bloque 4 se salta y el resultado es Standard (L1) por regla estatal.',
  },
  'navigator.ea.ffsSkipTitle': { en: 'This block does not apply', es: 'Este bloque no aplica' },
  'navigator.ea.ffsSkipBody': {
    en: 'The form itself says it: "If member is Fee-for-service, they should only be determined as Standard Services." Asking these questions cannot change the outcome — it is half a call saved.',
    es: 'Lo dice el propio formulario: "If member is Fee-for-service, they should only be determined as Standard Services." Preguntar esto no puede cambiar el resultado — es media llamada ahorrada.',
  },
  'navigator.ea.lockedBanner': {
    en: 'This assessment is closed. Re-open it from the case if an answer has to change.',
    es: 'Esta evaluación está cerrada. Reábrala desde el caso si hay que cambiar una respuesta.',
  },
  'navigator.ea.reopen': { en: 'Re-open assessment', es: 'Reabrir evaluación' },
  'navigator.ea.reopened': { en: 'Assessment re-opened', es: 'Evaluación reabierta' },
  'navigator.ea.householdBanner': {
    en: 'Someone else in the household has Medicaid. When you finish THIS assessment, screen them — same call, same navigator. (The flow arrives in Wave 12; leave it in a note for now.)',
    es: 'Hay otra persona del hogar con Medicaid. Al terminar ESTA evaluación, screenéela — misma llamada, mismo navigator. (El flujo llega en la Wave 12; por ahora déjelo en una nota.)',
  },
  'navigator.ea.emptyInBlock': { en: 'Still empty in this block', es: 'Todavía vacíos en este bloque' },
  'navigator.ea.quickNote': { en: 'Quick note', es: 'Nota rápida' },
  'navigator.ea.quickNotePlaceholder': {
    en: 'What the member just said. It is tagged with this block automatically.',
    es: 'Lo que el miembro acaba de decir. Se etiqueta sola con este bloque.',
  },
  'navigator.ea.noteSaved': { en: 'Note saved', es: 'Nota guardada' },
  'navigator.ea.done': { en: 'Assessment completed', es: 'Evaluación completada' },
  'navigator.ea.doneL1': { en: 'Result: Standard (L1).', es: 'Resultado: Standard (L1).' },
  'navigator.ea.doneL2': { en: 'Result: Enhanced (L2).', es: 'Resultado: Enhanced (L2).' },
  'navigator.ea.doneNoConsent': { en: 'The member did not consent', es: 'El miembro no dio consentimiento' },
  'navigator.ea.doneNoConsentDesc': {
    en: 'The episode is closed and this time is not billable. One step left: mark it in FindHelp — the case shows the button.',
    es: 'El episodio quedó cerrado y este tiempo no se factura. Falta un paso: marcalo en FindHelp — el caso te muestra el botón.',
  },
  'navigator.ea.noConsentTitle': {
    en: 'The member does not consent',
    es: 'El miembro no consiente',
  },
  'navigator.ea.noConsentBody': {
    en: 'Close it here and now: the episode closes, the clock stops and this time is not billed. The script is unambiguous — do not insist, do not argue, do not call back about this case.',
    es: 'Se cierra acá y ahora: el episodio se cierra, el reloj para y este tiempo no se factura. El guion es inequívoco — no insista, no argumente, no vuelva a llamar por este caso.',
  },
  'navigator.ea.noConsentCta': {
    en: 'Close for no consent',
    es: 'Cerrar por no consentimiento',
  },
  'navigator.ea.noConsentIrrev': {
    en: 'This closes the episode. It is irreversible.',
    es: 'Esto cierra el episodio. Es irreversible.',
  },
  'navigator.ea.noConsentConfirm': {
    en: 'Confirm — close the episode',
    es: 'Confirmar — cerrar el episodio',
  },
  'navigator.ea.screeningPositive': {
    en: 'Positive in the screening',
    es: 'Positiva en el screening',
  },
  'navigator.ea.screenerNote': {
    en: "screener's note",
    es: 'nota del screener',
  },
  'navigator.workspace.form.no_consent_findhelp': {
    en: 'No-consent mark (FindHelp)',
    es: 'Marca de sin consentimiento (FindHelp)',
  },
  'navigator.noConsentMark.kicker': {
    en: 'Missing in FindHelp',
    es: 'Falta en FindHelp',
  },
  'navigator.noConsentMark.body': {
    en: 'Mark the screening in FindHelp as “no consent”. When you have done it there, record it here — that mark closes your work on this case.',
    es: 'Marcá el screening en FindHelp como «sin consentimiento». Cuando lo hayas hecho allá, registralo acá — esa marca termina tu trabajo con este caso.',
  },
  'navigator.noConsentMark.cta': {
    en: 'I marked it in FindHelp',
    es: 'Ya lo marqué en FindHelp',
  },
  'navigator.noConsentMark.saved': {
    en: 'Recorded. You are done with this case.',
    es: 'Registrado. Terminaste con este caso.',
  },
  'navigator.noConsentMark.doneTitle': {
    en: 'Marked in FindHelp as no consent',
    es: 'Marcado en FindHelp como sin consentimiento',
  },
  'navigator.noConsentMark.doneMeta': {
    en: '{who} · {when} (declared)',
    es: '{who} · {when} (declarado)',
  },
  // E-19 — la marca del NEGATIVO: en FindHelp se usa la vía de no-consent
  // (limitación de allá); acá el registro dice lo que de verdad pasó.
  'navigator.workspace.form.negative_closed_findhelp': {
    en: 'Negative disposed (FindHelp)',
    es: 'Negativo dispuesto (FindHelp)',
  },
  'navigator.negativeMark.kicker': {
    en: 'Missing in FindHelp',
    es: 'Falta en FindHelp',
  },
  'navigator.negativeMark.body': {
    en: 'In FindHelp, close this screening with the “no consent” mark — it is the only closing mechanic there. In Pathway this case is recorded as what it is: negative, no needs. When you have done it there, record it here.',
    es: 'En FindHelp, cerrá este screening con la marca de «sin consentimiento» — es la única mecánica de cierre allá. En Pathway este caso queda registrado como lo que es: negativo, sin necesidades. Cuando lo hayas hecho allá, registralo acá.',
  },
  'navigator.negativeMark.cta': {
    en: 'I marked it in FindHelp',
    es: 'Ya lo marqué en FindHelp',
  },
  'navigator.negativeMark.saved': {
    en: 'Recorded. You are done with this case.',
    es: 'Registrado. Terminaste con este caso.',
  },
  'navigator.negativeMark.doneTitle': {
    en: 'Disposed in FindHelp (negative — no needs)',
    es: 'Dispuesto en FindHelp (negativo — sin necesidades)',
  },
  'navigator.negativeMark.doneMeta': {
    en: '{who} · {when} (declared)',
    es: '{who} · {when} (declarado)',
  },
  'navigator.ea.panic': { en: 'Safety', es: 'Seguridad' },
  'navigator.ea.panicTitle': { en: 'Stop the script — safety first', es: 'Pare el guion — primero la seguridad' },
  'navigator.ea.panicMark': { en: 'Mark the case urgent and log it', es: 'Marcar el caso urgente y registrarlo' },
  'navigator.ea.panicLogged': { en: 'Logged on the case', es: 'Registrado en el caso' },
  'navigator.ea.crisis': { en: 'Crisis / suicide', es: 'Crisis / suicidio' },
  'navigator.ea.emergency': { en: 'Emergency', es: 'Emergencia' },
  'navigator.ea.dv': { en: 'Domestic violence NY 24/7', es: 'Violencia doméstica NY 24/7' },
  'navigator.ea.dvText': { en: 'Domestic violence — text', es: 'Violencia doméstica — texto' },
  'navigator.ea.fichaTitle': {
    en: 'This differs from what we have on file',
    es: 'Esto no coincide con lo que tenemos en la ficha',
  },
  'navigator.ea.fichaUpdate': { en: 'Update the client file', es: 'Actualizar la ficha del cliente' },
  'navigator.ea.fichaUpdated': { en: 'Client file updated', es: 'Ficha actualizada' },
  'navigator.ea.fichaFailed': { en: 'The file could not be updated', es: 'No se pudo actualizar la ficha' },
  'navigator.ea.fichaLangNote': {
    en: 'Preferred language differs too, but it is not editable from here yet — raise it with an admin.',
    es: 'El idioma preferido también difiere, pero todavía no se edita desde acá — coménteselo a un admin.',
  },
  'navigator.ea.outcomeTitle': { en: 'Where this lands', es: 'En qué queda' },
  'navigator.ea.outcomeHint': {
    en: 'Computed from the criteria as you answered them. One confirmed indicator is Enhanced (L2); zero is Standard (L1).',
    es: 'Calculado desde los criterios como los respondió. Un indicador confirmado es Enhanced (L2); cero es Standard (L1).',
  },
  'navigator.ea.criteriaConfirmed': { en: 'Confirmed criteria', es: 'Criterios confirmados' },
  'navigator.ea.attestationsPending': { en: 'Provider attestations needed', es: 'Atestaciones de proveedor necesarias' },
  'navigator.ea.attestationWarning': {
    en: 'The SOMOS form requires the attestation BEFORE referring to Enhanced services. The package is generated in Wave 11.',
    es: 'El formulario de SOMOS exige la atestación ANTES de referir a servicios Enhanced. El paquete se genera en la Wave 11.',
  },
  'navigator.ea.recapTitle': { en: 'Mirror into findhelp', es: 'Espejar a findhelp' },
  'navigator.ea.recapHint': {
    en: 'In findhelp\u2019s own section order. This is a closing checklist — most of the pasting probably happened during the call, with the copy buttons on each step.',
    es: 'En el orden de secciones del propio findhelp. Es un checklist de cierre — lo más probable es que ya haya pegado durante la llamada, con los botones de copiar de cada paso.',
  },
  'navigator.ea.fieldsFilled': { en: '{n} answered', es: '{n} respondidos' },
  'navigator.ea.sectionEmpty': {
    en: 'Nothing answered in this section.',
    es: 'Nada respondido en esta sección.',
  },
  'navigator.ea.mirrorFooter': {
    en: 'Dispatch is the main record. What is not mirrored into findhelp does not get billed.',
    es: 'Dispatch es el registro principal. Lo que no se espeja a findhelp no se factura.',
  },
  'navigator.timer.breakdownTitle': { en: 'Time on this case', es: 'Tiempo en este caso' },
  'navigator.timer.breakdownHint': {
    en: 'Measured, split by what you were doing. The total pre-fills the session form; you can still correct the minutes there.',
    es: 'Medido, separado por lo que estabas haciendo. El total pre-llena el formulario de sesión; ahí podés corregir los minutos igual.',
  },
  // REORG-2 punto 2 — la pausa por inactividad se explica, y retomar es un click.
  'navigator.timer.idlePaused': {
    en: 'Paused: no activity on the case', es: 'Pausado: sin actividad en el caso',
  },
  // E-30 (CEO, 2026-09-11): el texto mandaba a «apretá iniciar» y ese botón no
  // existía en ninguna pantalla desde que E-06 se llevó el control manual del
  // reloj — la pista describía una puerta cerrada.
  //
  // ENMIENDA (CEO, 2026-09-12): tampoco nombra «Retomar el reloj», porque ese
  // botón se sacó.
  //
  // SEGUNDA ENMIENDA (CEO, 2026-09-13): y dejó de prometer que abrir FindHelp
  // arranca un tramo. Desde que el click no declara nada —la declaración se mudó
  // al submit del EA— eso era falso, y una pista falsa es el mismo defecto que
  // E-30 vino a arreglar: la navegadora busca la puerta que la pista nombra, no
  // la encuentra, y concluye que el reloj se rompió. Ahora nombra las dos cosas
  // que SÍ existen: la política (cerrar la sesión antes de irse) y de qué vuelve
  // el reloj (trabajo declarado: completar el EA, o el próximo contacto).
  'navigator.timer.idlePausedHint': {
    en: 'The stretch closed at the last real event, not now. Save the session before stepping away; the clock comes back with declared work — completing the EA, or the next contact you record.',
    es: 'El tramo se cerró en el último evento real, no ahora. Guardá la sesión antes de irte; el reloj vuelve con trabajo declarado: completar el EA, o el próximo contacto que registres.',
  },

  // ===== REORG-5 — la pre-llamada completa y el cierre del camino feliz (2026-08-15) =====
  'navigator.case.fullName': { en: 'Name', es: 'Nombre' },
  'navigator.case.dob': { en: 'Date of birth', es: 'Fecha de nacimiento' },
  'navigator.case.language': { en: 'Language', es: 'Idioma' },
  'navigator.case.address': { en: 'Address', es: 'Dirección' },
  'navigator.case.noAddress': { en: 'No address on file', es: 'Sin dirección registrada' },
  'navigator.case.phoneCell': { en: 'mobile', es: 'celular' },
  'navigator.case.phoneHome': { en: 'home', es: 'casa' },
  'navigator.case.phoneOther': { en: 'other', es: 'otro' },
  // Mejora F — corregir los teléfonos del miembro desde la ficha del caso.
  // Las etiquetas de campo y el bloque del motivo se reusan de
  // `editClientInfo.*`, que ya existía para el modal del agente.
  'navigator.contact.edit': { en: 'Correct phones', es: 'Corregir teléfonos' },
  'navigator.contact.kicker': { en: 'Member file', es: 'Ficha del miembro' },
  'navigator.contact.title': { en: 'Correct the phone numbers', es: 'Corregir los teléfonos' },
  'navigator.contact.subtitle': {
    en: 'What the member just told you. The change is audit-logged with your reason.',
    es: 'Lo que el miembro te acaba de decir. El cambio queda en la auditoría con tu motivo.',
  },
  'navigator.contact.cta': { en: 'Save phones', es: 'Guardar teléfonos' },
  'navigator.contact.saved': { en: 'Phones updated', es: 'Teléfonos actualizados' },
  'navigator.contact.errorFormat': {
    en: 'Enter a 10-digit number.',
    es: 'Escribí un número de 10 dígitos.',
  },
  'navigator.contact.errorCannotClear': {
    en: 'A number cannot be erased from here — only corrected.',
    es: 'Desde acá no se borra un número, solo se corrige.',
  },
  'navigator.contact.notOwner': {
    en: 'You can only correct the file of a member whose open case you hold.',
    es: 'Solo podés corregir la ficha de un miembro cuyo caso abierto sostenés.',
  },
  'navigator.screeningNotes.positive': { en: 'Positive', es: 'Positivo' },
  'navigator.screeningNotes.noAnswer': {
    en: 'Answered, with no detail recorded.',
    es: 'Contestado, sin detalle registrado.',
  },
  'navigator.close.closeCase': { en: 'Close the case', es: 'Cerrar el caso' },
  'navigator.close.closeCaseGate': {
    en: 'Available once all steps are done: {done} of {total}.',
    es: 'Se habilita con todos los pasos hechos: {done} de {total}.',
  },
  'navigator.close.celebrateTitle': { en: 'Case closed — nice work', es: 'Caso cerrado — buen trabajo' },
  'navigator.close.celebrateHint': {
    en: 'Everything is documented. Nothing else is pending on this case.',
    es: 'Todo quedó documentado. Este caso no tiene nada más pendiente.',
  },
  'navigator.close.backToQueue': { en: 'Back to my queue', es: 'Volver a mi cola' },

  // ===== Ronda 2 — a quién se está llamando, visible en los 12 bloques (2026-08-16) =====
  'navigator.ea.who.age': { en: '{n} yrs', es: '{n} años' },
  'navigator.ea.who.cin': { en: 'CIN', es: 'CIN' },

  // ===== S1 Fase B — helpers de apertura/cierre, demografía silenciosa (2026-08-16) =====
  'navigator.ea.callOpeningTitle': {
    en: 'Before you dial — identity first',
    es: 'Antes de marcar — primero la identidad',
  },
  'navigator.ea.step1Moved': {
    en: 'Opening lives on stage 1 (pre-call), next to name and date of birth. This step has no form fields.',
    es: 'La apertura vive en la etapa 1 (pre-llamada), junto al nombre y la fecha de nacimiento. Este paso no tiene campos de formulario.',
  },
  'navigator.ea.silentDropdown': {
    en: 'Do not ask this out loud — findhelp’s options are not in the specimen. Transcribe what you see on the other screen, or leave it empty.',
    es: 'No lo preguntes en voz alta: las opciones de findhelp no están en el espécimen. Transcribí lo que ves en la otra pantalla, o dejalo vacío.',
  },
  'navigator.ea.coiDerived': {
    en: 'Dual-role is OFF. The conflict-of-interest answer is pre-filled as “No, Navigator does not work for an organization that is also an Enhanced HRSN Service Provider.” It stays visible (source: derived). If the flag is turned on, this becomes a question again.',
    es: 'Dual-role está APAGADO. La respuesta de conflicto de interés queda prellenada: “No, Navigator does not work for an organization that is also an Enhanced HRSN Service Provider.” Se ve (origen: derived). Si el flag se enciende, vuelve a ser pregunta.',
  },
  'navigator.ea.scriptLibertad': {
    en: 'Freedom of choice (only if Pathway also provides the service)',
    es: 'Libertad de elección (sólo si Pathway también provee el servicio)',
  },
  'navigator.ea.closeHelpersTitle': {
    en: 'Say before you hang up',
    es: 'Decir antes de colgar',
  },

  // ===== Navigator personal dashboard (assigned-only, no pool) =====
  'navigator.dash.nav': { en: 'My cases', es: 'Mis casos' },
  'navigator.dash.title': { en: 'My navigation work', es: 'Mi trabajo de navegación' },
  'navigator.dash.subtitle': {
    en: 'Only episodes assigned to you. Open a card to filter, then open a row to work the case.',
    es: 'Sólo episodios asignados a vos. Abrí una tarjeta para filtrar, y una fila para trabajar el caso.',
  },
  'navigator.dash.loading': { en: 'Loading your cases…', es: 'Cargando tus casos…' },
  'navigator.dash.loadFailed': {
    en: 'The dashboard could not be loaded.',
    es: 'No se pudo cargar el tablero.',
  },
  'navigator.dash.empty': {
    en: 'You have no open episodes.',
    es: 'No tenés episodios abiertos.',
  },
  'navigator.dash.emptyFilter': {
    en: 'Nothing in this list right now.',
    es: 'Nada en esta lista por ahora.',
  },
  'navigator.dash.filterAll': { en: 'Show all assigned', es: 'Ver todos los asignados' },
  'navigator.dash.open': { en: 'Open workspace', es: 'Abrir workspace' },
  'navigator.dash.nextAppt': { en: 'Next appointment', es: 'Próxima cita' },
  'navigator.dash.pendingEvidence': {
    en: '{n} pending evidence',
    es: '{n} evidencia(s) pendiente(s)',
  },
  'navigator.dash.card.cases': { en: 'My cases', es: 'Mis casos' },
  'navigator.dash.card.appointments_today': { en: "Today's appointments", es: 'Citas de hoy' },
  'navigator.dash.card.contacts_pending': { en: 'Pending contacts', es: 'Contactos pendientes' },
  'navigator.dash.card.followups_due': {
    en: 'Follow-ups today / overdue',
    es: 'Seguimientos hoy / vencidos',
  },
  'navigator.dash.card.no_shows': { en: 'No-shows', es: 'No-shows' },
  'navigator.dash.card.in_progress_today': { en: 'In progress', es: 'En curso' },
  'navigator.dash.card.ready_to_close': { en: 'Ready to close', es: 'Listos para cerrar' },
  'navigator.dash.card.attempts_exhausted': {
    en: 'Attempts exhausted',
    es: 'Intentos agotados',
  },

  // ===== Navigator workspace =====
  'navigator.workspace.back': { en: 'Back to my cases', es: 'Volver a mis casos' },
  'navigator.workspace.loading': { en: 'Loading workspace…', es: 'Cargando el workspace…' },
  'navigator.workspace.loadFailed': {
    en: 'This workspace could not be loaded.',
    es: 'No se pudo cargar este workspace.',
  },
  'navigator.workspace.notOwner': {
    en: 'This episode belongs to another navigator. Refresh if it was just reassigned.',
    es: 'Este episodio es de otro navigator. Recargá si recién lo reasignaron.',
  },
  'navigator.workspace.owner': { en: 'Owner', es: 'Responsable' },
  'navigator.workspace.status': { en: 'Status', es: 'Estado' },
  // Design spec §02/§03: el bloque se llama «Qué hacer ahora» — es la promesa
  // de la pantalla, no una etiqueta técnica.
  'navigator.workspace.nextAction': { en: 'What to do now', es: 'Qué hacer ahora' },
  'navigator.workspace.sla': { en: 'SLA', es: 'SLA' },
  'navigator.workspace.level': { en: 'Level', es: 'Nivel' },
  'navigator.workspace.units': { en: 'Remaining units', es: 'Unidades restantes' },
  'navigator.workspace.nextAppt': { en: 'Next appointment', es: 'Próxima cita' },
  'navigator.workspace.pendingEvidence': { en: 'Pending evidence', es: 'Evidencia pendiente' },
  'navigator.workspace.none': { en: 'None', es: 'Ninguna' },
  'navigator.workspace.noAppt': { en: 'None scheduled', es: 'Sin agendar' },
  'navigator.workspace.copy': { en: 'Copy', es: 'Copiar' },
  'navigator.workspace.copied': { en: 'Copied', es: 'Copiado' },
  'navigator.workspace.copyFailed': { en: 'Could not copy', es: 'No se pudo copiar' },
  'navigator.workspace.findhelp': { en: 'Open FindHelp', es: 'Abrir FindHelp' },
  'navigator.workspace.findhelpPlaceholder': {
    en: 'FindHelp URL is not configured. Open FindHelp in another tab; you do not paste IDs here.',
    es: 'La URL de FindHelp no está configurada. Abrí FindHelp en otra pestaña; no hay que pegar IDs acá.',
  },
  'navigator.workspace.callNow': { en: 'Call now', es: 'Llamar ahora' },
  'navigator.workspace.schedule': { en: 'Schedule', es: 'Agendar' },
  // D-15 (CEO, 2026-08-21) — el atajo del camino feliz. La ventana de
  // «Registrar intento» queda para los intentos que NO fueron por telefono y
  // para los fallidos.
  'navigator.workspace.answered': { en: 'Answered', es: 'Contestó' },
  // La nota automatica del intento la compone el SERVIDOR (ver logOutreach en
  // navigation.service.ts): el ordinal real y el idioma canonico no los sabe la
  // pantalla. Por eso no hay clave de i18n para ella.
  'navigator.workspace.logAttempt': { en: 'Log attempt', es: 'Registrar intento' },
  'navigator.workspace.logSession': { en: 'Log billable contact', es: 'Registrar contacto facturable' },
  'navigator.workspace.closeEpisode': { en: 'Close episode', es: 'Cerrar episodio' },
  'navigator.workspace.gateTitle': {
    en: 'Hard gate — cannot bill or close',
    es: 'Gate duro — no se puede cobrar ni cerrar',
  },
  'navigator.workspace.timerLocked': {
    en: 'Timer starts only after the member answers (reached). Log the attempt first.',
    es: 'El timer arranca sólo cuando el miembro contesta (reached). Registrá el intento primero.',
  },
  'navigator.workspace.needsTitle': { en: 'Needs and services', es: 'Necesidades y servicios' },
  'navigator.workspace.needsEmpty': {
    en: 'No service tracks yet. Review the screening needs and add only what is still missing.',
    es: 'Todavía no hay servicios. Revisá las necesidades del screening y agregá sólo lo que falta.',
  },
  'navigator.workspace.service': { en: 'Service', es: 'Servicio' },
  'navigator.workspace.goal': { en: 'Goal ID', es: 'ID de Goal' },
  'navigator.workspace.referral': { en: 'Referral ID', es: 'ID de Referral' },
  'navigator.workspace.consent': { en: 'Referral consent', es: 'Consentimiento de referido' },
  'navigator.workspace.attestation': { en: 'Attestation', es: 'Atestación' },
  'navigator.workspace.preauth': { en: 'Preauthorization', es: 'Preautorización' },
  'navigator.workspace.notReferred': { en: 'Not referred', es: 'No referido' },
  'navigator.workspace.terminal': { en: 'Terminal', es: 'Terminal' },
  'navigator.workspace.followupAt': { en: 'Next follow-up', es: 'Próximo seguimiento' },
  'navigator.workspace.formsTitle': { en: 'Forms ledger', es: 'Ledger de forms' },
  'navigator.workspace.formsHint': {
    en: 'FindHelp is where forms are filled. Record the submitted ID here. HRSN Screener is read-only from the source screening.',
    es: 'FindHelp es donde se llenan los forms. Acá se registra el ID enviado. El HRSN Screener es sólo lectura del screening fuente.',
  },
  'navigator.workspace.form.hrsn_screener': { en: 'HRSN Screener', es: 'HRSN Screener' },
  'navigator.workspace.form.eligibility_assessment': {
    en: 'Eligibility Assessment',
    es: 'Eligibility Assessment',
  },
  'navigator.workspace.form.outreach_tracker': { en: 'Outreach Tracker', es: 'Outreach Tracker' },
  'navigator.workspace.form.navigation_reimbursement': {
    en: 'Navigation Reimbursement Tracker',
    es: 'Navigation Reimbursement Tracker',
  },
  'navigator.workspace.formStatus.draft': { en: 'Draft', es: 'Borrador' },
  'navigator.workspace.formStatus.submitted': { en: 'Submitted', es: 'Enviado' },
  'navigator.workspace.formStatus.verified': { en: 'Verified', es: 'Verificado' },
  'navigator.workspace.formStatus.pending_evidence': {
    en: 'Pending evidence',
    es: 'Evidencia pendiente',
  },
  'navigator.workspace.formEmpty': { en: 'No entries yet.', es: 'Sin entradas todavía.' },
  'navigator.workspace.recordForm': { en: 'Record FindHelp evidence', es: 'Registrar evidencia de FindHelp' },
  'navigator.workspace.findhelpId': { en: 'FindHelp ID', es: 'ID de FindHelp' },
  'navigator.workspace.pasteEaId': {
    en: 'Paste the FindHelp Eligibility Assessment ID',
    es: 'Pegá el ID de FindHelp del Eligibility Assessment',
  },
  'navigator.workspace.confirmation': { en: 'Confirmation', es: 'Confirmación' },
  'navigator.workspace.exceptionReason': {
    en: 'Why evidence is pending',
    es: 'Por qué falta la evidencia',
  },
  'navigator.workspace.attempt': { en: 'Outreach attempt', es: 'Intento de outreach' },
  'navigator.workspace.session': { en: 'Work session', es: 'Sesión de trabajo' },
  'navigator.workspace.reportedMinutes': { en: 'Minutes reported on the Tracker', es: 'Minutos reportados en el Tracker' },
  'navigator.workspace.justification': { en: 'Justification', es: 'Justificación' },
  'navigator.workspace.justificationRequired': {
    en: 'Attestation is still pending. Write why this referral proceeds anyway — the API rejects it without that text.',
    es: 'La atestación sigue pendiente. Escribí por qué este referido sigue igual — la API lo rechaza sin ese texto.',
  },
  'navigator.workspace.saveReferral': { en: 'Save referral', es: 'Guardar referido' },
  'navigator.workspace.saveForm': { en: 'Save evidence', es: 'Guardar evidencia' },
  'navigator.workspace.formSaved': { en: 'Form evidence saved', es: 'Evidencia del form guardada' },
  'navigator.workspace.readOnlyScreener': {
    en: 'Read-only from the source screening. Do not recreate it.',
    es: 'Sólo lectura del screening fuente. No lo vuelvas a crear.',
  },
  'navigator.workspace.agendaTitle': { en: 'Agenda and follow-ups', es: 'Agenda y seguimientos' },
  'navigator.workspace.agendaEmpty': { en: 'No appointments on this episode.', es: 'Sin citas en este episodio.' },
  'navigator.workspace.followupsTitle': { en: 'Follow-ups', es: 'Seguimientos' },
  'navigator.workspace.followupsEmpty': { en: 'No follow-ups.', es: 'Sin seguimientos.' },
  'navigator.workspace.reschedule': { en: 'Reschedule', es: 'Reprogramar' },
  'navigator.workspace.cancelAppt': { en: 'Cancel', es: 'Cancelar' },
  'navigator.workspace.completeAppt': { en: 'Complete', es: 'Completar' },
  'navigator.workspace.noShow': { en: 'No-show', es: 'No-show' },
  'navigator.workspace.cancelReason': { en: 'Cancel reason', es: 'Motivo de cancelación' },
  'navigator.workspace.rescheduled': { en: 'Appointment rescheduled', es: 'Cita reprogramada' },
  'navigator.workspace.cancelled': { en: 'Appointment cancelled', es: 'Cita cancelada' },
  'navigator.workspace.completedAppt': { en: 'Appointment completed', es: 'Cita completada' },
  'navigator.workspace.markedNoShow': {
    en: 'Marked no-show — next action is to reschedule.',
    es: 'Marcado no-show — la próxima acción es reprogramar.',
  },
  'navigator.workspace.completeFollowup': { en: 'Complete follow-up', es: 'Completar seguimiento' },
  'navigator.workspace.todayContact': {
    en: 'Member answered — start today’s session',
    es: 'Contestó — registrar el contacto de hoy',
  },
  'navigator.workspace.followupDone': { en: 'Follow-up completed', es: 'Seguimiento completado' },
  'navigator.workspace.outreachForm': { en: 'Outreach evidence', es: 'Evidencia de Outreach' },
  'navigator.workspace.reimbForm': { en: 'Reimbursement evidence (if reached)', es: 'Evidencia de Reimbursement (si contestó)' },
  'navigator.workspace.terminalOutcome': { en: 'Terminal outcome (optional)', es: 'Resultado terminal (opcional)' },
  'navigator.workspace.timelineTitle': { en: 'Timeline', es: 'Línea de tiempo' },
  'navigator.workspace.timelineEmpty': { en: 'Nothing recorded yet.', es: 'Todavía no hay nada registrado.' },
  'navigator.workspace.tl.assignment': { en: 'Assignment', es: 'Asignación' },
  'navigator.workspace.tl.session': { en: 'Session', es: 'Sesión' },
  'navigator.workspace.tl.form': { en: 'Form', es: 'Form' },
  'navigator.workspace.tl.referral': { en: 'Referral', es: 'Referido' },
  'navigator.workspace.tl.billing': { en: 'Billing', es: 'Cobro' },
  'navigator.workspace.tl.closure': { en: 'Closure', es: 'Cierre' },
  'navigator.workspace.tl.attempt': { en: 'Contact attempt', es: 'Intento de contacto' },
  'navigator.workspace.tl.appointment': { en: 'Appointment', es: 'Cita' },
  'navigator.workspace.notes': { en: 'Notes', es: 'Notas' },
  'navigator.workspace.screening': { en: 'Source screening', es: 'Screening fuente' },
  'navigator.workspace.documents': { en: 'Attachments', es: 'Adjuntos' },
  'navigator.workspace.eaTitle': { en: 'Eligibility Assessment', es: 'Eligibility Assessment' },
  'navigator.workspace.eaReusable': {
    en: 'A valid EA is on file. Reuse it — do not start a new one unless something material changed.',
    es: 'Hay una EA válida. Reusala — no abras una nueva salvo que haya un cambio material.',
  },
  'navigator.workspace.eaRequired': {
    en: 'Open the EA wizard only because the current assessment is missing, expired, incomplete, or a material change was recorded.',
    es: 'Abrí el wizard del EA sólo porque la evaluación actual falta, está vencida, incompleta, o se registró un cambio material.',
  },
  'navigator.workspace.eaNeedsFindhelp': {
    en: 'The Pathway wizard is done. Paste the FindHelp Eligibility Assessment ID — do not reopen the interview.',
    es: 'El wizard de Pathway ya está hecho. Pegá el ID de FindHelp del Eligibility Assessment — no reabras la entrevista.',
  },
  'navigator.workspace.outreachStandingTitle': {
    en: 'Outreach Tracker',
    es: 'Outreach Tracker',
  },
  'navigator.workspace.outreachStandingHint': {
    en: 'Log every attempt throughout the episode. After five non-reach attempts the episode is parked until you unblock it.',
    es: 'Registrá cada intento durante el episodio. A los cinco intentos sin contacto el episodio se aparca hasta que lo desbloqueés.',
  },
  'navigator.workspace.outreachEmpty': {
    en: 'No attempts logged yet.',
    es: 'Todavía no hay intentos registrados.',
  },
  'navigator.workspace.eaLocked': {
    en: 'EA starts only after the member answers (Contacted).',
    es: 'El EA arranca sólo cuando el miembro contesta (Contactado).',
  },
  'navigator.workspace.eaLockedBody': {
    en: 'Log a reached outreach attempt first. Saving No answer or Voicemail records the attempt but does not unlock the interview.',
    es: 'Registrá un intento Contactado primero. Guardar No contestó o Buzón deja el registro, pero no desbloquea la entrevista.',
  },
  'navigator.workspace.openEa': { en: 'Open Eligibility Assessment', es: 'Abrir Eligibility Assessment' },
  'navigator.workspace.reopenEa': { en: 'Re-open Eligibility Assessment', es: 'Reabrir Eligibility Assessment' },
  'navigator.workspace.viewEa': { en: 'View completed EA', es: 'Ver EA completada' },
  'navigator.workspace.materialChange': {
    en: 'Material change since last EA',
    es: 'Cambio material desde la última EA',
  },
  'navigator.workspace.materialSaved': { en: 'Material-change flag updated', es: 'Cambio material actualizado' },
  'navigator.workspace.validUntil': { en: 'Valid until', es: 'Válida hasta' },
  'navigator.workspace.priorTitle': { en: 'Prior navigation', es: 'Navegación previa' },
  'navigator.workspace.priorHint': {
    en: 'If FindHelp already has complete navigation and the member confirms it, close without new billing.',
    es: 'Si FindHelp ya tiene la navegación completa y el miembro lo confirma, cerrá sin cobro nuevo.',
  },
  'navigator.workspace.memberConfirmed': {
    en: 'Member confirmed prior navigation is complete',
    es: 'El miembro confirmó que la navegación previa está completa',
  },
  'navigator.workspace.findhelpRef': { en: 'FindHelp reference', es: 'Referencia de FindHelp' },
  'navigator.workspace.priorNote': { en: 'Note', es: 'Nota' },
  'navigator.workspace.verifyPrior': { en: 'Verify and close', es: 'Verificar y cerrar' },
  'navigator.workspace.priorClosed': {
    en: 'Prior navigation verified. Episode closed without new billing.',
    es: 'Navegación previa verificada. Episodio cerrado sin cobro nuevo.',
  },
  'navigator.workspace.closedNotice': { en: 'This episode is closed.', es: 'Este episodio está cerrado.' },
  'navigator.workspace.action.call_or_schedule': {
    en: 'Call now or schedule',
    es: 'Llamar ahora o agendar',
  },
  'navigator.workspace.action.verify_prior_navigation': {
    en: 'Verify prior navigation',
    es: 'Verificar navegación previa',
  },
  'navigator.workspace.action.complete_ea': {
    en: 'Complete Eligibility Assessment',
    es: 'Completar Eligibility Assessment',
  },
  'navigator.workspace.action.record_ea_findhelp': {
    en: 'Record EA FindHelp ID',
    es: 'Registrar ID de FindHelp del EA',
  },
  'navigator.workspace.action.review_needs': { en: 'Review pending needs', es: 'Revisar necesidades pendientes' },
  'navigator.workspace.action.record_goal': {
    en: 'Create the FindHelp Goal',
    es: 'Crear el Goal en FindHelp',
  },
  'navigator.workspace.action.confirm_referral_consent': {
    en: 'Confirm referral consent',
    es: 'Confirmar consentimiento de referido',
  },
  'navigator.workspace.action.record_referral': {
    en: 'Create the FindHelp Referral',
    es: 'Crear el Referral en FindHelp',
  },
  'navigator.workspace.action.complete_followup': { en: 'Complete follow-up', es: 'Completar seguimiento' },
  'navigator.workspace.action.continue_service_followup': {
    en: 'Continue service follow-up',
    es: 'Seguir el seguimiento del servicio',
  },
  'navigator.workspace.action.close_episode': { en: 'Close the episode', es: 'Cerrar el episodio' },
  'navigator.workspace.action.attempts_exhausted': {
    en: 'Attempts exhausted',
    es: 'Intentos agotados',
  },
  'navigator.workspace.action.closed': { en: 'Episode closed', es: 'Episodio cerrado' },
  'navigator.workspace.exhaustedTitle': {
    en: 'Attempts exhausted',
    es: 'Intentos agotados',
  },
  'navigator.workspace.exhaustedBody': {
    en: 'Five outreach attempts did not reach the member. The episode stays assigned to you. Unblock if the client calls back — history is kept.',
    es: 'Cinco intentos de outreach no alcanzaron al miembro. El episodio sigue asignado a vos. Desbloqueá si el cliente llama de vuelta — el historial se conserva.',
  },
  'navigator.workspace.unblock': {
    en: 'Unblock — client called back',
    es: 'Desbloquear — el cliente llamó de vuelta',
  },
  'navigator.workspace.unblocked': {
    en: 'Episode unblocked. Continue outreach or service work.',
    es: 'Episodio desbloqueado. Seguí el outreach o el trabajo de servicios.',
  },
  'navigator.workspace.logAttemptBlocked': {
    en: 'Logging another attempt is blocked until you unblock this episode.',
    es: 'No se puede registrar otro intento hasta desbloquear este episodio.',
  },
  'navigator.workspace.addService': { en: 'Add a service', es: 'Agregar un servicio' },
  'navigator.workspace.chooseService': { en: 'Choose a service', es: 'Elegí un servicio' },
  'navigator.workspace.markGoal': { en: 'Mark Goal done', es: 'Marcar Goal hecho' },
  'navigator.workspace.markConsent': { en: 'Mark referral consent', es: 'Marcar consentimiento de referido' },
  'navigator.workspace.markReferral': { en: 'Mark referral done', es: 'Marcar referido hecho' },
  'navigator.workspace.goalSaved': { en: 'Goal marked. Continue in FindHelp if you have not created it yet.', es: 'Goal marcado. Seguí en FindHelp si todavía no lo creaste.' },
  'navigator.workspace.consentSaved': { en: 'Referral consent marked.', es: 'Consentimiento de referido marcado.' },
  'navigator.workspace.referralSaved': { en: 'Referral marked. Continue in FindHelp if you have not created it yet.', es: 'Referido marcado. Seguí en FindHelp si todavía no lo creaste.' },
  'navigator.workspace.when': { en: 'Date and time (NY)', es: 'Fecha y hora (NY)' },
  'navigator.workspace.scheduled': { en: 'Appointment scheduled', es: 'Cita agendada' },
  'navigator.workspace.noPhone': { en: 'No phone on file', es: 'Sin teléfono en ficha' },
  'navigator.workspace.unitsL2': { en: 'L2 {used}/{cap}', es: 'L2 {used}/{cap}' },
  'navigator.workspace.unitsL1': { en: 'L1 {used}/{cap}', es: 'L1 {used}/{cap}' },
  'navigator.workspace.unitsLeft': {
    en: 'L2 {l2} · L1 {l1}',
    es: 'L2 {l2} · L1 {l1}',
  },
  'navigator.workspace.showHistory': { en: 'Show closed / older', es: 'Mostrar cerrados / anteriores' },
  'navigator.workspace.hideHistory': { en: 'Hide history', es: 'Ocultar historial' },
  'navigator.workspace.sessionOpen': { en: 'In progress', es: 'En curso' },
  'navigator.workspace.sessionEnded': { en: 'Ended ({reason})', es: 'Cerrada ({reason})' },
  'navigator.workspace.preauthBlocks': {
    en: 'Preauthorization pending: you may refer, but you cannot mark the service started or received.',
    es: 'Preautorización pendiente: podés referir, pero no marcar el servicio como iniciado o recibido.',
  },
  'navigator.workspace.attestationWarn': {
    en: 'Attestation pending: you can still mark the referral. Write why if the provider form is not back yet.',
    es: 'Atestación pendiente: igual podés marcar el referido. Anotá por qué si el formulario del proveedor todavía no volvió.',
  },
  'navigator.followup.rule.no_show_reschedule': {
    en: 'Reschedule after a no-show',
    es: 'Reprogramar después de una inasistencia',
  },

  // ===== Navigation redesign — documents upload-only + supervisor exceptions =====
  'navigator.docs.uploadOnlyHint': {
    en: 'Pathway does not generate Provider Attestation or Preauthorization PDFs. Upload the completed external form as evidence.',
    es: 'Pathway no genera PDFs de atestación ni de preautorización. Subí el formulario externo completo como evidencia.',
  },
  'navigator.docs.attestationPendingAlert': {
    en: 'Attestation is still required for this service.',
    es: 'Este servicio todavía exige atestación.',
  },
  'navigator.docs.attestationPendingReferral': {
    en: 'You may still refer elsewhere, but the referral needs a written justification on the workspace. Missing attestation is a billing risk.',
    es: 'Podés referir igual, pero el referido pide justificación escrita en el workspace. Sin atestación hay riesgo de cobro.',
  },
  'navigator.docs.preauthBlocksDelivery': {
    en: 'Referral is allowed. Do not mark this service as initiated or received until a signed preauthorization is on file — without it the service is not paid.',
    es: 'El referido está permitido. No marques el servicio como iniciado o recibido hasta tener preautorización firmada — sin ella no se paga.',
  },
  'navigator.docs.sourceLabel': { en: 'Source', es: 'Fuente' },
  'navigator.docs.documentDate': { en: 'Document date', es: 'Fecha del documento' },
  'navigator.docs.externalRef': { en: 'External ID / confirmation', es: 'ID externo / confirmación' },
  'navigator.docs.expiresAt': { en: 'Expires', es: 'Vence' },
  'navigator.docs.supersede': { en: 'Replaces file', es: 'Reemplaza archivo' },
  'navigator.docs.formLedger': { en: 'Link to form ledger row', es: 'Vincular a fila del ledger' },
  'navigator.docs.memberConfirm': { en: 'I confirm this document belongs to this member', es: 'Confirmo que este documento es de este miembro' },
  'navigator.docs.memberConfirmRequired': {
    en: 'Required for screener, EA, Outreach, Reimbursement, attestation and preauthorization.',
    es: 'Obligatorio para screener, EA, Outreach, Reimbursement, atestación y preautorización.',
  },
  'navigator.docs.duplicateWarning': {
    en: 'This file is already on the case. Nothing new was stored.',
    es: 'Este archivo ya está en el caso. No se guardó nada nuevo.',
  },
  'navigator.docs.expiredWarning': {
    en: 'This document is already expired (New York date). Upload it only if you still need the historical copy.',
    es: 'Este documento ya está vencido (fecha de Nueva York). Subilo sólo si hace falta la copia histórica.',
  },
  'navigator.docs.expiredBadge': { en: 'expired', es: 'vencido' },
  'navigator.docs.uploadCta': { en: 'Attach file', es: 'Adjuntar archivo' },
  'navigator.docs.chooseFile': { en: 'Choose a file first', es: 'Elegí un archivo primero' },
  'navigator.docs.kind.hrsn_screener': { en: 'HRSN screener', es: 'Screener HRSN' },
  'navigator.docs.kind.eligibility_assessment': { en: 'Eligibility Assessment', es: 'Evaluación de elegibilidad' },
  'navigator.docs.kind.outreach_tracker': { en: 'Outreach Tracker', es: 'Outreach Tracker' },
  'navigator.docs.kind.navigation_reimbursement': { en: 'Navigation Reimbursement Tracker', es: 'Navigation Reimbursement Tracker' },
  'navigator.docs.kind.attestation': { en: 'Provider attestation', es: 'Atestación del proveedor' },
  'navigator.docs.kind.preauthorization': { en: 'Preauthorization', es: 'Preautorización' },
  'navigator.docs.source.findhelp': { en: 'FindHelp', es: 'FindHelp' },
  'navigator.docs.source.member': { en: 'Member', es: 'Miembro' },
  'navigator.docs.source.provider': { en: 'Provider', es: 'Proveedor' },
  'navigator.docs.source.insurer': { en: 'Insurer', es: 'Aseguradora' },
  'navigator.docs.source.pathway': { en: 'Pathway', es: 'Pathway' },
  'navigator.docs.source.other': { en: 'Other', es: 'Otra' },

  'admin.nav.navigation': { en: 'Navigation', es: 'Navegación' },
  'admin.nav.subtitle': {
    en: 'Exceptions and money at risk first. Assignment loads, reassignment by member, and operational settings.',
    es: 'Primero excepciones y dinero en riesgo. Cargas, reasignación por miembro y parámetros operativos.',
  },
  'admin.nav.tab.exceptions': { en: 'Exceptions', es: 'Excepciones' },
  'admin.nav.moneyAtRisk': {
    en: 'Money at risk: billable sessions without a submitted Reimbursement Tracker, missing forms, exhausted units, and timer gaps that still need a reason.',
    es: 'Dinero en riesgo: sesiones facturables sin Reimbursement Tracker enviado, forms faltantes, unidades agotadas y deltas de timer sin motivo.',
  },
  'admin.nav.exceptionsHint': {
    en: 'Open cases with missing evidence, unpaid sessions, overdue follow-ups, stale appointments, overrides, or exhausted units.',
    es: 'Casos abiertos con evidencia faltante, sesiones sin cobro, follow-ups vencidos, citas estancadas, overrides o unidades agotadas.',
  },
  'admin.nav.stat.pendingForms': { en: 'Missing forms', es: 'Forms faltantes' },
  'admin.nav.stat.unreimbursed': { en: 'Unreimbursed sessions', es: 'Sesiones sin reembolso' },
  'admin.nav.stat.overdueFollowups': { en: 'Overdue follow-ups', es: 'Follow-ups vencidos' },
  'admin.nav.stat.unitsExhausted': { en: 'Units exhausted', es: 'Unidades agotadas' },
  'admin.nav.stat.timerDiscrepancies': { en: 'Timer discrepancies', es: 'Discrepancias de timer' },
  'admin.nav.stat.overrides': { en: 'Overrides', es: 'Overrides' },
  'admin.nav.stat.staleAppointments': { en: 'Stale appointments', es: 'Citas estancadas' },
  'admin.nav.emptyExceptions': { en: 'No open exceptions right now.', es: 'No hay excepciones abiertas ahora.' },
  'admin.nav.unassigned': { en: 'Unassigned', es: 'Sin owner' },
  'admin.nav.openCase': { en: 'Open case', es: 'Abrir caso' },
  'admin.nav.showDetails': { en: 'Details / reassign', es: 'Detalle / reasignar' },
  'admin.nav.hideDetails': { en: 'Hide', es: 'Ocultar' },
  'admin.nav.flag.unreimbursed': { en: '{n} unreimbursed', es: '{n} sin reembolso' },
  'admin.nav.flag.pendingForms': { en: '{n} missing forms', es: '{n} forms faltantes' },
  'admin.nav.flag.overdue': { en: '{n} overdue follow-ups', es: '{n} follow-ups vencidos' },
  'admin.nav.flag.timer': { en: '{n} timer gaps', es: '{n} deltas de timer' },
  'admin.nav.flag.stale': { en: '{n} stale appointments', es: '{n} citas estancadas' },
  'admin.nav.flag.units': { en: 'units exhausted', es: 'unidades agotadas' },
  'admin.nav.flag.override': { en: 'overridden', es: 'override' },
  'admin.nav.reassign': { en: 'Reassign member', es: 'Reasignar miembro' },
  'admin.nav.reassignTo': { en: 'New navigator', es: 'Navigator nuevo' },
  'admin.nav.reassignReason': { en: 'Reason', es: 'Motivo' },
  'admin.nav.reassignHint': {
    en: 'Moves every open episode for this member, future appointments and tasks. Historical actors stay on the record.',
    es: 'Mueve todos los episodios abiertos de este miembro, citas futuras y tareas. Los actores históricos quedan en el expediente.',
  },
  'admin.nav.reassignDone': { en: 'Member reassigned', es: 'Miembro reasignado' },
  'admin.nav.reassignDoneDetail': {
    en: '{n} episode(s), {appts} future appointment(s) transferred.',
    es: '{n} episodio(s), {appts} cita(s) futura(s) transferidas.',
  },
  'admin.nav.reassignFailed': { en: 'Could not reassign', es: 'No se pudo reasignar' },
  'admin.nav.loads.title': { en: 'Assignment loads', es: 'Carga de asignación' },
  'admin.nav.loads.openEpisodes': { en: 'Open episodes', es: 'Episodios abiertos' },
  'admin.nav.loads.overdueFollowups': { en: 'Overdue follow-ups', es: 'Follow-ups vencidos' },
  'admin.nav.loads.upcoming': { en: 'Appointments (horizon)', es: 'Citas (horizonte)' },
  'admin.nav.loads.score': { en: 'Score', es: 'Puntaje' },
  'admin.nav.forms.title': { en: 'Form ledger', es: 'Ledger de forms' },
  'admin.nav.forms.empty': { en: 'No form rows on this episode.', es: 'Este episodio no tiene filas de form.' },
  'admin.nav.settings.hint': {
    en: 'These keys drive assignment weights, EA validity, timer idle/max, no-show grace, follow-up days and the FindHelp URL. The URL must be HTTPS. Changes apply immediately and are audited.',
    es: 'Estas claves mueven pesos de carga, vigencia de EA, idle/máximo del timer, gracia de no-show, días de follow-up y la URL de FindHelp. La URL tiene que ser HTTPS. Aplican al instante y quedan auditadas.',
  },
  'admin.nav.settings.httpsOnly': { en: 'HTTPS only', es: 'Sólo HTTPS' },
  'admin.nav.settings.badUrl': {
    en: 'FindHelp URL must be empty or start with https://',
    es: 'La URL de FindHelp tiene que estar vacía o empezar con https://',
  },
  'admin.nav.settings.badNumber': {
    en: 'Use a number between 0 and 10000.',
    es: 'Usá un número entre 0 y 10000.',
  },
  'admin.nav.settings.weightOpen': { en: 'Weight: each open episode', es: 'Peso: cada episodio abierto' },
  'admin.nav.settings.weightOverdue': { en: 'Weight: each overdue follow-up', es: 'Peso: cada follow-up vencido' },
  'admin.nav.settings.weightUpcoming': { en: 'Weight: each appointment in the horizon', es: 'Peso: cada cita en el horizonte' },
  'admin.nav.settings.horizon': { en: 'Appointment horizon (days)', es: 'Horizonte de citas (días)' },
  'admin.nav.settings.eaValidity': { en: 'EA validity (days)', es: 'Vigencia de EA (días)' },
  'admin.nav.settings.timerIdle': { en: 'Timer idle close (minutes)', es: 'Cierre por idle del timer (minutos)' },
  'admin.nav.settings.timerMax': { en: 'Timer max duration (minutes)', es: 'Duración máxima del timer (minutos)' },
  'admin.nav.settings.timerDelta': { en: 'Timer vs reported minutes threshold', es: 'Umbral de delta timer vs reportado' },
  'admin.nav.settings.noShowGrace': { en: 'No-show grace (minutes)', es: 'Gracia de no-show (minutos)' },
  'admin.nav.settings.followupDays': { en: 'General follow-up (days after billing)', es: 'Follow-up general (días post cobro)' },
  'admin.nav.settings.referralSilence': { en: 'Referral no-response (business days, provisional)', es: 'Silencio de referido (días hábiles, provisional)' },
  'admin.nav.settings.findhelpUrl': { en: 'FindHelp base URL (HTTPS)', es: 'URL base de FindHelp (HTTPS)' },

  // ===== Design spec 2026-08-19 — «Patient Navigation Pre-Call Screen» =====
  // ES primero (el navigator trabaja en español); EN completo igual — regla de
  // la casa: un idioma a la vez, nunca mezclado.

  // -- shell: las tres superficies
  'navigator.nav.today': { en: 'Today', es: 'Hoy' },
  'navigator.nav.agenda': { en: 'My agenda', es: 'Mi agenda' },
  'navigator.nav.numbers': { en: 'My numbers', es: 'Mis números' },

  // -- §01 Hoy
  // F5-T3 (mockup Navigator 01) — saludo con desglose, filas del dibujo,
  // «Contestó» en la fila con su razón impresa, búsqueda al pie.
  'navigator.today.hello': { en: 'Good day, {name}', es: 'Buen día, {name}' },
  'navigator.today.greetLineResume': { en: '{ec} in progress, {now} for right now and {ap} appointments. Start with what you already started.', es: '{ec} en curso, {now} para ahora mismo y {ap} citas. Empezá por lo que ya arrancaste.' },
  'navigator.today.greetLine': { en: '{now} for right now, {ap} appointments and {un} not yet contacted.', es: '{now} para ahora mismo, {ap} citas y {un} sin contactar.' },
  'navigator.today.minutes': { en: '{n} min', es: '{n} min' },
  'navigator.today.openAppt': { en: 'Open the appointment', es: 'Abrir la cita' },
  'navigator.today.answered': { en: 'Answered', es: 'Contestó' },
  'navigator.today.answeredTitle': { en: 'Seals first contact and starts billing', es: 'Sella el primer contacto y arranca la facturación' },
  'navigator.today.logAttempt': { en: 'Log attempt', es: 'Registrar intento' },
  'navigator.today.answerNote': { en: '"Answered" asks first: the accidental click has a way out. Confirmed, it seals first contact — irreversible — and starts the billable clock. To read the case before calling, open the profile.', es: '«Contestó» pregunta antes: el click accidental tiene salida. Confirmado, sella el primer contacto —irreversible— y arranca el reloj facturable. Para leer el caso antes de llamar, abrí el perfil.' },
  // E-31 (CEO 2026-09-12, opción C) — la confirmación de «Contestó». El sello
  // NO cambió: mismo endpoint, mismos parámetros, sólo que ahora detrás de una
  // pregunta. La salida del click accidental es «Ver perfil primero», que
  // navega al caso sin sellar nada.
  'navigator.today.answerConfirmKicker': { en: 'Seal first contact', es: 'Sellar el primer contacto' },
  'navigator.today.answerConfirmTitle': { en: 'Did the patient answer? This starts the case session', es: '¿El paciente contestó? Esto inicia la sesión del caso' },
  'navigator.today.answerConfirmBody': { en: 'Sealing first contact cannot be undone: it starts the billable clock and unlocks the Eligibility Assessment. If you only wanted to read the case, open the profile.', es: 'Sellar el primer contacto no se puede deshacer: arranca el reloj facturable y desbloquea el Eligibility Assessment. Si sólo querías leer el caso, abrí el perfil.' },
  'navigator.today.answerConfirmYes': { en: 'Yes, they answered', es: 'Sí, contestó' },
  'navigator.today.answerConfirmProfile': { en: 'See the profile first', es: 'Ver perfil primero' },
  'navigator.today.noPhone': { en: 'No phone on file', es: 'Sin teléfono en ficha' },
  'navigator.today.tagOverdue': { en: 'Overdue', es: 'Vencido' },
  'navigator.today.tagSoon': { en: 'In {m} min', es: 'En {m} min' },
  'navigator.today.tagSla': { en: 'SLA', es: 'SLA' },
  'navigator.dash.loadFailedBody': { en: 'Your cases are safe — nothing was lost. The list just could not refresh.', es: 'Tus casos están a salvo — no se perdió nada. Solo falló el refresco de la lista.' },
  'navigator.dash.retry': { en: 'Retry', es: 'Reintentar' },
  'navigator.search.clear': { en: 'Clear', es: 'Limpiar' },
  'navigator.search.hint': { en: 'For when someone calls back: name or full phone number, over your own caseload.', es: 'Para cuando alguien devuelve la llamada: nombre o teléfono completo, sobre tu propia cartera.' },
  'navigator.today.backToday': { en: '‹ Back to Today', es: '‹ Volver a Hoy' },
  // Paginación 25 en 25 (CEO 2026-08-26): 921 casos se pintaban en una sola
  // página. El kicker dice el total real; estos son solo los controles.
  'navigator.pager.prev': { en: '← Previous', es: '← Anterior' },
  'navigator.pager.next': { en: 'Next →', es: 'Siguiente →' },
  'navigator.pager.page': { en: 'Page {page} of {pages}', es: 'Página {page} de {pages}' },
  'navigator.today.open': { en: 'Open', es: 'Abrir' },
  'navigator.today.openCase': { en: 'Open the case', es: 'Abrir el caso' },
  'navigator.today.call': { en: 'Call', es: 'Llamar' },
  'navigator.today.see': { en: 'See', es: 'Ver' },
  // E-16 «EN CURSO» (A+C): el caso que se estaba trabajando no puede
  // desaparecer — sección primera en Hoy (sesión abierta o sesión de hoy sin
  // su tracker) + chip persistente en el header con sesión abierta.
  'navigator.today.encursoKicker': {
    en: 'IN PROGRESS — pick up where you left off',
    es: 'EN CURSO — retomá donde quedaste',
  },
  'navigator.today.resume': { en: 'Resume', es: 'Retomar' },
  'navigator.today.whyOpenSession': {
    en: 'Open session · {n} min worked today',
    es: 'Sesión abierta · {n} min trabajados hoy',
  },
  'navigator.today.whyRetryable': {
    en: "Today's session has no tracker yet · {n} min — resume and close it with its record",
    es: 'La sesión de hoy no tiene su tracker · {n} min — retomá y cerrala con su registro',
  },
  'navigator.shell.openSessionChip': {
    en: 'Open session with {name} — go back',
    es: 'Sesión abierta con {name} — volver',
  },
  'navigator.today.nowKicker': { en: 'Right now', es: 'Ahora mismo' },
  'navigator.today.apptsKicker': { en: "Today's appointments", es: 'Citas de hoy' },
  'navigator.today.uncontactedKicker': { en: 'Not yet contacted', es: 'Sin contactar' },
  'navigator.today.waitingKicker': { en: 'Waiting on the provider', es: 'Esperando al proveedor' },
  'navigator.agenda.waitingKicker': { en: 'Waiting on the provider', es: 'Esperando al proveedor' },
  'navigator.numbers.closedToday': { en: 'Closed today', es: 'Cerrados hoy' },
  'navigator.numbers.completedToday': { en: 'Completed today', es: 'Completados hoy' },
  'navigator.numbers.completedRecent': { en: 'Completed last 7 days', es: 'Completados (7 días)' },
  'navigator.numbers.openList': { en: 'See the list', es: 'Ver la lista' },
  'navigator.numbers.recentKicker': { en: 'Closed and completed', es: 'Cerrados y completados' },
  'navigator.numbers.listEmpty': { en: 'Nothing here yet.', es: 'Todavía no hay nada acá.' },
  'navigator.today.emptyTitle': { en: 'Nothing left for today.', es: 'No queda nada para hoy.' },
  'navigator.today.emptyBody': {
    en: 'You have {n} open cases; none is due today.',
    es: 'Tenés {n} casos abiertos, ninguno vence hoy.',
  },
  'navigator.today.closedThisMonth': { en: 'Closed this month', es: 'Cerrados este mes' },
  'navigator.today.allMyCases': { en: 'All my cases', es: 'Todos mis casos' },
  'navigator.today.noVoicemail': { en: 'No voicemail', es: 'No dejar mensaje' },
  'navigator.today.whyParked': {
    en: '{n} attempts without contact. The case is parked.',
    es: '{n} intentos sin contacto. El caso está aparcado.',
  },
  'navigator.today.whyFollowup': { en: 'A follow-up is due.', es: 'Un seguimiento está vencido o vence hoy.' },
  'navigator.today.whyAppt': { en: 'Appointment at {when}.', es: 'Cita a las {when}.' },
  'navigator.today.firstContactMeta': { en: 'First contact', es: 'Primer contacto' },
  'navigator.today.attemptsMeta': {
    en: '{n} of {max} attempts this cycle',
    es: '{n} de {max} intentos de este ciclo',
  },
  'navigator.today.whyWaiting': {
    en: 'Referral sent; the provider has not moved yet.',
    es: 'Referral enviado; el proveedor todavía no respondió.',
  },
  'navigator.today.unblocked': { en: 'Case unblocked — new cycle of attempts.', es: 'Caso desbloqueado — ciclo nuevo de intentos.' },
  'navigator.dash.card.waiting_provider': { en: 'Waiting on provider', es: 'Esperando proveedor' },

  // -- §08 búsqueda
  'navigator.search.kicker': { en: 'Search · someone called back', es: 'Buscar · alguien devolvió la llamada' },
  'navigator.search.title': { en: 'Who called?', es: '¿Quién llamó?' },
  'navigator.search.placeholder': { en: 'Name or full phone number', es: 'Nombre o teléfono completo' },
  'navigator.search.noResults': { en: 'No case of yours matches.', es: 'Ningún caso tuyo coincide.' },
  'navigator.search.parked': { en: 'Parked', es: 'Aparcado' },
  'navigator.search.parkedMeta': { en: '{n} attempts without contact · yours', es: '{n} intentos sin contacto · tuyo' },
  'navigator.search.activeMeta': { en: 'Active · yours', es: 'Activo · tuyo' },
  'navigator.search.unblockOpen': { en: 'Unblock and open', es: 'Desbloquear y abrir' },
  'navigator.search.unblockHint': {
    en: 'Unblocking returns the case to the active flow and starts a new cycle of attempts. It is recorded with your name.',
    es: 'Desbloquear vuelve el caso al flujo activo y empieza un ciclo nuevo de intentos. Queda registrado con tu nombre.',
  },

  // -- las tres acciones nuevas del motor (reasonKey del server: no renombrar)
  'navigator.workspace.action.register_ea': { en: 'Register the EA in FindHelp', es: 'Pasar la EA a FindHelp' },
  'navigator.workspace.action.notify_member': { en: 'Tell the member about the referral', es: 'Avisarle el referido' },
  'navigator.workspace.action.close_session': { en: 'Close the session', es: 'Cerrar la sesión' },
  'navigator.workspace.reason.register_ea': {
    en: 'Copy the EA summary section by section into FindHelp and mark it registered. The referral is offered only after this mark.',
    es: 'Pasá el resumen de la EA a FindHelp, sección por sección, y marcala registrada. Hasta esa marca no se ofrece el referral.',
  },
  'navigator.workspace.reason.notify_member': {
    en: 'The referral is out. The member needs to know where to go, with whom and what to bring.',
    es: 'El referral ya salió. El miembro tiene que saber a dónde ir, con quién y qué llevar.',
  },
  'navigator.workspace.reason.close_session': {
    en: 'You hung up with an open session: level, Reimbursement Tracker mark and note — or it does not save.',
    es: 'Colgaste con la sesión abierta: nivel, marca del Reimbursement Tracker y nota — o no se guarda.',
  },

  // -- Qué hacer ahora: CTAs
  'navigator.workspace.backToday': { en: '‹ Back to Today', es: '‹ Volver a Hoy' },
  'navigator.workspace.unblockCta': { en: 'Unblock — the member called back', es: 'Desbloquear — el cliente llamó de vuelta' },
  'navigator.workspace.startEa': { en: 'Start the EA', es: 'Empezar la EA' },
  'navigator.workspace.reuseEa': { en: 'Reuse it — nothing changed', es: 'Reutilizarla — nada cambió' },
  'navigator.workspace.registerEaCta': { en: 'Copy it to FindHelp', es: 'Pasarla a FindHelp' },
  'navigator.workspace.markGoalDone': { en: 'Mark the Goal done', es: 'Marcar Goal hecho' },
  'navigator.workspace.markConsentDone': { en: 'They consented — record it', es: 'Consintió — marcar' },
  'navigator.workspace.markReferralSent': { en: 'Mark referral sent', es: 'Marcar referral enviado' },
  'navigator.workspace.notifyCta': { en: 'Tell them about the referral', es: 'Avisarle el referido' },
  'navigator.workspace.closeSessionCta': { en: 'Close the session', es: 'Cerrar la sesión' },
  'navigator.workspace.seeNeeds': { en: 'See the needs', es: 'Ver las necesidades' },
  'navigator.workspace.escalateCta': { en: 'Escalate to supervision', es: 'Escalar a supervisión' },
  'navigator.workspace.eaNotRegistered': {
    en: 'Register the EA in FindHelp first',
    es: 'Primero pasá la EA a FindHelp',
  },
  'navigator.workspace.reassignedKicker': { en: 'Reassigned', es: 'Reasignado' },
  'navigator.workspace.reassignedBody': {
    en: 'You can read it complete. Future appointments moved with the new navigator; past ones stayed here.',
    es: 'Podés leerlo completo. Las citas futuras se fueron con quien lo tiene ahora; las pasadas quedaron acá.',
  },

  // -- §04 «¿alguien ya la ayudó?»
  'navigator.priorHelp.none': { en: 'Nobody has helped — start', es: 'Nadie la ha ayudado — empezar' },
  'navigator.priorHelp.partial': { en: 'They got part of it — we continue with the rest', es: 'Recibió una parte — seguimos con lo que falta' },
  'navigator.priorHelp.provider_working': { en: 'A provider is working on it — follow-up only', es: 'Hay un proveedor trabajando — sólo seguimiento' },
  'navigator.priorHelp.full': { en: 'They already got everything — document and close', es: 'Ya recibió todo — documentar y cerrar' },
  'navigator.priorHelp.fullHint': {
    en: 'Verify the completed work in FindHelp and confirm it with the member. Closing this way bills nothing.',
    es: 'Verificá el trabajo completo en FindHelp y confirmalo con el miembro. Cerrar así no factura nada.',
  },
  'navigator.priorHelp.closeCta': { en: 'Document and close', es: 'Documentar y cerrar' },
  'navigator.priorHelp.saved': { en: 'Answer recorded.', es: 'Respuesta registrada.' },

  // -- header del caso
  'navigator.case.ageYears': { en: '{n} years old', es: '{n} años' },
  'navigator.case.bornShort': { en: 'born', es: 'nacido' },
  'navigator.case.yoursSince': { en: 'Yours since {date}', es: 'Tuyo desde el {date}' },
  'navigator.case.whyHereKicker': { en: 'Why they are here', es: 'Por qué está acá' },
  'navigator.case.whyHere': {
    en: 'Screening on {date}, done by {agent} in {min} minutes. {n} needs came out.',
    es: 'Screening del {date}, hecho por {agent} en {min} minutos. Salieron {n} necesidades.',
  },
  'navigator.case.noteBy': { en: 'Note by {agent}', es: 'Nota de {agent}' },
  'navigator.case.seeAnswers': { en: 'See the {n} screening answers', es: 'Ver las {n} respuestas del screening' },
  'navigator.case.attemptsKicker': { en: 'Attempts', es: 'Intentos' },
  'navigator.noVm.bannerTitle': {
    en: 'Never leave this person a message',
    es: 'A esta persona no se le deja mensaje',
  },
  'navigator.noVm.bannerBody': {
    en: 'If they do not answer, hang up without recording anything. The reason lives in the case note.',
    es: 'Si no contesta, cortá sin grabar nada. El motivo está en la nota del caso.',
  },

  // -- la ruta de la necesidad activa
  'navigator.route.of': { en: 'Route of «{service}»', es: 'Ruta de «{service}»' },
  'navigator.route.outreach': { en: 'Outreach', es: 'Outreach' },
  'navigator.route.goal': { en: 'Goal', es: 'Goal' },
  'navigator.route.referral': { en: 'Referral', es: 'Referral' },
  'navigator.route.notify': { en: 'Tell them', es: 'Avisarle' },
  'navigator.route.tracker': { en: 'Tracker', es: 'Tracker' },
  'navigator.route.followup': { en: 'Follow-up', es: 'Seguimiento' },
  'navigator.route.provider': { en: 'Provider', es: 'Proveedor' },
  'navigator.route.received': { en: 'Received', es: 'Recibido' },

  // -- D-17: las formas HECHO y EN CURSO de cada paso de la necesidad. Las
  //    cinco claves de arriba quedan como la forma PENDIENTE. Verbatim del
  //    spec: «Goal hecho» (Navigation.dc.html:323), «Referral enviado» (:347),
  //    «Referral pendiente» (:325), «Proveedor trabajando» (:349) y «Confirmar
  //    que llegó» (:351). Las otras cinco se completaron por simetría.
  //    «Avisarle» va neutro y no «Avisarle a ella» como la maqueta, que tiene
  //    género porque su miembro de ejemplo es mujer.
  'navigator.route.goalDone': { en: 'Goal done', es: 'Goal hecho' },
  'navigator.route.goalNow': { en: 'Create the Goal', es: 'Crear el Goal' },
  'navigator.route.referralDone': { en: 'Referral sent', es: 'Referral enviado' },
  'navigator.route.referralNow': { en: 'Referral pending', es: 'Referral pendiente' },
  'navigator.route.notifyDone': { en: 'Told them', es: 'Avisado' },
  'navigator.route.notifyNow': { en: 'Tell them', es: 'Avisarle' },
  'navigator.route.providerDone': { en: 'Provider working', es: 'Proveedor trabajando' },
  'navigator.route.providerNow': { en: 'Waiting on provider', es: 'Esperando al proveedor' },
  'navigator.route.receivedDone': { en: 'Received', es: 'Recibido' },
  'navigator.route.receivedNow': { en: 'Confirm it arrived', es: 'Confirmar que llegó' },

  // -- timer del caso
  // V1.2 — el acumulado del caso, dicho aparte para no confundirlo con lo que se
  // va a facturar hoy. El numero grande es la SESION.
  'navigator.timer.caseTotal': { en: 'Earlier on this case: {total}', es: 'Antes en este caso: {total}' },
  'navigator.timer.billableSession': { en: 'Billable session', es: 'Sesión facturable' },
  'navigator.timer.notRunning': { en: 'The clock is not running', es: 'El reloj no corre' },
  'navigator.timer.startsOnAnswer': { en: 'It starts when they answer', es: 'Arranca cuando conteste' },
  'navigator.timer.unitsLine': { en: '{level} · {used} of {cap} units', es: '{level} · {used} de {cap} unidades' },

  // -- §03 lo formal en FindHelp
  'navigator.formal.kicker': { en: 'The formal side in FindHelp', es: 'Lo formal en FindHelp' },
  'navigator.formal.ea': {
    en: 'EA — done here on {date} and registered in FindHelp. Valid until {until}.',
    es: 'EA — hecha acá el {date} y registrada en FindHelp. Vale hasta el {until}.',
  },
  'navigator.formal.eaPending': { en: 'EA — still pending.', es: 'EA — todavía pendiente.' },
  'navigator.timer.closedChip': { en: 'Case closed · no clock', es: 'Caso cerrado · sin reloj' },
  // F-9: la CUENTA (intentos acá) y la DECLARACIÓN (tracker al día allá)
  // son dos cosas — la fila ya no se marca sola.
  'navigator.formal.outreachMissing': {
    en: 'Outreach Tracker — needs confirming · {n} attempts made here.',
    es: 'Outreach Tracker — falta confirmarlo · {n} intentos hechos acá.',
  },
  'navigator.formal.outreachConfirmCta': { en: 'I filled it in FindHelp', es: 'Ya lo llené en FindHelp' },
  'navigator.formal.outreachConfirmed': { en: '{n} attempts declared in the tracker', es: '{n} intentos declarados en el tracker' },
  'navigator.formal.outreachDeclared': {
    en: 'Outreach Tracker — up to date · {who}, {when}.',
    es: 'Outreach Tracker — al día · {who}, {when}.',
  },
  'navigator.formal.outreachPending': {
    en: 'Outreach Tracker — no attempts yet.',
    es: 'Outreach Tracker — todavía sin intentos.',
  },
  'navigator.formal.referralDone': { en: 'Referral of {service} — out.', es: 'Referral de {service} — enviado.' },
  'navigator.formal.referralNext': { en: 'Referral — it is the next action, above.', es: 'Referral — es la próxima acción, arriba.' },
  'navigator.formal.trackerDone': { en: 'Reimbursement Tracker — marked for this work.', es: 'Reimbursement Tracker — marcado para este trabajo.' },
  'navigator.formal.trackerDue': {
    en: 'Reimbursement Tracker — filled in FindHelp when you close the session. Without the mark, the session does not save.',
    es: 'Reimbursement Tracker — se llena en FindHelp al cerrar la sesión. Sin marcarlo, la sesión no se guarda.',
  },

  // -- §07 registrar la EA
  'navigator.registerEa.kicker': { en: 'Now copy it to FindHelp, section by section', es: 'Ahora pasala a FindHelp, sección por sección' },
  'navigator.registerEa.metaBlocks': { en: '{n} blocks', es: '{n} bloques' },
  'navigator.registerEa.metaL2': {
    en: 'Level 2, decided by the confirmed health criterion',
    es: 'Nivel 2, decidido por el criterio de salud confirmado',
  },
  'navigator.registerEa.metaL1': { en: 'Level 1 — Standard', es: 'Nivel 1 — Standard' },
  'navigator.registerEa.hint': {
    en: 'Made for a split screen: read on this side, type on the other. Each section opens only when you need it.',
    es: 'Pensado para pantalla dividida: de este lado leés, del otro escribís. Cada sección se abre sólo cuando la necesitás.',
  },
  'navigator.registerEa.cta': { en: 'Mark the EA registered in FindHelp', es: 'Marcar EA registrada en FindHelp' },
  'navigator.registerEa.note': {
    en: 'Without this mark the case does not move to the referral — so the transcription is never forgotten. No ID is pasted.',
    es: 'Sin esta marca el caso no avanza al referral — así la transcripción nunca se olvida. No se pega ningún ID.',
  },
  'navigator.registerEa.saved': { en: 'EA registered — the referral is now offered.', es: 'EA registrada — ahora sigue el referral.' },
  'navigator.registerEa.done': { en: 'EA registered — {at}, {who}.', es: 'EA registrada — {at}, {who}.' },

  // -- necesidades
  'navigator.needs.fromScreening': { en: 'Came out in the screening. Not started yet.', es: 'Salió en el screening. Todavía sin trabajar.' },
  'navigator.needs.startWork': { en: 'Start working on it', es: 'Empezar a trabajarla' },
  'navigator.needs.gateHint': {
    en: 'Unlocks after the EA is completed and registered in FindHelp.',
    es: 'Se habilita después de completar la EA y registrarla en FindHelp.',
  },
  'navigator.needs.pillClosed': { en: 'Closed', es: 'Cerrada' },
  'navigator.needs.pillReceived': { en: 'Received', es: 'Recibido' },
  'navigator.needs.pillReceiving': { en: 'Receiving', es: 'Recibiendo' },
  'navigator.needs.pillConsented': { en: 'Consented', es: 'Consintió' },
  'navigator.needs.yesReceiving': { en: 'Yes, they are receiving it', es: 'Sí, lo está recibiendo' },
  // D-17: 'navigator.needs.notYet' se elimina con su botón — era su único
  // consumidor (grep sobre web/src y smokes/ = 0 usos restantes).

  // -- §03 cuando cuelgues / timeline / referencia
  // D-04: 'navigator.hangup.kicker' se elimina con el bloque «Cuando cuelgues».
  'navigator.timeline.kicker': { en: 'Everything that happened', es: 'Todo lo que pasó' },
  'navigator.ref.notes': { en: 'Case notes', es: 'Notas del caso' },
  'navigator.ref.history': { en: 'FindHelp history', es: 'Historia en FindHelp' },
  'navigator.ref.briefing': { en: 'Closing note', es: 'Nota de cierre' },
  'navigator.caseAgenda.title': { en: 'Appointments and follow-ups of this case', es: 'Citas y seguimientos de este caso' },

  // -- §08 aviso del referido
  'navigator.notify.kicker': { en: 'Tell them about the referral', es: 'Avisarle el referido' },
  'navigator.notify.title': { en: 'Send them the details for {service}.', es: 'Mandale los datos de {service}.' },
  'navigator.notify.subtitle': {
    en: 'The referral is out. They need to know where to go, with whom and what to bring.',
    es: 'El referral ya salió. Tiene que saber a dónde ir, con quién y qué llevar.',
  },
  'navigator.notify.template': {
    en: 'Hello {name}, this is Pathway. About your help with {service}: the provider details are in the referral — bring your ID and any letters you have.',
    es: 'Hola {name}, le habla Pathway. Sobre su ayuda con {service}: los datos del proveedor están en el referido — lleve su ID y las cartas que tenga.',
  },
  'navigator.notify.ctaSms': { en: 'I sent the SMS — document it', es: 'Envié el SMS — documentar' },
  'navigator.notify.ctaEmail': { en: 'I sent the email — document it', es: 'Envié el email — documentar' },
  'navigator.notify.ctaTold': { en: 'I told them on the call — document it', es: 'Se lo dije en la llamada — documentar' },
  'navigator.notify.noteLabel': { en: 'What you told them', es: 'Qué se le dijo' },
  'navigator.notify.notePlaceholder': { en: 'e.g. address, who to ask for, what to bring', es: 'p. ej. dirección, con quién preguntar, qué llevar' },
  'navigator.notify.noteHint': { en: 'Goes to the case notes and to FindHelp.', es: 'Va a las notas del caso y a FindHelp.' },
  'navigator.notify.footer': {
    en: 'It stays in the timeline and in the FindHelp notes, with channel and time.',
    es: 'Queda en la línea de tiempo y en las notas de FindHelp, con canal y hora.',
  },
  'navigator.notify.saved': { en: 'Notice recorded, with channel and time.', es: 'Aviso registrado, con canal y hora.' },
  'navigator.notify.channel.sms': { en: 'By SMS', es: 'Por SMS' },
  'navigator.notify.channel.email': { en: 'By email', es: 'Por email' },
  'navigator.notify.channel.told_in_call': { en: 'Told them on the call', es: 'Se lo dije en la llamada' },

  // -- §08 escalar
  'navigator.escalate.kicker': { en: 'Escalate to supervision', es: 'Escalar a supervisión' },
  'navigator.escalate.title': { en: 'Supervision sees it on their board.', es: 'Supervisión lo ve en su tablero.' },
  'navigator.escalate.subtitle': {
    en: 'The case is still yours. Units are a billing limit, not a reason to drop the case.',
    es: 'El caso sigue siendo tuyo. Las unidades son un límite de cobro, no una razón para soltar el caso.',
  },
  'navigator.escalate.reasonLabel': { en: 'Reason', es: 'Motivo' },
  'navigator.escalate.reason.units_exhausted': { en: 'Units exhausted', es: 'Unidades agotadas' },
  'navigator.escalate.reason.provider_no_response': { en: 'Provider not responding', es: 'Proveedor sin respuesta' },
  'navigator.escalate.reason.other': { en: 'Other', es: 'Otro' },
  'navigator.escalate.noteLabel': { en: 'What supervision needs to know — required', es: 'Qué necesita supervisión saber — obligatorio' },
  'navigator.escalate.notePlaceholder': {
    en: 'e.g. 24 of 24 units used, the arrears are $2,140 and the cap is $2,000',
    es: 'p. ej. 24 de 24 unidades usadas, el atraso es de $2,140 y el tope $2,000',
  },
  'navigator.escalate.noteHint': { en: 'It stays in the timeline, with time and your name.', es: 'Queda en la línea de tiempo, con hora y tu nombre.' },
  'navigator.escalate.cta': { en: 'Escalate', es: 'Escalar' },
  'navigator.escalate.saved': { en: 'Escalated. Supervision will answer here.', es: 'Escalado. Supervisión te responde por acá.' },
  'navigator.escalate.pending': { en: 'Escalated to supervision on {date}.', es: 'Escalado a supervisión el {date}.' },

  // -- §08 agregar necesidad
  'navigator.addNeed.kicker': { en: 'Add a need', es: 'Agregar necesidad' },
  'navigator.addNeed.title': { en: 'What came up in the call?', es: '¿Qué salió en la llamada?' },
  'navigator.addNeed.subtitle': {
    en: 'It is born «pending» and follows the same route: Goal → Referral → Tell them.',
    es: 'Nace como «pendiente» y sigue la misma ruta: Goal → Referral → Avisarle.',
  },
  'navigator.addNeed.domainLabel': { en: 'Domain', es: 'Dominio' },
  'navigator.addNeed.noteLabel': { en: 'What they told you — required', es: 'Qué contó — obligatorio' },
  'navigator.addNeed.notePlaceholder': {
    en: 'e.g. the electricity bill came doubled, they fear a cutoff',
    es: 'p. ej. la carta de la luz vino doble, teme el corte',
  },
  'navigator.addNeed.cta': { en: 'Add the need', es: 'Agregar la necesidad' },
  'navigator.addNeed.saved': { en: 'Need added.', es: 'Necesidad agregada.' },
  'navigator.addNeed.trigger': {
    en: 'Add a need that came up in the call',
    es: 'Agregar una necesidad que salió en la llamada',
  },

  // -- §08 referir a otro
  // V1.2 · el día del seguimiento — a QUIÉN se refirió, por nombre. El nombre
  // lo escribe la navigator porque Pathway no tiene catálogo de proveedores: el
  // proveedor se elige en findhelp, que ella tiene abierto al lado.
  'navigator.needs.providerLabel': { en: 'Which organization?', es: '¿A qué organización?' },
  'navigator.needs.providerHint': { en: 'The name as it appears in findhelp', es: 'El nombre como aparece en findhelp' },
  'navigator.needs.providerUnnamed': { en: 'Provider without a name on file', es: 'Proveedor sin nombre registrado' },
  'navigator.needs.referredTo': { en: 'Referred to {provider} · {date}', es: 'Referida a {provider} · {date}' },
  'navigator.needs.previouslyTried': { en: 'Previously tried', es: 'Antes se intentó con' },
  'navigator.referOther.current': { en: 'Currently with {provider}', es: 'Ahora con {provider}' },
  'navigator.referOther.kicker': { en: 'Refer to another provider', es: 'Referir a otro proveedor' },
  'navigator.referOther.title': { en: 'Change the provider for {service}.', es: 'Cambiar el proveedor de {service}.' },
  'navigator.referOther.subtitle': {
    en: 'Pick the new provider IN FindHelp and create the referral there; here the change is documented and audited.',
    es: 'El proveedor nuevo se elige EN findhelp y el referral se crea allá; acá queda documentado y auditado el cambio.',
  },
  'navigator.referOther.noteLabel': { en: 'Why it changes — required', es: 'Por qué se cambia — obligatorio' },
  'navigator.referOther.notePlaceholder': {
    en: 'e.g. POTS silent after two calls; BronxWorks has room this week',
    es: 'p. ej. POTS sin respuesta tras dos llamadas; BronxWorks tiene cupo esta semana',
  },
  'navigator.referOther.cta': { en: 'Record the new referral', es: 'Registrar el nuevo referral' },
  'navigator.referOther.saved': { en: 'Referral re-recorded; now tell the member.', es: 'Referral re-registrado; ahora avisale al miembro.' },
  'navigator.referOther.trigger': { en: 'Refer to another provider', es: 'Referir a otro' },

  // -- §08 drawer del screening
  'navigator.screeningDrawer.kicker': { en: '{name}’s screening', es: 'El screening de {name}' },
  'navigator.screeningDrawer.subtitle': {
    en: 'By {agent} · {min} minutes. Read-only — it slides over the case; close it and you continue where you were.',
    es: '{agent} · {min} minutos. Sólo lectura — se desliza sobre el caso y al cerrarlo seguís donde estabas.',
  },
  'navigator.screeningDrawer.noScreening': { en: 'No linked screening.', es: 'Sin screening vinculado.' },
  'navigator.screeningDrawer.need': { en: 'Need', es: 'Necesidad' },
  'navigator.screeningDrawer.ok': { en: 'Fine', es: 'Bien' },

  // -- §08 registrar intento (capa)
  'navigator.outreach.kicker': { en: 'Record attempt', es: 'Registrar intento' },
  'navigator.outreach.whatHappened': { en: 'What happened with the call?', es: '¿Qué pasó con la llamada?' },
  // F5-T4/N2-b (acta E-25) — el modal del intento al dibujo: sin «contestó»
  // (el sello tiene UNA entrada), sin nota (la compone el server), dos
  // números separados, inubicable con checklist del server.
  'navigator.outreach.notReachedSub': {
    en: 'An attempt that did not end in a conversation. If you spoke with them, that is «Answered» — it lives in one place.',
    es: 'Un intento que no terminó en conversación. Si hablaste, eso es «Contestó» — y vive en un solo lugar.',
  },
  'navigator.outreach.dutyKicker': { en: 'Contractual duty', es: 'Obligación contractual' },
  'navigator.outreach.dutyLine': { en: '{required} attempts fulfill it. This is your {n}.', es: '{required} intentos la cumplen. Vas por el {n}.º.' },
  'navigator.outreach.parkKicker': { en: 'Automatic parking', es: 'Parqueo automático' },
  'navigator.outreach.parkLine': { en: 'At the {max}th the case parks itself. Reversible, and still yours.', es: 'Al {max}.º el caso se parquea solo. Es reversible y sigue siendo tuyo.' },
  'navigator.outreach.noNoteLock': {
    en: 'No note field. The server composes the attempt note with the channel, the real time and the result: saving personal information here is forbidden — every attempt is declared in the Outreach Tracker.',
    es: 'No hay campo de nota. La nota del intento la compone el servidor con el canal, la hora real y el resultado: está prohibido guardar información de la persona acá — todo intento se declara en el Outreach Tracker.',
  },
  'navigator.outreach.unreachTitle': { en: 'Close as unreachable · another action, irreversible', es: 'Cerrar por inubicable · otra acción, irreversible' },
  'navigator.outreach.unreachAttempts': { en: 'At least {required} attempts', es: 'Al menos {required} intentos' },
  'navigator.outreach.unreachTypes': { en: 'At least 2 different channels', es: 'Al menos 2 vías distintas' },
  'navigator.outreach.unreachBands': { en: 'At least 2 different time bands', es: 'Al menos 2 franjas distintas' },
  'navigator.outreach.unreachCta': { en: 'Close as unreachable', es: 'Cerrar por inubicable' },
  'navigator.outreach.unreachReadyNote': { en: 'The coverage is met — the server verified it. Closing opens the episode close with this cause.', es: 'La cobertura está cumplida — la verificó el servidor. Cerrar abre el cierre del episodio con esta causa.' },
  'navigator.outreach.unreachBlockedNote': { en: 'The button stays inert until the coverage is real: the checklist is measured by the server, not this screen.', es: 'El botón queda inerte hasta que la cobertura sea real: el checklist lo mide el servidor, no esta pantalla.' },
  'navigator.outreach.unreachOutOfHours': { en: '{n} attempts fell outside every band — recorded, they do not prove coverage.', es: '{n} intentos cayeron fuera de toda franja — se registraron, no prueban cobertura.' },
  // F5-T4/N2-c — el protocolo de seguridad (señal del screening) y el dueño
  // del caso ajeno (acta E-26).
  // F5-T4/N4-a — la escalera con prueba, preauth dura/denegada, salvia, >10d.
  'navigator.needs.unconfirmed': { en: 'unconfirmed', es: 'sin confirmar' },
  'navigator.needs.emptyTitle': { en: 'The EA found no needs', es: 'La EA no detectó necesidades' },
  'navigator.needs.emptyBody': { en: 'Nothing to navigate here: the case can be closed as completed.', es: 'No hay nada que navegar: el caso se puede cerrar como completado.' },
  'navigator.needs.preauthDenied': { en: 'Preauthorization DENIED · supervisor wrote: {reason}', es: 'Preautorización DENEGADA · el supervisor escribió: {reason}' },
  'navigator.needs.preauthResubmit': { en: 'Resubmit preauthorization', es: 'Volver a someter la preautorización' },
  'navigator.needs.preauthResubmitted': { en: 'Preauthorization resubmitted', es: 'Preautorización re-sometida' },
  'navigator.needs.preauthHardWhy': { en: 'A supervisor must sign the preauthorization. Until that signature: referring yes — marking received, no.', es: 'La preautorización la firma un supervisor. Hasta esa firma: referir sí — marcar recibido, no.' },
  'navigator.needs.staleReferral': { en: 'Referral without provider response for {n} days.', es: 'Referido sin respuesta del proveedor hace {n} días.' },
  // Bug 3 (2026-09-12) — marcar «no referida» por error dejaba la necesidad
  // apagada para siempre. El botón vive en la tarjeta gris, que es donde la
  // navigator está mirando cuando se da cuenta.
  'navigator.needs.reopen': { en: 'Reopen this need', es: 'Volver a abrir esta necesidad' },
  'navigator.needs.reopenHint': {
    en: 'It goes back to being an open need: the episode cannot be closed as completed until it is resolved or marked not referred again.',
    es: 'Vuelve a ser una necesidad abierta: el episodio no se puede cerrar como completado hasta resolverla o volver a marcarla como no referida.',
  },
  'navigator.needs.reopened': { en: 'Need reopened.', es: 'Necesidad reabierta.' },
  'navigator.safety.kicker': { en: 'Safety protocol', es: 'Protocolo de seguridad' },
  'navigator.safety.title': { en: 'The screening flagged a safety risk', es: 'El screening marcó un riesgo de seguridad' },
  'navigator.safety.body': { en: 'Put the person’s safety before the script. Escalate to your supervisor NOW — this banner exists because the flag is real, not decoration.', es: 'Priorizá la seguridad de la persona antes que el guion. Escalá a tu supervisor AHORA — este aviso existe porque la marca es real, no decorativa.' },
  'navigator.safety.escalateNow': { en: 'Escalate to supervisor now', es: 'Escalar al supervisor ahora' },
  'navigator.case.readOnlyOwner': { en: 'Case owned by {name} — read only.', es: 'Caso de {name} — solo lectura.' },
  'navigator.outreach.hintNoVoicemail': { en: 'Flag active: no message', es: 'Flag activo: sin mensaje' },
  'navigator.outreach.hintDeceased': { en: 'Opens the close by external cause', es: 'Abre el cierre por causa externa' },
  'navigator.outreach.trackerFooter': {
    en: 'It is also documented in the Outreach Tracker, in FindHelp.',
    es: 'Se documenta también en Outreach Tracker, en FindHelp.',
  },

  // -- §08 cerrar la sesión
  'navigator.session.spokeFor': { en: 'You talked for {n} minutes.', es: 'Hablaron {n} minutos.' },
  'navigator.session.step3': { en: '3 · Session note — required', es: '3 · Nota de la sesión — obligatoria' },
  'navigator.session.l1Label': { en: 'Level 1', es: 'Nivel 1' },
  'navigator.session.l2Label': { en: 'Level 2', es: 'Nivel 2' },
  'navigator.session.l1Hint': { en: 'orientation and management', es: 'orientación y gestión' },
  'navigator.session.l2Hint': { en: 'enhanced criterion confirmed', es: 'criterio reforzado confirmado' },
  'navigator.session.trackerDone': { en: '✓ Tracker filled — {at}', es: '✓ Tracker llenado — {at}' },
  'navigator.session.notePlaceholder': { en: 'What was discussed, what is pending.', es: 'Qué se habló, qué quedó pendiente.' },
  'navigator.session.noteHint': { en: 'Goes to the case notes and to FindHelp.', es: 'Va a las notas del caso y a FindHelp.' },
  'navigator.session.reconLabel': { en: 'Why the minutes differ from the timer', es: 'Por qué los minutos difieren del timer' },
  'navigator.session.reconPlaceholder': { en: 'e.g. part of the call was on another line', es: 'p. ej. parte de la llamada fue por otra línea' },
  // D-04: el motivo mas comun del delta es el tiempo de transcribir a FindHelp,
  // que corre despues de que el numero se congelo. Decirlo evita que la agente
  // tenga que explicar una diferencia que no causo.
  'navigator.session.reconWhy': {
    en: 'The number was frozen when you opened the Tracker step; the clock kept running while you transcribed. That gap is the usual reason.',
    es: 'El número se congeló al abrir el paso del Tracker; el reloj siguió corriendo mientras transcribías. Esa diferencia es el motivo habitual.',
  },
  'navigator.session.reconRequired': { en: 'Explain the difference with the timer.', es: 'Explicá la diferencia con el timer.' },
  'navigator.session.followupProposed': { en: 'Proposed follow-up: {date}', es: 'Seguimiento propuesto: {date}' },
  'navigator.session.continueUnbilled': { en: 'Continue without billing', es: 'Seguir sin cobrar' },
  'navigator.session.closedBilled': { en: 'Session saved — {units} unit(s).', es: 'Sesión guardada — {units} unidad(es).' },
  'navigator.session.closedUnbilled': { en: 'Session closed without billing.', es: 'Sesión cerrada sin cobrar.' },
  // D-04: se retiran 10 claves del modal viejo de una sola pantalla —
  //   step1/step2, levelChosen/levelNote (el nivel ya no se elige),
  //   trackerMark/trackerHint/trackerDoneHint (el tracker tiene paso propio),
  //   timerHint (los minutos son solo lectura), kicker y noHalfSave.
  //   Grep sobre web/src y smokes/: 0 usos restantes de cada una.
  'navigator.session.saveAndFollowup': { en: 'Save session and follow-up', es: 'Guardar sesión y seguimiento' },
  'navigator.session.backToCall': { en: 'Back to the call', es: 'Volver a la llamada' },

  // -- D-04: la secuencia de cierre. Diseño aprobado por el CEO 2026-08-22.
  //    La numeración cuenta 3 pasos; el paso 1 (agendar) llega en el bloque C3.
  // D-04 C3: el total es dinamico — con seguimiento que agendar son 3 pasos,
  // sin el son 2. Nunca se muestra «paso 2 de 3» sin un paso 1.
  'navigator.session.stepOf': { en: 'Close the session · step {n} of {total}', es: 'Cerrar la sesión · paso {n} de {total}' },

  // D-04 (revision adversarial): el reloj y las unidades los dice el servidor,
  // y cuando no los dice todavia la pantalla lo admite en vez de inventar.
  'navigator.session.clockLoading': { en: 'Reading the clock…', es: 'Leyendo el reloj…' },
  'navigator.session.dataTimeUnbillable': {
    en: '{n} minutes — does not bill (minimum {min})',
    es: '{n} minutos — no factura (mínimo {min})',
  },
  // La glosa del nivel va en la fila que se copia a FindHelp: el pagador lee
  // «L1 — orientación y gestión», no «L1» pelado.
  'navigator.session.l1Row': { en: 'L1 \u2014 orientation and case management', es: 'L1 \u2014 orientaci\u00f3n y gesti\u00f3n' },
  'navigator.session.l2Row': { en: 'L2 \u2014 confirmed enhanced criteria', es: 'L2 \u2014 criterio reforzado confirmado' },
  // El reloj sigue corriendo despu\u00e9s de que ella copi\u00f3 el n\u00famero. Las unidades
  // s\u00f3lo cambian cada 15 minutos, as\u00ed que casi nunca pasa \u2014 y cuando pasa, esto
  // es lo \u00fanico que evita que FindHelp y el registro interno queden distintos.
  'navigator.session.unitsChanged': {
    en: 'Careful: you copied {antes} unit(s) to FindHelp and the time since then makes it {ahora}. Update the Tracker before you leave.',
    es: 'Ojo: copiaste {antes} unidad(es) a FindHelp y el tiempo que pas\u00f3 las hace {ahora}. Actualiz\u00e1 el Tracker antes de salir.',
  },
  'navigator.session.unitsChangedDone': {
    en: 'I already updated it in FindHelp',
    es: 'Ya lo actualicé en FindHelp',
  },
  'navigator.session.doneFollowup': { en: 'Follow-up: {date}', es: 'Seguimiento: {date}' },
  'navigator.session.doneFooter': {
    en: 'The case is waiting on the provider. It comes back only on the follow-up day.',
    es: 'El caso queda esperando al proveedor. Vuelve solo el d\u00eda del seguimiento.',
  },
  'navigator.session.doneSubtitleScheduled': {
    en: 'The navigation is done. The providers still have to respond \u2014 that is what the follow-up is for, and it is already scheduled.',
    es: 'La navegaci\u00f3n est\u00e1 hecha. Falta que los proveedores respondan \u2014 para eso queda el seguimiento, y ya est\u00e1 agendado.',
  },
  'navigator.session.clockFailed': {
    en: 'The clock could not be read — do not transcribe anything yet.',
    es: 'No se pudo leer el reloj — no transcribas nada todavía.',
  },
  'navigator.session.clockRetry': { en: 'Try again', es: 'Reintentar' },
  // El servidor no recorta por tope: acepta o rechaza. Asi que cuando el cupo
  // no alcanza NO se ofrece una cifra recortada — se dice que no se puede.
  // SIN NUMERO A PROPOSITO. La condicion del servidor es «rinde mas unidades de
  // las que quedan», no «quedan cero»: decir «quedan 1» imprime una cifra que
  // parece transcribible, y transcribirla produce la divergencia que todo esto
  // existe para evitar. El remanente se muestra aparte, en su propio renglon.
  'navigator.session.dataTimeCapped': {
    en: '{n} minutes — this session needs more units than are left: it cannot be billed',
    es: '{n} minutos — esta sesión necesita más unidades de las que quedan: no se puede facturar',
  },
  // Un congelado de un build anterior: se conserva su numero de minutos, pero
  // nadie verifico si el cupo alcanza. No se afirma que si.
  'navigator.session.dataTimeUnknown': {
    en: '{n} minutes — the billing check could not be confirmed: saved without billing',
    es: '{n} minutos — no se pudo confirmar el chequeo de facturación: se guarda sin facturar',
  },
  'navigator.session.saveUnbilled': { en: 'Save without billing', es: 'Guardar sin facturar' },
  'navigator.session.dataTimeNoLevel': {
    en: '{n} minutes — without a level from the EA nothing can be billed',
    es: '{n} minutos — sin nivel de la EA no se puede facturar nada',
  },
  'navigator.session.trackerConfirmCapped': {
    en: 'I recorded the Reimbursement Tracker in FindHelp with the data above: {date}, {activity}, {level}. The cap for this member is used up, so this session bills no units.',
    es: 'Registré el Reimbursement Tracker en FindHelp con los datos de arriba: {date}, {activity}, {level}. El cupo de este miembro está agotado, así que esta sesión no factura unidades.',
  },
  'navigator.session.trackerConfirmNoLevel': {
    en: 'I recorded the Reimbursement Tracker in FindHelp with the data above: {date}, {activity}, {level}. The EA did not determine a level, so this session bills no units.',
    es: 'Registré el Reimbursement Tracker en FindHelp con los datos de arriba: {date}, {activity}, {level}. La EA no determinó un nivel, así que esta sesión no factura unidades.',
  },
  'navigator.session.levelMissing': {
    en: 'No level — the EA did not determine one',
    es: 'Sin nivel — la EA no lo determinó',
  },
  'navigator.session.trackerConfirmUnbillable': {
    en: 'I recorded the Reimbursement Tracker in FindHelp with the data above: {date}, {activity}, {level}. This session does not reach the billable minimum, so it bills no units.',
    es: 'Registré el Reimbursement Tracker en FindHelp con los datos de arriba: {date}, {activity}, {level}. Esta sesión no llega al mínimo facturable, así que no factura unidades.',
  },

  // paso 1 · agendar el seguimiento (D-04 C3)
  'navigator.session.scheduleTitle': { en: 'Schedule the follow-up', es: 'Agendá el seguimiento' },
  // GC-3 #5 — el paso 1 SIEMPRE existe (CEO: «pueden ser siempre los tres
  // pasos»). Sin nada esperando, ofrece el default de un click o agendar igual.
  'navigator.session.noFollowupTitle': { en: 'One more visit?', es: '¿Otra cita con el miembro?' },
  'navigator.session.noFollowupLead': {
    en: 'Nothing is waiting on a provider right now. If this member is all set, no follow-up is needed — or schedule another visit anyway (second and third visits happen often).',
    es: 'No hay nada esperando a un proveedor ahora mismo. Si el miembro quedó al día, no hace falta seguimiento — o agendá otra cita igual (la 2ª o 3ª visita pasa seguido).',
  },
  'navigator.session.noFollowupSkip': {
    en: 'No follow-up needed',
    es: 'No hace falta otro seguimiento',
  },
  'navigator.session.noFollowupSchedule': {
    en: 'Schedule another visit',
    es: 'Agendar otra cita igual',
  },
  'navigator.session.scheduleLeadExtra': {
    en: 'Pick the day and time for the next visit with this member.',
    es: 'Elegí día y hora para la próxima cita con este miembro.',
  },
  'navigator.session.scheduleLead': {
    en: 'This is the {rule} follow-up. What is left is waiting on the provider — set when you will check on it.',
    es: 'Es el seguimiento de {rule}. Lo que queda es esperar al proveedor — poné cuándo lo vas a chequear.',
  },
  'navigator.session.scheduleGo': { en: 'Schedule and continue', es: 'Agendar y seguir' },
  'navigator.session.dayLabel': { en: 'Day', es: 'Día' },
  'navigator.session.dayProposed': { en: 'The system proposes {date}. Move it if you want.', es: 'El sistema propone el {date}. Movelo si querés.' },
  'navigator.session.hourLabel': { en: 'Time', es: 'Hora' },
  'navigator.session.hourHint': { en: 'Pick it below.', es: 'Elegila abajo.' },
  'navigator.session.dayHeading': { en: 'How {date} looks for you', es: 'Cómo está tu {date}' },
  'navigator.session.dayCount': { en: '{n} appointment(s) that day', es: '{n} cita(s) ese día' },
  'navigator.session.dayFailed': { en: 'The day could not be loaded. Try again.', es: 'No se pudo cargar el día. Intentá de nuevo.' },
  'navigator.session.afterShift': { en: 'Outside your shift · only if you want', es: 'Fuera de tu jornada · sólo si querés' },
  'navigator.session.dayLegend': {
    en: 'Struck-through times are already taken. The window runs from {from}:00 to {to}:00 — from {shift}:00 on it is your call.',
    es: 'Las tachadas ya están ocupadas. La ventana va de {from}:00 a {to}:00 — desde las {shift}:00 es tu decisión.',
  },
  'navigator.session.takenNavigator': { en: 'You already have an appointment at that time.', es: 'Ya tenés una cita a esa hora.' },
  'navigator.session.takenMember': { en: 'The member already has an appointment at that time.', es: 'El miembro ya tiene una cita a esa hora.' },
  'navigator.session.takenPast': { en: 'That time already passed.', es: 'Esa hora ya pasó.' },
  'navigator.session.slotGone': { en: 'That time was taken while you were choosing. Pick another one.', es: 'Esa hora se ocupó mientras elegías. Elegí otra.' },
  'navigator.session.dismissOpen': { en: 'No follow-up — explain why', es: 'Sin seguimiento — explicar por qué' },
  'navigator.session.dismissTitle': { en: 'Why is there no follow-up?', es: '¿Por qué no se hace el seguimiento?' },
  'navigator.session.dismissWhy': {
    en: 'A follow-up is billable while units remain, so dropping it has to say why. It is saved on the case; it does not go to the audit log.',
    es: 'Un seguimiento es facturable mientras queden unidades, así que dejarlo caer tiene que decir por qué. Se guarda en el caso; no va al audit log.',
  },
  'navigator.session.dismissPlaceholder': { en: 'For example: she already got the apartment.', es: 'Por ejemplo: ya consiguió el apartamento.' },
  'navigator.session.dismissConfirm': { en: 'Drop the follow-up', es: 'Descartar el seguimiento' },
  'navigator.session.dismissReasonRequired': { en: 'Write why there is no follow-up.', es: 'Escribí por qué no se hace este seguimiento.' },
  'navigator.session.lastStep': { en: 'This is the last one: saving stops the clock.', es: 'Es lo último: al guardar, el reloj se apaga.' },
  'navigator.session.backStep': { en: 'Back', es: 'Volver' },
  'navigator.session.saveAndFinish': { en: 'Save and finish', es: 'Guardar y terminar' },

  // paso 2 · el Reimbursement Tracker
  'navigator.session.trackerTitle': { en: 'Record the Reimbursement Tracker', es: 'Registrá el Reimbursement Tracker' },
  'navigator.session.trackerSubtitle': {
    en: 'This is what gets this work paid. It goes in FindHelp, with these exact figures.',
    es: 'Es lo que hace que este trabajo se pague. Va en FindHelp, con estos datos exactos.',
  },
  'navigator.session.trackerWhy': {
    en: 'Without this mark the session is not saved — so a billable contact never ends up without its tracker.',
    es: 'Sin esta marca la sesión no se guarda — así nunca queda un contacto facturable sin su tracker.',
  },
  'navigator.session.clockFrozen': {
    // EL RELOJ NO SE DETIENE ACA, y decirlo era mentir. El CEO lo decidio el
    // 2026-08-22: «se cobra todo hasta el ultimo minuto gastado en este caso»,
    // asi que el rato que lleva cerrar TAMBIEN se factura y el numero sube
    // mientras la ventana esta abierta. Lo que se registra es lo medido al
    // guardar, no una foto de cuando se abrio.
    en: 'The clock is still running: {n} minutes so far. Closing this out counts too — what gets recorded is the reading at the moment you save.',
    es: 'El reloj sigue corriendo: {n} minutos hasta ahora. El rato que lleva cerrar también se cobra — se registra lo que marque al guardar.',
  },
  'navigator.session.trackerCopy': { en: '1 · Copy this into FindHelp', es: '1 · Copiá esto en FindHelp' },
  'navigator.session.copyData': { en: 'Copy the figures', es: 'Copiar los datos' },
  'navigator.session.copied': { en: 'Copied', es: 'Copiado' },
  'navigator.session.trackerConfirmStep': { en: '2 · Confirm you did it', es: '2 · Confirmá que lo hiciste' },
  'navigator.session.trackerConfirmLabel': {
    en: 'I recorded the Reimbursement Tracker in FindHelp with the figures above: {date}, {activity}, {level}, {u} unit(s).',
    es: 'Registré el Reimbursement Tracker en FindHelp con los datos de arriba: {date}, {activity}, {level}, {u} unidad(es).',
  },
  'navigator.session.trackerStamp': { en: 'It is stamped with your name and the time.', es: 'Queda con tu nombre y la hora.' },
  'navigator.session.trackerNext': { en: 'Yes, I recorded it — continue', es: 'Sí, lo registré — seguir' },
  'navigator.session.trackerGateHint': {
    en: 'The button turns on when you tick the box.',
    es: 'El botón se habilita cuando marcás la casilla.',
  },
  'navigator.session.trackerRecorded': { en: 'Tracker recorded at {at}.', es: 'Tracker registrado a las {at}.' },

  // los datos que se transcriben
  'navigator.session.dataMember': { en: 'Member', es: 'Miembro' },
  'navigator.session.dataDate': { en: 'Service date', es: 'Fecha de servicio' },
  'navigator.session.dataActivity': { en: 'Activity', es: 'Actividad' },
  'navigator.session.dataLevel': { en: 'Level', es: 'Nivel' },
  'navigator.session.dataTime': { en: 'Time and units', es: 'Tiempo y unidades' },
  'navigator.session.dataTimeValue': { en: '{n} minutes → {u} unit(s)', es: '{n} minutos → {u} unidad(es)' },
  'navigator.session.activityCare': { en: 'Care management', es: 'Gestión de cuidado' },

  // paso 3 · el nivel ya no se elige
  'navigator.session.levelLabel': { en: 'Level of this session', es: 'Nivel de esta sesión' },
  'navigator.session.levelFromEa': {
    en: 'The EA determines it. Billing never picks on its own.',
    es: 'Lo determina la EA. La facturación nunca elige sola.',
  },
  'navigator.session.minutesLocked': {
    en: 'Already recorded in the Tracker — not editable here.',
    es: 'Ya registrados en el Tracker — acá no se editan.',
  },

  // la pantalla final
  'navigator.session.doneKicker': { en: 'Done', es: 'Listo' },
  'navigator.session.doneTitle': { en: 'You are done with {name} for today.', es: 'Terminaste con {name} por hoy.' },
  'navigator.session.doneSubtitle': {
    en: 'The navigation is done. What is left is for the providers to respond — that is what the follow-up is for.',
    es: 'La navegación está hecha. Falta que los proveedores respondan — para eso queda el seguimiento.',
  },
  'navigator.session.doneReferrals': { en: '{n} need(s) referred and told to the member', es: '{n} necesidad(es) referidas y avisadas' },
  'navigator.session.doneTracker': { en: 'Reimbursement Tracker recorded', es: 'Reimbursement Tracker registrado' },
  'navigator.session.doneSession': { en: 'Session saved — {n} unit(s) {level}', es: 'Sesión guardada — {n} unidad(es) {level}' },
  'navigator.session.doneBack': { en: 'Back to Today', es: 'Volver a Hoy' },

  // -- §05 mi agenda
  'navigator.agenda.title': { en: 'My agenda', es: 'Mi agenda' },
  'navigator.agenda.ownOnly': {
    en: 'Only your cases. Nobody schedules over them and you do not schedule over anyone else’s.',
    es: 'Sólo tus casos. Nadie agenda sobre ellos y vos no agendás sobre los de nadie.',
  },
  'navigator.agenda.overdue': { en: 'Overdue', es: 'Vencido' },
  // §05 «Todo lo que viene» — los horizontes son ACUMULATIVOS hacia adelante:
  // «esta semana» incluye hoy y mañana. Las vencidas van aparte, que es lo que
  // las hace visibles.
  'navigator.agenda.tomorrow': { en: 'Tomorrow', es: 'Ma\u00f1ana' },
  'navigator.agenda.month': { en: 'This month', es: 'Este mes' },
  'navigator.agenda.later': { en: 'Later on', es: 'M\u00e1s adelante' },
  'navigator.agenda.episode': { en: 'episode #{n}', es: 'episodio #{n}' },
  'navigator.agenda.noTime': { en: 'No time yet', es: 'Sin hora' },
  'navigator.agenda.badgeFollowup': { en: 'Follow-up', es: 'Seguimiento' },
  'navigator.agenda.badgeAppt': { en: 'Appointment', es: 'Cita' },
  'navigator.agenda.showing': { en: 'Showing {desde}\u2013{hasta} of {total}', es: 'Mostrando {desde}\u2013{hasta} de {total}' },
  'navigator.agenda.prev': { en: 'Previous', es: 'Anterior' },
  'navigator.agenda.next': { en: 'Next', es: 'Siguiente' },
  // «Llamar» está en TODAS las filas: a veces hay que llamar antes de la cita.
  'navigator.appt.call': { en: 'Call', es: 'Llamar' },
  'navigator.agenda.today': { en: 'Today', es: 'Hoy' },
  'navigator.agenda.week': { en: 'This week', es: 'Esta semana' },
  'navigator.agenda.followup': { en: 'Follow-up', es: 'Seguimiento' },
  'navigator.agenda.rescheduled': { en: 'rescheduled ×{n}', es: 'reagendada ×{n}' },
  'navigator.agenda.move': { en: 'Move', es: 'Mover' },
  'navigator.agenda.cancelBtn': { en: 'Cancel', es: 'Cancelar' },
  'navigator.agenda.noShow': { en: 'No-show', es: 'No llegó' },
  // N4 · D4.2 — la nota de la cita no se vuelca en la fila: se pide.
  'navigator.agenda.seeNote': { en: 'See the note', es: 'Ver la nota' },
  'navigator.agenda.hideNote': { en: 'Hide the note', es: 'Ocultar la nota' },
  // N5 · D5.1/D5.2 — marcar la cita como completada, y dónde queda después.
  'navigator.agenda.complete': { en: 'Completed', es: 'Completada' },
  'navigator.agenda.completedChip': { en: 'Completed', es: 'Completada' },
  'navigator.agenda.completeKicker': { en: 'Complete appointment', es: 'Completar la cita' },
  'navigator.agenda.completeTitle': {
    en: 'Did this appointment happen?',
    es: '¿La cita ocurrió?',
  },
  'navigator.agenda.completeBody': {
    en: 'Mark it as completed. It stays visible under “Done today” and then leaves the agenda. This does not change the clock or anything billed.',
    es: 'Se marca como completada. Queda a la vista en «Hechas hoy» y después sale de la agenda. No cambia el reloj ni nada de lo facturado.',
  },
  'navigator.agenda.completeConfirm': { en: 'Mark as completed', es: 'Marcar completada' },
  'navigator.agenda.completed': { en: 'Appointment completed.', es: 'Cita completada.' },
  'f5t5.agenda.doneKicker': { en: 'Done today', es: 'Hechas hoy' },
  'navigator.agenda.moveKicker': { en: 'Move appointment', es: 'Mover la cita' },
  'navigator.agenda.moveTitle': { en: 'When do you call them?', es: '¿Cuándo lo llamás?' },
  'navigator.agenda.newWhen': { en: 'New date and time', es: 'Nueva fecha y hora' },
  'navigator.agenda.slotTaken': {
    en: 'You already have an appointment at that time. Pick another.',
    es: 'A esa hora ya tenés otra cita. Elegí otra hora.',
  },
  'navigator.agenda.saveMove': { en: 'Save the appointment', es: 'Guardar la cita' },
  'navigator.agenda.moved': { en: 'Appointment moved.', es: 'Cita movida.' },
  'navigator.agenda.cancelKicker': { en: 'Cancel appointment', es: 'Cancelar la cita' },
  'navigator.agenda.cancelTitle': { en: 'Why is it cancelled?', es: '¿Por qué se cancela?' },
  'navigator.agenda.cancelReason': { en: 'Reason', es: 'Motivo' },
  'navigator.agenda.confirmCancel': { en: 'Cancel the appointment', es: 'Cancelar la cita' },
  'navigator.agenda.cancelled': { en: 'Appointment cancelled.', es: 'Cita cancelada.' },
  'navigator.agenda.cancel.member_request': { en: 'The member asked for it', es: 'Lo pidió el miembro' },
  'navigator.agenda.cancel.navigator_request': { en: 'Navigator request', es: 'Lo pedí yo' },
  'navigator.agenda.cancel.no_show': { en: 'No-show', es: 'No llegó' },
  'navigator.agenda.cancel.lost_coverage': { en: 'Lost coverage', es: 'Perdió la cobertura' },
  'navigator.agenda.cancel.member_unreachable': { en: 'Member unreachable', es: 'Miembro inubicable' },
  'navigator.agenda.cancel.duplicate': { en: 'Duplicate', es: 'Duplicada' },
  'navigator.agenda.cancel.other': { en: 'Other', es: 'Otro' },
  'navigator.agenda.noShowKicker': { en: 'No-show', es: 'No llegó' },
  'navigator.agenda.noShowTitle': { en: 'They did not make the appointment.', es: 'No llegó a la cita.' },
  'navigator.agenda.noShowBody': {
    en: 'It counts as an attempt of the outreach cycle, and a reschedule follow-up is proposed right away.',
    es: 'Cuenta como intento del ciclo de outreach, y se propone reagendar ahí mismo.',
  },
  'navigator.agenda.noShowConfirm': { en: 'Record the no-show', es: 'Registrar el no-show' },
  'navigator.agenda.noShowSaved': { en: 'No-show recorded — attempt {n} of the cycle.', es: 'No-show registrado — intento {n} del ciclo.' },
  'navigator.agenda.graceActive': { en: 'Too early to mark a no-show', es: 'Todavía es temprano para marcar no-show' },

  // -- ola de fidelidad v2 (2026-08-20): §05 tabs + agendar, §08 capas nuevas
  'navigator.agenda.tab.all': { en: 'All', es: 'Todo' },
  'navigator.agenda.tab.overdue': { en: 'Overdue', es: 'Vencidos' },
  'navigator.agenda.tab.today': { en: 'Today', es: 'Hoy' },
  'navigator.agenda.tab.week': { en: 'This week', es: 'Esta semana' },
  'navigator.agenda.newAppt': { en: 'Schedule on one of my cases', es: 'Agendar sobre uno de mis casos' },
  'navigator.appt.kicker': { en: 'Schedule appointment', es: 'Agendar cita' },
  'navigator.appt.whenTitle': { en: 'When do you call them?', es: '¿Cuándo lo llamás?' },
  'navigator.appt.ownAgendaHint': {
    en: 'The appointment lands on your agenda and on the case. No one else schedules this member.',
    es: 'La cita queda en tu agenda y en el caso. Al miembro nadie más lo agenda.',
  },
  'navigator.appt.pickCase': { en: 'On which of your cases', es: 'Sobre cuál de tus casos' },
  'navigator.appt.casesFailed': {
    en: 'Your cases could not be loaded — try again in a moment.',
    es: 'No se pudieron cargar tus casos — probá de nuevo en un momento.',
  },
  'navigator.appt.noCases': {
    en: 'You have no open cases to schedule on.',
    es: 'No tenés casos abiertos sobre los que agendar.',
  },
  'navigator.appt.day': { en: 'Day', es: 'Día' },
  'navigator.appt.hour': { en: 'Time', es: 'Hora' },
  'navigator.appt.whatFor': { en: 'What it is for', es: 'Para qué es' },
  'navigator.appt.badDate': {
    en: 'That date does not work — check the day and time.',
    es: 'Esa fecha no sirve — revisá el día y la hora.',
  },
  'navigator.fu.kicker': { en: 'Record follow-up', es: 'Registrar seguimiento' },
  'navigator.fu.title': { en: 'How is the service going?', es: '¿Cómo va el servicio?' },
  'navigator.fu.whoLabel': { en: 'Who you spoke with', es: 'Con quién hablaste' },
  'navigator.fu.who.provider': { en: 'The provider', es: 'Con el proveedor' },
  'navigator.fu.who.member': { en: 'The member', es: 'Con el miembro' },
  'navigator.fu.who.nobody': { en: 'Could not reach anyone', es: 'No pude hablar con nadie' },
  'navigator.fu.howLabel': { en: 'How it is going', es: 'Cómo va' },
  'navigator.fu.result.recibido': { en: 'Already receiving it', es: 'Ya lo está recibiendo' },
  'navigator.fu.result.proceso': { en: 'In progress', es: 'En proceso' },
  'navigator.fu.result.trabado': { en: 'Stuck — needs a push', es: 'Trabado — hay que empujarlo' },
  'navigator.fu.noteLabel': { en: 'What happened — the note is required', es: 'Qué pasó — la nota es obligatoria' },
  'navigator.fu.notePlaceholder': {
    en: 'e.g. the provider says the box ships Thursday',
    es: 'p. ej. el proveedor dice que la caja sale el jueves',
  },
  'navigator.fu.noteHint': { en: 'The note goes to the case and to FindHelp.', es: 'La nota va al caso y a FindHelp.' },
  'navigator.fu.cta': { en: 'Save the follow-up', es: 'Guardar el seguimiento' },
  'navigator.fu.footer': {
    en: 'If they already receive it, the follow-up closes; otherwise the next one is recalculated.',
    es: 'Si ya lo recibe, el seguimiento se cierra; si no, se recalcula el próximo.',
  },
  'navigator.fu.saved': { en: 'Follow-up saved.', es: 'Seguimiento guardado.' },
  'navigator.close.group.completed': {
    en: 'Completed — member already receives everything',
    es: 'Completado / Miembro ya recibe todo',
  },
  'navigator.close.group.declined_by_patient': { en: 'The member declined', es: 'El miembro no quiso' },
  'navigator.close.group.not_required': { en: 'Not needed', es: 'No hacía falta' },
  'navigator.close.group.closed_external': { en: 'External cause', es: 'Causa externa' },
  'navigator.close.noteLabel': { en: 'What happened with this person', es: 'Qué pasó con esta persona' },
  'navigator.close.notePlaceholder': {
    en: 'The short story, in your words — it stays on the episode.',
    es: 'La historia corta, con tus palabras — queda en el episodio.',
  },
  'navigator.close.confirm': {
    en: 'Close the episode as “{reason}”. This is irreversible.',
    es: 'Cerrar el episodio como «{reason}». Esto es irreversible.',
  },
  'navigator.workspace.closedCheer': { en: 'Episode closed.', es: 'Episodio cerrado.' },
  'navigator.close.orRather': { en: 'or rather:', es: 'o más bien:' },
  'navigator.close.backToDefault': { en: 'no, the usual one', es: 'no, el normal' },
  'navigator.close.needGroup': { en: 'pick how it closes', es: 'elegí cómo cierra' },
  'navigator.close.needReason': { en: 'pick the reason', es: 'elegí el motivo' },
  'navigator.close.needNote': { en: 'write what happened', es: 'escribí qué pasó' },
  'navigator.close.missing': { en: 'Still missing: {falta}.', es: 'Falta: {falta}.' },
  'navigator.close.cta': { en: 'Close the episode', es: 'Cerrar el episodio' },
  'navigator.close.saved': { en: 'Episode closed.', es: 'Episodio cerrado.' },
  // V1.2 · «Seguimiento del 1 de septiembre» — el encabezado dice por que el
  // caso esta abierto hoy. Va junto al episodio, que es como lo dibuja el mockup.
  'navigator.case.followupOf': { en: 'Follow-up of {date}', es: 'Seguimiento del {date}' },
  'navigator.case.episodeN': { en: 'Episode #{n}', es: 'Episodio #{n}' },
  // GC-3 #5: cuántos seguimientos completados lleva el episodio.
  'navigator.case.followupsDone': { en: '{n} follow-up(s) done', es: '{n} seguimiento(s) hechos' },
  'navigator.case.prevEpisode': {
    en: 'previous one closed as {reason} in {date}',
    es: 'el anterior cerró por {reason} en {date}',
  },
  // D-04: 'navigator.timer.resume' se elimina con el control manual del reloj.
  'navigator.registerEa.completeTitle': { en: "{name}'s EA is complete.", es: 'La EA de {name} quedó completa.' },

  // -- fixes de la revisión adversarial 2026-08-19
  'navigator.closeReason.prior_navigation_verified': {
    en: 'Prior navigation verified — nothing new to bill',
    es: 'Navegación previa verificada — nada nuevo que facturar',
  },
  'navigator.case.openingScript': {
    en: 'Opening and verification script',
    es: 'Guion de apertura y verificación',
  },

  // -- §06 EA guiada
  'navigator.ea.blockOf': { en: 'Block {i} of {total}', es: 'Bloque {i} de {total}' },
  'navigator.ea.autosaveHint': { en: 'It saves itself while you type', es: 'Se guarda solo mientras escribís' },
  'navigator.ea.saveExit': {
    en: 'Save and leave — you resume where you left off',
    es: 'Guardar y salir — retomás donde quedaste',
  },
  'navigator.ea.encryptedHint': { en: 'Everything is saved encrypted', es: 'Todo se guarda cifrado' },
  'navigator.ea.crumb': { en: 'EA of', es: 'EA de' },
  'navigator.ea.session': { en: 'Session', es: 'Sesión' },
  'navigator.ea.findhelpField': { en: 'FindHelp field', es: 'Campo de FindHelp' },
  // E-13 — la serie Yes/No como lista de selección (GC-3 #6/7).
  'navigator.ea.compactHint': {
    en: 'Check only what the member DOES have. Anything unchecked is saved as No.',
    es: 'Marcá solo lo que el miembro SÍ tiene. Lo que no marques se guarda como No.',
  },
  'navigator.ea.noneOfTheAbove': { en: 'None of the above', es: 'Ninguna de las anteriores' },
  'navigator.ea.noneOfTheAboveHint': {
    en: 'Writes No on every field of this list.',
    es: 'Escribe No en todos los campos de esta lista.',
  },
  'navigator.ea.motorBox': {
    en: 'The engine decides on its own: if a criterion is confirmed, the EA moves to Level 2. With fee-for-service coverage it would take the Standard route and this block would be skipped.',
    es: 'El motor decide solo: si confirma un criterio, la EA pasa a Nivel 2. Con cobertura fee-for-service iría por la ruta Standard y este bloque se salta.',
  },

  // -- §09 mis números
  'navigator.numbers.title': { en: 'My numbers', es: 'Mis números' },
  'navigator.numbers.goToday': { en: 'Go to Today', es: 'Ir a Hoy' },
  'navigator.numbers.error': { en: 'The numbers could not be loaded.', es: 'No se pudieron cargar los números.' },
  'navigator.numbers.todayKicker': { en: 'Today', es: 'Hoy' },
  'navigator.numbers.contactsOfAttempts': { en: 'Contacts made, of attempts', es: 'Contactos logrados de los intentos' },
  'navigator.numbers.billableSessions': { en: 'Billable sessions', es: 'Sesiones facturables' },
  'navigator.numbers.billableTime': { en: 'Billable time', es: 'Tiempo facturable' },
  'navigator.numbers.unitsBreakdown': { en: 'Units · L2 {l2}, L1 {l1}', es: 'Unidades · L2 {l2}, L1 {l1}' },
  'navigator.numbers.invariant': {
    en: 'Sessions without tracker or note: {n} — the close does not allow it.',
    es: 'Sesiones sin tracker o sin nota: {n} — el cierre no lo permite.',
  },
  'navigator.numbers.overdueKicker': { en: 'What is falling behind', es: 'Lo que se está atrasando' },
  'navigator.numbers.followupsOverdue': { en: 'Overdue follow-ups', es: 'Seguimientos vencidos' },
  'navigator.numbers.referralsStale': {
    en: 'Referrals with no answer for over {days} days',
    es: 'Referrals sin respuesta hace más de {days} días',
  },
  'navigator.numbers.noShowsUnrescheduled': { en: 'No-shows without a new date', es: 'No-shows sin reagendar' },
  'navigator.numbers.untouchedCases': {
    en: 'Cases untouched for {days} days or more',
    es: 'Casos sin tocar hace {days} días o más',
  },
  'navigator.numbers.see': { en: 'See them', es: 'Verlos' },
  'navigator.numbers.weeklyKicker': { en: 'Goals for the week', es: 'Metas de la semana' },
  'navigator.numbers.goalContacts': { en: 'Contacts made', es: 'Contactos logrados' },
  'navigator.numbers.goalReceived': { en: 'Services confirmed as received', es: 'Servicios confirmados como recibidos' },
  'navigator.numbers.goalCompleted': { en: 'Episodes closed «completed»', es: 'Episodios cerrados «completado»' },
  'navigator.numbers.of': { en: 'of', es: 'de' },
  'navigator.numbers.portfolioKicker': { en: 'My portfolio · {n} cases', es: 'Mi cartera · {n} casos' },
  'navigator.numbers.pfUncontacted': { en: 'Not contacted', es: 'Sin contactar' },
  'navigator.numbers.pfWorking': { en: 'In progress', es: 'En trabajo' },
  'navigator.numbers.pfWaiting': { en: 'Waiting on provider', es: 'Esperando proveedor' },
  'navigator.numbers.pfParked': { en: 'Parked', es: 'Aparcados' },
  'navigator.numbers.pfReady': { en: 'Ready to close', es: 'Listos para cerrar' },

  // F5-T5 — Navigator 05 «Agenda, números y notas» (contrato 2026-08-28).
  // Los ES son canónicos (verbatim del mockup); los EN se derivan de ellos.
  // -- Mi agenda: 3 horizontes fusionados + «Esperando al proveedor» aparte.
  'f5t5.agenda.overdueKicker': { en: 'Overdue', es: 'Vencidos' },
  'f5t5.agenda.weekKicker': { en: 'This week', es: 'Esta semana' },
  'f5t5.agenda.laterKicker': { en: 'Later on', es: 'Más adelante' },
  'f5t5.agenda.dayOne': { en: '1 day', es: '1 día' },
  'f5t5.agenda.dayN': { en: '{n} days', es: '{n} días' },
  'f5t5.agenda.openCase': { en: 'Open the case', es: 'Abrir el caso' },
  'f5t5.agenda.reschedule': { en: 'Reschedule', es: 'Reagendar' },
  'f5t5.agenda.pageOf': {
    en: '{hasta} of {total} · page {p} of {pp}',
    es: '{hasta} de {total} · página {p} de {pp}',
  },
  'f5t5.agenda.empty': { en: 'Nothing scheduled and nothing overdue', es: 'Nada agendado y nada vencido' },
  'f5t5.agenda.emptyBody': {
    en: 'When you schedule an appointment or a follow-up, it shows up here.',
    es: 'Cuando agendes una cita o un seguimiento, aparece acá.',
  },
  'f5t5.agenda.errorTitle': { en: 'The agenda did not load', es: 'La agenda no cargó' },
  'f5t5.agenda.errorBody': {
    en: 'Without it you cannot see what is overdue nor what is coming — retry before working blind.',
    es: 'Sin ella no se ven los vencidos ni lo que viene — reintentá antes de trabajar a ciegas.',
  },
  'f5t5.agenda.retry': { en: 'Retry', es: 'Reintentar' },
  'f5t5.agenda.waitingIntro': {
    en: 'This is not today’s work: it is a state. It lives here because it needs a look once a week, not every morning. Past 10 days with no answer, the case moves up to «Right now» in Today.',
    es: 'No es trabajo de hoy: es un estado. Está acá porque hay que verlo una vez por semana, no cada mañana. Al pasar los 10 días sin respuesta, el caso sube a «Ahora mismo» en Hoy.',
  },
  // -- Mis números: compuerta roja (SOLO de tracker; la nota es NOT NULL).
  'f5t5.gate.title': {
    en: '{n} billable sessions without their tracker mark',
    es: '{n} sesiones facturables sin su marca de tracker',
  },
  'f5t5.gate.body': {
    en: 'The reaper closed them at 480 minutes, so they were saved without going through step 2. They are the only ones that can get in without a mark, and until you set it that time is not declared.',
    es: 'Las cerró el reaper al llegar a 480 minutos, así que se guardaron sin pasar por el paso 2. Son las únicas que pueden entrar sin marca, y hasta que la pongas ese tiempo no se declara.',
  },
  'f5t5.gate.cta': { en: 'See the {n} and mark them', es: 'Ver las {n} y marcarlas' },
  // -- «Qué se está cayendo»: catálogo de 4 filas con regla y aria verbatim.
  'f5t5.fall.kicker': { en: 'What is falling behind', es: 'Qué se está cayendo' },
  'f5t5.fall.followups': { en: 'Overdue follow-ups', es: 'Seguimientos vencidos' },
  'f5t5.fall.followupsRule': {
    en: 'The agreed date passed and the call was not logged',
    es: 'Pasó la fecha acordada y no se registró la llamada',
  },
  'f5t5.fall.followupsAria': { en: 'See overdue follow-ups', es: 'Ver seguimientos vencidos' },
  'f5t5.fall.referrals': { en: 'Referrals with no answer', es: 'Referidos sin respuesta' },
  'f5t5.fall.referralsRule': {
    en: 'More than {days} days without the provider confirming',
    es: 'Más de {days} días sin que el proveedor confirme',
  },
  'f5t5.fall.referralsAria': { en: 'See referrals with no answer', es: 'Ver referidos sin respuesta' },
  'f5t5.fall.noshows': { en: 'No-show appointments not rescheduled', es: 'Citas no-show sin reagendar' },
  'f5t5.fall.noshowsRule': {
    en: 'The no-show counts as an attempt and does not reset the count',
    es: 'El no-show cuenta como intento y no resetea el conteo',
  },
  'f5t5.fall.noshowsAria': { en: 'See no-shows not rescheduled', es: 'Ver no-shows sin reagendar' },
  'f5t5.fall.waiting': { en: 'Waiting on the provider', es: 'Esperando al proveedor' },
  'f5t5.fall.waitingRule': {
    en: 'Referral made, still within the {days} days',
    es: 'Referido hecho, todavía dentro de los {days} días',
  },
  'f5t5.fall.waitingAria': { en: 'See cases waiting on the provider', es: 'Ver casos esperando al proveedor' },
  'f5t5.fall.see': { en: 'See', es: 'Ver' },
  // -- Cartera por estado: la decisión de las metas se declara por escrito.
  'f5t5.pf.kicker': { en: 'Portfolio by state', es: 'Cartera por estado' },
  'f5t5.pf.note': {
    en: 'No weekly goals here: they belong to the admin view. What is yours are the numbers above and the gate.',
    es: 'Sin metas semanales acá: son de la vista del admin. Lo que sí es tuyo son los números de arriba y la compuerta.',
  },
  // -- Notas del caso: sección fija, aviso de espejado siempre visible.
  'f5t5.notes.mirrorChip': { en: 'Mirrored to FindHelp', es: 'Se espejan a FindHelp' },
  'f5t5.notes.warnLead': {
    en: 'Everything you write here is copied to FindHelp',
    es: 'Todo lo que escribas acá se copia a FindHelp',
  },
  'f5t5.notes.warnMid': {
    en: ', which is an external system. Write what belongs in the member’s file. ',
    es: ', que es un sistema externo. Escribí lo que corresponde al expediente del miembro. ',
  },
  'f5t5.notes.warnStrong': {
    en: 'Internal context does not go here',
    es: 'El contexto interno no va acá',
  },
  'f5t5.notes.warnTail': { en: ': that is what the team chat is for.', es: ': para eso está el chat del equipo.' },
  'f5t5.notes.newLabel': { en: 'New note', es: 'Nota nueva' },
  'f5t5.notes.placeholder': {
    en: 'What was worked on, what is pending on the member’s side',
    es: 'Qué se trabajó, qué quedó pendiente del lado del miembro',
  },
  'f5t5.notes.autoHint': {
    en: 'Attempt, session and follow-up notes save themselves: these are the case’s own.',
    es: 'Las notas de intento, sesión y seguimiento se guardan solas: éstas son las del caso.',
  },
  'f5t5.notes.save': { en: 'Save the note', es: 'Guardar la nota' },
  'f5t5.notes.prevKicker': { en: 'Previous notes · {n}', es: 'Notas anteriores · {n}' },
  'f5t5.notes.empty': { en: 'No notes on this case yet', es: 'Todavía no hay notas en este caso' },
  'f5t5.notes.copy': { en: 'Copy', es: 'Copiar' },
  'f5t5.notes.copyAria': {
    en: 'Copy this note to paste it in FindHelp',
    es: 'Copiar esta nota para pegarla en FindHelp',
  },
  'f5t5.notes.copied': { en: 'Note copied', es: 'Nota copiada' },
  'f5t5.notes.copyFailed': { en: 'The note could not be copied', es: 'No se pudo copiar la nota' },
  // -- Historia en FindHelp: panel de sólo lectura (ventana 11 que vuelve).
  'f5t5.fh.readOnly': { en: 'Read-only', es: 'Sólo lectura' },
  'f5t5.fh.intro': {
    en: 'Every mirror declaration with FindHelp for this case, newest to oldest. Nothing gets marked here: each mark is declared where the work happens, and this is the receipt.',
    es: 'Todas las declaraciones del espejo con FindHelp para este caso, de la más nueva a la más vieja. Acá no se marca nada: cada marca se declara donde se trabaja, y esto es el recibo.',
  },
  'f5t5.fh.empty': {
    en: 'No mirror declarations on this case yet.',
    es: 'Todavía no hay declaraciones espejadas en este caso.',
  },

  // ===== F5-T1a · Admin 08 Configuración =====
  'f5t1a.kicker': { en: 'Admin · Configuration', es: 'Admin · Configuración' },
  'f5t1a.title': { en: 'Configuration', es: 'Configuración' },
  'f5t1a.subtitle': {
    en: 'One screen for every live key, in five groups. Real forms with a unit. The raw JSON box is gone.',
    es: 'Una sola pantalla para cada clave viva, en cinco grupos. Formularios reales con unidad. El recuadro de JSON crudo ya no existe.',
  },
  'f5t1a.t1bNote': {
    en: 'Effective-date versioning (history of every key, scheduled changes, payroll replay by service date) lands in T1b. This screen does not fake it.',
    es: 'La vigencia versionada (historial de cada clave, cambios programados, lectura por fecha de servicio) llega en T1b. Esta pantalla no la finge.',
  },
  'f5t1a.overrideNote': {
    en: 'Per-person daily-target overrides stay on Team, not here',
    es: 'El override individual de meta diaria vive en Equipo, no acá',
  },
  'f5t1a.overrideLink': { en: 'Open Team', es: 'Abrir Equipo' },
  'f5t1a.groupsAria': { en: 'Configuration groups', es: 'Grupos de configuración' },
  'f5t1a.groupsKicker': { en: 'Groups · {n} keys', es: 'Grupos · {n} claves' },
  'f5t1a.group.operacion': { en: 'Operations & security', es: 'Operación y seguridad' },
  'f5t1a.group.metas': { en: 'Goals & levels', es: 'Metas y niveles' },
  'f5t1a.group.nucleo': { en: 'Core', es: 'Núcleo' },
  'f5t1a.group.navegacion': { en: 'Navigation', es: 'Navegación' },
  'f5t1a.group.congeladas': { en: 'Frozen', es: 'Congeladas' },
  'f5t1a.group.operacion.note': { en: 'Direct edit with a unit', es: 'Edición directa con unidad' },
  'f5t1a.group.metas.note': { en: 'Payroll-tracked keys keep the existing safety modal', es: 'Las de nómina conservan el modal de salvaguardas que ya existía' },
  'f5t1a.group.nucleo.note': { en: 'FindHelp entry and digest list', es: 'Entrada a FindHelp y lista del digest' },
  'f5t1a.group.navegacion.note': { en: 'Units, timer, outreach — forms, not JSON', es: 'Unidades, reloj, outreach — formularios, no JSON' },
  'f5t1a.group.congeladas.note': { en: 'Shown so nobody hunts them in the database', es: 'Se muestran para que nadie las busque en la base' },
  'f5t1a.group.metas.warn': { en: 'Touches payroll', es: 'Toca nómina' },
  'f5t1a.group.navegacion.warn': { en: 'Touches billing', es: 'Toca facturación' },
  'f5t1a.mutedBanner': {
    en: 'T1b will add a required effective date, in-panel history and two confirmations for payroll and billing keys. Not on this screen.',
    es: 'T1b suma fecha de vigencia obligatoria, historial dentro del panel y dos confirmaciones para claves de nómina y facturación. No en esta pantalla.',
  },
  'f5t1a.loading': { en: 'Loading configuration…', es: 'Cargando configuración…' },
  'f5t1a.edit': { en: 'Edit', es: 'Editar' },
  'f5t1a.save': { en: 'Save', es: 'Guardar' },
  'f5t1a.cancel': { en: 'Cancel', es: 'Cancelar' },
  'f5t1a.currentValue': { en: 'Value in force today', es: 'Valor vigente hoy' },
  'f5t1a.newValue': { en: 'New value', es: 'Valor nuevo' },
  'f5t1a.yes': { en: 'On', es: 'Sí' },
  'f5t1a.no': { en: 'Off', es: 'No' },
  'f5t1a.emptyRoles': { en: 'No roles', es: 'Ningún rol' },
  'f5t1a.nobodyEdits': { en: 'Nobody edits this', es: 'Nadie la edita' },
  'f5t1a.tag.frozen': { en: 'Frozen', es: 'Congelada' },
  'f5t1a.tag.payroll': { en: 'Payroll', es: 'Nómina' },
  'f5t1a.tag.billing': { en: 'Billing', es: 'Facturación' },
  'f5t1a.dailyFolded': {
    en: 'This is the one daily target. daily_screening_target is the same number (folded, not a second row). STREAK 25 is no longer a source on this screen.',
    es: 'Esta es la meta diaria única. daily_screening_target es el mismo número (plegada, no es una segunda fila). El 25 de STREAK ya no es fuente en esta pantalla.',
  },
  'f5t1a.dailyDiverge': {
    en: 'agent_daily_target and daily_screening_target currently differ. This screen does not silently write either one.',
    es: 'agent_daily_target y daily_screening_target hoy difieren. Esta pantalla no escribe ninguna en silencio.',
  },
  'f5t1a.dupes.title': { en: 'The duplicates, resolved', es: 'Las duplicaciones, resueltas' },
  'f5t1a.dupes.dailyTitle': { en: 'Agent daily target.', es: 'Meta diaria del agente.' },
  'f5t1a.dupes.dailyBody': {
    en: 'There were two keys (and a hardcoded 25). The number shown is the live one. Plan default 15 was not written — live seed is 25.',
    es: 'Había dos claves (y un 25 hardcodeado). El número que se muestra es el vivo. El default del plan (15) no se escribió: el seed vivo es 25.',
  },
  'f5t1a.dupes.ladderTitle': { en: 'Level ladder.', es: 'Escalera de niveles.' },
  'f5t1a.dupes.ladderBody': {
    en: 'One monthly ladder. Weekly is “rhythm” — no metal names. Stored numbers are not changed.',
    es: 'Queda una, mensual. Lo semanal se llama «ritmo» y no tiene nombre de metal. Los números guardados no se tocan.',
  },
  'f5t1a.uncatalogued': { en: '{n} uncatalogued key(s) — they still load from the API.', es: '{n} clave(s) fuera de catálogo — igual llegan del API.' },
  'f5t1a.admin06.bridge': {
    en: 'Navigation keys are edited on Configuration (Admin 08), not as raw fields here.',
    es: 'Las claves de navegación se editan en Configuración (Admin 08), no como campos crudos acá.',
  },
  'f5t1a.admin06.cta': { en: 'Open Configuration · Navigation', es: 'Abrir Configuración · Navegación' },
  'f5t1a.unit.minutes': { en: 'minutes', es: 'minutos' },
  'f5t1a.unit.seconds': { en: 'seconds', es: 'segundos' },
  'f5t1a.unit.attempts': { en: 'attempts', es: 'intentos' },
  'f5t1a.unit.days': { en: 'days', es: 'días' },
  'f5t1a.unit.years': { en: 'years', es: 'años' },
  'f5t1a.unit.points': { en: 'points', es: 'puntos' },
  'f5t1a.unit.hours': { en: 'hours', es: 'horas' },
  'f5t1a.unit.screenings': { en: 'screenings / day', es: 'screenings / día' },
  'f5t1a.unit.uploads': { en: 'uploads', es: 'cargas' },
  'f5t1a.unit.units': { en: 'units', es: 'unidades' },
  'f5t1a.unit.followups': { en: 'count', es: 'conteo' },
  'f5t1a.unit.dayOfMonth': { en: 'day of month', es: 'día del mes' },
  'f5t1a.unit.weight': { en: 'points', es: 'puntos' },
  'f5t1a.rhythm.step1': { en: 'Step 1', es: 'Paso 1' },
  'f5t1a.rhythm.step2': { en: 'Step 2', es: 'Paso 2' },
  'f5t1a.rhythm.step3': { en: 'Step 3', es: 'Paso 3' },
  'f5t1a.metal.bronce': { en: 'Bronze', es: 'Bronce' },
  'f5t1a.metal.plata': { en: 'Silver', es: 'Plata' },
  'f5t1a.metal.oro': { en: 'Gold', es: 'Oro' },
  'f5t1a.metal.platino': { en: 'Platinum', es: 'Platino' },
  'f5t1a.timeBands.short': { en: 'morning / afternoon / evening', es: 'mañana / tarde / noche' },
  'f5t1a.band.manana': { en: 'Morning', es: 'Mañana' },
  'f5t1a.band.tarde': { en: 'Afternoon', es: 'Tarde' },
  'f5t1a.band.noche': { en: 'Evening', es: 'Noche' },
  'f5t1a.intArrayHint': { en: 'User ids, comma-separated', es: 'IDs de usuario, separados por coma' },
  'f5t1a.stringArrayHint': { en: 'Values, comma-separated', es: 'Valores, separados por coma' },
  'f5t1a.unsupported': { en: 'This field type has no form yet.', es: 'Este tipo de campo no tiene formulario todavía.' },
  'f5t1a.fact.tz_america_new_york': { en: 'Operations timezone', es: 'Zona horaria de operación' },
  'f5t1a.fact.tz_america_new_york.desc': {
    en: 'Hardcoded in the app. Defines the day cut for every daily count. Not a system_config row — shown so nobody looks it up in the database.',
    es: 'Está hardcodeada en la app. Define el corte del día para todos los conteos diarios. No es una fila de system_config: se muestra para que nadie la busque en la base.',
  },
  'f5t1a.fact.phi_cipher': { en: 'Patient-data cipher', es: 'Esquema de cifrado de datos del paciente' },
  'f5t1a.fact.phi_cipher.desc': {
    en: 'pgcrypto / app_encrypt. Changing it would make already-encrypted rows unreadable. Only via a planned migration.',
    es: 'pgcrypto / app_encrypt. Cambiarlo dejaría ilegible lo ya cifrado. Sólo por migración planificada.',
  },

  'f5t1a.key.session_timeout_min': { en: 'Idle minutes before sign-out', es: 'Minutos de inactividad para cerrar sesión' },
  'f5t1a.desc.session_timeout_min': { en: 'Applies to all five roles. Does not cut a call in progress.', es: 'Se aplica a los cinco roles. No corta una llamada en curso.' },
  'f5t1a.key.idle_threshold_sec': { en: 'Idle threshold', es: 'Umbral de inactividad' },
  'f5t1a.desc.idle_threshold_sec': { en: 'Seconds without a heartbeat before the agent is marked idle.', es: 'Segundos sin heartbeat antes de marcar al agente inactivo.' },
  'f5t1a.key.lockout_after_failed_logins': { en: 'Lockout after failed logins', es: 'Bloqueo tras inicios fallidos' },
  'f5t1a.desc.lockout_after_failed_logins': { en: 'Failed attempts before the account locks.', es: 'Intentos fallidos antes de bloquear la cuenta.' },
  'f5t1a.key.lockout_duration_min': { en: 'Lockout duration', es: 'Duración del bloqueo' },
  'f5t1a.desc.lockout_duration_min': { en: 'How long the lockout lasts.', es: 'Cuánto dura el bloqueo.' },
  'f5t1a.key.heartbeat_interval_sec': { en: 'Heartbeat interval', es: 'Intervalo de heartbeat' },
  'f5t1a.desc.heartbeat_interval_sec': { en: 'How often the live-status ping fires.', es: 'Cada cuánto corre el ping de estado en vivo.' },
  'f5t1a.key.max_call_attempts': { en: 'Max call attempts', es: 'Máx. intentos de llamada' },
  'f5t1a.desc.max_call_attempts': { en: 'Call attempts before the screening queue parks the patient.', es: 'Intentos de llamada antes de que la cola de screening parquee al paciente.' },
  'f5t1a.key.min_call_duration_sec': { en: 'Minimum call duration', es: 'Duración mínima de llamada' },
  'f5t1a.desc.min_call_duration_sec': { en: 'Seconds that count as a real call.', es: 'Segundos que cuentan como una llamada real.' },
  'f5t1a.key.rescreening_blackout_days': { en: 'Re-screening blackout', es: 'Bloqueo de re-screening' },
  'f5t1a.desc.rescreening_blackout_days': { en: 'Days before the same member can be screened again.', es: 'Días antes de poder volver a screener al mismo miembro.' },
  'f5t1a.key.phi_retention_years': { en: 'PHI retention', es: 'Retención de PHI' },
  'f5t1a.desc.phi_retention_years': { en: 'Years patient data is kept.', es: 'Años que se conservan los datos del paciente.' },
  'f5t1a.key.audit_retention_years': { en: 'Audit retention', es: 'Retención de auditoría' },
  'f5t1a.desc.audit_retention_years': { en: 'Years the audit feed is kept.', es: 'Años que se conserva el feed de auditoría.' },
  'f5t1a.key.safety_score_alert_threshold': { en: 'Safety alert score', es: 'Umbral de alerta de Safety' },
  'f5t1a.desc.safety_score_alert_threshold': { en: 'Score that raises a safety alert.', es: 'Puntaje que dispara una alerta de seguridad.' },
  'f5t1a.key.eligible_zip_prefix': { en: 'Eligible ZIP prefix', es: 'Prefijo de ZIP elegible' },
  'f5t1a.desc.eligible_zip_prefix': { en: 'Members outside this prefix are out of catchment.', es: 'Los miembros fuera de este prefijo quedan fuera de cobertura.' },
  'f5t1a.key.privacy_officer_user_id': { en: 'Privacy officer', es: 'Oficial de privacidad' },
  'f5t1a.desc.privacy_officer_user_id': { en: 'User who owns privacy-officer duties.', es: 'Usuario que tiene el rol de oficial de privacidad.' },
  'f5t1a.key.config_cache_ttl_sec': { en: 'Config cache TTL', es: 'TTL del caché de configuración' },
  'f5t1a.desc.config_cache_ttl_sec': { en: 'In-process cache; invalidated on every PATCH.', es: 'Caché en proceso; se invalida en cada PATCH.' },
  'f5t1a.key.uploader_sla_avg_time_to_upload_hours': { en: 'Uploader SLA · average hours to upload', es: 'SLA uploader · horas promedio hasta subir' },
  'f5t1a.desc.uploader_sla_avg_time_to_upload_hours': { en: 'Hours that count as the average-upload SLA.', es: 'Horas que cuentan como el SLA de promedio de subida.' },
  'f5t1a.key.uploader_sla_oldest_pending_hours': { en: 'Uploader SLA · oldest pending', es: 'SLA uploader · pendiente más vieja' },
  'f5t1a.desc.uploader_sla_oldest_pending_hours': { en: 'Hours before the oldest pending row is late.', es: 'Horas antes de que la fila pendiente más vieja esté atrasada.' },

  'f5t1a.key.agent_daily_target': { en: 'Agent daily target', es: 'Meta diaria del agente' },
  'f5t1a.desc.agent_daily_target': { en: 'The number shown to the agent and the one demanded. One row — not two.', es: 'El número que se muestra al agente y el que se exige. Una sola fila, no dos.' },
  'f5t1a.key.daily_screening_target': { en: 'Daily screening target (folded)', es: 'Meta diaria de screening (plegada)' },
  'f5t1a.desc.daily_screening_target': { en: 'Folded into the unique daily target. Not a second row.', es: 'Plegada en la meta diaria única. No es una segunda fila.' },
  'f5t1a.key.agent_tier_thresholds': { en: 'Monthly agent ladder', es: 'Escalera mensual del agente' },
  'f5t1a.desc.agent_tier_thresholds': { en: 'Bronze / Silver / Gold / Platinum as display names. Stored values stay as they are.', es: 'Bronce / Plata / Oro / Platino son nombres de display. Los valores guardados se quedan como están.' },
  'f5t1a.key.uploader_daily_target': { en: 'Uploader daily target', es: 'Meta diaria del uploader' },
  'f5t1a.desc.uploader_daily_target': { en: 'Motivational: does not change pay.', es: 'Motivacional: no afecta el pago.' },
  'f5t1a.key.uploader_tier_thresholds': { en: 'Uploader monthly ladder', es: 'Escalera mensual del uploader' },
  'f5t1a.desc.uploader_tier_thresholds': { en: 'Motivational labels. Not a pay ladder.', es: 'Etiquetas motivacionales. No es escalera de pago.' },
  'f5t1a.key.weekly_thresholds': { en: 'Weekly rhythm', es: 'Ritmo semanal' },
  'f5t1a.desc.weekly_thresholds': { en: 'Weekly pace. No metal names. Stored bronze/silver/gold keys are unchanged.', es: 'Ritmo de la semana. Sin nombres de metal. Las claves bronze/silver/gold guardadas no se tocan.' },
  'f5t1a.key.approval_thresholds': { en: 'Approval-rate steps', es: 'Pasos de tasa de aprobación' },
  'f5t1a.desc.approval_thresholds': { en: 'Percent steps. Shown as rhythm, not metals.', es: 'Pasos en porcentaje. Se muestran como ritmo, no como metales.' },
  'f5t1a.key.uploader_weekly_target': { en: 'Uploader weekly target', es: 'Meta semanal del uploader' },
  'f5t1a.desc.uploader_weekly_target': { en: 'Motivational weekly upload count.', es: 'Conteo semanal motivacional de subidas.' },
  'f5t1a.key.metrics_cycle_start_day': { en: 'Pay-cycle start day', es: 'Día de apertura del ciclo de pago' },
  'f5t1a.desc.metrics_cycle_start_day': { en: 'Day of month the 20th-to-19th style cycle opens (America/New_York).', es: 'Día del mes en que abre el ciclo estilo 20-al-19 (America/New_York).' },

  'f5t1a.key.nav_findhelp_base_url': { en: 'FindHelp URL', es: 'URL de FindHelp' },
  'f5t1a.desc.nav_findhelp_base_url': { en: 'HTTPS only. Empty leaves the FindHelp buttons inert with a note.', es: 'Sólo HTTPS. Vacía deja los botones FindHelp inertes con una nota.' },
  'f5t1a.key.findhelp_sla_digest_recipients': { en: 'FindHelp SLA digest recipients', es: 'Destinatarios del digest SLA de FindHelp' },
  'f5t1a.desc.findhelp_sla_digest_recipients': { en: 'User ids that receive the daily SLA digest. Empty = nobody.', es: 'IDs de usuario que reciben el digest diario de SLA. Vacío = nadie.' },

  'f5t1a.key.nav_assignment_weight_open_episode': { en: 'Assignment weight · open episode', es: 'Peso de asignación · episodio abierto' },
  'f5t1a.desc.nav_assignment_weight_open_episode': { en: 'Points per open episode in weighted auto-assign.', es: 'Puntos por episodio abierto en el auto-reparto ponderado.' },
  'f5t1a.key.nav_assignment_weight_overdue_followup': { en: 'Assignment weight · overdue follow-up', es: 'Peso de asignación · seguimiento vencido' },
  'f5t1a.desc.nav_assignment_weight_overdue_followup': { en: 'Points per overdue follow-up.', es: 'Puntos por seguimiento vencido.' },
  'f5t1a.key.nav_assignment_weight_upcoming_appointment': { en: 'Assignment weight · upcoming appointment', es: 'Peso de asignación · cita próxima' },
  'f5t1a.desc.nav_assignment_weight_upcoming_appointment': { en: 'Points per appointment inside the horizon.', es: 'Puntos por cita dentro del horizonte.' },
  'f5t1a.key.nav_assignment_appointment_horizon_days': { en: 'Assignment appointment horizon', es: 'Horizonte de citas para asignar' },
  'f5t1a.desc.nav_assignment_appointment_horizon_days': { en: 'Days ahead counted in navigator load.', es: 'Días hacia adelante que cuentan en la carga del navigator.' },
  'f5t1a.key.nav_ea_validity_days': { en: 'EA validity', es: 'Vigencia de la EA' },
  'f5t1a.desc.nav_ea_validity_days': { en: 'Days a completed EA stays valid.', es: 'Días que una EA completa sigue vigente.' },
  'f5t1a.key.nav_timer_idle_minutes': { en: 'Timer idle minutes', es: 'Minutos idle del reloj' },
  'f5t1a.desc.nav_timer_idle_minutes': { en: 'Auto-pause after this many idle minutes. Reaper-480 is still the only pay gate today (F-3).', es: 'Auto-pausa a tantos minutos idle. El reaper-480 sigue siendo la única vía de la compuerta hoy (F-3).' },
  'f5t1a.key.nav_timer_idle_espejo_minutes': { en: 'Idle minutes while mirroring to FindHelp', es: 'Minutos idle espejando a FindHelp' },
  'f5t1a.desc.nav_timer_idle_espejo_minutes': { en: 'Idle threshold that applies while the open segment is declared as mirroring to FindHelp — opening FindHelp is the declaration. Replaces the general threshold only for that segment (15-240).', es: 'Umbral de inactividad mientras el tramo está declarado como espejo a FindHelp — abrir FindHelp es la declaración. Reemplaza al umbral general sólo para ese tramo (15-240).' },
  'f5t1a.key.nav_timer_max_minutes': { en: 'Session minute cap', es: 'Tope de minutos por sesión' },
  'f5t1a.desc.nav_timer_max_minutes': { en: 'A session open longer than this closes itself. Caps billing, never the work.', es: 'Una sesión abierta más que esto se cierra sola. El tope limita el cobro, nunca el trabajo.' },
  'f5t1a.key.nav_timer_delta_threshold_minutes': { en: 'Measured-vs-reported delta', es: 'Delta medido versus declarado' },
  'f5t1a.desc.nav_timer_delta_threshold_minutes': { en: 'Minute gap that requires a reason.', es: 'Diferencia de minutos que exige un motivo.' },
  'f5t1a.key.nav_no_show_grace_minutes': { en: 'No-show grace', es: 'Gracia de no-show' },
  'f5t1a.desc.nav_no_show_grace_minutes': { en: 'Minutes after a navigation appointment before a no-show.', es: 'Minutos después de una cita de navegación antes de marcar no-show.' },
  'f5t1a.key.nav_general_followup_days': { en: 'General follow-up days', es: 'Días del seguimiento general' },
  'f5t1a.desc.nav_general_followup_days': { en: 'Target days after the first reimbursement submission.', es: 'Días objetivo después de la primera presentación de reembolso.' },
  'f5t1a.key.nav_referral_no_response_business_days': { en: 'Provider silence (business days)', es: 'Silencio del proveedor (días hábiles)' },
  'f5t1a.desc.nav_referral_no_response_business_days': { en: 'Business days without a provider reply before re-refer is offered.', es: 'Días hábiles sin respuesta del proveedor antes de proponer re-referir.' },
  'f5t1a.key.nav_auto_suggest_enabled': { en: 'Auto-create navigation cases', es: 'Crear casos de navegación solos' },
  'f5t1a.desc.nav_auto_suggest_enabled': { en: 'Kill switch for opening a case when a screening submits with a positive domain.', es: 'Interruptor para abrir un caso al enviar un screening con un dominio positivo.' },
  'f5t1a.key.nav_followup_continue_until_terminal': { en: 'Follow-ups continue until terminal', es: 'Seguimientos hasta un terminal' },
  'f5t1a.desc.nav_followup_continue_until_terminal': { en: 'Units are only the billing ceiling — follow-ups keep going.', es: 'Las unidades son sólo el techo de facturación: los seguimientos siguen.' },
  'f5t1a.key.nav_pool_enabled': { en: 'Appointment pool', es: 'Piscina de citas' },
  'f5t1a.desc.nav_pool_enabled': { en: 'Deprecated. Off means navigators schedule directly.', es: 'Deprecada. Apagada = el navigator agenda en directo.' },
  'f5t1a.key.nav_dual_role_enabled': { en: 'Dual-role / COI flow', es: 'Flujo de doble rol / COI' },
  'f5t1a.desc.nav_dual_role_enabled': { en: 'On = wizard demands freedom of choice before referral consent.', es: 'Encendido = el wizard exige libertad de elección antes del consentimiento de referido.' },
  'f5t1a.key.nav_unit_minutes': { en: 'Minutes per billing step', es: 'Minutos de un escalón de facturación' },
  'f5t1a.desc.nav_unit_minutes': { en: 'The session clock climbs a step every this many minutes.', es: 'El reloj de la sesión sube de escalón cada tantos minutos.' },
  'f5t1a.key.nav_min_billable_minutes': { en: 'Minimum billable minutes', es: 'Mínimo de minutos facturables' },
  'f5t1a.desc.nav_min_billable_minutes': { en: 'Below this a session bills zero units.', es: 'Por debajo de esto la sesión factura cero unidades.' },
  'f5t1a.key.nav_l1_max_units_event': { en: 'L1 units per event', es: 'Unidades L1 por evento' },
  'f5t1a.desc.nav_l1_max_units_event': { en: 'How many L1 units one event may declare. A count, not money.', es: 'Cuántas unidades L1 declara un evento. Es una cantidad, no un monto.' },
  'f5t1a.key.nav_l1_max_units_year': { en: 'L1 units per year', es: 'Unidades L1 por año' },
  'f5t1a.desc.nav_l1_max_units_year': { en: 'L1 cap per client per year.', es: 'Tope L1 por cliente por año.' },
  'f5t1a.key.nav_l2_max_units_month': { en: 'L2 units per month', es: 'Unidades L2 por mes' },
  'f5t1a.desc.nav_l2_max_units_month': { en: 'L2 cap per member per month.', es: 'Tope L2 por miembro por mes.' },
  'f5t1a.key.nav_outreach_attempts_required': { en: 'Outreach attempts before unreachable', es: 'Intentos de outreach antes de inubicable' },
  'f5t1a.desc.nav_outreach_attempts_required': { en: 'Attempts required before a case can close unreachable (SOMOS).', es: 'Intentos exigidos antes de poder cerrar por inubicable (SOMOS).' },
  'f5t1a.key.nav_outreach_window_days': { en: 'Outreach window', es: 'Ventana de outreach' },
  'f5t1a.desc.nav_outreach_window_days': { en: 'Business days to complete the required attempts.', es: 'Días hábiles para completar los intentos exigidos.' },
  'f5t1a.key.nav_outreach_max_attempts': { en: 'Attempts before parking a case', es: 'Intentos antes de parquear un caso' },
  'f5t1a.desc.nav_outreach_max_attempts': { en: 'At this many attempts without a reach, the episode parks. Reversible.', es: 'A tantos intentos sin contacto, el episodio se parquea. Reversible.' },
  'f5t1a.key.nav_outreach_time_bands': { en: 'Outreach time bands', es: 'Franjas horarias de outreach' },
  'f5t1a.desc.nav_outreach_time_bands': { en: 'New York hours used to prove two distinct bands before an unreachable close.', es: 'Horas de Nueva York para probar dos franjas distintas antes de un cierre por inubicable.' },
  'f5t1a.key.nav_insurance_carriers': { en: 'Insurance carriers offered', es: 'Aseguradoras que se ofrecen' },
  'f5t1a.desc.nav_insurance_carriers': { en: 'List shown when capturing insurance. Free text still accepted.', es: 'Lista que se ofrece al capturar el seguro. El texto libre sigue válido.' },
  'f5t1a.key.nav_preauth_required_services': { en: 'Services that need preauthorization', es: 'Servicios que exigen preautorización' },
  'f5t1a.desc.nav_preauth_required_services': { en: 'Categories that require a signed preauth before the service.', es: 'Categorías que exigen preautorización firmada antes del servicio.' },
  'f5t1a.key.nav_referral_stale_days': { en: 'Days without a provider reply (stale)', es: 'Días sin respuesta del proveedor (atraso)' },
  'f5t1a.desc.nav_referral_stale_days': { en: 'After this, the referral shows as stale in the navigator numbers.', es: 'Después de esto, el referido aparece atrasado en los números del navigator.' },
  'f5t1a.key.nav_untouched_case_days': { en: 'Days untouched before a cold case', es: 'Días sin tocar antes de marcar un caso frío' },
  'f5t1a.desc.nav_untouched_case_days': { en: 'Feeds the control-room “untouched” column and navigator delays.', es: 'Alimenta la columna «sin tocar» del control room y los atrasos del navigator.' },
  'f5t1a.key.nav_weekly_contact_goal': { en: 'Weekly contacts goal', es: 'Meta semanal de contactos' },
  'f5t1a.desc.nav_weekly_contact_goal': { en: 'My numbers: contacts made this week.', es: 'Mis números: contactos logrados esta semana.' },
  'f5t1a.key.nav_weekly_received_goal': { en: 'Weekly received-services goal', es: 'Meta semanal de servicios recibidos' },
  'f5t1a.desc.nav_weekly_received_goal': { en: 'My numbers: services confirmed as received.', es: 'Mis números: servicios confirmados como recibidos.' },
  'f5t1a.key.nav_weekly_completed_goal': { en: 'Weekly completed-episodes goal', es: 'Meta semanal de episodios completados' },
  'f5t1a.desc.nav_weekly_completed_goal': { en: 'My numbers: episodes closed completed.', es: 'Mis números: episodios cerrados completado.' },
  'f5t1a.key.nav_day_window_start': { en: 'Scheduling grid · first hour', es: 'Grilla de agenda · primera hora' },
  'f5t1a.desc.nav_day_window_start': { en: 'First New York hour offered when booking a follow-up.', es: 'Primera hora de Nueva York que ofrece la grilla al agendar un seguimiento.' },
  'f5t1a.key.nav_day_window_end': { en: 'Scheduling grid · last hour', es: 'Grilla de agenda · última hora' },
  'f5t1a.desc.nav_day_window_end': { en: 'Last New York hour offered (inclusive).', es: 'Última hora de Nueva York que ofrece la grilla (inclusive).' },
  'f5t1a.key.nav_day_shift_end': { en: 'Scheduling grid · end of shift', es: 'Grilla de agenda · fin de jornada' },
  'f5t1a.desc.nav_day_shift_end': { en: 'From this hour the slot is marked after-hours — still choosable.', es: 'Desde esta hora el turno se marca fuera de jornada — se puede elegir igual.' },

  'f5t1a.key.mfa_required_roles': { en: 'Roles that must enroll MFA', es: 'Roles que deben inscribir MFA' },
  'f5t1a.desc.mfa_required_roles': {
    en: 'The only system_config key the API refuses to PATCH (READONLY_KEYS). Empty array = MFA off. Editing it from the UI can lock everyone out.',
    es: 'La única clave de system_config que el API se niega a PATCH (READONLY_KEYS). Array vacío = MFA apagado. Editarla desde la UI puede dejar a todos afuera.',
  },

  // F5-T8 — Uploader 01 (shell y cola) + Uploader 03 (ID tardío) + variante
  // Supervisor (Preautorizaciones). Contratos 2026-08-28.
  'f5t8.up.title': { en: 'Upload to FindHelp', es: 'Cargar a FindHelp' },
  'f5t8.up.profile': { en: 'My profile', es: 'Mi perfil' },
  'f5t8.up.signOut': { en: 'Sign out', es: 'Cerrar sesión' },
  'f5t8.prog.kicker': { en: 'My progress · {m}', es: 'Mi progreso · {m}' },
  'f5t8.prog.monthNote': { en: 'The month is the calendar: the 1st to the 31st', es: 'El mes es el calendario: del 1 al 31' },
  'f5t8.prog.today': { en: 'Uploads today', es: 'Cargas de hoy' },
  'f5t8.prog.month': { en: 'Uploads this month', es: 'Cargas del mes' },
  'f5t8.prog.avg': { en: 'Average per day', es: 'Promedio por día' },
  'f5t8.prog.streak': { en: 'Active days in a row', es: 'Días seguidos activa' },
  'f5t8.prog.goal': { en: 'Daily goal · {n} uploads', es: 'Meta diaria · {n} cargas' },
  'f5t8.prog.note1': { en: 'These goals are', es: 'Estas metas son' },
  'f5t8.prog.noteBold': { en: 'motivational', es: 'motivacionales' },
  'f5t8.prog.note2': { en: ': they do not affect your pay. They are here so you know how your day is going, nothing more.', es: ': no afectan tu pago. Están acá para que sepas cómo viene tu día, nada más.' },
  'f5t8.tab.pending': { en: 'Pending', es: 'Pendientes' },
  'f5t8.tab.flagged': { en: 'Flagged', es: 'Marcados' },
  'f5t8.tab.uploaded': { en: 'Uploaded', es: 'Subidos' },
  'f5t8.tab.all': { en: 'All', es: 'Todos' },
  'f5t8.tab.household': { en: 'Household', es: 'Hogar' },
  'f5t8.claim.have': { en: 'You have claimed', es: 'Tenés reclamada' },
  'f5t8.claim.mid': { en: 'the row for {name}. It frees itself in', es: 'la fila de {name}. Se libera sola en' },
  'f5t8.claim.tail': { en: 'if you do not finish it.', es: 'si no la terminás.' },
  'f5t8.claim.release': { en: 'Release it', es: 'Liberarla' },
  'f5t8.claim.mineFor': { en: 'yours for {t}', es: 'tuyo por {t}' },
  'f5t8.claim.freesIn': { en: 'frees in {t}', es: 'se libera en {t}' },
  'f5t8.claim.lost': { en: 'it was released at {time}; claim it again if you are still on it', es: 'se liberó a las {time}; volvé a reclamarla si seguís' },
  'f5t8.claim.minutesLeft': { en: '{n} min of claim left', es: '{n} min de reclamo restantes' },
  'f5t8.row.overdue': { en: 'Overdue +24 h', es: 'Atrasado +24 h' },
  'f5t8.row.sent': { en: 'sent', es: 'enviado' },
  'f5t8.row.today': { en: 'Today', es: 'Hoy' },
  'f5t8.row.review': { en: 'Review', es: 'Revisar' },
  'f5t8.row.continue': { en: 'Continue', es: 'Seguir' },
  'f5t8.row.heldBy': { en: '{name} has it', es: 'Lo tiene {name}' },
  'f5t8.row.householdOf': { en: 'household of {parent}', es: 'hogar de {parent}' },
  'f5t8.pager.label': { en: '{shown} of {total} · page {p} of {tp}', es: '{shown} de {total} · página {p} de {tp}' },
  'f5t8.pager.prev': { en: 'Previous', es: 'Anterior' },
  'f5t8.pager.next': { en: 'Next', es: 'Siguiente' },
  'f5t8.empty.pending.title': { en: 'There is nothing to upload right now', es: 'No hay nada para cargar ahora mismo' },
  'f5t8.empty.pending.body': { en: 'New screenings land here on their own when the agents submit them.', es: 'Los screenings nuevos caen acá solos cuando las agentes los envían.' },
  'f5t8.empty.tab': { en: 'No rows in this tab.', es: 'No hay filas en esta pestaña.' },
  'f5t8.error.title': { en: 'The queue did not load', es: 'La cola no cargó' },
  'f5t8.error.body': { en: 'While it fails you cannot claim or upload; nothing is lost, it just is not visible.', es: 'Mientras falla no se puede reclamar ni cargar; no se pierde nada, solo no se ve.' },
  'f5t8.error.retry': { en: 'Retry', es: 'Reintentar' },
  'f5t8.legend.title': { en: 'The row color', es: 'El color de la fila' },
  'f5t8.legend.late': { en: 'Overdue more than 24 hours since the agent sent it', es: 'Atrasado más de 24 horas desde que el agente lo envió' },
  'f5t8.legend.mine': { en: 'You have it: claimed for 5 minutes', es: 'Lo tenés vos: reclamado por 5 minutos' },
  'f5t8.legend.other': { en: 'Someone else has it: it cannot be opened, and it says who', es: 'Lo tiene otra persona: no se puede abrir, y dice quién' },
  'f5t8.cin.title': { en: 'The Medicaid ID is missing', es: 'Falta el ID de Medicaid' },
  'f5t8.cin.aria': { en: 'Medicaid ID capture for {name}', es: 'Captura del ID de Medicaid de {name}' },
  'f5t8.cin.body': { en: '{name}. FindHelp does not create the record without the ID, and returning a whole screening over a nine-character field costs one more call.', es: '{name}. FindHelp no crea el registro sin el ID, y devolver el screening entero por un dato de nueve caracteres cuesta una llamada más.' },
  'f5t8.cin.label': { en: 'Medicaid ID', es: 'ID de Medicaid' },
  'f5t8.cin.hint.neutral': { en: 'Two letters, five digits and a final letter, as issued by New York State.', es: 'Dos letras, cinco dígitos y una letra al final, como lo emite el estado de Nueva York.' },
  'f5t8.cin.hint.letters': { en: 'It starts with two letters.', es: 'Empieza con dos letras.' },
  'f5t8.cin.hint.digits1': { en: '1 digit missing.', es: 'Falta 1 dígito.' },
  'f5t8.cin.hint.digitsN': { en: '{n} digits missing.', es: 'Faltan {n} dígitos.' },
  'f5t8.cin.hint.extra': { en: 'That does not match the format: two letters, five digits and a final letter.', es: 'No cierra con el formato: dos letras, cinco dígitos y una letra al final.' },
  'f5t8.cin.hint.tail': { en: 'The final letter is missing.', es: 'Falta la letra del final.' },
  'f5t8.cin.hint.valid': { en: 'The format checks out. FindHelp can still reject it if coverage is not active.', es: 'El formato cierra. FindHelp igual puede rechazarlo si la cobertura no está activa.' },
  'f5t8.cin.originLabel': { en: 'Where did you get it?', es: '¿De dónde lo sacaste?' },
  'f5t8.cin.origin.expediente': { en: 'From the batch file', es: 'Del expediente del lote' },
  'f5t8.cin.origin.expedienteDesc': { en: 'It was in the payer file this patient came in with.', es: 'Estaba en el archivo del pagador con el que entró este paciente.' },
  'f5t8.cin.origin.findhelp': { en: 'I found it in FindHelp', es: 'Lo encontré en FindHelp' },
  'f5t8.cin.origin.findhelpDesc': { en: 'The patient already had a record there and the ID shows on their profile.', es: 'El paciente ya tenía registro allá y el ID figura en su ficha.' },
  'f5t8.cin.origin.agente': { en: 'The agent passed it to me', es: 'Me lo pasó la agente' },
  'f5t8.cin.origin.agenteDesc': { en: 'They had it written down but it did not make it into the screening.', es: 'Lo tenía anotado pero no quedó en el screening.' },
  'f5t8.cin.audit': { en: 'It is recorded that you added this ID, with the time. The ID goes into the patient record; the rest of their data is not touched.', es: 'Queda registrado que vos agregaste este ID, con la hora. El ID entra en la ficha del paciente; el resto de sus datos no se toca.' },
  'f5t8.cin.return': { en: 'Return the screening instead', es: 'Devolver el screening en vez de esto' },
  'f5t8.cin.save': { en: 'Save the ID and continue', es: 'Guardar el ID y seguir' },
  'f5t8.cin.whyFormat': { en: 'Type an ID with a valid format', es: 'Escribí un ID con formato válido' },
  'f5t8.cin.whyOrigin': { en: 'Choose where you got it', es: 'Elegí de dónde lo sacaste' },
  'f5t8.cin.dupTitle': { en: 'That ID already belongs to another patient', es: 'Ese ID ya pertenece a otro paciente' },
  'f5t8.cin.dupBody': { en: 'It is the most expensive mistake: it is not saved. Return the screening so it gets corrected with the person on the phone.', es: 'Es el error más caro: no se guarda. Devolvé el screening para que se corrija con la persona al teléfono.' },
  'f5t8.cin.dupReturn': { en: 'Return the screening', es: 'Devolver el screening' },
  'f5t8.cin.closeAria': { en: 'Close', es: 'Cerrar' },
  'f5t8.sup.preauth': { en: 'Preauthorizations', es: 'Preautorizaciones' },
  'f5t8.sup.preauthKicker': { en: 'The only thing only the supervisor signs', es: 'Lo único que sólo firma el supervisor' },
  'f5t8.sup.preauthPending': { en: 'Under construction — the signing tray (list ordered by age plus an Approve/Deny panel with a required reason) is signed in the F5 contract and arrives with its API. This entry exists so the window has its place in the tree.', es: 'En construcción — la bandeja de firma (lista por antigüedad más el panel Aprobar/Denegar con motivo obligatorio) está firmada en el contrato F5 y llega con su API. Esta entrada existe para que la ventana tenga su lugar en el árbol.' },
  'f5t8.sup.preauthSemantics': { en: 'When approved, the navigator can mark the service as started and received. Preauthorization never blocked referring: it blocks marking started.', es: 'Al aprobar, el navigator puede marcar el servicio como iniciado y recibido. La preautorización nunca bloqueó referir: bloquea marcar iniciado.' },
  // ===== F5-T7 — Tanda 3 · Agente (Mi cola · Pre-llamada · Screening · Login/MFA) =====
  // El castellano es verbatim de los contratos Agente-01/02/03/05 donde el
  // dato vivo lo permite; el inglés es su traducción.

  // --- Agente 01 · Mi cola ---
  'agentF5.q.greet': { en: 'Good day, {name}', es: 'Buen día, {name}' },
  'agentF5.q.greetIdle': {
    en: '{appts} appointments agreed for today. Your queue has {queue} people not yet contacted.',
    es: '{appts} citas acordadas para hoy. Tu cola tiene {queue} personas sin contactar.',
  },
  'agentF5.q.greetActive': {
    en: 'You have an open call. Finish it before taking another: opening a new case closes the previous one.',
    es: 'Tenés una llamada abierta. Terminala antes de tomar otra: abrir un caso nuevo cierra el anterior.',
  },
  'agentF5.q.kickerIdle': { en: 'What follows', es: 'Lo que sigue' },
  'agentF5.q.kickerActive': { en: 'Open call · pick up where you left off', es: 'Llamada abierta · retomá donde quedaste' },
  'agentF5.q.heroTitleIdle': { en: 'Take the next one from your queue', es: 'Tomar el siguiente de tu cola' },
  'agentF5.q.heroTitleActiveDomain': { en: '{name} · domain {n} of {total}', es: '{name} · dominio {n} de {total}' },
  'agentF5.q.heroBodyIdle': {
    en: 'You will get the oldest uncontacted case in your queue, skipping those with an appointment and those out of attempts. {n} left.',
    es: 'Te va a tocar el más antiguo de tu cola sin contactar, salteando los que tienen cita agendada y los que agotaron los intentos. Quedan {n}.',
  },
  'agentF5.q.heroBodyActiveScreening': {
    en: 'You started {min} minutes ago. The screening saves itself; when you resume you return to the domain you were on.',
    es: 'Empezaste hace {min} minutos. El screening se guarda solo; al reanudar volvés al dominio donde estabas.',
  },
  'agentF5.q.heroBodyActivePrecall': {
    en: 'You have a case open in pre-call. Resume it to keep going.',
    es: 'Tenés un caso abierto en pre-llamada. Reanudalo para seguir.',
  },
  'agentF5.q.ctaIdle': { en: 'Take the next one', es: 'Tomar el siguiente' },
  'agentF5.q.ctaActive': { en: 'Resume', es: 'Reanudar' },
  'agentF5.q.ctaNoteIdle': {
    en: 'The name appears once the case is yours: that way what you see and what you get cannot differ.',
    es: 'El nombre aparece cuando el caso ya es tuyo: así lo que ves y lo que se te entrega no pueden diferir.',
  },
  'agentF5.q.ctaNoteActive': { en: 'While it is open, you cannot take another case.', es: 'Mientras esté abierta, no se puede tomar otro caso.' },
  'agentF5.q.emptyTitle': { en: 'No one left uncontacted in your queue', es: 'No queda nadie sin contactar en tu cola' },
  'agentF5.q.emptyBody': {
    en: 'When new cases arrive they will show up here.',
    es: 'Cuando entren casos nuevos van a aparecer acá.',
  },
  'agentF5.q.emptyCta': { en: 'See my cases with an appointment', es: 'Ver mis casos con cita' },
  'agentF5.q.metaLabel': { en: 'My target for today', es: 'Mi meta de hoy' },
  'agentF5.q.metaValue': { en: '{n} of {target} screenings', es: '{n} de {target} screenings' },
  'agentF5.q.metaNote': {
    en: 'It is the only daily target in the system: the number you see here is the one asked of you.',
    es: 'Es la única meta diaria del sistema: el número que ves acá es el que se te pide.',
  },
  'agentF5.q.metaDone': { en: 'You reached {target}; what follows counts toward the month.', es: 'Llegaste a {target}; lo que sigue suma al mes.' },
  'agentF5.q.amberKicker': { en: 'Needs action today', es: 'Exige acción hoy' },
  'agentF5.q.apptsTitle': { en: "Today's appointments", es: 'Citas de hoy' },
  'agentF5.q.apptOpen': { en: 'Open the appointment', es: 'Abrir la cita' },
  'agentF5.q.apptsFoot': {
    en: '"Open the appointment" opens the detail of this appointment, with the time and what was noted when it was booked.',
    es: '«Abrir la cita» abre el detalle de esta cita, con la hora y lo que quedó anotado al agendarla.',
  },
  'agentF5.q.apptMeta': { en: 'ID ****{id} · agreed {when}', es: 'ID ****{id} · acordada {when}' },
  'agentF5.q.whenToday': { en: 'today {time}', es: 'hoy {time}' },
  'agentF5.q.whenYesterday': { en: 'yesterday {time}', es: 'ayer {time}' },
  'agentF5.q.whenOn': { en: 'on {date}', es: 'el {date}' },
  'agentF5.q.pendTitle': { en: 'Waiting for FindHelp upload', es: 'Esperando subida a FindHelp' },
  'agentF5.q.pendBody': {
    en: '{n} household-member screenings are done but do not count yet: they are credited when the uploader loads them. Nothing for you to do; they are here so you know they are missing.',
    es: '{n} screenings de miembros del hogar están hechos pero todavía no cuentan: se acreditan cuando el uploader los carga. No hay nada que hagas vos; están acá para que sepas que faltan.',
  },
  'agentF5.q.pendBody1': {
    en: 'One household-member screening is done but does not count yet: it is credited when the uploader loads it. Nothing for you to do.',
    es: 'Un screening de miembro del hogar está hecho pero todavía no cuenta: se acredita cuando el uploader lo carga. No hay nada que hagas vos.',
  },
  'agentF5.q.pendSent': { en: 'sent {when}', es: 'enviado {when}' },
  'agentF5.q.dayKicker': { en: 'My day', es: 'Mi día' },
  'agentF5.q.tileContacts': { en: 'Contacts made out of attempts', es: 'Contactos logrados de los intentos' },
  'agentF5.q.tileSent': { en: 'Screenings sent today', es: 'Screenings enviados hoy' },
  'agentF5.q.tileTime': { en: 'Time on call (logged attempts)', es: 'Tiempo en llamada (intentos registrados)' },
  'agentF5.q.tilePending': { en: "Today's pending calls", es: 'Llamadas pendientes de hoy' },
  'agentF5.q.tileSentAria': { en: 'Open the list of screenings sent today', es: 'Abrir la lista de screenings enviados hoy' },
  'agentF5.q.monthKicker': { en: 'My month', es: 'Mi mes' },
  'agentF5.q.monthCount': { en: 'screenings this month', es: 'screenings del mes' },
  'agentF5.q.monthNext': { en: '{n} to go for {tier}.', es: 'Faltan {n} para {tier}.' },
  'agentF5.q.monthLadder': {
    en: 'The ladder is measured over the calendar month, and it is the only one in the system.',
    es: 'La escalera se mide sobre el mes calendario, y es la única del sistema.',
  },
  'agentF5.q.monthTop': { en: 'Top level of the month reached.', es: 'Nivel máximo del mes alcanzado.' },
  'agentF5.q.weekTitle': { en: 'This week´s pace', es: 'Ritmo de esta semana' },
  'agentF5.q.weekOf': { en: 'of {target} · {pct}%', es: 'de {target} · {pct}%' },
  'agentF5.q.weekNote': {
    en: 'The week has no level: it is pace, as a count. «Bronce» and «Plata» belong to the month, and to nothing else.',
    es: 'La semana no tiene nivel: es ritmo, en conteo. «Bronce» y «Plata» son del mes, y de nada más.',
  },
  'agentF5.q.searchKicker': { en: 'Find a case', es: 'Buscar un caso' },
  'agentF5.q.searchPlaceholder': { en: 'Full Medicaid ID or full phone number', es: 'ID de Medicaid completo o teléfono completo' },
  'agentF5.q.searchAria': { en: 'Search case by Medicaid ID or phone', es: 'Buscar caso por ID de Medicaid o teléfono' },
  'agentF5.q.searchClear': { en: 'Clear', es: 'Limpiar' },
  'agentF5.q.searchOpen': { en: 'Open', es: 'Abrir' },
  'agentF5.q.searchNoteIdle': {
    en: 'Search takes a full Medicaid ID or a full phone number: there is no partial or name search. Useful when someone calls back.',
    es: 'Se busca por ID de Medicaid completo o teléfono completo: no hay búsqueda por nombre ni parcial. Sirve cuando alguien devuelve la llamada.',
  },
  'agentF5.q.searchNoteResults': { en: '{n} matches.', es: '{n} coincidencias.' },
  'agentF5.q.searchNoteOne': { en: 'One match.', es: 'Una coincidencia.' },
  'agentF5.q.searchNoteNone': {
    en: 'No matches. There is no partial or name search: the ID or the phone must be complete.',
    es: 'Sin coincidencias. No hay búsqueda parcial ni por nombre: el ID o el teléfono tienen que estar completos.',
  },
  'agentF5.q.searchMeta': { en: 'ID ****{id} · {dob}', es: 'ID ****{id} · {dob}' },
  'agentF5.q.citaKicker': { en: 'Appointment', es: 'Cita' },
  'agentF5.q.citaTime': { en: 'Time', es: 'Hora' },
  'agentF5.q.citaNotes': { en: 'Noted when booked', es: 'Lo que quedó anotado al agendarla' },
  'agentF5.q.citaNoNotes': { en: 'No notes were taken when booking.', es: 'Sin notas al agendar.' },
  'agentF5.q.citaProfile': { en: 'View profile', es: 'Ver el perfil' },
  'agentF5.q.citaClose': { en: 'Close', es: 'Cerrar' },
  'agentF5.q.errTitle': { en: 'The queue did not load', es: 'La cola no cargó' },
  'agentF5.q.errBody': {
    en: 'Your queue and counts are not visible while this fails. Nothing was lost.',
    es: 'Mientras falla no se ven tu cola ni tus conteos. No se perdió nada.',
  },
  'agentF5.q.errRetry': { en: 'Try again', es: 'Probar de nuevo' },

  // --- Agente 02 · Pre-llamada ---
  'agentF5.pc.chipNoTimer': { en: 'No stopwatch: the clock starts when they answer', es: 'Sin cronómetro: el reloj arranca cuando contesta' },
  'agentF5.pc.headerMeta': { en: '{age} yrs · {dob} · ID {cin} · in your queue since {date}', es: '{age} años · {dob} · ID {cin} · en tu cola desde {date}' },
  'agentF5.pc.sec1': { en: '1 · Verification', es: '1 · Verificación' },
  'agentF5.pc.manualLabel': { en: 'These you confirm yourself', es: 'Estos los confirmás vos' },
  'agentF5.pc.mMedicaid': { en: 'I confirmed active Medicaid', es: 'Confirmé el Medicaid activo' },
  'agentF5.pc.mMedicaidDesc': { en: 'You verified it in the state portal or the record, today.', es: 'Lo verificaste en el portal del estado o en el expediente, hoy.' },
  'agentF5.pc.mRecording': { en: 'I gave the recording notice', es: 'Di el aviso de grabación' },
  'agentF5.pc.mRecordingDesc': { en: 'You must say it when the call starts, before the first question.', es: 'Se lo tenés que decir al empezar la llamada, antes de la primera pregunta.' },
  'agentF5.pc.sec2': { en: '2 · Language and start', es: '2 · Idioma y arranque' },
  'agentF5.pc.langNoteEs': {
    en: 'The script and the interface go in Spanish. You can switch mid-call without losing answers.',
    es: 'El guion y la interfaz van en español. Podés cambiarlo a mitad de la llamada sin perder respuestas.',
  },
  'agentF5.pc.langNoteEn': {
    en: 'The script and the interface go in English. You can switch mid-call without losing answers.',
    es: 'El guion y la interfaz van en inglés. Podés cambiarlo a mitad de la llamada sin perder respuestas.',
  },
  'agentF5.pc.langNoteOther': {
    en: 'Another language: the interface stays as it is; log a language barrier if you cannot continue.',
    es: 'Otro idioma: la interfaz queda como está; registrá barrera de idioma si no podés seguir.',
  },
  'agentF5.pc.start': { en: 'Start screening', es: 'Comenzar screening' },
  'agentF5.pc.starting': { en: 'Starting…', es: 'Comenzando…' },
  'agentF5.pc.diagTitle1': { en: 'One thing missing to start', es: 'Falta una cosa para empezar' },
  'agentF5.pc.diagTitleN': { en: '{n} things missing to start', es: 'Faltan {n} cosas para empezar' },
  'agentF5.pc.ready': {
    en: 'All set. When you start, the clock starts and the screening saves itself on every domain.',
    es: 'Todo listo. Al empezar, el reloj arranca y el screening se guarda solo en cada dominio.',
  },
  'agentF5.pc.ifNoTalk': { en: 'If you do not get to talk to the person:', es: 'Si no llegás a hablar con la persona:' },
  'agentF5.pc.logAttempt': { en: 'Log the attempt', es: 'Registrar el intento' },
  'agentF5.pc.phonesTitle': { en: 'Phones and address', es: 'Teléfonos y dirección' },
  'agentF5.pc.chipFix': { en: 'Fixed right here', es: 'Se corrige acá mismo' },
  'agentF5.pc.kindCell': { en: 'Cell', es: 'Celular' },
  'agentF5.pc.kindHome': { en: 'Home', es: 'Fijo' },
  'agentF5.pc.kindOther': { en: 'Other', es: 'Otro' },
  'agentF5.pc.kindAddress': { en: 'Address', es: 'Dirección' },
  'agentF5.pc.kindLang': { en: 'Preferred language on file', es: 'Idioma preferido en ficha' },
  'agentF5.pc.principal': { en: 'Primary', es: 'Principal' },
  'agentF5.pc.editAria': { en: 'Edit {kind}', es: 'Editar {kind}' },
  'agentF5.pc.noPhone': { en: 'No phone on file', es: 'Sin teléfono en ficha' },
  'agentF5.pc.attTitle': { en: 'Previous attempts · {n}', es: 'Intentos anteriores · {n}' },
  'agentF5.pc.attSub': {
    en: 'When attempts run out without contact, the case parks itself. Parked is reversible and it is still yours.',
    es: 'Cuando se agotan los intentos sin contacto, el caso se parquea solo. Parqueado es reversible y sigue siendo tuyo.',
  },
  'agentF5.pc.attFootMore': { en: 'The last 3 are shown; the rest lives in the case.', es: 'Se muestran los 3 últimos; el resto vive en el caso.' },
  'agentF5.pc.amTitle': { en: 'What happened with the call?', es: '¿Qué pasó con la llamada?' },
  'agentF5.pc.amSub': { en: '{name} · attempt {n}.', es: '{name} · intento {n}.' },
  'agentF5.pc.o1': { en: 'No answer', es: 'No contestó' },
  'agentF5.pc.o1d': { en: 'It rings and nobody picks up. Counts as an attempt.', es: 'Suena y nadie atiende. Cuenta como intento.' },
  'agentF5.pc.o2': { en: 'Went to voicemail', es: 'Cayó en el buzón' },
  'agentF5.pc.o2d': {
    en: 'Answering machine. Counts as an attempt; no message is left if the case has the do-not-leave-message mark.',
    es: 'Contestador. Cuenta como intento; no se deja mensaje si el caso tiene la marca de no dejar mensaje.',
  },
  'agentF5.pc.o3': { en: 'Wrong number', es: 'Número equivocado' },
  'agentF5.pc.o3d': { en: 'Someone else answers. The number is marked as invalid on the record.', es: 'Atiende otra persona. El número queda marcado como no válido en la ficha.' },
  'agentF5.pc.o4': { en: 'Refused to participate', es: 'Se negó a participar' },
  'agentF5.pc.o4d': { en: 'The person does not want the screening.', es: 'La persona no quiere el screening.' },
  'agentF5.pc.o4chip': { en: 'Never call again', es: 'No llamar nunca más' },
  'agentF5.pc.o5': { en: 'Language barrier', es: 'Barrera de idioma' },
  'agentF5.pc.o5d': {
    en: 'We do not speak their language. The case goes back to the queue to be reassigned to someone who does.',
    es: 'No hablamos su idioma. El caso vuelve a la cola para reasignarlo a alguien que sí.',
  },
  'agentF5.pc.o6': { en: 'Not eligible', es: 'No es elegible' },
  'agentF5.pc.o6d': { en: 'No active Medicaid coverage in the program.', es: 'No tiene cobertura de Medicaid activa en el programa.' },
  'agentF5.pc.o6chip': { en: 'Only an admin can revert it', es: 'Sólo un admin lo revierte' },
  'agentF5.pc.o7': { en: 'Wants a call at another time', es: 'Quiere que la llame en otro momento' },
  'agentF5.pc.o7d': {
    en: 'An appointment is booked. It does not count as a failed attempt and the appointment stays under your name.',
    es: 'Se agenda una cita. No cuenta como intento fallido y la cita queda a tu nombre.',
  },
  'agentF5.pc.hardWarnRefused': {
    en: 'On save, this member gets a permanent do-not-call mark: nobody on the team can contact them again, and the case closes. It cannot be undone from any screen.',
    es: 'Al guardar, este miembro queda con marca de no llamar permanente: nadie del equipo puede volver a contactarlo, y el caso se cierra. No se deshace desde ninguna pantalla.',
  },
  'agentF5.pc.hardWarnInelig': {
    en: 'On save, the case closes for ineligibility and leaves every queue. Only an admin can revert it.',
    es: 'Al guardar, el caso se cierra por inelegibilidad y sale de todas las colas. Sólo un admin puede revertirlo.',
  },
  'agentF5.pc.optKicker': { en: 'Optional · if you have it at hand', es: 'Opcional · si lo tenés a mano' },
  'agentF5.pc.optNote': {
    en: 'None of this is required. It helps the next attempt know what to try.',
    es: 'Nada de esto es obligatorio. Sirve para que el próximo intento sepa qué probar.',
  },
  'agentF5.pc.optPhone': { en: 'Phone you used', es: 'Teléfono que usaste' },
  'agentF5.pc.optDur': { en: 'Duration', es: 'Duración' },
  'agentF5.pc.optNoteLabel': { en: 'Note for the next attempt', es: 'Nota para el próximo intento' },
  'agentF5.pc.optNotePh': { en: 'E.g.: answers better after 6', es: 'Ej.: contesta mejor después de las 6' },
  'agentF5.pc.amCancel': { en: 'Cancel', es: 'Cancelar' },
  'agentF5.pc.amSave': { en: 'Save the attempt', es: 'Guardar el intento' },
  'agentF5.pc.amSaveHard': { en: 'Save and close the case', es: 'Guardar y cerrar el caso' },
  'agentF5.pc.amHintPick': { en: 'Pick what happened with the call', es: 'Elegí qué pasó con la llamada' },
  'agentF5.pc.amHintHard': { en: 'You will see a confirmation before saving', es: 'Vas a ver una confirmación antes de guardar' },
  'agentF5.pc.amConfirmKicker': { en: 'Confirmation', es: 'Confirmación' },
  'agentF5.pc.amConfirmRefused': { en: 'Yes, close and never call again', es: 'Sí, cerrar y no llamar nunca más' },
  'agentF5.pc.amBack': { en: 'Go back', es: 'Volver' },
  'agentF5.pc.mmTitle': { en: 'Your active call is another one', es: 'Tu llamada activa es otra' },
  'agentF5.pc.mmBody': {
    en: 'You opened another pre-call, but you have an open call with {current}. Opening this one would close that one and what you have would be lost.',
    es: 'Abriste otra pre-llamada, pero tenés una llamada abierta con {current}. Abrir esta cerraría esa llamada y se perdería lo que llevás.',
  },
  'agentF5.pc.oblChip': { en: 'Obligation met at the 3rd', es: 'Obligación cumplida al 3.º' },
  // R2 — lista admin de intentos agotados (caminata CEO 2026-08-28)
  'admin.exhausted.title': { en: 'Exhausted attempts ({n})', es: 'Intentos agotados ({n})' },
  'admin.exhausted.capNote': { en: 'Cap: {max} attempts (Configuration)', es: 'Tope: {max} intentos (Configuración)' },
  'admin.exhausted.body': { en: 'Patients at the cap: out of the agent queue, out of take-next, out of reassignment. They live here, admin-only.', es: 'Pacientes en el tope: fuera de la cola del agente, del tomar-siguiente y de la reasignación. Viven acá, solo admin.' },
  'admin.exhausted.empty': { en: 'Nobody at the cap right now.', es: 'Nadie en el tope ahora mismo.' },
  'admin.exhausted.colAttempts': { en: 'Attempts', es: 'Intentos' },
  'admin.exhausted.colLast': { en: 'Last attempt', es: 'Último intento' },
  'admin.exhausted.colAgent': { en: 'Assigned agent', es: 'Agente asignada' },
  'admin.exhausted.prev': { en: 'Previous', es: 'Anterior' },
  'admin.exhausted.next': { en: 'Next', es: 'Siguiente' },
  'admin.exhausted.exportNote': { en: 'Full-identity download: the audited call-outcomes export below (exhausted filter).', es: 'La descarga con identidad completa: el export auditado de resultados de llamada de abajo (filtro de agotados).' },
  'agentF5.q.viewBookings': { en: 'My appointments ({n} today)', es: 'Mis citas ({n} hoy)' },
  'agentF5.pc.exhaustedToastT': { en: 'Attempts exhausted', es: 'Intentos agotados' },
  'agentF5.pc.exhaustedToastB': { en: 'This patient left your queue and reassignment; admin keeps them on the exhausted list.', es: 'Este paciente sali\u00f3 de tu cola y de la reasignaci\u00f3n; admin lo conserva en la lista de agotados.' },
  'agentF5.pc.capLine': { en: 'Cap: {max} attempts — at the cap this patient leaves your queue and reassignment, and lands on the admin exhausted list.', es: 'Tope: {max} intentos — al llegar, este paciente sale de tu cola y de la reasignación, y queda en la lista admin de agotados.' },
  'agentF5.pc.autoFailTitle': { en: 'Automatic checks not passing', es: 'Chequeos automáticos sin pasar' },
  'agentF5.pc.mmBack': { en: 'Back to my call', es: 'Volver a mi llamada' },
  'agentF5.pc.mmSwitch': { en: 'Cancel mine and open this one', es: 'Cancelar la mía y abrir esta' },
  'agentF5.pc.mmQueue': { en: 'Back to the queue', es: 'Volver a la cola' },
  'agentF5.pc.autoTag': { en: 'auto', es: 'auto' },
  'agentF5.pc.bookTitle': { en: 'Book the appointment', es: 'Agendar la cita' },

  // --- Agente 03 · Screening ---
  'agentF5.sc.stepLiving': { en: 'Housing', es: 'Vivienda' },
  'agentF5.sc.stepConditions': { en: 'Conditions', es: 'Condiciones' },
  'agentF5.sc.stepUtilities': { en: 'Utilities', es: 'Servicios' },
  'agentF5.sc.stepFood': { en: 'Food', es: 'Alimentación' },
  'agentF5.sc.stepTransport': { en: 'Transport', es: 'Transporte' },
  'agentF5.sc.stepCareer': { en: 'Career', es: 'Carrera' },
  'agentF5.sc.stepSchool': { en: 'Education', es: 'Educación' },
  'agentF5.sc.stepSafety': { en: 'Safety', es: 'Seguridad' },
  'agentF5.sc.stepPhysical': { en: 'Physical', es: 'Físico' },
  'agentF5.sc.stepHome': { en: 'Household', es: 'Hogar' },
  'agentF5.sc.domainLine': { en: 'Domain {n} of 9 · {title}', es: 'Dominio {n} de 9 · {title}' },
  'agentF5.sc.rangeEnter': { en: 'You entered {range}', es: 'Entraste en {range}' },
  'agentF5.sc.r1': { en: 'under 15 min', es: 'menos de 15 min' },
  'agentF5.sc.r2': { en: '15-20 min', es: '15-20 min' },
  'agentF5.sc.r3': { en: '20-30 min', es: '20-30 min' },
  'agentF5.sc.r4': { en: 'over 30 min', es: 'más de 30 min' },
  'agentF5.sc.safeNone': { en: 'No signals', es: 'Sin señales' },
  'agentF5.sc.safeResp': { en: 'Alert · high-risk response', es: 'Alerta · respuesta de alto riesgo' },
  'agentF5.sc.safeScore': { en: 'Alert · aggregate score', es: 'Alerta · puntaje' },
  'agentF5.sc.exit': { en: 'Exit without sending', es: 'Salir sin enviar' },
  'agentF5.sc.next': { en: 'Next · {name}', es: 'Siguiente · {name}' },
  'agentF5.sc.exTitle': { en: 'Exiting cancels this screening', es: 'Salir cancela este screening' },
  'agentF5.sc.exKicker': { en: 'What is lost', es: 'Qué se pierde' },
  'agentF5.sc.exDomains': { en: '{n} of 9 domains already answered.', es: '{n} de 9 dominios ya respondidos.' },
  'agentF5.sc.exMembers': { en: 'The household members you captured, with their sub-screenings.', es: 'Los miembros del hogar que capturaste, con sus sub-screenings.' },
  'agentF5.sc.exMinutes': { en: 'The {m} minutes of this call: they do not count as a sent screening.', es: 'Los {m} minutos de esta llamada: no cuentan como screening enviado.' },
  'agentF5.sc.exFoot': {
    en: '{name} stays in your queue with one more attempt spent. If you can, close the screening with what you have instead of exiting.',
    es: '{name} queda en tu cola con un intento más gastado. Si podés, cerrá el screening con lo que tengas en vez de salir.',
  },
  'agentF5.sc.exStay': { en: 'Continue the screening', es: 'Seguir con el screening' },
  'agentF5.sc.exLeave': { en: 'Exit and lose everything', es: 'Salir y perder todo' },
  'agentF5.sc.saving': { en: 'Saving…', es: 'Guardando…' },
  'agentF5.sc.saved': { en: 'Saved', es: 'Guardado' },
  'agentF5.sc.saveFail': { en: 'Not saved — retrying as you edit', es: 'No se guardó — se reintenta mientras editás' },
  'agentF5.sc.doneTitle': { en: 'Sent', es: 'Enviado' },
  'agentF5.sc.doneSub': { en: "{name}'s screening is now in the uploader's queue.", es: 'El screening de {name} ya está en la cola del uploader.' },
  'agentF5.sc.doneSr': { en: 'Screening sent. You are at {n} of {target} today.', es: 'Screening enviado. Vas {n} de {target} hoy.' },
  'agentF5.sc.stat1': { en: 'Screenings today', es: 'Screenings de hoy' },
  'agentF5.sc.stat2': { en: 'Positive domains', es: 'Dominios positivos' },
  'agentF5.sc.stat3': { en: 'Members awaiting upload', es: 'Miembros esperando subida' },
  'agentF5.sc.doneCta': { en: 'Take the next one', es: 'Tomar el siguiente' },

  // --- Agente 05 · Login e inscripción MFA ---
  'agentF5.lg.forgotText': {
    en: 'Forgot your password? Ask an admin for the reset.',
    es: '¿Olvidaste tu contraseña? Pedile el reseteo a un admin.',
  },
  'agentF5.lg.lockedBtn': { en: 'Locked · wait a few minutes', es: 'Bloqueada · esperá unos minutos' },
  'agentF5.mfa.kicker': { en: 'Second factor', es: 'Segundo factor' },
  'agentF5.mfa.title': { en: 'Enroll your second factor', es: 'Inscribí tu segundo factor' },
  'agentF5.mfa.sub': {
    en: 'One time only. After this you will sign in with your password and a code from the app.',
    es: 'Una vez sola. Después vas a entrar con tu contraseña y un código de la app.',
  },
  'agentF5.mfa.qrAria': { en: 'QR code to enroll the second factor', es: 'Código QR para inscribir el segundo factor' },
  'agentF5.mfa.scanHint': { en: 'Scan it with Google Authenticator, Microsoft Authenticator or the one you use.', es: 'Escanealo con Google Authenticator, Microsoft Authenticator o el que uses.' },
  'agentF5.mfa.manualHint': { en: 'If you cannot scan, type this code by hand:', es: 'Si no podés escanear, escribí este código a mano:' },
  'agentF5.mfa.copy': { en: 'Copy', es: 'Copiar' },
  'agentF5.mfa.copied': { en: 'Copied', es: 'Copiado' },
  'agentF5.mfa.codeLabel': { en: 'Code the app shows', es: 'Código que muestra la app' },
  'agentF5.mfa.codeHint': {
    en: 'With this we confirm it was enrolled correctly. You will need the app every time you sign in.',
    es: 'Con esto confirmamos que quedó bien inscripto. Vas a necesitar la app cada vez que entres.',
  },
  'agentF5.mfa.confirm': { en: 'Confirm and enroll', es: 'Confirmar e inscribir' },
  'agentF5.mfa.confirming': { en: 'Confirming…', es: 'Confirmando…' },
  'agentF5.mfa.cancel': { en: 'Cancel', es: 'Cancelar' },
  'agentF5.mfa.genFail': { en: 'Could not generate the enrollment', es: 'No se pudo generar la inscripción' },
  'agentF5.mfa.verifyFail': { en: 'Could not verify the code', es: 'No se pudo verificar el código' },

  // F5-R1a — ronda de correcciones post-caminata del CEO (2026-08-28):
  // «Ver mi cola» de vuelta tras botón · franja FindHelp degradada al pie ·
  // CompletedListModal por i18n (estaba clavado en inglés). ES canónico.
  // -- «Ver mi cola» (encargo 2)
  'agentF5.r1a.viewQueue': { en: 'View my queue ({n})', es: 'Ver mi cola ({n})' },
  'agentF5.r1a.queueModalTitle': { en: 'My queue', es: 'Mi cola' },
  'agentF5.r1a.queueModalCount': { en: '{n} patients waiting', es: '{n} pacientes pendientes' },
  'agentF5.r1a.queueModalCountOne': { en: '1 patient waiting', es: '1 paciente pendiente' },
  'agentF5.r1a.queueModalHint': {
    en: 'Tap a row to open that patient. With a call open, the list is read-only.',
    es: 'Tocá una fila para abrir ese paciente. Con llamada abierta, la lista es solo lectura.',
  },
  'agentF5.r1a.queueEmptyTitle': { en: 'Your queue is empty', es: 'No hay pacientes en tu cola' },
  'agentF5.r1a.queueEmptyBody': {
    en: 'When patients are assigned to you they will show up here.',
    es: 'Cuando te asignen pacientes van a aparecer acá.',
  },
  'agentF5.r1a.queueErrTitle': { en: 'The queue did not load', es: 'No se pudo cargar la cola' },
  'agentF5.r1a.queueErrBody': {
    en: 'The list is not available right now. Your assignments are intact.',
    es: 'La lista no está disponible ahora mismo. Tus asignaciones siguen intactas.',
  },
  'agentF5.r1a.queueRetry': { en: 'Retry', es: 'Reintentar' },
  'agentF5.r1a.rowAttempts': { en: '{n} attempts', es: '{n} intentos' },
  'agentF5.r1a.rowAttemptsOne': { en: '1 attempt', es: '1 intento' },
  'agentF5.r1a.pagerPrev': { en: 'Previous', es: 'Anterior' },
  'agentF5.r1a.pagerNext': { en: 'Next', es: 'Siguiente' },
  'agentF5.r1a.pagerPage': { en: 'Page {p} of {pp}', es: 'Página {p} de {pp}' },
  // -- Franja FindHelp degradada (encargo 3)
  'agentF5.r1a.fhPendingToggle': {
    en: 'Waiting for FindHelp upload ({n})',
    es: 'Esperando subida a FindHelp ({n})',
  },
  'agentF5.r1a.fhPendingNote': {
    en: 'The uploader team handles the upload — this is informational only.',
    es: 'La subida la hace el equipo uploader — esto es solo informativo.',
  },
  // -- CompletedListModal (encargo 4: al brand Y por i18n)
  'agentF5.r1a.cl.title': { en: 'Completed screenings', es: 'Screenings completados' },
  'agentF5.r1a.cl.tabAll': { en: 'All', es: 'Todos' },
  'agentF5.r1a.cl.tabNeedsNav': { en: 'Needs navigation', es: 'Necesita navegación' },
  'agentF5.r1a.cl.tabNoFindings': { en: 'No findings', es: 'Sin hallazgos' },
  'agentF5.r1a.cl.empty': {
    en: 'No completed screenings match this filter yet.',
    es: 'Ningún screening completado coincide con este filtro todavía.',
  },
  'agentF5.r1a.cl.errLoad': { en: "Couldn't load", es: 'No se pudo cargar' },
  'agentF5.r1a.cl.loading': { en: 'Loading…', es: 'Cargando…' },
  'agentF5.r1a.cl.findingOne': { en: '1 finding', es: '1 hallazgo' },
  'agentF5.r1a.cl.findingMany': { en: '{n} findings', es: '{n} hallazgos' },
  'agentF5.r1a.cl.clearChip': { en: 'Clear', es: 'Limpio' },
  'agentF5.r1a.cl.safetyFlagged': { en: 'Safety concern flagged', es: 'Alerta de seguridad marcada' },
  'agentF5.r1a.cl.hasFindings': { en: 'Has positive findings', es: 'Tiene hallazgos positivos' },
  'agentF5.r1a.cl.needsNavBanner1': {
    en: 'Needs navigation: 1 positive finding — {domains}',
    es: 'Necesita navegación: 1 hallazgo positivo — {domains}',
  },
  'agentF5.r1a.cl.needsNavBannerN': {
    en: 'Needs navigation: {n} positive findings — {domains}',
    es: 'Necesita navegación: {n} hallazgos positivos — {domains}',
  },
  'agentF5.r1a.cl.noFindingsBanner': {
    en: 'No positive findings — member completed screening, no immediate navigation needed.',
    es: 'Sin hallazgos positivos — el miembro completó el screening, no necesita navegación inmediata.',
  },
  'agentF5.r1a.cl.duration': { en: 'Duration', es: 'Duración' },
  'agentF5.r1a.cl.timeBucket': { en: 'Time bucket', es: 'Bloque de tiempo' },
  'agentF5.r1a.cl.navigatorQ': { en: 'Navigator?', es: '¿Navegación?' },
  'agentF5.r1a.cl.yes': { en: 'Yes', es: 'Sí' },
  'agentF5.r1a.cl.no': { en: 'No', es: 'No' },
  'agentF5.r1a.cl.fhSubmitted': { en: '✓ Submitted', es: '✓ Subido' },
  'agentF5.r1a.cl.fhPending': { en: 'Pending', es: 'Pendiente' },
  'agentF5.r1a.cl.contact': { en: 'Contact for follow-up', es: 'Contacto para seguimiento' },
  'agentF5.r1a.cl.phoneCell': { en: 'Cell', es: 'Celular' },
  'agentF5.r1a.cl.phoneHome': { en: 'Home', es: 'Casa' },
  'agentF5.r1a.cl.phoneOther': { en: 'Other', es: 'Otro' },
  'agentF5.r1a.cl.domainAnswers': { en: 'Domain answers', es: 'Respuestas por dominio' },
  'agentF5.r1a.cl.positive': { en: 'Positive', es: 'Positivo' },
  'agentF5.r1a.cl.safetyScore': {
    en: 'Safety total score: {n} — flagged for supervisor review.',
    es: 'Puntaje total de seguridad: {n} — marcado para revisión del supervisor.',
  },
  'agentF5.r1a.cl.done': { en: 'Done', es: 'Listo' },

  // F5-T6a — Tanda 2 · Admin 02 «Monitoreo en vivo» + Admin 03 «Progreso de
  // agentes» + Admin 04 «Centro de equipo» (contratos 2026-08-28). ES canónico
  // verbatim del contrato; EN derivado.
  // -- compartidas (pie de tabla + pager del chasis)
  'f5t6a.foot.people': { en: '{n} people · page {p} of {pp}', es: '{n} personas · página {p} de {pp}' },
  'f5t6a.foot.person': { en: '1 person · page {p} of {pp}', es: '1 persona · página {p} de {pp}' },
  'f5t6a.pager.prev': { en: 'Previous', es: 'Anterior' },
  'f5t6a.pager.next': { en: 'Next', es: 'Siguiente' },
  'f5t6a.pager.page': { en: 'Page {p} of {pp}', es: 'Página {p} de {pp}' },
  // -- Admin 02 · Monitoreo en vivo
  'f5t6a.lm.now': { en: 'Right now', es: 'Ahora mismo' },
  'f5t6a.lm.shift': { en: "Today's shift · 06:00–20:00 · Bronx", es: 'Turno de hoy · 06:00–20:00 · Bronx' },
  'f5t6a.lm.card.onCall': { en: 'Agents on a call', es: 'Agentes en llamada' },
  'f5t6a.lm.card.available': { en: 'Available', es: 'Disponibles' },
  'f5t6a.lm.card.paused': { en: 'On break', es: 'En pausa' },
  'f5t6a.lm.card.off': { en: 'Disconnected', es: 'Desconectados' },
  'f5t6a.lm.card.subs': { en: 'Household sub-screenings in progress', es: 'Sub-screenings de hogar en curso' },
  'f5t6a.lm.card.uploaders': { en: 'Uploaders working', es: 'Uploaders trabajando' },
  'f5t6a.lm.floor': { en: 'Agent floor', es: 'Piso de agentes' },
  'f5t6a.lm.floorCount': { en: '{n} agents', es: '{n} agentes' },
  'f5t6a.lm.floorCountOne': { en: '1 agent', es: '1 agente' },
  'f5t6a.lm.clearFilter': { en: 'Clear the filter', es: 'Quitar el filtro' },
  'f5t6a.lm.state.onCall': { en: 'On a call', es: 'En llamada' },
  'f5t6a.lm.state.available': { en: 'Available', es: 'Disponible' },
  'f5t6a.lm.state.paused': { en: 'On break', es: 'En pausa' },
  'f5t6a.lm.state.off': { en: 'Disconnected', es: 'Desconectado' },
  'f5t6a.lm.ctx.screening': { en: 'Screening in progress', es: 'Screening en curso' },
  'f5t6a.lm.ctx.sub': { en: 'Household · sub-screening in progress', es: 'Hogar · sub-screening en curso' },
  'f5t6a.lm.ctx.documenting': { en: 'Documenting the call', es: 'Documentando la llamada' },
  'f5t6a.lm.ctx.none': { en: 'No active call', es: 'Sin llamada activa' },
  'f5t6a.lm.ctx.paused': { en: 'On break', es: 'En pausa' },
  'f5t6a.lm.ctx.off': { en: 'Shift closed', es: 'Turno cerrado' },
  'f5t6a.lm.since': { en: 'since {t}', es: 'desde {t}' },
  'f5t6a.lm.left': { en: 'left {t}', es: 'salió {t}' },
  'f5t6a.lm.ofToday': { en: '/ {n} today', es: '/ {n} hoy' },
  'f5t6a.lm.attempts': { en: '{n} attempts', es: '{n} intentos' },
  'f5t6a.lm.attemptsOne': { en: '1 attempt', es: '1 intento' },
  'f5t6a.lm.household': { en: 'Household · today', es: 'Hogar · hoy' },
  'f5t6a.lm.members': { en: 'Members captured', es: 'Miembros capturados' },
  'f5t6a.lm.subsDone': { en: 'Sub-screenings completed', es: 'Sub-screenings completados' },
  'f5t6a.lm.breakdown': { en: 'See the per-agent breakdown', es: 'Ver el desglose por agente' },
  'f5t6a.lm.action': { en: 'Needs action today', es: 'Exige acción hoy' },
  'f5t6a.lm.missed': { en: 'Missed bookings unattended', es: 'Citas perdidas sin atender' },
  'f5t6a.lm.pendingUpload': { en: 'Screenings awaiting upload', es: 'Screenings esperando subida' },
  'f5t6a.lm.openMissed': { en: 'Open missed bookings', es: 'Abrir citas perdidas' },
  'f5t6a.lm.emptyTitle': { en: 'Nobody is online yet', es: 'Todavía no hay nadie en línea' },
  'f5t6a.lm.emptyBody': {
    en: "The shift starts at 06:00. Until someone signs in, the floor is empty: nothing is being lost, but nobody is being called either. Today's counts appear here as soon as the first agent connects.",
    es: 'El turno arranca a las 06:00. Hasta que alguien entre, el piso está vacío: no se pierde nada, pero tampoco se está llamando a nadie. Los conteos del día aparecen acá en cuanto el primer agente se conecte.',
  },
  'f5t6a.lm.emptyCta': { en: 'Open Team center', es: 'Abrir Centro de equipo' },
  'f5t6a.lm.errTitle': { en: 'The floor did not load', es: 'No se pudo cargar el piso' },
  'f5t6a.lm.errBody': {
    en: "While this fails you cannot see who is online nor today's counts. The data itself is intact — only this view is down.",
    es: 'Mientras falla no se ve quién está en línea ni los conteos del día. La data está intacta — es solo esta vista.',
  },
  'f5t6a.lm.retry': { en: 'Retry', es: 'Reintentar' },
  // -- Admin 03 · Progreso de agentes
  'f5t6a.ap.tabAgents': { en: 'Agents', es: 'Agentes' },
  'f5t6a.ap.tabUploaders': { en: 'Uploaders', es: 'Uploaders' },
  'f5t6a.ap.period': { en: 'Period', es: 'Período' },
  'f5t6a.ap.goalAgents': {
    en: 'Current daily goal: {n} screenings per agent. The number shown is the number required.',
    es: 'Meta diaria vigente: {n} screenings por agente. Es el número que se muestra y el que se exige.',
  },
  'f5t6a.ap.goalUploaders': {
    en: 'Current daily goal: {n} uploads per uploader (uploader_daily_target). Motivational: it does not affect pay.',
    es: 'Meta diaria vigente: {n} cargas por uploader (uploader_daily_target). Motivacional: no afecta el pago.',
  },
  'f5t6a.ap.goalScheduled': { en: 'Scheduled change: {v} starting {d}.', es: 'Cambio programado: {v} a partir del {d}.' },
  'f5t6a.ap.seeConfig': { en: 'View in Settings', es: 'Ver en Configuración' },
  'f5t6a.ap.distTitle': { en: 'Tier distribution · {period}', es: 'Distribución de niveles · {period}' },
  'f5t6a.ap.distMeta': {
    en: '{n} active agents · the tier closes on the last day of the month',
    es: '{n} agentes activos · el nivel se cierra el último día del mes',
  },
  'f5t6a.ap.byAgent': { en: 'By agent', es: 'Por agente' },
  'f5t6a.ap.byUploader': { en: 'By uploader', es: 'Por uploader' },
  'f5t6a.ap.export': { en: 'Export CSV', es: 'Exportar CSV' },
  'f5t6a.ap.col.person': { en: 'Person', es: 'Persona' },
  'f5t6a.ap.col.month': { en: 'Screenings this month', es: 'Screenings del mes' },
  'f5t6a.ap.col.uploadsMonth': { en: 'Uploads this month', es: 'Cargas del mes' },
  'f5t6a.ap.col.tier': { en: 'Tier', es: 'Nivel' },
  'f5t6a.ap.col.toNext': { en: 'To the next tier', es: 'Falta para el próximo' },
  'f5t6a.ap.col.avg': { en: 'Daily average', es: 'Promedio diario' },
  'f5t6a.ap.col.pace': { en: 'Pace this week', es: 'Ritmo de la semana' },
  'f5t6a.ap.col.days': { en: 'Active days', es: 'Días activos' },
  'f5t6a.ap.toNextVal': { en: '{n} to {tier}', es: '{n} para {tier}' },
  'f5t6a.ap.ladderTop': { en: 'Top of the ladder', es: 'Tope de la escalera' },
  'f5t6a.ap.rowHint': { en: "Click a row to open that person's screenings", es: 'Click en una fila abre los screenings de esa persona' },
  'f5t6a.ap.ladderTitle': { en: 'The ladder · a single one across the system', es: 'La escalera · una sola en todo el sistema' },
  'f5t6a.ap.ladderBody': {
    en: 'It is measured over screenings sent in the calendar month, the same period payroll settles on. The thresholds live in Settings with an effective date: changing them does not re-price closed months.',
    es: 'Se mide sobre screenings enviados en el mes calendario, el mismo período con el que se liquida. Los umbrales viven en Configuración con fecha de vigencia: cambiarlos no re-tarifa meses cerrados.',
  },
  'f5t6a.ap.ladderSub': { en: 'screenings in the month', es: 'screenings en el mes' },
  'f5t6a.ap.orMore': { en: '{n} or more', es: '{n} o más' },
  'f5t6a.ap.uploaderNote': {
    en: 'Uploader goals are motivational: they do not affect pay. That is why this view has no tier ladder — it counts uploads, average and streak, and nothing else.',
    es: 'Las metas del uploader son motivacionales: no afectan el pago. Por eso esta vista no usa la escalera de niveles — cuenta cargas, promedio y racha, y nada más.',
  },
  'f5t6a.ap.emptyMonth': { en: 'No screenings sent in {period} yet', es: 'Todavía no hay screenings enviados en {period}' },
  'f5t6a.ap.emptyMonthBody': {
    en: 'The month just opened. Sent screenings appear here as soon as the first one of the period comes in.',
    es: 'El mes recién abre. Los envíos aparecen acá apenas entre el primer screening del período.',
  },
  'f5t6a.ap.emptyPrev': { en: 'See the previous month', es: 'Ver el mes anterior' },
  'f5t6a.ap.noTierRows': { en: 'No one is at that tier this month', es: 'Nadie está en ese nivel este mes' },
  'f5t6a.ap.filterAria': { en: 'Filter by tier', es: 'Filtrar por nivel' },
  'f5t6a.ap.errTitle': { en: 'Progress did not load', es: 'No se pudo cargar el progreso' },
  'f5t6a.ap.errBody': {
    en: 'While this fails you cannot see tiers nor month counts. Nothing is lost — the payroll math lives on the server.',
    es: 'Mientras falla no se ven niveles ni conteos del mes. No se pierde nada — la cuenta de nómina vive en el servidor.',
  },
  // -- Admin 04 · Centro de equipo
  'f5t6a.tc.filter.all': { en: 'All', es: 'Todos' },
  'f5t6a.tc.filter.agent': { en: 'Agents', es: 'Agentes' },
  'f5t6a.tc.filter.navigator': { en: 'Navigators', es: 'Navigators' },
  'f5t6a.tc.filter.uploader': { en: 'Uploaders', es: 'Uploaders' },
  'f5t6a.tc.filter.supervisor': { en: 'Supervisors', es: 'Supervisors' },
  'f5t6a.tc.filter.admin': { en: 'Admins', es: 'Admins' },
  'f5t6a.tc.filterAria': { en: 'Filter by role', es: 'Filtrar por rol' },
  'f5t6a.tc.export': { en: 'Export users', es: 'Exportar usuarios' },
  'f5t6a.tc.new': { en: 'New user', es: 'Nuevo usuario' },
  'f5t6a.tc.searchPh': { en: 'Search by name or email', es: 'Buscar por nombre o correo' },
  'f5t6a.tc.col.person': { en: 'Person', es: 'Persona' },
  'f5t6a.tc.col.role': { en: 'Role', es: 'Rol' },
  'f5t6a.tc.col.status': { en: 'Status', es: 'Estado' },
  'f5t6a.tc.col.lastLogin': { en: 'Last sign-in', es: 'Último ingreso' },
  'f5t6a.tc.col.activity': { en: "Today's activity", es: 'Actividad de hoy' },
  'f5t6a.tc.col.actions': { en: 'Actions', es: 'Acciones' },
  'f5t6a.tc.status.active': { en: 'Active', es: 'Activo' },
  'f5t6a.tc.status.invited': { en: 'Invitation pending', es: 'Invitación pendiente' },
  'f5t6a.tc.status.off': { en: 'Deactivated', es: 'Desactivado' },
  'f5t6a.tc.scope.title': { en: 'Navigator scope', es: 'Alcance del navigator' },
  'f5t6a.tc.scope.full': { en: 'Navigates positives', es: 'Navega positivos' },
  'f5t6a.tc.scope.closer': { en: 'Only closes negatives', es: 'Sólo cierra negativos' },
  'f5t6a.tc.scope.pool': { en: 'Pool inbox', es: 'Buzón del pool' },
  'f5t6a.tc.act.edit': { en: 'Edit', es: 'Editar' },
  'f5t6a.tc.act.reset': { en: 'Reset password', es: 'Resetear contraseña' },
  'f5t6a.tc.act.deactivate': { en: 'Deactivate', es: 'Desactivar' },
  'f5t6a.tc.act.reactivate': { en: 'Reactivate', es: 'Reactivar' },
  'f5t6a.tc.never': { en: 'Never', es: 'Nunca' },
  'f5t6a.tc.today': { en: 'Today {t}', es: 'Hoy {t}' },
  'f5t6a.tc.mfaOn': { en: 'MFA active', es: 'MFA activo' },
  'f5t6a.tc.footNote': {
    en: 'The activity column replaces the standalone «uploader activity» screen',
    es: 'La columna de actividad reemplaza la pantalla suelta de «actividad de uploaders»',
  },
  'f5t6a.tc.deact.title': { en: 'Deactivate {name}?', es: '¿Desactivar a {name}?' },
  'f5t6a.tc.deact.body': {
    en: 'They will lose access to Pathway until an admin reactivates them. The account is not deleted: their work history stays intact and auditable.',
    es: 'Perderá el acceso a Pathway hasta que un admin la reactive. La cuenta no se borra: su historial de trabajo queda intacto y auditable.',
  },
  'f5t6a.tc.deact.note': {
    en: 'It is recorded in the audit log: who deactivated, whom and when. To remove only part of their access, edit their role instead of deactivating them.',
    es: 'Queda registrado en auditoría: quién desactivó, a quién y cuándo. Para quitarle sólo una parte del acceso, editá su rol en vez de desactivarla.',
  },
  'f5t6a.tc.deact.confirm': { en: 'Deactivate and end their sessions', es: 'Desactivar y cerrar sus sesiones' },
  'f5t6a.tc.reset.title': { en: "Reset {name}'s password?", es: '¿Resetear la contraseña de {name}?' },
  'f5t6a.tc.reset.body': {
    en: 'Their current password is invalidated and they are sent a one-time invitation to define a new one.',
    es: 'Se invalida su contraseña actual y se le envía una invitación de un solo uso para definir una nueva.',
  },
  'f5t6a.tc.reset.note': {
    en: 'It is recorded in the audit log. If they only lost their second factor, reset the MFA in their profile: that does not kick them out of the session.',
    es: 'Queda registrado en auditoría. Si sólo perdió el segundo factor, restablecé el MFA en su ficha: eso no la echa de la sesión.',
  },
  'f5t6a.tc.reset.confirm': { en: 'Reset and end their sessions', es: 'Resetear y cerrar sus sesiones' },
  'f5t6a.tc.boxTitle': { en: 'What happens immediately', es: 'Lo que pasa en el momento' },
  'f5t6a.tc.bullet1': { en: 'All their open sessions are revoked, on every device.', es: 'Se le revocan todas las sesiones abiertas, en todos los equipos.' },
  'f5t6a.tc.bullet2': {
    en: 'If they are on a call, they lose it instantly: they land on the sign-in screen with no prior warning.',
    es: 'Si está en una llamada, la pierde al instante: sale a la pantalla de ingreso sin aviso previo.',
  },
  'f5t6a.tc.bullet3Deact': { en: 'They cannot sign in again until an admin reactivates them.', es: 'No podrá volver a entrar hasta que un admin la reactive.' },
  'f5t6a.tc.bullet3Reset': {
    en: 'They can sign in again only after defining the new password with the invitation.',
    es: 'Podrá volver a entrar recién después de definir la contraseña nueva con la invitación.',
  },
  'f5t6a.tc.cancel': { en: 'Cancel', es: 'Cancelar' },
  'f5t6a.tc.emptyRole': { en: 'No {role} yet', es: 'Todavía no hay ningún {role}' },
  'f5t6a.tc.emptyAll': { en: 'No users yet', es: 'Todavía no hay usuarios' },
  'f5t6a.tc.emptyBody': {
    en: 'Create the account and it will appear in this table with its role and status.',
    es: 'Creá la cuenta y va a aparecer en esta tabla con su rol y su estado.',
  },
  'f5t6a.tc.role1.agent': { en: 'agent', es: 'agente' },
  'f5t6a.tc.role1.navigator': { en: 'navigator', es: 'navigator' },
  'f5t6a.tc.role1.uploader': { en: 'uploader', es: 'uploader' },
  'f5t6a.tc.role1.supervisor': { en: 'supervisor', es: 'supervisor' },
  'f5t6a.tc.role1.admin': { en: 'admin', es: 'admin' },
  'f5t6a.tc.doneDeact': { en: '{name} was deactivated.', es: '{name} quedó desactivada.' },
  'f5t6a.tc.doneReact': { en: '{name} was reactivated.', es: '{name} fue reactivada.' },
  'f5t6a.tc.errTitle': { en: 'The team did not load', es: 'No se pudo cargar el equipo' },
  'f5t6a.tc.errBody': {
    en: 'While this fails you cannot manage accounts. Nobody lost access — only this view is down.',
    es: 'Mientras falla no se pueden administrar cuentas. Nadie perdió acceso — es solo esta vista.',
  },
  // F5-T6c — Tanda 2 · Admin 10 «Reportes de bugs» · Admin 11 «Citas perdidas»
  // · Admin 12 «Lotes de pacientes» · Admin 13 «Mi perfil» (contratos 2026-08-28).
  // ES canónico verbatim del contrato; EN derivado.

  // -- Roles (meta de fila y subtítulo del perfil) --
  'f5t6c.role.admin': { en: 'Admin', es: 'Admin' },
  'f5t6c.role.supervisor': { en: 'Supervisor', es: 'Supervisor' },
  'f5t6c.role.agent': { en: 'Agent', es: 'Agente' },
  'f5t6c.role.navigator': { en: 'Navigator', es: 'Navigator' },
  'f5t6c.role.uploader': { en: 'Uploader', es: 'Uploader' },
  'f5t6c.pager.prev': { en: 'Previous', es: 'Anterior' },
  'f5t6c.pager.next': { en: 'Next', es: 'Siguiente' },

  // -- Admin 10 · Reportes de bugs (bandeja) --
  'f5t6c.bugs.title': { en: 'Bug reports', es: 'Reportes de bugs' },
  'f5t6c.bugs.subtitle': {
    en: 'What the five roles report, with status and detail. Simple on purpose: a list, status filters, and a panel that says what happened, who saw it and on which screen.',
    es: 'Lo que reportan los cinco roles, con estado y detalle. Simple a propósito: lista, filtros por estado, y un panel que dice qué pasó, quién lo vio y en qué pantalla.',
  },
  'f5t6c.bugs.note': {
    en: 'Tap a row to open the detail; change the status from the panel itself.',
    es: 'Tocá una fila para abrir el detalle; cambiá el estado desde el propio panel.',
  },
  'f5t6c.bugs.filter.all': { en: 'All', es: 'Todos' },
  'f5t6c.bugs.filter.new': { en: 'New', es: 'Nuevos' },
  'f5t6c.bugs.filter.review': { en: 'In review', es: 'En revisión' },
  'f5t6c.bugs.filter.resolved': { en: 'Resolved', es: 'Resueltos' },
  'f5t6c.bugs.status.new': { en: 'New', es: 'Nuevo' },
  'f5t6c.bugs.status.review': { en: 'In review', es: 'En revisión' },
  'f5t6c.bugs.status.resolved': { en: 'Resolved', es: 'Resuelto' },
  'f5t6c.bugs.status.nobug': { en: 'Not a bug', es: 'No es un bug' },
  'f5t6c.bugs.blocksMe': { en: 'blocks me', es: 'me bloquea' },
  'f5t6c.bugs.pageLabel': { en: '{n} reports · page {page} of {pages}', es: '{n} reportes · página {page} de {pages}' },
  'f5t6c.bugs.pageLabelOne': { en: '1 report · page {page} of {pages}', es: '1 reporte · página {page} de {pages}' },
  'f5t6c.bugs.panel.who': { en: 'Who reported it', es: 'Quién lo reportó' },
  'f5t6c.bugs.panel.where': { en: 'Where it happened', es: 'Dónde pasó' },
  'f5t6c.bugs.panel.what': { en: 'What they told', es: 'Qué contó' },
  'f5t6c.bugs.panel.when': { en: 'When', es: 'Cuándo' },
  'f5t6c.bugs.panel.tech': { en: 'What the system attached', es: 'Lo que adjuntó el sistema' },
  'f5t6c.bugs.panel.status': { en: 'Status', es: 'Estado' },
  'f5t6c.bugs.panel.note': {
    en: 'The status change is announced to the reporter through the bell. There is no reply field here: if a conversation is needed, it happens over chat.',
    es: 'El cambio de estado se le avisa a quien lo reportó por la campana. No hay campo de respuesta acá: si hace falta hablar, se habla por chat.',
  },
  'f5t6c.bugs.panel.noPage': { en: 'No screen recorded', es: 'Sin pantalla registrada' },
  'f5t6c.bugs.tech.screen': { en: 'screen', es: 'pantalla' },
  'f5t6c.bugs.tech.role': { en: 'role', es: 'rol' },
  'f5t6c.bugs.tech.noSession': { en: 'no session', es: 'sin sesión' },
  'f5t6c.bugs.empty.title': { en: 'Nobody reported problems this week', es: 'Nadie reportó problemas esta semana' },
  'f5t6c.bugs.empty.body': {
    en: 'When someone reports a problem from their top bar, it will show up here.',
    es: 'Cuando alguien reporte un problema desde su barra superior, va a aparecer acá.',
  },
  'f5t6c.bugs.emptyFilter.title': { en: 'No reports with that status', es: 'Ningún reporte con ese estado' },
  'f5t6c.bugs.emptyFilter.body': {
    en: 'The filter left nothing to show. The rest of the reports are under "All".',
    es: 'El filtro no dejó nada para mostrar. El resto de los reportes está en «Todos».',
  },
  'f5t6c.bugs.emptyFilter.cta': { en: 'See all', es: 'Ver todos' },
  'f5t6c.bugs.error.title': { en: 'Could not load the reports', es: 'No se pudieron cargar los reportes' },
  'f5t6c.bugs.error.body': {
    en: 'While this fails you cannot see what the team reported broken.',
    es: 'Mientras falla, no ves lo que el equipo reportó como roto.',
  },
  'f5t6c.bugs.error.retry': { en: 'Retry', es: 'Reintentar' },
  'f5t6c.bugs.aria.statusChanged': { en: 'The report is now “{status}”', es: 'El reporte pasó a «{status}»' },

  // -- Admin 10 · formulario del rol operativo («Reportar un problema») --
  'f5t6c.bugs.form.title': { en: 'Report a problem', es: 'Reportar un problema' },
  'f5t6c.bugs.form.what': { en: 'What happened', es: 'Qué pasó' },
  'f5t6c.bugs.form.whatPh': { en: 'The save button does not respond', es: 'El botón de guardar no responde' },
  'f5t6c.bugs.form.more': { en: 'Tell me a bit more', es: 'Contame un poco más' },
  'f5t6c.bugs.form.morePh': {
    en: 'I finished the screening of a household member and nothing happens when I press save. I tried twice.',
    es: 'Terminé el screening de un miembro del hogar y al apretar guardar no pasa nada. Probé dos veces.',
  },
  'f5t6c.bugs.form.blocks': { en: 'Does it block you?', es: '¿Te bloquea?' },
  'f5t6c.bugs.form.blocksYes': { en: 'It blocks me', es: 'Me bloquea' },
  'f5t6c.bugs.form.blocksNo': { en: 'I can keep going', es: 'Puedo seguir' },
  'f5t6c.bugs.form.auto': {
    en: 'The screen you were on, your role, the browser and the time attach themselves. ',
    es: 'Se adjuntan solos la pantalla en la que estabas, tu rol, el navegador y la hora. ',
  },
  'f5t6c.bugs.form.autoPhi': { en: 'Do not write patient data here', es: 'No escribas datos del paciente acá' },
  'f5t6c.bugs.form.autoTail': { en: ': that is what the case note is for.', es: ': para eso está la nota del caso.' },
  'f5t6c.bugs.form.cancel': { en: 'Cancel', es: 'Cancelar' },
  'f5t6c.bugs.form.send': { en: 'Send the report', es: 'Enviar el reporte' },
  'f5t6c.bugs.form.sending': { en: 'Sending…', es: 'Enviando…' },
  'f5t6c.bugs.form.sentTitle': { en: 'Report sent', es: 'Reporte enviado' },
  'f5t6c.bugs.form.sentDesc': {
    en: 'Status changes will reach you through the bell.',
    es: 'El cambio de estado te va a llegar por la campana.',
  },
  'f5t6c.bugs.form.error': { en: 'Could not send the report', es: 'No se pudo enviar el reporte' },

  // -- Admin 11 · Citas perdidas --
  'f5t6c.missed.title': { en: 'Missed appointments', es: 'Citas perdidas' },
  'f5t6c.missed.subtitle': {
    en: 'The work queue of appointments nobody attended: each row carries its computed action and the case next to it.',
    es: 'La cola de trabajo de las citas que nadie atendió: cada fila trae su acción calculada y el caso al lado.',
  },
  'f5t6c.missed.header.count': { en: '{n} overdue appointments unattended', es: '{n} citas vencidas sin atender' },
  'f5t6c.missed.header.countOne': { en: '1 overdue appointment unattended', es: '1 cita vencida sin atender' },
  'f5t6c.missed.header.sub': {
    en: 'A no-show counts as an attempt and does not reset the patient’s count. Until the appointment is rescheduled or closed, the patient keeps waiting and the agent does not have them in today’s queue.',
    es: 'Un no-show cuenta como intento y no resetea el conteo del paciente. Mientras la cita no se reagende ni se cierre, el paciente sigue esperando y el agente no lo tiene en su cola de hoy.',
  },
  'f5t6c.missed.bucket.all': { en: 'All', es: 'Todas' },
  'f5t6c.missed.bucket.today': { en: 'Today’s', es: 'De hoy' },
  'f5t6c.missed.bucket.b13': { en: '1 to 3 days', es: '1 a 3 días' },
  'f5t6c.missed.bucket.b47': { en: '4 to 7 days', es: '4 a 7 días' },
  'f5t6c.missed.bucket.b7p': { en: 'More than 7', es: 'Más de 7' },
  'f5t6c.missed.export': { en: 'Export CSV', es: 'Exportar CSV' },
  'f5t6c.missed.exportDisabled': {
    en: 'The export with names must leave the server audited; that endpoint does not exist yet.',
    es: 'El export con nombres tiene que salir auditado del servidor; ese endpoint todavía no existe.',
  },
  'f5t6c.missed.agendo': { en: 'Booked by', es: 'Agendó' },
  'f5t6c.missed.aged.h': { en: '{n} h ago', es: 'hace {n} h' },
  'f5t6c.missed.aged.d': { en: '{n} d ago', es: 'hace {n} d' },
  'f5t6c.missed.meta': { en: 'ID ****{mrn} · appt {when}', es: 'ID ****{mrn} · cita {when}' },
  'f5t6c.missed.action.resched': { en: 'Reschedule', es: 'Reagendar' },
  'f5t6c.missed.action.queue': { en: 'Return to the queue', es: 'Devolver a la cola' },
  'f5t6c.missed.action.view': { en: 'See the case', es: 'Ver el caso' },
  'f5t6c.missed.action.rescheduling': { en: 'Rescheduling…', es: 'Reagendando…' },
  'f5t6c.missed.action.confirm': { en: 'Confirm', es: 'Confirmar' },
  'f5t6c.missed.action.cancel': { en: 'Cancel', es: 'Cancelar' },
  'f5t6c.missed.pick': { en: 'Pick the new date and time', es: 'Elegí la nueva fecha y hora' },
  'f5t6c.missed.queueDisabled': {
    en: '“Return to the queue” needs server: today there is no path to send an appointment back to the general queue.',
    es: '«Devolver a la cola» necesita servidor: hoy no existe el camino para devolver una cita a la cola general.',
  },
  'f5t6c.missed.pageLabel': {
    en: '{n} of {total} overdue appointments · page {page} of {pages}',
    es: '{n} de {total} citas vencidas · página {page} de {pages}',
  },
  'f5t6c.missed.rule': {
    en: 'The appointment stays with whoever booked it, even if the patient changed agents',
    es: 'La cita se queda con quien la agendó, incluso si el paciente cambió de agente',
  },
  'f5t6c.missed.aria.resched': { en: 'Appointment rescheduled for {date}', es: 'Cita reagendada para el {date}' },
  'f5t6c.missed.empty.title': { en: 'No appointment was left unattended', es: 'Ninguna cita quedó sin atender' },
  'f5t6c.missed.empty.body': {
    en: 'Every overdue appointment of the last 30 days was rescheduled or closed. When someone misses one, it shows up here the same day.',
    es: 'Todas las citas vencidas de los últimos 30 días se reagendaron o se cerraron. Cuando alguien falte a una, aparece acá el mismo día.',
  },
  'f5t6c.missed.empty.cta': { en: 'See the appointment KPIs', es: 'Ver los KPIs de citas' },
  'f5t6c.missed.bucketEmpty.title': { en: 'No appointments in this bracket', es: 'Ninguna cita en este tramo' },
  'f5t6c.missed.bucketEmpty.body': {
    en: 'The rest of the overdue appointments live under the other brackets.',
    es: 'El resto de las citas vencidas vive en los otros tramos.',
  },
  'f5t6c.missed.bucketEmpty.cta': { en: 'See all', es: 'Ver todas' },
  'f5t6c.missed.error.title': { en: 'Could not load the overdue appointments', es: 'No se pudieron cargar las citas vencidas' },
  'f5t6c.missed.error.body': {
    en: 'While this fails you cannot see which patients are waiting for a reschedule.',
    es: 'Mientras falla, no ves qué pacientes esperan un reagendado.',
  },
  'f5t6c.missed.error.retry': { en: 'Retry', es: 'Reintentar' },

  // -- Admin 12 · Lotes de pacientes --
  'f5t6c.batches.title': { en: 'Patient batches', es: 'Lotes de pacientes' },
  'f5t6c.batches.subtitle': {
    en: 'The batch history is the screen; uploading a batch is its only primary button.',
    es: 'El historial de lotes es la pantalla; subir un lote es su único botón primario.',
  },
  'f5t6c.batches.kicker': { en: 'Batch history · {n}', es: 'Historial de lotes · {n}' },
  'f5t6c.batches.upload': { en: 'Upload a batch', es: 'Subir un lote' },
  'f5t6c.batches.col.file': { en: 'File', es: 'Archivo' },
  'f5t6c.batches.col.uploaded': { en: 'Uploaded', es: 'Subido' },
  'f5t6c.batches.col.rows': { en: 'Rows', es: 'Filas' },
  'f5t6c.batches.col.created': { en: 'Created', es: 'Creados' },
  'f5t6c.batches.col.ignored': { en: 'Ignored', es: 'Ignorados' },
  'f5t6c.batches.col.rejected': { en: 'Rejected', es: 'Rechazados' },
  'f5t6c.batches.col.status': { en: 'Status', es: 'Estado' },
  'f5t6c.batches.by': { en: 'Uploaded by {name}', es: 'Subió {name}' },
  'f5t6c.batches.status.processed': { en: 'Processed', es: 'Procesado' },
  'f5t6c.batches.footer': { en: 'Page {page} of {pages} · {n} batches', es: 'Página {page} de {pages} · {n} lotes' },
  'f5t6c.batches.footerHint': { en: 'Click on a row opens the batch detail', es: 'Click en una fila abre el detalle del lote' },
  'f5t6c.batches.detail.stamp': { en: 'Uploaded {when} by {name}', es: 'Subido el {when} por {name}' },
  'f5t6c.batches.detail.close': { en: 'Close', es: 'Cerrar' },
  'f5t6c.batches.detail.stat.rows': { en: 'File rows', es: 'Filas del archivo' },
  'f5t6c.batches.detail.stat.created': { en: 'Patients created', es: 'Pacientes creados' },
  'f5t6c.batches.detail.stat.ignored': { en: 'Ignored · repeated ID', es: 'Ignorados · ID repetido' },
  'f5t6c.batches.detail.stat.rejected': { en: 'Rejected', es: 'Rechazados' },
  'f5t6c.batches.dedupe.title': { en: 'How it was deduplicated', es: 'Cómo se deduplicó' },
  'f5t6c.batches.dedupe.p1': {
    en: 'Same file: rejected whole. A batch is not uploaded twice, not even under another name — the content is compared.',
    es: 'Mismo archivo: se rechaza entero. Un lote no se sube dos veces, ni con otro nombre — se compara el contenido.',
  },
  'f5t6c.batches.dedupe.p2': {
    en: 'Same Medicaid ID: the new row is ignored. The existing row is never modified: if the file brings a different phone, the old phone stays, because it may have been corrected by hand by an agent during a call.',
    es: 'Mismo ID de Medicaid: se ignora la fila nueva. La fila existente nunca se modifica: si el archivo trae un teléfono distinto, el teléfono viejo se queda, porque puede haber sido corregido a mano por un agente durante una llamada.',
  },
  'f5t6c.batches.detail.report': { en: 'Download the batch report', es: 'Bajar el reporte del lote' },
  'f5t6c.batches.detail.reportNote': {
    en: 'CSV with one line per rejected or ignored row and its reason. It leaves audited.',
    es: 'CSV con una línea por fila rechazada o ignorada y su razón. Sale auditado.',
  },
  'f5t6c.batches.detail.reportDisabled': {
    en: 'The per-batch report needs server: the audited endpoint does not exist yet.',
    es: 'El reporte por lote necesita servidor: el endpoint auditado todavía no existe.',
  },
  'f5t6c.batches.detail.notes': { en: 'Notes', es: 'Notas' },
  'f5t6c.batches.detail.statusTitle': { en: 'Where each patient of the batch is', es: 'Dónde está cada paciente del lote' },
  'f5t6c.batches.detail.agentsTitle': { en: 'Distributed across agents', es: 'Repartido entre agentes' },
  'f5t6c.batches.modal.title': { en: 'Upload a patient batch', es: 'Subir un lote de pacientes' },
  'f5t6c.batches.modal.step1': { en: 'Step 1 of 2 · choose the file', es: 'Paso 1 de 2 · elegir el archivo' },
  'f5t6c.batches.modal.step2': { en: 'Step 2 of 2 · review before creating', es: 'Paso 2 de 2 · revisar antes de crear' },
  'f5t6c.batches.modal.close': { en: 'Close', es: 'Cerrar' },
  'f5t6c.batches.modal.dropIdle': {
    en: 'Drag the file here, or tap to choose it',
    es: 'Arrastrá el archivo acá, o tocá para elegirlo',
  },
  'f5t6c.batches.modal.limits': { en: 'Limits', es: 'Límites' },
  'f5t6c.batches.modal.limitsText': {
    en: 'Only .xlsx or .xls · max 10 MB · one row per patient, with Medicaid ID, first name, last name, phone and date of birth. The same file already uploaded is rejected whole.',
    es: 'Sólo .xlsx o .xls · máximo 10 MB · una fila por paciente, con ID de Medicaid, nombre, apellido, teléfono y fecha de nacimiento. El mismo archivo ya subido se rechaza entero.',
  },
  'f5t6c.batches.modal.cancel': { en: 'Cancel', es: 'Cancelar' },
  'f5t6c.batches.modal.preview': { en: 'See the preview', es: 'Ver el preview' },
  'f5t6c.batches.modal.reading': { en: 'Reading the file…', es: 'Leyendo el archivo…' },
  'f5t6c.batches.modal.readonly': {
    en: 'Read-only preview · nothing was created yet',
    es: 'Preview de sólo lectura · todavía no se creó nada',
  },
  'f5t6c.batches.modal.stat.create': { en: 'Would be created', es: 'Se crearían' },
  'f5t6c.batches.modal.stat.ignored': { en: 'Ignored · repeated ID', es: 'Ignorados · ID repetido' },
  'f5t6c.batches.modal.stat.rejected': { en: 'Rejected', es: 'Rechazados' },
  'f5t6c.batches.modal.stat.minors': { en: 'Under 18', es: 'Menores de 18' },
  'f5t6c.batches.modal.minors': {
    en: '{n} rows belong to minors under 18. They will be created anyway, but flagged: a minor’s screening has a different consent and the agent has to see it before calling. Check below in case one slipped in by a date error.',
    es: '{n} filas son de menores de 18. Se van a crear igual, pero quedan marcadas: el screening de un menor tiene otro consentimiento y el agente tiene que verlo antes de llamar. Revisá abajo si alguno entró por error de fecha.',
  },
  'f5t6c.batches.modal.obsNote': {
    en: 'Showing {shown} of {total} rows with observations. The full report downloads after confirming.',
    es: 'Mostrando {shown} de {total} filas con observación. El reporte completo se baja después de confirmar.',
  },
  'f5t6c.batches.modal.back': { en: 'Back', es: 'Volver' },
  'f5t6c.batches.modal.create': { en: 'Create {n} patients', es: 'Crear {n} pacientes' },
  'f5t6c.batches.modal.creating': { en: 'Creating…', es: 'Creando…' },
  'f5t6c.batches.modal.zero': { en: 'The {n} rows already exist', es: 'Las {n} filas ya existen' },
  'f5t6c.batches.modal.zeroBody': {
    en: 'Confirming would do nothing: every row is already in the base.',
    es: 'Confirmar no haría nada: todas las filas ya están en la base.',
  },
  'f5t6c.batches.modal.rejTitle': { en: 'The file was rejected', es: 'El archivo se rechazó' },
  'f5t6c.batches.modal.rejType': { en: 'Only .xlsx or .xls files are accepted.', es: 'Sólo se aceptan archivos .xlsx o .xls.' },
  'f5t6c.batches.modal.rejSize': { en: 'The file is over 10 MB.', es: 'El archivo pasa de 10 MB.' },
  'f5t6c.batches.modal.rejDup': {
    en: 'The same file was already uploaded: it is rejected whole.',
    es: 'El mismo archivo ya se subió: se rechaza entero.',
  },
  'f5t6c.batches.modal.pickAnother': { en: 'Choose another file', es: 'Elegir otro archivo' },
  'f5t6c.batches.modal.chip.minor': { en: 'Under 18', es: 'Menor de 18' },
  'f5t6c.batches.modal.chip.ignored': { en: 'Ignored · repeated ID', es: 'Ignorado · ID repetido' },
  'f5t6c.batches.modal.chip.rejected': { en: 'Rejected', es: 'Rechazado' },
  'f5t6c.batches.modal.rowIgnored': { en: 'ID …{cin} · already exists', es: 'ID …{cin} · ya existe' },
  'f5t6c.batches.modal.rowRejected': { en: 'Row {row} · {detail}', es: 'Fila {row} · {detail}' },
  'f5t6c.batches.modal.metaTitle': { en: 'Batch data (optional)', es: 'Datos del lote (opcional)' },
  'f5t6c.batches.modal.metaProgram': { en: 'Program', es: 'Programa' },
  'f5t6c.batches.modal.metaDate': { en: 'Source date', es: 'Fecha de origen' },
  'f5t6c.batches.modal.metaNotes': { en: 'Notes', es: 'Notas' },
  'f5t6c.batches.modal.warnTitle': { en: 'File warnings', es: 'Avisos del archivo' },
  'f5t6c.batches.modal.demoTitle': { en: 'File demographics', es: 'Demografía del archivo' },
  'f5t6c.batches.aria.figures': {
    en: 'Would be created {create} · ignored {ignored} · rejected {rejected} · minors {minors}',
    es: 'Se crearían {create} · ignorados {ignored} · rechazados {rejected} · menores {minors}',
  },
  'f5t6c.batches.empty.title': { en: 'No batch was uploaded yet', es: 'Todavía no se subió ningún lote' },
  'f5t6c.batches.empty.body': {
    en: 'When the first batch is uploaded, its history will live here, with the detail one click away.',
    es: 'Cuando se suba el primer lote, su historial va a vivir acá, con el detalle a un click.',
  },
  'f5t6c.batches.error.title': { en: 'Could not load the batches', es: 'No se pudieron cargar los lotes' },
  'f5t6c.batches.error.body': {
    en: 'While this fails you cannot see the history nor confirm what a new upload deduplicates against.',
    es: 'Mientras falla, no ves el historial ni podés confirmar contra qué se deduplica una subida nueva.',
  },
  'f5t6c.batches.error.retry': { en: 'Retry', es: 'Reintentar' },

  // -- Admin 13 · Mi perfil --
  'f5t6c.profile.since': { en: 'at Pathway since {when}', es: 'en Pathway desde {when}' },
  'f5t6c.profile.tab.datos': { en: 'Details', es: 'Datos' },
  'f5t6c.profile.tab.seguridad': { en: 'Security', es: 'Seguridad' },
  'f5t6c.profile.tab.actividad': { en: 'Activity', es: 'Actividad' },
  'f5t6c.profile.name': { en: 'First and last name', es: 'Nombre y apellido' },
  'f5t6c.profile.email': { en: 'Work email', es: 'Correo de trabajo' },
  'f5t6c.profile.emailNote': {
    en: 'Your email is your sign-in identity: another admin changes it from the Team center, not you from here.',
    es: 'El correo es tu identidad de ingreso: lo cambia otro admin desde el Centro de equipo, no vos desde acá.',
  },
  'f5t6c.profile.lang': { en: 'Interface language', es: 'Idioma de la interfaz' },
  'f5t6c.profile.langEs': { en: 'Español', es: 'Español' },
  'f5t6c.profile.langEn': { en: 'English', es: 'English' },
  'f5t6c.profile.langNote': {
    en: 'It is the same preference as the EN/ES toggle of the top bar: it saves here and can be changed on any screen.',
    es: 'Es la misma preferencia que el toggle EN/ES de la barra superior: se guarda acá y se puede cambiar en cualquier pantalla.',
  },
  'f5t6c.profile.discard': { en: 'Discard', es: 'Descartar' },
  'f5t6c.profile.save': { en: 'Save changes', es: 'Guardar cambios' },
  'f5t6c.profile.saving': { en: 'Saving…', es: 'Guardando…' },
  'f5t6c.profile.saved': { en: 'Changes saved', es: 'Cambios guardados' },
  'f5t6c.profile.pw.title': { en: 'Password', es: 'Contraseña' },
  'f5t6c.profile.pw.current': { en: 'Current password', es: 'Contraseña actual' },
  'f5t6c.profile.pw.new': { en: 'New', es: 'Nueva' },
  'f5t6c.profile.pw.repeat': { en: 'Repeat the new one', es: 'Repetir la nueva' },
  // NECESITA-SERVER (medido 2026-08-28): changeOwnPassword NO revoca otras
  // sesiones y el JWT no mira passwordChangedAt — el verbatim del mockup
  // («se cierran todas tus otras sesiones») prometia de mas. Este copy dice
  // la verdad; la revocacion real es un arco de seguridad que sube al CEO.
  'f5t6c.profile.pw.warn1': { en: 'Careful: sessions open on ', es: 'Ojo: las sesiones abiertas en ' },
  'f5t6c.profile.pw.warnBold': { en: 'other computers stay active', es: 'otros equipos siguen activas' },
  'f5t6c.profile.pw.warn2': {
    en: ' until they expire — close them there if the computer is not yours. This one stays open.',
    es: ' hasta expirar: cerralas allá si el equipo no es tuyo. La de acá sigue abierta.',
  },
  'f5t6c.profile.pw.submit': { en: 'Change the password', es: 'Cambiar la contraseña' },
  'f5t6c.profile.mfa.title': { en: 'Second factor', es: 'Segundo factor' },
  'f5t6c.profile.mfa.offChip': { en: 'Off in the system', es: 'Apagado en el sistema' },
  'f5t6c.profile.mfa.offBody': {
    en: 'MFA is disabled by configuration for every role. When it turns on, you will enroll your second factor at your next sign-in, with the enrollment screen.',
    es: 'El MFA está desactivado por configuración para todos los roles. Cuando se encienda, vas a inscribir tu segundo factor en el próximo ingreso, con la pantalla de inscripción.',
  },
  'f5t6c.profile.mfa.enroll': { en: 'Enroll a second factor', es: 'Inscribir un segundo factor' },
  'f5t6c.profile.mfa.offWhy': {
    en: 'Disabled because MFA is off in Configuration › Operation and security, not because of a problem with your account.',
    es: 'Deshabilitado porque el MFA está apagado en Configuración › Operación y seguridad, no por un problema de tu cuenta.',
  },
  'f5t6c.profile.mfa.activeChip': { en: 'Active', es: 'Activo' },
  'f5t6c.profile.mfa.activeBody': {
    en: 'Your second factor is enrolled: the code is asked at every sign-in.',
    es: 'Tu segundo factor ya está inscripto: el código se pide en cada ingreso.',
  },
  'f5t6c.profile.mfa.pendingChip': { en: 'Pending enrollment', es: 'Pendiente de inscripción' },
  'f5t6c.profile.mfa.pendingBody': {
    en: 'MFA is on for your role: enroll your second factor now or at your next sign-in.',
    es: 'El MFA está encendido para tu rol: inscribí tu segundo factor ahora o en el próximo ingreso.',
  },
  'f5t6c.profile.sessions.title': { en: 'My open sessions · {n}', es: 'Mis sesiones abiertas · {n}' },
  'f5t6c.profile.sessions.this': { en: 'This session', es: 'Esta sesión' },
  'f5t6c.profile.sessions.open': { en: 'open {when}', es: 'abierta {when}' },
  'f5t6c.profile.sessions.note': {
    en: 'Sessions on other computers and the button to close them need server: today only this one is visible.',
    es: 'Las sesiones en otros equipos y el botón para cerrarlas necesitan servidor: hoy solo se ve la de acá.',
  },
  'f5t6c.profile.act.title': { en: 'My recent activity', es: 'Mi actividad reciente' },
  'f5t6c.profile.act.intro': {
    en: 'What you did, taken from the same audit log. It is here so you can review your own changes without opening the full feed.',
    es: 'Lo que hiciste vos, sacado del mismo registro de auditoría. Está acá para que puedas revisar tus propios cambios sin abrir el feed completo.',
  },
  'f5t6c.profile.act.close': {
    en: 'It cannot be edited or deleted: it is the same immutable log of the Audit log screen, filtered by your user.',
    es: 'No se puede editar ni borrar: es el mismo registro inmutable del Registro de auditoría, filtrado por tu usuario.',
  },
  'f5t6c.profile.act.empty': { en: 'You have not recorded activity today yet', es: 'Todavía no registraste actividad hoy' },
  'f5t6c.profile.act.error.title': { en: 'Could not load your activity', es: 'No se pudo cargar tu actividad' },
  'f5t6c.profile.act.error.body': {
    en: 'While this fails you cannot review your own recent changes.',
    es: 'Mientras falla, no podés revisar tus propios cambios recientes.',
  },
  'f5t6c.profile.act.error.retry': { en: 'Retry', es: 'Reintentar' },
  // ===== F5-T6b — Tanda 2 · Admin 05 (Reasignación de agentes) · Admin 07
  // (Reportes y exports) · Admin 09 (Auditoría). ES canónico del contrato;
  // EN derivado. Copys adaptados por honestidad (server no cubre el dibujo)
  // van declarados en el reporte del draft. =====

  // --- Admin 05 · Reasignación de agentes ---
  'f5t6b.rs.kicker': { en: 'Admin · Reassignment', es: 'Admin · Reasignación' },
  'f5t6b.rs.title': { en: 'Agent reassignment', es: 'Reasignación de agentes' },
  'f5t6b.rs.lead': {
    en: 'One surface, three mechanisms: auto-rebalance, manual one-by-one, and admin-only bulk.',
    es: 'Una sola superficie con tres mecanismos: auto-rebalanceo, manual de a uno, y masivo solo para admin.',
  },
  'f5t6b.rs.sub': {
    en: 'Switch mechanisms with the tabs. In auto-rebalance, "Apply" does not exist until you run the preview.',
    es: 'Cambiá de mecanismo con las pestañas. En auto-rebalanceo, «Aplicar» no existe hasta correr el preview.',
  },
  'f5t6b.rs.locks.title': { en: 'What this screen never does', es: 'Lo que esta pantalla nunca hace' },
  'f5t6b.rs.locks.1b': { en: 'It does not reset attempts.', es: 'No resetea los intentos.' },
  'f5t6b.rs.locks.1t': {
    en: 'The new agent inherits the attempts already spent. Resetting them burned patients in a loop.',
    es: 'El agente nuevo hereda los intentos ya gastados. Resetearlos agotaba pacientes en loop.',
  },
  'f5t6b.rs.locks.2b': { en: 'It does not move appointments.', es: 'No mueve las citas.' },
  'f5t6b.rs.locks.2t': {
    en: 'Each appointment stays with whoever booked it, even if the patient changes agents.',
    es: 'Cada cita se queda con quien la agendó, incluso si el paciente cambia de agente.',
  },
  'f5t6b.rs.locks.3b': { en: 'It does not touch the exhausted or the booked.', es: 'No toca a los agotados ni a los que tienen cita.' },
  'f5t6b.rs.locks.3t': {
    en: 'They stay out of the pool, and the ones skipped are listed one by one with their reason when you apply.',
    es: 'Quedan fuera del pool, y los salteados se listan uno por uno con su razón al aplicar.',
  },
  'f5t6b.rs.locks.4b': { en: 'It does not browse in silence.', es: 'No busca en silencio.' },
  'f5t6b.rs.locks.4t': {
    en: 'Search takes a complete ID or phone only, and opening a profile from here is logged with your name.',
    es: 'La búsqueda acepta solo identificadores completos, y abrir un perfil desde acá queda auditado con tu nombre.',
  },
  'f5t6b.rs.tab.auto': { en: 'Auto-rebalance', es: 'Auto-rebalanceo' },
  'f5t6b.rs.tab.manual': { en: 'Manual by drag', es: 'Manual por arrastre' },
  'f5t6b.rs.tab.bulk': { en: 'Bulk', es: 'Masivo' },
  'f5t6b.rs.tab.bulkChip': { en: 'admin only', es: 'solo admin' },
  'f5t6b.rs.auto.card1': { en: '1 · What gets rebalanced', es: '1 · Qué se rebalancea' },
  'f5t6b.rs.auto.motorNote': {
    en: 'The engine balances pending patients across active agents. Exhausted patients and patients with a scheduled appointment never enter the pool.',
    es: 'El motor equilibra pacientes pendientes entre los agentes activos. Los agotados y los que tienen cita agendada nunca entran al pool.',
  },
  'f5t6b.rs.auto.card2': { en: '2 · Preview', es: '2 · Preview' },
  'f5t6b.rs.chip.fresh': { en: 'Preview up to date', es: 'Preview al día' },
  'f5t6b.rs.chip.stale': { en: 'Parameters changed', es: 'Parámetros cambiados' },
  'f5t6b.rs.pv.emptyTitle': { en: 'You have not run the preview yet', es: 'Todavía no corriste el preview' },
  'f5t6b.rs.pv.emptyBody': {
    en: 'Nothing moves until you see exactly who changes agents and who stays out. The apply button does not exist before this.',
    es: 'Nada se mueve hasta que veas exactamente quién cambia de agente y quién queda afuera. El botón de aplicar no existe antes de esto.',
  },
  'f5t6b.rs.pv.run': { en: 'Run preview', es: 'Correr preview' },
  'f5t6b.rs.pv.running': { en: 'Computing…', es: 'Calculando…' },
  'f5t6b.rs.pv.moves': { en: 'Patients that move', es: 'Pacientes que se mueven' },
  'f5t6b.rs.pv.receivers': { en: 'Agents that receive', es: 'Agentes que reciben' },
  'f5t6b.rs.pv.discard': { en: 'Discard', es: 'Descartar' },
  'f5t6b.rs.pv.apply': { en: 'Apply the move', es: 'Aplicar el movimiento' },
  'f5t6b.rs.pv.applying': { en: 'Applying…', es: 'Aplicando…' },
  'f5t6b.rs.pv.noneTitle': { en: 'Nothing to move with these parameters', es: 'Nada que mover con estos parámetros' },
  'f5t6b.rs.pv.noneBody': {
    en: 'No caseload needs rebalancing right now.',
    es: 'Ninguna cartera necesita rebalanceo ahora mismo.',
  },
  'f5t6b.rs.pv.staysAt': { en: 'Ends at {n}', es: 'Queda en {n}' },
  'f5t6b.rs.pv.aria': { en: '{m} move, {r} agents receive', es: '{m} se mueven, {r} agentes reciben' },
  'f5t6b.rs.pv.skipNote': {
    en: 'The skipped ones, with their reason, are listed when you apply: today the preview does not anticipate them.',
    es: 'Los salteados, con su razón, se listan al aplicar: hoy el preview no los adelanta.',
  },
  'f5t6b.rs.ap.done': { en: '{n} patients moved', es: 'Se movieron {n} pacientes' },
  'f5t6b.rs.ap.skipTitle': { en: 'Left out of the pool · {n}', es: 'Quedaron fuera del pool · {n}' },
  'f5t6b.rs.ap.skipPatient': { en: 'Patient #{id}', es: 'Paciente #{id}' },
  'f5t6b.rs.skip.EXHAUSTED': { en: 'Attempts exhausted', es: 'Intentos agotados' },
  'f5t6b.rs.skip.HAS_SCHEDULED_BOOKING': { en: 'Has a scheduled appointment', es: 'Con cita agendada' },
  'f5t6b.rs.man.title': { en: 'Manual · drag between caseloads', es: 'Manual · arrastrar entre carteras' },
  'f5t6b.rs.man.hint': {
    en: 'Drag a card to the other column, or check it and use the Move button with the keyboard',
    es: 'Arrastrá una tarjeta a la otra columna, o marcala y usá el botón «Mover» con el teclado',
  },
  'f5t6b.rs.man.desc': {
    en: 'Pick the source and destination caseloads; the move is confirmed with its preview. Exhausted patients and patients with an appointment do not appear: they are not reassigned.',
    es: 'Elegí la cartera de origen y la de destino; el movimiento se confirma con su preview. Los agotados y los que tienen cita no aparecen: no se reasignan.',
  },
  'f5t6b.rs.man.excluded': {
    en: 'Exhausted (attempts at the cap) and patients with a scheduled appointment are not listed: they are not reassigned.',
    es: 'Agotados (intentos al tope) y pacientes con cita agendada no aparecen: no se reasignan.',
  },
  'f5t6b.rs.man.excludedCount': { en: 'Hidden now: {n}.', es: 'Ocultos ahora: {n}.' },
  'f5t6b.rs.man.load': { en: '{n} pending', es: '{n} pendientes' },
  'f5t6b.rs.blk.title': { en: 'Bulk · admin only', es: 'Masivo · sólo admin' },
  'f5t6b.rs.blk.desc': {
    en: 'Same engine as the auto-rebalance and the same gate: source, destination, quantity, preview, apply. The difference is that here you pick the set by hand instead of letting the cap decide.',
    es: 'Mismo motor que el auto-rebalanceo y la misma compuerta: origen, destino, cantidad, preview, aplicar. La diferencia es que acá elegís el conjunto a mano en vez de dejar que el tope decida.',
  },
  'f5t6b.rs.blk.from': { en: 'Source agent', es: 'Agente de origen' },
  'f5t6b.rs.blk.to': { en: 'Destination agent', es: 'Agente de destino' },
  'f5t6b.rs.blk.qty': { en: 'How many patients', es: 'Cuántos pacientes' },
  'f5t6b.rs.blk.note': {
    en: 'The bulk preview says how many of the requested patients will really move. If some are exhausted or booked, fewer move and the rest are listed: the quantity you ask for is a ceiling, not a promise.',
    es: 'El preview del masivo dice cuántos de los pedidos se van a mover de verdad. Si algunos están agotados o con cita, se mueven menos y el resto se lista: la cantidad que pedís es un techo, no una promesa.',
  },
  'f5t6b.rs.blk.capHint': { en: 'Up to 200 per run from this screen.', es: 'Hasta 200 por corrida desde esta pantalla.' },
  'f5t6b.rs.blk.short': {
    en: 'The source has {have} eligible patients: {take} will be previewed.',
    es: 'El origen tiene {have} elegibles: se van a previsualizar {take}.',
  },
  'f5t6b.rs.blk.clear': { en: 'Clear', es: 'Limpiar' },
  'f5t6b.rs.blk.invalid': {
    en: 'Pick a source, a different destination, and a quantity above zero.',
    es: 'Elegí un origen, un destino distinto y una cantidad mayor a cero.',
  },
  'f5t6b.rs.tab.orphaned': { en: 'Cases without an active owner', es: 'Casos sin dueño activo' },
  'f5t6b.rs.tab.orphanedChip': { en: 'admin only', es: 'solo admin' },
  'f5t6b.rs.orp.title': {
    en: 'Cases without an active owner (uploaders/inactive supervisors)',
    es: 'Casos sin dueño activo (uploaders/supervisores inactivos)',
  },
  'f5t6b.rs.orp.note': {
    en: 'These patients belong to a user who is not an active agent. Pick one to move their pending cases to an active agent.',
    es: 'Estos pacientes son de un usuario que no es un agente activo. Elegí uno para mover sus casos pendientes a un agente activo.',
  },
  'f5t6b.rs.orp.colName': { en: 'Name', es: 'Nombre' },
  'f5t6b.rs.orp.colEmail': { en: 'Email', es: 'Email' },
  'f5t6b.rs.orp.colRole': { en: 'Role', es: 'Rol' },
  'f5t6b.rs.orp.colActive': { en: 'Active', es: 'Activo' },
  'f5t6b.rs.orp.colPending': { en: 'Pending', es: 'Pendientes' },
  'f5t6b.rs.orp.active': { en: 'Active', es: 'Activo' },
  'f5t6b.rs.orp.inactive': { en: 'Inactive', es: 'Inactivo' },
  'f5t6b.rs.orp.reassignBtn': { en: 'Reassign these cases', es: 'Reasignar estos casos' },
  'f5t6b.rs.orp.empty': { en: 'No non-agent owner has pending cases.', es: 'Ningún dueño no-agente tiene casos pendientes.' },
  'f5t6b.rs.orp.back': { en: '← Back to the list', es: '← Volver a la lista' },
  'f5t6b.rs.se.title': { en: 'Find a patient', es: 'Buscar un paciente' },
  'f5t6b.rs.se.audit': { en: 'Opening a profile is logged', es: 'Abrir un perfil queda auditado' },
  'f5t6b.rs.se.ph': { en: 'Complete Medicaid ID or complete phone', es: 'ID de Medicaid completo o teléfono completo' },
  'f5t6b.rs.se.aria': { en: 'Find patient by Medicaid ID or phone', es: 'Buscar paciente por ID de Medicaid o teléfono' },
  'f5t6b.rs.se.clear': { en: 'Clear', es: 'Limpiar' },
  'f5t6b.rs.se.open': { en: 'Open the profile', es: 'Abrir el perfil' },
  'f5t6b.rs.se.searching': { en: 'Searching…', es: 'Buscando…' },
  'f5t6b.rs.se.emptyTitle': { en: 'No patient with that ID or phone', es: 'Ningún paciente con ese ID o teléfono' },
  'f5t6b.rs.se.emptyBody': {
    en: 'Partial search does not exist: try the complete identifier.',
    es: 'La búsqueda parcial no existe: probá con el identificador completo.',
  },
  'f5t6b.rs.se.noteIdle': {
    en: 'Without an active search no patient is shown. Search takes a complete Medicaid ID or a complete phone: there is no partial name search.',
    es: 'Sin búsqueda activa no se muestra ningún paciente. La búsqueda acepta ID de Medicaid completo o teléfono completo: no hay búsqueda parcial por nombre.',
  },
  'f5t6b.rs.se.noteActive': {
    en: 'Active search: data is shown only for the matches, and opening a profile is recorded with your name.',
    es: 'Búsqueda activa: los datos se muestran sólo para las coincidencias, y abrir un perfil queda registrado con tu nombre.',
  },
  'f5t6b.rs.se.id': { en: 'ID ****{last}', es: 'ID ****{last}' },
  'f5t6b.rs.overview': { en: 'Team panorama', es: 'Panorama del equipo' },

  // --- Admin 07 · Reportes y exports ---
  'f5t6b.rp.kicker': { en: 'Admin · Reports', es: 'Admin · Reportes' },
  'f5t6b.rp.title': { en: 'Reports & exports', es: 'Reportes y exports' },
  'f5t6b.rp.lead': {
    en: 'The aggregate reports that moved out of live monitoring, and a single export table where each one declares its columns. All exports are CSV.',
    es: 'Los reportes agregados que salieron del monitoreo en vivo, y una sola mesa de exports donde cada uno declara sus columnas. Todos los exports son CSV.',
  },
  'f5t6b.rp.sub': {
    en: 'Pick a period and look at the reports; below, each export says its columns before you download it.',
    es: 'Elegí un período y mirá los reportes; abajo, cada export dice sus columnas antes de bajarlo.',
  },
  'f5t6b.rp.period': { en: 'Period', es: 'Período' },
  'f5t6b.rp.p.7d': { en: '7 days', es: '7 días' },
  'f5t6b.rp.p.30d': { en: '30 days', es: '30 días' },
  'f5t6b.rp.p.mes': { en: 'This month', es: 'Este mes' },
  'f5t6b.rp.p.6m': { en: '6 months', es: '6 meses' },
  'f5t6b.rp.range.mes': { en: '{month} · up to today', es: '{month} · hasta hoy' },
  'f5t6b.rp.serie.kicker': { en: 'Screenings per day', es: 'Screenings por día' },
  'f5t6b.rp.serie.desc': {
    en: 'The series that came out of live monitoring: a trend does not belong on a screen that refreshes every 5 s.',
    es: 'La serie que salió del monitoreo en vivo: una tendencia no pertenece a una pantalla que se refresca cada 5 s.',
  },
  'f5t6b.rp.serie.total': { en: 'Period total', es: 'Total del período' },
  'f5t6b.rp.serie.avg': { en: 'Average per day', es: 'Promedio por día' },
  'f5t6b.rp.serie.aria': {
    en: 'Period {label}: {total} screenings, average {avg} per day',
    es: 'Período {label}: {total} screenings, promedio {avg} por día',
  },
  'f5t6b.rp.serie.na6mTitle': { en: 'The 6-month series needs monthly data', es: 'La serie de 6 meses necesita datos mensuales' },
  'f5t6b.rp.serie.na6mBody': {
    en: 'The server serves daily series up to 90 days. The period total and the list below do cover the 6 months.',
    es: 'El servidor sirve series diarias hasta 90 días. El total del período y la lista de abajo sí cubren los 6 meses.',
  },
  'f5t6b.rp.citas.kicker': { en: 'Appointments · 30-day KPIs', es: 'Citas · KPIs de 30 días' },
  'f5t6b.rp.citas.desc': {
    en: 'The monthly series that also came down from monitoring. Overdue and unattended lives in Missed bookings.',
    es: 'La serie mensual que también bajó del monitoreo. Lo vencido y sin atender vive en Citas perdidas.',
  },
  'f5t6b.rp.citas.agendadas': { en: 'Booked', es: 'Agendadas' },
  'f5t6b.rp.citas.atendidas': { en: 'Attended', es: 'Atendidas' },
  'f5t6b.rp.citas.noshow': { en: 'No-show', es: 'No-show' },
  'f5t6b.rp.lista.kicker': { en: 'Screenings of the period', es: 'Screenings del período' },
  'f5t6b.rp.lista.desc': {
    en: 'The global list that came out of monitoring, here with pagination. 25 per page.',
    es: 'La lista global que salió del monitoreo, acá con paginación. 25 por página.',
  },
  'f5t6b.rp.lista.page': {
    en: 'Page {p} of {tp} · {total} screenings in the period',
    es: 'Página {p} de {tp} · {total} screenings en el período',
  },
  'f5t6b.rp.lista.emptyTitle': { en: 'No screenings in the chosen period', es: 'No hay screenings en el período elegido' },
  'f5t6b.rp.lista.emptyBody': {
    en: 'Nothing was submitted between those dates.',
    es: 'No se envió nada entre esas fechas.',
  },
  'f5t6b.rp.lista.seeMonth': { en: 'See the full month', es: 'Ver el mes completo' },
  'f5t6b.rp.st.uploaded': { en: 'Uploaded', es: 'Subido' },
  'f5t6b.rp.st.pending': { en: 'Awaiting upload', es: 'Esperando subida' },
  'f5t6b.rp.st.flagged': { en: 'Flagged', es: 'Marcado' },
  'f5t6b.rp.st.no_medicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'f5t6b.rp.exp.kicker': { en: 'Exports · all CSV', es: 'Exports · todos en CSV' },
  'f5t6b.rp.exp.chip': { en: 'Exports with patient data are audited', es: 'Los exports con datos de paciente quedan auditados' },
  'f5t6b.rp.exp.desc': {
    en: 'The rule is one: if the file carries patient data, who downloaded it, when, and with which filter gets recorded. Each card says its columns before downloading anything.',
    es: 'La regla es una: si el archivo lleva datos de paciente, queda registrado quién lo bajó, cuándo y con qué filtro. Cada tarjeta dice sus columnas antes de bajar nada.',
  },
  'f5t6b.rp.exp.byagent.name': { en: 'Screenings by agent', es: 'Screenings por agente' },
  'f5t6b.rp.exp.byagent.cols': {
    en: 'Agent · submitted · uploaded · upload rate · calls · avg call · missed bookings · household · tier. No patients, no amounts.',
    es: 'Agente · enviados · subidos · tasa de subida · llamadas · duración media · citas perdidas · hogar · nivel. Sin pacientes, sin montos.',
  },
  'f5t6b.rp.exp.byuploader.name': { en: 'Activity by uploader', es: 'Actividad por uploader' },
  'f5t6b.rp.exp.byuploader.cols': {
    en: 'Uploader · uploaded · flagged · no Medicaid · avg hours to upload · SLA breaches. No patients, no amounts.',
    es: 'Uploader · subidos · marcados · sin Medicaid · horas promedio a subida · incumplimientos SLA. Sin pacientes, sin montos.',
  },
  'f5t6b.rp.exp.summary.name': { en: 'Period summary', es: 'Resumen del período' },
  'f5t6b.rp.exp.summary.cols': {
    en: 'Aggregate period metrics: screenings, bookings, household, calls. No identifiers.',
    es: 'Métricas agregadas del período: screenings, citas, hogar, llamadas. Sin identificadores.',
  },
  'f5t6b.rp.exp.tag.agg': { en: 'Aggregate · no names', es: 'Agregado · sin nombres' },
  'f5t6b.rp.exp.tag.staff': { en: 'Staff · no patients', es: 'Equipo · sin pacientes' },
  'f5t6b.rp.exp.download': { en: 'Download CSV', es: 'Bajar CSV' },
  'f5t6b.rp.exp.preparing': { en: 'Preparing…', es: 'Preparando…' },
  'f5t6b.rp.exp.emptyGuard': {
    en: 'This export has no rows in the chosen period — a file with only headers looks like a system error, so nothing was downloaded.',
    es: 'Este export no tiene filas en el período elegido — un archivo con sólo encabezados parece un error del sistema, así que no se bajó nada.',
  },
  'f5t6b.rp.conserved': { en: 'Team detail & tools', es: 'Detalle por equipo y herramientas' },

  // --- Admin 09 · Auditoría ---
  'f5t6b.au.kicker': { en: 'Admin · Audit', es: 'Admin · Auditoría' },
  'f5t6b.au.title': { en: 'Audit', es: 'Auditoría' },
  'f5t6b.au.lead': {
    en: 'The feed of what happened in the system, with each event’s entity navigable. It is the screen you consult when someone asks who touched what, and the one shown in a HIPAA review.',
    es: 'El feed de lo que pasó en el sistema, con la entidad de cada evento navegable. Es la pantalla que se consulta cuando alguien pregunta quién tocó qué, y la que se muestra en una revisión de HIPAA.',
  },
  'f5t6b.au.sub': {
    en: 'Tap the underlined name of any event to go to that entity, or "See detail" to open the modal with the before and after.',
    es: 'Tocá el nombre subrayado de cualquier evento para ir a esa entidad, o «Ver detalle» para abrir el modal con el antes y el después.',
  },
  'f5t6b.au.f.todo': { en: 'All', es: 'Todo' },
  'f5t6b.au.f.paciente': { en: 'Patient data access', es: 'Accesos a datos de paciente' },
  'f5t6b.au.f.config': { en: 'Configuration changes', es: 'Cambios de configuración' },
  'f5t6b.au.f.usuarios': { en: 'Users & sessions', es: 'Usuarios y sesiones' },
  'f5t6b.au.f.exports': { en: 'Exports', es: 'Exports' },
  'f5t6b.au.export': { en: 'Export the feed', es: 'Exportar el feed' },
  'f5t6b.au.k.lectura': { en: 'Read', es: 'Lectura' },
  'f5t6b.au.k.cambio': { en: 'Change', es: 'Cambio' },
  'f5t6b.au.k.sensible': { en: 'Sensitive', es: 'Sensible' },
  'f5t6b.au.k.irreversible': { en: 'Irreversible', es: 'Irreversible' },
  'f5t6b.au.detail': { en: 'See detail', es: 'Ver detalle' },
  'f5t6b.au.today': { en: 'Today {t}', es: 'Hoy {t}' },
  'f5t6b.au.yesterday': { en: 'Yesterday {t}', es: 'Ayer {t}' },
  'f5t6b.au.system': { en: 'System', es: 'Sistema' },
  'f5t6b.au.footer.immutable': {
    en: 'The feed is never deleted or edited: it only grows',
    es: 'El feed no se borra ni se edita: sólo crece',
  },
  'f5t6b.au.footer.page': {
    en: '{n} of {total} events · page {p} · 50 per page',
    es: '{n} de {total} eventos · página {p} · 50 por página',
  },
  'f5t6b.au.pager.prev': { en: 'Previous', es: 'Anterior' },
  'f5t6b.au.pager.next': { en: 'Next', es: 'Siguiente' },
  'f5t6b.au.pager.pageOf': { en: 'Page {p} of {tp}', es: 'Página {p} de {tp}' },
  'f5t6b.au.empty.day1Title': { en: 'No events recorded yet', es: 'Todavía no hay eventos registrados' },
  'f5t6b.au.empty.day1Body': {
    en: 'The feed grows only with system activity.',
    es: 'El feed crece solo con la actividad del sistema.',
  },
  'f5t6b.au.empty.filteredTitle': { en: 'No events of this type on this page', es: 'Ningún evento de este tipo en esta página' },
  'f5t6b.au.empty.filteredBody': {
    en: 'The filter applies to the loaded page. Try another page or see the whole feed.',
    es: 'El filtro aplica sobre la página cargada. Probá otra página o mirá el feed completo.',
  },
  'f5t6b.au.empty.seeAll': { en: 'See the whole feed', es: 'Ver todo el feed' },
  'f5t6b.au.advanced': { en: 'Fine filters (action and dates)', es: 'Filtros finos (acción y fechas)' },
  'f5t6b.au.m.who': { en: 'Who', es: 'Quién' },
  'f5t6b.au.m.what': { en: 'On what', es: 'Sobre qué' },
  'f5t6b.au.m.where': { en: 'From where', es: 'Desde dónde' },
  'f5t6b.au.m.changed': { en: 'What changed', es: 'Qué cambió' },
  'f5t6b.au.m.context': { en: 'Access context', es: 'Contexto del acceso' },
  'f5t6b.au.m.close': { en: 'Close', es: 'Cerrar' },
  'f5t6b.au.m.open': { en: 'Open {entity}', es: 'Abrir {entity}' },
  'f5t6b.au.m.immutable': { en: 'Event {id} · immutable', es: 'Evento {id} · inmutable' },
  'f5t6b.au.m.stamp': { en: '{time} · event {id}', es: '{time} · evento {id}' },
  'f5t6b.au.m.before': { en: 'before', es: 'antes' },
  'f5t6b.au.m.after': { en: 'now', es: 'ahora' },

  // F5-R4b · «Uploaders v2 — Actividad y cola» (dibujo del CEO, 2026-08-28).
  // ES canónico verbatim del dibujo; EN traducción. Bloque único.
  'f5r4b.view.activity': { en: 'Uploader activity', es: 'Actividad de uploaders' },
  'f5r4b.view.queue': { en: 'Upload queue', es: 'Cola de carga' },
  'f5r4b.title.activity': { en: 'Uploader activity', es: 'Actividad de uploaders' },
  'f5r4b.title.queue': { en: 'Upload queue · FindHelp team', es: 'Cola de carga · equipo FindHelp' },
  'f5r4b.title.ficha': { en: 'Uploader activity · {name}', es: 'Actividad de uploaders · {name}' },
  'f5r4b.subtitle.activity': {
    en: 'What each uploader is doing, with detail and over the range you choose.',
    es: 'Qué hace cada uploader, con detalle y en el rango que elijas.',
  },
  'f5r4b.subtitle.queue': {
    en: 'Read-only view of the queue and the flow into FindHelp.',
    es: 'Vista de sólo lectura de la cola y del flujo hacia FindHelp.',
  },
  'f5r4b.subtitle.ficha': {
    en: 'Their full profile: metrics, rhythm over time and all their uploads.',
    es: 'Su ficha completa: métricas, ritmo en el tiempo y todas sus cargas.',
  },
  'f5r4b.live.updated': { en: 'Updated {n} s ago', es: 'Actualizado hace {n} s' },
  'f5r4b.range.label': { en: 'Range', es: 'Rango' },
  'f5r4b.range.hoy': { en: 'Today', es: 'Hoy' },
  'f5r4b.range.7d': { en: '7 days', es: '7 días' },
  'f5r4b.range.30d': { en: '30 days', es: '30 días' },
  'f5r4b.range.mes': { en: 'This month', es: 'Este mes' },
  'f5r4b.range.trim': { en: 'Quarter', es: 'Trimestre' },
  'f5r4b.range.anio': { en: 'This year', es: 'Este año' },
  'f5r4b.range.custom': { en: 'Custom', es: 'Personalizado' },
  'f5r4b.range.deadDiag': {
    en: 'The server does not compute metrics for this range yet — needs backend.',
    es: 'El servidor todavía no calcula métricas para este rango — pendiente de backend.',
  },
  'f5r4b.range.note.hoy': { en: '{date} · since 00:00', es: '{date} · desde las 00:00' },
  'f5r4b.range.note.7d': { en: '{from} – {to} · rolling 7 days', es: '{from} – {to} · 7 días corridos' },
  'f5r4b.range.note.30d': { en: '{from} – {to} · rolling 30 days', es: '{from} – {to} · 30 días corridos' },
  'f5r4b.range.note.mes': { en: '{month} {year} · {n} days so far', es: '{month} {year} · {n} días corridos' },
  'f5r4b.a11y.rangeAnnounce': { en: 'Range {range}: {n} uploads', es: 'Rango {range}: {n} cargas' },
  'f5r4b.kpi.cargas': { en: 'Uploads in range', es: 'Cargas del rango' },
  'f5r4b.kpi.promedio': { en: 'Average per day', es: 'Promedio por día' },
  'f5r4b.kpi.promedio.sub': { en: 'whole team', es: 'equipo completo' },
  'f5r4b.kpi.tiempo': { en: 'Avg time to upload', es: 'Tiempo medio de revisión' },
  'f5r4b.kpi.tiempo.sub': { en: 'today only', es: 'sólo hoy' },
  'f5r4b.kpi.tiempo.diag': {
    en: 'The server only computes TODAY’s average review time — range version needs backend.',
    es: 'El servidor sólo calcula el tiempo medio de revisión de HOY — la versión por rango pide backend.',
  },
  'f5r4b.kpi.marcados': { en: 'Flagged with error', es: 'Marcados con error' },
  'f5r4b.kpi.marcados.sub': { en: 'right now · waiting on the agent', es: 'ahora mismo · esperando al agente' },
  'f5r4b.kpi.sinmed': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'f5r4b.kpi.sinmed.sub': { en: '{pct}% of processed · {range}', es: '{pct}% de lo procesado · {range}' },
  'f5r4b.kpi.reclamos': { en: 'Expired claims', es: 'Reclamos vencidos' },
  'f5r4b.kpi.reclamos.diag': {
    en: 'The server does not expose an expired-claims count — needs backend.',
    es: 'El servidor no expone el conteo de reclamos vencidos — pendiente de backend.',
  },
  'f5r4b.diag.devueltos': {
    en: 'The server does not count returns per uploader over a range — needs backend.',
    es: 'El servidor no cuenta las devoluciones por uploader en un rango — pendiente de backend.',
  },
  'f5r4b.diag.spark': {
    en: 'The server has no per-day series for this range — needs backend.',
    es: 'El servidor no tiene la serie por día para este rango — pendiente de backend.',
  },
  'f5r4b.list.kicker': { en: 'Per uploader · {range}', es: 'Por uploader · {range}' },
  'f5r4b.list.filter.placeholder': { en: 'Filter by name or email', es: 'Filtrar por nombre o correo' },
  'f5r4b.list.filter.aria': { en: 'Filter uploaders', es: 'Filtrar uploaders' },
  'f5r4b.list.activeOnly': { en: 'Active only', es: 'Sólo activos' },
  'f5r4b.row.cargas': { en: 'uploads', es: 'cargas' },
  'f5r4b.row.porDia': { en: 'per day', es: 'por día' },
  'f5r4b.row.devueltos': { en: 'returned', es: 'devueltos' },
  'f5r4b.row.inactive': { en: 'Inactive account', es: 'Cuenta inactiva' },
  'f5r4b.list.footHint': {
    en: 'Click a row to open their detail on the right',
    es: 'Click en una fila abre su detalle a la derecha',
  },
  'f5r4b.list.footActive': { en: '{n} active uploaders', es: '{n} uploaders activos' },
  'f5r4b.list.empty': { en: 'No uploaders match the filter.', es: 'Ningún uploader coincide con el filtro.' },
  'f5r4b.detail.stat.cargas': { en: 'Uploads', es: 'Cargas' },
  'f5r4b.detail.stat.porDia': { en: 'Per day', es: 'Por día' },
  'f5r4b.detail.stat.tiempo': { en: 'Avg time · today', es: 'Tiempo medio · hoy' },
  'f5r4b.detail.stat.devueltos': { en: 'Returned', es: 'Devueltos' },
  'f5r4b.detail.stat.sinmed': { en: 'No Medicaid · this month', es: 'Sin Medicaid · este mes' },
  'f5r4b.detail.stat.racha': { en: 'Active streak', es: 'Racha activa' },
  'f5r4b.detail.racha.dias': { en: '{n} days', es: '{n} días' },
  'f5r4b.detail.hours.kicker': { en: 'Uploads per hour · today only', es: 'Cargas por hora · sólo hoy' },
  'f5r4b.detail.hours.title': { en: '{h}:00 · {n} uploads', es: '{h}:00 · {n} cargas' },
  'f5r4b.detail.mix.kicker': {
    en: 'How their reviews ended · last 20',
    es: 'Cómo terminaron sus revisiones · últimas 20',
  },
  'f5r4b.mix.uploaded': { en: 'Uploaded', es: 'Cargado' },
  'f5r4b.mix.nomed': { en: 'Uploaded · no Medicaid', es: 'Cargado · sin Medicaid' },
  'f5r4b.mix.flagged': { en: 'Returned with error', es: 'Devuelto con error' },
  'f5r4b.detail.recent.kicker': {
    en: 'Latest reviews · not filtered by range',
    es: 'Últimas revisiones · sin filtrar por rango',
  },
  'f5r4b.detail.recent.empty': { en: 'No processed reviews yet.', es: 'Todavía no hay revisiones procesadas.' },
  'f5r4b.detail.note': {
    en: 'Uploader goals are motivational and do not affect their pay. These numbers are operational, not payroll.',
    es: 'Las metas del uploader son motivacionales y no afectan su pago. Estos números son de operación, no de nómina.',
  },
  'f5r4b.detail.openFicha': { en: 'Open their full profile', es: 'Abrir su ficha completa' },
  'f5r4b.detail.export': { en: 'Export their detail', es: 'Exportar su detalle' },
  'f5r4b.detail.pick': { en: 'Pick an uploader from the list.', es: 'Elegí un uploader de la lista.' },
  'f5r4b.rel.min': { en: '{n} min ago', es: 'hace {n} min' },
  'f5r4b.rel.h': { en: '{n} h ago', es: 'hace {n} h' },
  'f5r4b.rel.d': { en: '{n} d ago', es: 'hace {n} d' },
  'f5r4b.ficha.back': { en: 'All uploaders', es: 'Todos los uploaders' },
  'f5r4b.ficha.lastLogin': { en: 'Last login: {date}', es: 'Último acceso: {date}' },
  'f5r4b.ficha.lastLogin.never': { en: 'no logins recorded', es: 'sin accesos registrados' },
  'f5r4b.ficha.export': { en: 'Export the profile', es: 'Exportar la ficha' },
  'f5r4b.ficha.team': { en: 'View in Team center', es: 'Ver en Centro de equipo' },
  'f5r4b.ficha.kpi.equipo': { en: 'team: {n}', es: 'equipo: {n}' },
  'f5r4b.ficha.kpi.rachaSub': { en: 'consecutive days with uploads', es: 'días seguidos con cargas' },
  'f5r4b.vol.kicker': { en: 'Their volume over time', es: 'Su volumen en el tiempo' },
  'f5r4b.gran.dia': { en: 'Day', es: 'Día' },
  'f5r4b.gran.semana': { en: 'Week', es: 'Semana' },
  'f5r4b.gran.mes': { en: 'Month', es: 'Mes' },
  'f5r4b.gran.anio': { en: 'Year', es: 'Año' },
  'f5r4b.gran.note.dia': { en: 'Last 7 days · one bar per day', es: 'Últimos 7 días · una barra por día' },
  'f5r4b.gran.note.mes': { en: 'Last 12 months · one bar per month', es: 'Últimos 12 meses · una barra por mes' },
  'f5r4b.gran.deadDiag': {
    en: 'The server does not keep a series at this granularity — needs backend.',
    es: 'El servidor no guarda la serie con esta granularidad — pendiente de backend.',
  },
  'f5r4b.vol.bar.title': { en: '{tick}: {n} uploads', es: '{tick}: {n} cargas' },
  'f5r4b.vol.total': { en: 'Chart total', es: 'Total del gráfico' },
  'f5r4b.vol.prom': { en: 'Average per {unit}', es: 'Promedio por {unit}' },
  'f5r4b.vol.mejor': { en: 'Their best {unit}', es: 'Su mejor {unit}' },
  'f5r4b.vol.sinAct': { en: '{unit}s without activity', es: '{unit}s sin actividad' },
  'f5r4b.vol.unit.dia': { en: 'day', es: 'día' },
  'f5r4b.vol.unit.mes': { en: 'month', es: 'mes' },
  'f5r4b.cmp.kicker': { en: 'Against the team average', es: 'Contra el promedio del equipo' },
  'f5r4b.cmp.cargasDia': { en: 'Uploads per day', es: 'Cargas por día' },
  'f5r4b.cmp.tiempo': { en: 'Avg review time · today', es: 'Tiempo medio · hoy' },
  'f5r4b.cmp.devueltos': { en: 'Returned with error', es: 'Devueltos con error' },
  'f5r4b.cmp.dias': { en: 'Active days · last 7 days', es: 'Días activos · últimos 7 días' },
  'f5r4b.cmp.equipo': { en: 'team {n}', es: 'equipo {n}' },
  'f5r4b.err.kicker': {
    en: 'Why uploads were returned · their last 20 reviews',
    es: 'Por qué le devolvieron cargas · sus últimas 20 revisiones',
  },
  'f5r4b.err.no_medicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'f5r4b.err.data_inconsistent': { en: 'Inconsistent data', es: 'Datos inconsistentes' },
  'f5r4b.err.cin_invalid': { en: 'Invalid Medicaid ID', es: 'ID de Medicaid inválido' },
  'f5r4b.err.duplicate': { en: 'Repeated screening', es: 'Screening repetido' },
  'f5r4b.err.other': { en: 'Other reason', es: 'Otro motivo' },
  'f5r4b.err.empty': {
    en: 'No returns among their last 20 reviews.',
    es: 'Sin devoluciones entre sus últimas 20 revisiones.',
  },
  'f5r4b.heat.kicker': { en: 'When they work · day × hour', es: 'Cuándo trabaja · día × hora' },
  'f5r4b.heat.diag': {
    en: 'The server does not keep the day × hour distribution over a range — needs backend. Today only «Uploads per hour · today only» exists.',
    es: 'El servidor no guarda la distribución día × hora del rango — pendiente de backend. Hoy sólo existe «Cargas por hora · sólo hoy».',
  },
  'f5r4b.log.kicker': { en: 'Their uploads · last 20', es: 'Sus cargas · últimas 20' },
  'f5r4b.log.f.todas': { en: 'All', es: 'Todas' },
  'f5r4b.log.f.ok': { en: 'Uploaded', es: 'Cargadas' },
  'f5r4b.log.f.err': { en: 'Returned', es: 'Devueltas' },
  'f5r4b.log.f.sinmed': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'f5r4b.log.page': { en: '{n} of their last 20 reviews', es: '{n} de sus últimas 20 revisiones' },
  'f5r4b.log.export': { en: 'Export CSV · audited', es: 'Exportar CSV · auditado' },
  'f5r4b.log.export.diag': {
    en: 'There is no audited export of a single uploader’s uploads yet — needs backend.',
    es: 'No existe todavía el export auditado de las cargas de un uploader — pendiente de backend.',
  },
  'f5r4b.q.pendientes': { en: 'Pending', es: 'Pendientes' },
  'f5r4b.q.ahora': { en: 'right now', es: 'ahora mismo' },
  'f5r4b.q.atrasados': { en: 'Overdue +24 h', es: 'Atrasados +24 h' },
  'f5r4b.q.atrasados.sub': { en: 'right now · oldest: {h} h', es: 'ahora mismo · el más viejo: {h} h' },
  'f5r4b.q.subidos': { en: 'Uploaded', es: 'Subidos' },
  'f5r4b.aging.kicker': { en: 'Age of what’s pending · right now', es: 'Antigüedad de lo pendiente · ahora mismo' },
  'f5r4b.aging.enCola': { en: '{n} in queue', es: '{n} en cola' },
  'f5r4b.aging.lt24': { en: 'Less than 24 h', es: 'Menos de 24 h' },
  'f5r4b.aging.d1a3': { en: '1 to 3 days', es: '1 a 3 días' },
  'f5r4b.aging.gt3': { en: 'More than 3 days', es: 'Más de 3 días' },
  'f5r4b.aging.lt24.diag': {
    en: 'The server does not list pending items under 24 h to the admin — needs backend.',
    es: 'El servidor no le lista al admin lo pendiente de menos de 24 h — pendiente de backend.',
  },
  'f5r4b.aging.seg.title': { en: '{name}: {n}', es: '{name}: {n}' },
  'f5r4b.src.kicker': {
    en: 'Where the overdue comes from · right now',
    es: 'De dónde viene lo atrasado · ahora mismo',
  },
  'f5r4b.src.note': {
    en: '{total} overdue split among {agents} agents.',
    es: '{total} atrasados repartidos entre {agents} agentes.',
  },
  'f5r4b.src.empty': { en: 'Nothing overdue right now.', es: 'Nada atrasado ahora mismo.' },
  'f5r4b.tabs.pendientes': { en: 'Pending', es: 'Pendientes' },
  'f5r4b.tabs.marcados': { en: 'Flagged', es: 'Marcados' },
  'f5r4b.tabs.subidos': { en: 'Uploaded', es: 'Subidos' },
  'f5r4b.tabs.todos': { en: 'All', es: 'Todos' },
  'f5r4b.tabs.hogar': { en: 'Household', es: 'Hogar' },
  'f5r4b.tabs.diag': {
    en: 'The server only exposes the +24 h overdue slice to the admin — the full queue list needs backend.',
    es: 'El servidor sólo le expone al admin los atrasados de +24 h — la lista completa de la cola pide backend.',
  },
  'f5r4b.table.scope': { en: 'Overdue +24 h · right now', es: 'Atrasados +24 h · ahora mismo' },
  'f5r4b.col.espera': { en: 'Wait', es: 'Espera' },
  'f5r4b.col.cliente': { en: 'Client and agent', es: 'Cliente y agente' },
  'f5r4b.col.med': { en: 'Medicaid', es: 'Medicaid' },
  'f5r4b.col.estado': { en: 'Status', es: 'Estado' },
  'f5r4b.col.med.diag': {
    en: 'The overdue list does not carry the Medicaid mark — needs backend.',
    es: 'La lista de atrasados no trae la marca de Medicaid — pendiente de backend.',
  },
  'f5r4b.col.estado.diag': {
    en: 'The overdue list does not carry the claim status — needs backend.',
    es: 'La lista de atrasados no trae el estado de reclamo — pendiente de backend.',
  },
  'f5r4b.row.revisar': { en: 'Review', es: 'Revisar' },
  'f5r4b.table.empty': { en: 'Nothing overdue more than 24 h.', es: 'Nada atrasado más de 24 h.' },
  'f5r4b.table.page': { en: '{shown} of {total} · page {p} of {tp}', es: '{shown} de {total} · página {p} de {tp}' },
  'f5r4b.pager.prev': { en: 'Previous', es: 'Anterior' },
  'f5r4b.pager.next': { en: 'Next', es: 'Siguiente' },
  'f5r4b.pager.pageOf': { en: 'Page {p} of {tp}', es: 'Página {p} de {tp}' },
  'f5r4b.queue.note': {
    en: 'Admin view: read-only. «Review» opens the screening in consult mode and does not claim the row — claiming belongs to the uploader, and an admin claiming from here takes work away from someone by accident.',
    es: 'Vista del admin: de sólo lectura. «Revisar» abre el screening en modo consulta y no reclama la fila — reclamar es del uploader, y un admin reclamando desde acá le saca el trabajo a alguien sin querer.',
  },
  'f5r4b.queue.export.diag': {
    en: 'There is no queue export for the admin — needs backend.',
    es: 'No existe el export de la cola para el admin — pendiente de backend.',
  },
  'f5r4b.error.title': { en: 'Could not load the team’s activity', es: 'No se pudo cargar la actividad del equipo' },
  // F5-R4a — Admin «Navegación · Centro de control» (dibujo del CEO
  // 2026-08-28, «Navegación v2 - Centro de control.dc.html»). ES canónico
  // verbatim del dibujo; EN derivado. Donde el dibujo afirma algo que el
  // server no reporta, el copy lo dice (necesita server) en vez de fingirlo.
  // -- cabecera de la tarjeta
  'f5r4a.head.title': { en: 'Navigation', es: 'Navegación' },
  'f5r4a.head.sub': {
    en: 'Exceptions and money at risk first. Then the caseload, the uploads and the parameters.',
    es: 'Primero las excepciones y el dinero en riesgo. Después la cartera, las cargas y los parámetros.',
  },
  'f5r4a.refresh': { en: 'Refresh', es: 'Actualizar' },
  'f5r4a.period.7d': { en: '7 days', es: '7 días' },
  'f5r4a.period.30d': { en: '30 days', es: '30 días' },
  'f5r4a.period.custom': { en: 'Custom', es: 'Personalizado' },
  'f5r4a.period.na': {
    en: 'Needs server: this range does not exist in the live endpoints yet.',
    es: 'Necesita server: ese rango todavía no existe en los endpoints vivos.',
  },
  'f5r4a.period.note': {
    en: 'The period drives Live and By navigator; Cases does not read it yet (needs server).',
    es: 'El período rige Resumen y Por navigator; Casos todavía no lo lee (necesita server).',
  },
  // -- Excepciones
  'f5r4a.exc.moneyKicker': { en: 'Money at risk · resolved today', es: 'Dinero en riesgo · se resuelve hoy' },
  'f5r4a.exc.m1.label': {
    en: 'Billable sessions without a tracker mark',
    es: 'Sesiones facturables sin marca de tracker',
  },
  'f5r4a.exc.m1.rule': {
    en: 'Closed by the reaper at 480 min: they never went through step 2. Until they are marked, that time is not declared.',
    es: 'Cerradas por el reaper a los 480 min: no pasaron por el paso 2. Hasta que se marquen, ese tiempo no se declara.',
  },
  'f5r4a.exc.m1.action': { en: 'See the {n}', es: 'Ver las {n}' },
  'f5r4a.exc.m2.label': {
    en: 'Preauthorizations waiting for a signature',
    es: 'Preautorizaciones esperando firma',
  },
  'f5r4a.exc.m2.rule': {
    en: 'Without the signature, the navigator cannot mark the service as started or received.',
    es: 'Sin firma, el navigator no puede marcar el servicio como iniciado ni recibido.',
  },
  'f5r4a.exc.m2.action': { en: 'Open the tray', es: 'Abrir la bandeja' },
  'f5r4a.exc.oldestNa': {
    en: 'Age of the oldest: the server does not report it yet.',
    es: 'Antigüedad del más viejo: el servidor todavía no la reporta.',
  },
  'f5r4a.exc.opsKicker': { en: 'Operational exceptions', es: 'Excepciones operativas' },
  'f5r4a.exc.note': {
    en: 'Each exception states its rule and whose it is: without an owner, a pending list belongs to nobody. The two above are amber because they are the only ones that touch billing; the rest is operations and stays neutral.',
    es: 'Cada excepción dice su regla y de quién es: sin dueño, una lista de pendientes se vuelve de nadie. Las dos de arriba van en ámbar porque son las únicas que tocan facturación; el resto es operación y va en neutro.',
  },
  'f5r4a.exc.owner.navigator': { en: 'Navigator', es: 'Navigator' },
  'f5r4a.exc.owner.supervisor': { en: 'Supervisor', es: 'Supervisor' },
  'f5r4a.exc.owner.admin': { en: 'Admin', es: 'Admin' },
  'f5r4a.exc.ver': { en: 'See', es: 'Ver' },
  'f5r4a.exc.verAria': { en: 'See {tipo}', es: 'Ver {tipo}' },
  'f5r4a.exc.t.pendingForms': { en: 'Forms pending evidence', es: 'Formularios pendientes de evidencia' },
  'f5r4a.exc.r.pendingForms': {
    en: 'The ledger is waiting for the evidence or the FindHelp upload',
    es: 'El ledger espera la evidencia o la subida a FindHelp',
  },
  'f5r4a.exc.t.overdue': { en: 'Overdue follow-ups', es: 'Seguimientos vencidos' },
  'f5r4a.exc.r.overdue': {
    en: 'The agreed date passed and no call was recorded',
    es: 'Pasó la fecha acordada y no se registró la llamada',
  },
  'f5r4a.exc.t.timer': { en: 'Timer discrepancies', es: 'Discrepancias de reloj' },
  'f5r4a.exc.r.timer': {
    en: 'Declared time and the timer differ beyond the threshold',
    es: 'El tiempo declarado y el del cronómetro difieren más del umbral',
  },
  'f5r4a.exc.t.stale': { en: 'Appointments without an outcome', es: 'Citas sin resultado' },
  'f5r4a.exc.r.stale': {
    en: 'The appointment already happened and nobody recorded what came of it',
    es: 'La cita ya pasó y nadie registró qué pasó',
  },
  'f5r4a.exc.t.units': { en: 'Units exhausted', es: 'Unidades agotadas' },
  'f5r4a.exc.r.units': {
    en: 'The case reached the period’s unit cap',
    es: 'El caso llegó al tope de unidades del período',
  },
  'f5r4a.exc.t.overrides': { en: 'Manual overrides', es: 'Overrides manuales' },
  'f5r4a.exc.r.overrides': {
    en: 'A human overrode the automatic computation — worth checking it still makes sense',
    es: 'Un humano pisó el cálculo automático — conviene revisar que siga teniendo sentido',
  },
  'f5r4a.exc.missingTypes': {
    en: 'Untouched ≥14 days · Referrals without a reply · Expired EAs · Parked with a call-back: the server does not report these counts yet (needs server).',
    es: 'Sin tocar ≥14 días · Referidos sin respuesta · EA vencidas · Parqueados con devolución: el servidor todavía no reporta estos conteos (necesita server).',
  },
  'f5r4a.exc.emptyTitle': { en: 'Nothing at risk right now', es: 'Nada en riesgo ahora mismo' },
  'f5r4a.exc.emptyBody': {
    en: 'Zero sessions without a tracker, zero preauthorizations waiting and no operational exceptions. That is the good news.',
    es: 'Cero sesiones sin tracker, cero preautorizaciones esperando y ninguna excepción operativa. Es la buena noticia.',
  },
  'f5r4a.exc.caseList': { en: 'Cases with exceptions', es: 'Casos con excepciones' },
  'f5r4a.exc.filterChip': { en: 'Filtered: {tipo}', es: 'Filtrado: {tipo}' },
  'f5r4a.exc.filterClear': { en: 'Clear filter', es: 'Quitar filtro' },
  // -- Resumen
  'f5r4a.sum.kpi.open': { en: 'Open cases', es: 'Casos abiertos' },
  'f5r4a.sum.kpi.somos': { en: 'Priority (open)', es: 'Prioritarios (abiertos)' },
  'f5r4a.sum.kpi.pool': { en: 'In the mailbox', es: 'En el buzón' },
  'f5r4a.sum.kpi.navs': { en: 'Active navigators', es: 'Navigators activos' },
  'f5r4a.sum.kpi.sessions': { en: 'Sessions', es: 'Sesiones' },
  'f5r4a.sum.kpi.closed': { en: 'Closed (all-time)', es: 'Cerrados (histórico)' },
  'f5r4a.sum.deltaNa': {
    en: 'The “vs. previous period” deltas need server; Sessions follows the period above, the rest are live totals.',
    es: 'Los deltas «vs. período previo» necesitan server; Sesiones sigue el período de arriba, el resto son totales vivos.',
  },
  'f5r4a.sum.poolKicker': { en: 'Pool mailbox', es: 'Buzón del pool' },
  'f5r4a.sum.poolUnit': { en: 'cases not yet dealt out', es: 'casos sin repartir' },
  'f5r4a.sum.poolNote': {
    en: 'It has to go down. Two days in a row going up means the deal-out is not happening.',
    es: 'Tiene que bajar. Dos días seguidos subiendo significa que el reparto no está pasando.',
  },
  'f5r4a.sum.poolSeriesNa': {
    en: 'The daily series and the weekly trend need server.',
    es: 'La serie diaria y la tendencia semanal necesitan server.',
  },
  'f5r4a.sum.closuresKicker': { en: 'Closures by reason', es: 'Cierres por razón' },
  'f5r4a.sum.closuresNa': {
    en: 'All-time total — the per-period cut needs server.',
    es: 'Total histórico — el corte por período necesita server.',
  },
  'f5r4a.sum.closuresEmpty': { en: 'No closures recorded yet.', es: 'Todavía no hay cierres registrados.' },
  // -- Por navigator
  'f5r4a.nav.col.open': { en: 'Open', es: 'Abiertos' },
  'f5r4a.nav.col.stale': { en: 'Untouched ≥14 d', es: 'Sin tocar ≥14 d' },
  'f5r4a.nav.col.due': { en: 'Overdue', es: 'Vencidos' },
  'f5r4a.nav.col.gate': { en: 'No tracker', es: 'Sin tracker' },
  'f5r4a.nav.staleNa': {
    en: '“Untouched ≥14 d” per navigator needs server; Overdue comes from assignment loads and No tracker from the live exceptions.',
    es: '«Sin tocar ≥14 d» por navigator necesita server; Vencidos sale de las cargas de asignación y Sin tracker de las excepciones vivas.',
  },
  'f5r4a.nav.scope.full': { en: 'Full caseload', es: 'Cartera completa' },
  'f5r4a.nav.scope.closer': { en: 'Only closes negatives', es: 'Sólo cierra negativos' },
  'f5r4a.nav.rowAria': { en: 'Open the cases of {name}', es: 'Abrir los casos de {name}' },
  'f5r4a.nav.drillTitle': { en: 'Cases of {name}', es: 'Casos de {name}' },
  'f5r4a.nav.drillClose': { en: 'Close', es: 'Cerrar' },
  'f5r4a.nav.drillNote': {
    en: 'These are the cases with live exceptions. The full per-navigator case list needs server (navigator filter on the admin list).',
    es: 'Estos son los casos con excepciones vivas. El listado completo por navigator necesita server (filtro por navigator en el listado admin).',
  },
  'f5r4a.nav.drillEmpty': {
    en: '{name} has no cases with exceptions.',
    es: '{name} no tiene casos con excepciones.',
  },
  // -- Casos
  'f5r4a.cases.pill.abiertos': { en: 'Open', es: 'Abiertos' },
  'f5r4a.cases.pill.positivos': { en: 'Positives', es: 'Positivos' },
  'f5r4a.cases.pill.negativos': { en: 'Negatives', es: 'Negativos' },
  'f5r4a.cases.pill.prioritarios': { en: 'Priority', es: 'Prioritarios' },
  'f5r4a.cases.pill.buzon': { en: 'In the mailbox', es: 'En el buzón' },
  'f5r4a.cases.pill.cerrados': { en: 'Closed', es: 'Cerrados' },
  'f5r4a.cases.pill.sinconsent': { en: 'No consent', es: 'Sin consentimiento' },
  'f5r4a.cases.pillNa': {
    en: 'Needs server: this filter is not on the admin list yet — use the selectors below.',
    es: 'Necesita server: ese filtro todavía no está en el listado admin — usá los selectores de abajo.',
  },
  'f5r4a.cases.pillsNaNote': {
    en: 'Open · Positives · Negatives · In the mailbox · Closed: the composite filter needs server; the counts shown are live.',
    es: 'Abiertos · Positivos · Negativos · En el buzón · Cerrados: el filtro compuesto necesita server; los conteos que se ven sí son vivos.',
  },
  'f5r4a.cases.open': { en: 'Open', es: 'Abrir' },
  'f5r4a.cases.close': { en: 'Close', es: 'Cerrar' },
  'f5r4a.cases.attempts': { en: '{n} attempts', es: '{n} intentos' },
  'f5r4a.cases.pageFoot': {
    en: 'Page {page} of {pages} · 25 per page',
    es: 'Página {page} de {pages} · 25 por página',
  },
  'f5r4a.cases.export': { en: 'Export CSV · audited', es: 'Exportar CSV · auditado' },
  // -- Lista prioritaria
  'f5r4a.prio.k1': { en: '1 · The payer’s file', es: '1 · El archivo del pagador' },
  'f5r4a.prio.k2': { en: '2 · Preview · 3 · Apply', es: '2 · Preview · 3 · Aplicar' },
  'f5r4a.prio.dropCta': {
    en: 'Choose the file (.xlsx · .csv · .txt)',
    es: 'Elegir el archivo (.xlsx · .csv · .txt)',
  },
  'f5r4a.prio.noFile': { en: 'No file chosen yet.', es: 'Ningún archivo elegido todavía.' },
  'f5r4a.prio.fileRows': { en: '{rows} rows · {size}', es: '{rows} filas · {size}' },
  'f5r4a.prio.dropHint': {
    en: 'One column of Medicaid IDs. Re-applying the same file duplicates nothing.',
    es: 'Una columna de IDs de Medicaid. Volver a aplicar el mismo archivo no duplica nada.',
  },
  'f5r4a.prio.stat.match': { en: 'Match', es: 'Matchean' },
  'f5r4a.prio.stat.create': { en: 'Would be created', es: 'Se crearían' },
  'f5r4a.prio.stat.invalid': { en: 'Invalid', es: 'Inválidas' },
  'f5r4a.prio.invalidHint': {
    en: 'Repeated or empty rows in the file.',
    es: 'Filas repetidas o vacías del archivo.',
  },
  'f5r4a.prio.amberPool': {
    en: 'Apply creates {c} new cases with no navigator — they enter the pool mailbox — and marks the list as priority.',
    es: 'Aplicar crea {c} casos nuevos sin navigator — entran al buzón del pool — y marca la lista como prioritaria.',
  },
  'f5r4a.prio.amberNav': {
    en: 'Apply creates {c} new cases assigned to {name} and marks the list as priority.',
    es: 'Aplicar crea {c} casos nuevos asignados a {name} y marca la lista como prioritaria.',
  },
  'f5r4a.prio.amberNoCreate': {
    en: 'Apply marks the list as priority. No new case is created (the checkbox is off).',
    es: 'Aplicar marca la lista como prioritaria. No se crea ningún caso nuevo (la casilla está apagada).',
  },
  'f5r4a.prio.discard': { en: 'Discard', es: 'Descartar' },
  'f5r4a.prio.apply': { en: 'Apply the list', es: 'Aplicar la lista' },
  // -- Reasignación
  // E-36: arriba vive «Repartir parejo»; esto es lo puntual, a mano.
  'f5r4a.re.k1': { en: '1 · Move by hand', es: '1 · Mover a mano' },
  // E-36 · el subtítulo según la pestaña. Reemplaza a la frase de «solo
  // campos de asignación», que era falsa para las bajas: de una cuenta de
  // baja el paciente se mueve completo, con citas y sesiones (E-35d).
  'e36.re.subPool': {
    en: 'Only takes cases from the pool. Nothing is taken from anyone.',
    es: 'Solo toma casos del buzón. No le quita nada a nadie.',
  },
  'e36.re.subPerson': {
    en: 'From an active person only cases nobody has contacted move. From a deactivated one everything moves, with appointments and sessions.',
    es: 'De una persona activa salen solo casos que nadie ha contactado. De una de baja sale todo, con citas y sesiones.',
  },
  'f5r4a.re.filter': { en: 'Which cases', es: 'Qué casos' },
  'f5r4a.re.dest': { en: 'Who gets them', es: 'Para quién' },
  'f5r4a.re.qty': { en: 'How many episodes', es: 'Cuántos episodios' },
  'f5r4a.re.qtyAria': { en: 'How many episodes to move', es: 'Cuántos episodios mover' },
  'f5r4a.re.reason': { en: 'Reason', es: 'Motivo' },
  'f5r4a.re.required': { en: '· required', es: '· obligatorio' },
  'f5r4a.re.reasonPh': { en: 'Why this batch is moving', es: 'Por qué se mueve este lote' },
  // El badge cuenta lo que hay EN EL FILTRO, no lo que se puede mover al
  // destino elegido: si los 31 ya son de ese navigator, el badge sigue
  // diciendo 31 y el preview dice 0. Los dos tienen razon; la palabra
  // «disponibles» era la que mentia.
  //
  // E-36: el server cuenta SOLO el buzón (E-35d, `soloPool`), así que el chip
  // lo dice: «N en el buzón». En «De una persona» ese número no es de ella —
  // ahí el chip muestra el suyo (source-count) o no muestra nada.
  'f5r4a.re.pool': { en: '{n} in the pool', es: '{n} en el buzón' },
  'e36.re.chipPerson': { en: '{n} from this person', es: '{n} de esta persona' },
  'f5r4a.re.poolErr': { en: 'no data', es: 'sin dato' },
  'f5r4a.re.qtyNote': {
    en: 'At most {max}: the cap is what is available with the chosen filter, so you cannot ask for more.',
    es: 'Como máximo {max}: el tope es lo disponible con el filtro elegido, así que no se puede pedir de más.',
  },
  'f5r4a.re.qtyCap500': {
    en: 'The server takes at most 500 per batch.',
    es: 'El servidor acepta hasta 500 por tanda.',
  },
  'f5r4a.re.k2': { en: '2 · Preview', es: '2 · Preview' },
  'f5r4a.re.fresh': { en: 'Up to date', es: 'Al día' },
  'f5r4a.re.noPrevTitle': { en: 'No preview, no button', es: 'Sin preview no hay botón' },
  'f5r4a.re.noPrevBody': {
    en: 'Nothing moves until you see who changes navigator and who stays out.',
    es: 'Nada se mueve hasta ver quién cambia de navigator y quién queda afuera.',
  },
  'f5r4a.re.run': { en: 'Run preview', es: 'Correr preview' },
  'f5r4a.re.why.dest': { en: 'Pick the destination navigator', es: 'Elegí el navigator destino' },
  'f5r4a.re.why.reason': { en: 'Write the reason for the move', es: 'Escribí el motivo del movimiento' },
  'f5r4a.re.why.pool': {
    en: 'The pool has no cases with this filter',
    es: 'En el buzón no hay casos con este filtro',
  },
  'f5r4a.re.move': { en: 'Move {n} to {name}', es: 'Se mueven {n} a {name}' },
  // E-36 · por qué se queda lo trabajado de una persona ACTIVA (MEMBER_HAS_WORK).
  'e36.re.stayWorked': {
    en: 'Stay with {name}: {n} already worked (they have contact, appointments or sessions)',
    es: 'Se quedan con {name}: {n} ya trabajados (tienen contacto, citas o sesiones)',
  },
  'f5r4a.re.skip': { en: 'Skipped with a reason', es: 'Salteados con razón' },
  'f5r4a.re.skippedKicker': { en: 'Left out', es: 'Quedaron fuera' },
  // E-36: en «De una persona» los candidatos son de ella, no del buzón.
  'f5r4a.re.candidates': { en: '{n} candidates with this filter', es: '{n} candidatos con este filtro' },
  'f5r4a.re.noneSkipped': { en: 'Nobody was left out.', es: 'Ninguno quedó afuera.' },
  'f5r4a.re.skipNamesNa': {
    en: 'The per-person detail of the skipped needs server.',
    es: 'El detalle por persona de los salteados necesita server.',
  },
  'f5r4a.re.discard': { en: 'Discard', es: 'Descartar' },
  'f5r4a.re.assign': { en: 'Assign {n} episodes', es: 'Asignar {n} episodios' },
  'f5r4a.re.openLoad': { en: '{n} open cases', es: '{n} casos abiertos' },
  // E-36 · las insignias del origen y del destino (propuesta B).
  'e36.re.badgeActive': { en: 'Active', es: 'Activa' },
  'e36.re.badgeInactive': { en: 'Deactivated', es: 'De baja' },
  'e36.re.srcActive': {
    en: '{n} open cases · only the uncontacted move',
    es: '{n} casos abiertos · solo se mueven los no contactados',
  },
  'e36.re.srcInactive': {
    en: '{n} open cases · everything moves',
    es: '{n} casos abiertos · se mueve todo',
  },
  'e36.re.badgeNav': { en: 'Navigator', es: 'Navegadora' },
  'e36.re.badgeCloser': { en: 'Closer', es: 'Closer' },
  // El server ya lo rechaza (CLOSER_ONLY_NEGATIVES); esto avisa antes.
  'e36.re.closerOnlyNeg': {
    en: 'A closer only receives negatives',
    es: 'Un closer solo recibe negativos',
  },
  // ===== E-36 · «Repartir parejo» (Edd, 2026-10-21, propuesta B). Usa el
  // emparejamiento a mano que el server ya tiene (POST rebalance, E-35b). =====
  'e36.rb.kicker': { en: 'Deal out evenly', es: 'Repartir parejo' },
  'e36.rb.body': {
    en: 'Takes what is in the pool and what deactivated accounts hold, and deals it evenly among the active ones. Never takes worked cases.',
    es: 'Toma lo que hay en el buzón y lo de cuentas de baja, y lo reparte igual entre las activas. Nunca quita casos trabajados.',
  },
  'e36.rb.btn.full': { en: 'Deal to navigators', es: 'Repartir navegadoras' },
  'e36.rb.btn.closer': { en: 'Deal to closers', es: 'Repartir closers' },
  'e36.rb.type.full': { en: 'navigators', es: 'navegadoras' },
  'e36.rb.type.closer': { en: 'closers', es: 'closers' },
  'e36.rb.confirmTitle': { en: 'Deal out evenly · {type}', es: 'Repartir parejo · {type}' },
  'e36.rb.confirmGo': { en: 'Yes, deal to {type}', es: 'Sí, repartir {type}' },
  'e36.rb.confirmNo': { en: 'Cancel', es: 'Cancelar' },
  'e36.rb.started': {
    en: 'The {type} deal started. It runs in the background.',
    es: 'Empezó el reparto de {type}. Corre en segundo plano.',
  },
  'e36.rb.switchOff': {
    en: 'Dealing to closers is turned off in Config.',
    es: 'El reparto a closers está apagado en Config.',
  },
  'e36.rb.alreadyRunning': {
    en: 'A {type} deal is already running. Wait for it to finish.',
    es: 'Ya hay un reparto de {type} corriendo. Espera a que termine.',
  },
  'e36.rb.notStarted': { en: 'It did not start ({reason}).', es: 'No arrancó ({reason}).' },
  'e36.rb.failed': { en: 'Could not start the deal', es: 'No se pudo arrancar el reparto' },
  'e36.rb.last': {
    en: 'Last time: {when} · {type} · {n} moved · {status}',
    es: 'Última vez: {when} · {type} · {n} movidos · {status}',
  },
  'e36.rb.lastRunning': {
    en: 'Running now: {type} · started {when}',
    es: 'Corriendo ahora: {type} · empezó {when}',
  },
  'e36.rb.never': { en: 'Last time · {type}: never', es: 'Última vez · {type}: nunca' },
  'e36.rb.today': { en: 'today {time}', es: 'hoy {time}' },
  'e36.rb.status.done': { en: 'finished fine', es: 'terminó bien' },
  'e36.rb.status.switchOff': {
    en: 'did not run: turned off in Config',
    es: 'no corrió: apagado en Config',
  },
  'e36.rb.status.emptyGroup': {
    en: 'did not run: nobody active of that type',
    es: 'no corrió: no hay nadie activo de ese tipo',
  },
  'e36.rb.status.stale': { en: 'cut off (it hung)', es: 'se cortó (quedó colgada)' },
  'e36.rb.status.failed': { en: 'failed', es: 'falló' },
  'e36.rb.auto': { en: 'automatic', es: 'automática' },
  'e36.rb.runsErr': {
    en: 'Could not load the last runs.',
    es: 'No se pudieron cargar las últimas corridas.',
  },
  // -- Base de conocimiento
  'f5r4a.kb.kicker': { en: '{n} articles in {g} groups', es: '{n} artículos en {g} grupos' },
  'f5r4a.kb.note': {
    en: 'The navigator always sees the published version; there are no drafts visible to the operating role. The supervisor also edits here — it is the only thing of these two tabs they can touch.',
    es: 'El navigator ve siempre la versión publicada; no hay borradores visibles para el rol operativo. El supervisor también edita acá — es lo único de estas dos pestañas que puede tocar.',
  },
  'f5r4a.kb.cat.faq': { en: 'FAQ', es: 'FAQ' },
  'f5r4a.kb.cat.momentos': { en: 'Case moments', es: 'Momentos del caso' },
  'f5r4a.kb.cat.operativo': { en: 'Operations', es: 'Operativo' },
  'f5r4a.kb.cat.referencia': { en: 'Reference', es: 'Referencia' },
  'f5r4a.kb.note.faq': {
    en: 'Ready answers for the questions that repeat.',
    es: 'Respuestas listas para las preguntas que se repiten.',
  },
  'f5r4a.kb.note.momentos': {
    en: 'What to say at each moment of the case.',
    es: 'Qué decir en cada momento del caso.',
  },
  'f5r4a.kb.note.operativo': {
    en: 'Day-to-day procedures and steps.',
    es: 'Procedimientos y pasos del día a día.',
  },
  'f5r4a.kb.note.referencia': {
    en: 'Reference material and sources.',
    es: 'Material de consulta y fuentes.',
  },
  'f5r4a.kb.edited': { en: 'Edited {date}', es: 'Editado {date}' },
  'f5r4a.kb.groupAria': { en: 'Filter by {cat}', es: 'Filtrar por {cat}' },
  'f5r4a.kb.filterOn': { en: 'Showing: {cat}', es: 'Mostrando: {cat}' },
  'f5r4a.kb.filterOff': { en: 'Show all groups', es: 'Ver todos los grupos' },
  // -- Config
  'f5r4a.cfg.title': {
    en: 'Navigation keys are not edited here',
    es: 'Las claves de navegación no se editan acá',
  },
  'f5r4a.cfg.body': {
    en: 'They touch billing: they live in Configuration with a banner, history and an effective date. The raw JSON textarea no longer exists — it was the only way to change a rate without leaving a trace.',
    es: 'Tocan facturación: viven en Configuración con banner, historial y fecha de vigencia. El textarea de JSON crudo ya no existe — era la única vía para cambiar una tarifa sin dejar rastro.',
  },

  // F5-R7 — reasignacion manual sin limite artificial (encargo CEO 2026-08-29)
  'f5r7.all.cta': { en: 'Move ALL ({n}) to destination', es: 'Pasar TODOS ({n}) al destino' },
  'f5r7.all.progress': { en: 'Moving… {done} of {total}', es: 'Pasando… {done} de {total}' },
  'f5r7.all.confirmT': { en: 'You are about to move {n} patients', es: 'Vas a pasar {n} pacientes' },
  'f5r7.all.confirmB': { en: 'Everything eligible from the source goes to the destination, in batches of 200. Exhausted patients and those with an appointment are never moved. This runs now.', es: 'Todo lo elegible del origen pasa al destino, por tandas de 200. Los agotados y los que tienen cita jamás se mueven. Corre ahora.' },
  'f5r7.all.confirmGo': { en: 'Yes, move the {n}', es: 'Sí, pasar los {n}' },
  'f5r7.all.confirmNo': { en: 'Cancel', es: 'Cancelar' },
  'f5r7.all.done': { en: '{n} patients moved. Source is empty of eligibles.', es: '{n} pacientes pasados. El origen quedó sin elegibles.' },
  'f5r7.all.partial': { en: '{n} moved; {resto} remain (became ineligible mid-run).', es: '{n} pasados; quedan {resto} (se volvieron no-elegibles en el medio).' },
  'f5r7.all.ofTotal': { en: 'of {total} total — oldest first', es: 'de {total} en total — los más antiguos primero' },
  // F5-R6a — «Ficha de agente» (dibujo del CEO, Monitoreo v2 · 2026-08-29).
  // ES canónico verbatim del dibujo; EN derivado.
  'f5r6a.close': { en: 'Close the card', es: 'Cerrar la ficha' },
  'f5r6a.role.agent': { en: 'Agent', es: 'Agente' },
  'f5r6a.lang.en': { en: 'English', es: 'Inglés' },
  'f5r6a.lang.es': { en: 'Spanish', es: 'Español' },
  'f5r6a.loading': { en: 'Loading the card…', es: 'Cargando la ficha…' },
  'f5r6a.error': { en: "The card did not load: {msg}", es: 'No se pudo cargar la ficha: {msg}' },
  'f5r6a.state.call': { en: 'On a call', es: 'En llamada' },
  'f5r6a.state.idle': { en: 'No activity', es: 'Sin actividad' },
  'f5r6a.state.available': { en: 'Available', es: 'Disponible' },
  'f5r6a.state.off': { en: 'Disconnected', es: 'Desconectado' },
  'f5r6a.state.deactivated': { en: 'Deactivated', es: 'Dado de baja' },
  'f5r6a.deac.chip': { en: 'Deactivated · {d}', es: 'Dado de baja · {d}' },
  // -- «Ahora mismo»
  'f5r6a.now.callTitle': { en: 'On a call with {name}', es: 'En llamada con {name}' },
  'f5r6a.now.callTitleNoName': { en: 'On a call', es: 'En llamada' },
  'f5r6a.now.callBody': {
    en: 'Started {m} minutes ago and has {n} of 9 domains saved. The screening saves itself at every domain.',
    es: 'Empezó hace {m} minutos y lleva {n} de 9 dominios guardados. El screening se guarda solo en cada dominio.',
  },
  'f5r6a.now.callBodyStart': {
    en: 'Started {m} minutes ago. The screening saves itself at every domain.',
    es: 'Empezó hace {m} minutos. El screening se guarda solo en cada dominio.',
  },
  'f5r6a.now.callAction': { en: 'Open that screening', es: 'Abrir ese screening' },
  'f5r6a.now.idleTitle': { en: 'No activity for {m} minutes', es: 'Sin actividad desde hace {m} minutos' },
  'f5r6a.now.idleTitleNoTime': { en: 'No activity', es: 'Sin actividad' },
  'f5r6a.now.idleBody': {
    en: 'Has assignment #{id} taken but has not logged anything since {t}. If they do not come back, the case is better returned to the queue.',
    es: 'Tiene la asignación #{id} tomada pero no registró nada desde las {t}. Si no vuelve, el caso conviene devolverlo a la cola.',
  },
  'f5r6a.now.idleBodyNoAssign': {
    en: 'Nothing logged since {t}, and no assignment taken — nothing is stuck on their side.',
    es: 'No registró nada desde las {t} y no tiene ninguna asignación tomada — no hay nada trabado de su lado.',
  },
  'f5r6a.now.idleAction': { en: 'Return to the queue', es: 'Devolver a la cola' },
  'f5r6a.now.offTitle': { en: 'Disconnected since {t}', es: 'Desconectado desde las {t}' },
  'f5r6a.now.offTitleNoTime': { en: 'Disconnected', es: 'Desconectado' },
  'f5r6a.now.offBody': {
    en: 'Shift closed. No assignment taken, so nothing is stuck on their side.',
    es: 'Turno cerrado. No tiene ninguna asignación tomada, así que no hay nada trabado de su lado.',
  },
  'f5r6a.now.offBodyAssign': {
    en: 'Shift closed. Assignment #{id} is still taken — if they do not come back, it is better returned to the queue.',
    es: 'Turno cerrado. Todavía tiene la asignación #{id} tomada — si no vuelve, conviene devolverla a la cola.',
  },
  'f5r6a.now.availTitle': { en: 'Available', es: 'Disponible' },
  'f5r6a.now.availBody': { en: 'Connected, with no active call, since {t}.', es: 'Conectado y sin llamada activa desde las {t}.' },
  'f5r6a.now.availBodyNoTime': { en: 'Connected, with no active call.', es: 'Conectado y sin llamada activa.' },
  'f5r6a.now.deacTitle': { en: 'Deactivated agent', es: 'Agente dado de baja' },
  'f5r6a.now.deacBody': {
    en: 'The account was deactivated on {d}. The card stays for reference: their numbers and screenings remain here.',
    es: 'La cuenta se dio de baja el {d}. La ficha queda de consulta: sus números y screenings siguen acá.',
  },
  'f5r6a.now.deacBodyNoDate': {
    en: 'The account is deactivated. The card stays for reference: their numbers and screenings remain here.',
    es: 'La cuenta está dada de baja. La ficha queda de consulta: sus números y screenings siguen acá.',
  },
  'f5r6a.now.f.assignment': { en: 'Current assignment', es: 'Asignación actual' },
  'f5r6a.now.f.heartbeat': { en: 'Last heartbeat', es: 'Último latido' },
  'f5r6a.now.f.screen': { en: 'Screen', es: 'Pantalla' },
  'f5r6a.now.none': { en: 'None', es: 'Ninguna' },
  'f5r6a.now.hb.seconds': { en: '{n} seconds ago', es: 'hace {n} segundos' },
  'f5r6a.now.hb.minutes': { en: '{n} minutes ago', es: 'hace {n} minutos' },
  'f5r6a.now.hb.today': { en: 'Today {t}', es: 'Hoy {t}' },
  'f5r6a.now.hb.never': { en: 'No record', es: 'Sin registro' },
  'f5r6a.screen.screening': { en: 'Screening · {n} of 9 domains', es: 'Screening · {n} de 9 dominios' },
  'f5r6a.screen.screeningStart': { en: 'Screening', es: 'Screening' },
  'f5r6a.screen.precall': { en: 'Pre-call', es: 'Pre-llamada' },
  'f5r6a.screen.queue': { en: 'Queue', es: 'Cola' },
  'f5r6a.screen.bookings': { en: 'Bookings', es: 'Citas' },
  'f5r6a.screen.loggedOut': { en: 'Signed out', es: 'Cerró sesión' },
  // -- tabla métrica × período
  'f5r6a.table.kicker': { en: 'Their numbers by period', es: 'Sus números por período' },
  'f5r6a.table.metric': { en: 'Metric', es: 'Métrica' },
  'f5r6a.p.today': { en: 'Today', es: 'Hoy' },
  'f5r6a.p.todaySub': { en: 'current shift', es: 'turno en curso' },
  'f5r6a.p.week': { en: 'Week', es: 'Semana' },
  'f5r6a.p.weekSub': { en: 'Mon {d} to today', es: 'lun {d} a hoy' },
  'f5r6a.p.month': { en: 'Month', es: 'Mes' },
  'f5r6a.p.total': { en: 'Total', es: 'Total' },
  'f5r6a.p.totalSub': { en: 'since {d}', es: 'desde el {d}' },
  'f5r6a.p.totalSubNone': { en: 'no activity yet', es: 'sin actividad aún' },
  'f5r6a.m.screenings': { en: 'Screenings sent', es: 'Screenings enviados' },
  'f5r6a.m.attempts': { en: 'Contact attempts', es: 'Intentos de contacto' },
  'f5r6a.m.attemptsHint': { en: 'calls that did not end in a screening', es: 'llamadas que no terminaron en screening' },
  'f5r6a.m.contacts': { en: 'Contacts achieved', es: 'Contactos logrados' },
  'f5r6a.m.contactsHint': { en: 'of the attempts made', es: 'de los intentos hechos' },
  'f5r6a.m.contactsOf': { en: 'of {n}', es: 'de {n}' },
  'f5r6a.m.missed': { en: 'Missed bookings', es: 'Citas perdidas' },
  'f5r6a.m.missedHint': { en: 'member no-show', es: 'no-show del miembro' },
  'f5r6a.m.callTime': { en: 'Time on calls', es: 'Tiempo en llamada' },
  'f5r6a.m.callTimeHint': { en: 'hours and minutes', es: 'horas y minutos' },
  'f5r6a.m.activeTime': { en: 'Active time', es: 'Tiempo activo' },
  'f5r6a.m.activeTimeHint': { en: 'with an open session in Pathway · h and min', es: 'con sesión abierta en Pathway · h y min' },
  'f5r6a.m.noMedicaid': { en: 'No active Medicaid', es: 'Sin Medicaid activo' },
  'f5r6a.m.noMedicaidHint': { en: 'closed for ineligibility', es: 'cerrados por inelegibilidad' },
  // -- «Sus últimos screenings»
  'f5r6a.log.kicker': { en: 'Their latest screenings', es: 'Sus últimos screenings' },
  'f5r6a.log.filter.all': { en: 'All', es: 'Todos' },
  'f5r6a.log.filter.pending': { en: 'Awaiting upload', es: 'Esperando subida' },
  'f5r6a.log.filter.flagged': { en: 'Flagged with an error', es: 'Marcados con error' },
  'f5r6a.log.col.member': { en: 'Member', es: 'Miembro' },
  'f5r6a.log.col.duration': { en: 'Duration', es: 'Duración' },
  'f5r6a.log.col.state': { en: 'FindHelp status', es: 'Estado en FindHelp' },
  'f5r6a.log.hh': { en: 'Household', es: 'Hogar' },
  'f5r6a.log.hhOf': { en: '{rel} of {name}', es: '{rel} de {name}' },
  'f5r6a.log.cin': { en: 'CIN {cin}', es: 'CIN {cin}' },
  'f5r6a.log.cinNone': { en: 'no CIN on file', es: 'sin CIN registrado' },
  'f5r6a.log.today': { en: 'today {t}', es: 'hoy {t}' },
  'f5r6a.log.yesterday': { en: 'yesterday {t}', es: 'ayer {t}' },
  'f5r6a.log.positives': { en: '{n} positive domains', es: '{n} dominios positivos' },
  'f5r6a.log.positiveOne': { en: '1 positive domain', es: '1 dominio positivo' },
  'f5r6a.log.noNeeds': { en: 'no needs', es: 'sin necesidades' },
  'f5r6a.dur.ms': { en: '{m} m {s} s', es: '{m} m {s} s' },
  'f5r6a.log.state.uploaded': { en: 'Uploaded', es: 'Subido' },
  'f5r6a.log.state.pending': { en: 'Awaiting upload', es: 'Esperando subida' },
  'f5r6a.log.state.flagged': { en: 'Flagged · {reason}', es: 'Marcado · {reason}' },
  'f5r6a.flag.no_medicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'f5r6a.flag.cin_invalid': { en: 'Invalid ID', es: 'ID inválido' },
  'f5r6a.flag.data_inconsistent': { en: 'Inconsistent data', es: 'Datos inconsistentes' },
  'f5r6a.flag.duplicate': { en: 'Duplicate', es: 'Duplicado' },
  'f5r6a.flag.other': { en: 'Other', es: 'Otro' },
  'f5r6a.log.notesAria': { en: "{n} notes from {name}'s screening", es: '{n} notas del screening de {name}' },
  'f5r6a.log.notesAriaOne': { en: "1 note from {name}'s screening", es: '1 nota del screening de {name}' },
  'f5r6a.log.noNotes': { en: 'No notes', es: 'Sin notas' },
  'f5r6a.log.openDetail': { en: "Open {name}'s screening", es: 'Abrir el screening de {name}' },
  'f5r6a.log.page': {
    en: '{n} of {total} screenings · most recent first',
    es: '{n} de {total} screenings · los más recientes primero',
  },
  'f5r6a.log.all': { en: 'See all their screenings', es: 'Ver todos sus screenings' },
  'f5r6a.log.emptyFiltered': {
    en: 'None with that status among the latest {n}.',
    es: 'Ninguno con ese estado entre los últimos {n}.',
  },
  'f5r6a.empty.title': { en: 'They have not sent any yet', es: 'Todavía no envió ninguno' },
  'f5r6a.empty.body': {
    en: 'When their first screening comes in, the list appears here.',
    es: 'Cuando entre su primer screening, la lista aparece acá.',
  },
  // -- pie
  'f5r6a.foot.readOnly': {
    en: 'Read only · opening this card is recorded',
    es: 'Sólo lectura · abrir esta ficha queda registrado',
  },
  'f5r6a.foot.progress': { en: 'See their progress', es: 'Ver su progreso' },
  'f5r6a.foot.reassign': { en: 'Reassign their patients', es: 'Reasignar sus pacientes' },
  // F5-R6c · Admin 14 «Chat y notificaciones» (dibujo del CEO, 2026-08-28).
  // ES canónico verbatim del dibujo; EN traducción. Bloque único.
  // Dos textos NO son verbatim a propósito (dibujo trazado sobre código viejo,
  // divergencia medida y declarada en el reporte): la regla de chat_message
  // (hoy SÍ es una notificación real) y la nota de Agente↔Agente (hoy vive
  // prendido por Cluster T-7 — apagarlo es click del CEO en la ventana).
  'f5r6c.nav': { en: 'Chat & notifications', es: 'Chat y notificaciones' },
  'f5r6c.title': { en: 'Settings › Chat & notifications', es: 'Configuración › Chat y notificaciones' },
  'f5r6c.sub': {
    en: 'These keys never touch billing: they save directly, with history and a heads-up to the team.',
    es: 'Estas claves no tocan facturación: se guardan directo, con historial y aviso al equipo.',
  },
  'f5r6c.safeChip': { en: 'No impact on payroll or billing', es: 'Sin impacto en nómina ni facturación' },
  'f5r6c.save.none': { en: 'No changes to save', es: 'Sin cambios para guardar' },
  'f5r6c.save.one': { en: 'Save 1 change', es: 'Guardar 1 cambio' },
  'f5r6c.save.n': { en: 'Save {n} changes', es: 'Guardar {n} cambios' },
  'f5r6c.save.saving': { en: 'Saving…', es: 'Guardando…' },
  'f5r6c.save.ok': {
    en: 'Saved. The team gets a bell notice about the change.',
    es: 'Guardado. El equipo queda avisado por la campana.',
  },
  'f5r6c.save.err': {
    en: 'Could not save. Nothing was lost — try again.',
    es: 'No se pudo guardar. No se perdió nada: probá de nuevo.',
  },
  'f5r6c.tab.events': { en: 'Events & channels', es: 'Eventos y canales' },
  'f5r6c.tab.quiet': { en: 'Hours & escalation', es: 'Horarios y escalado' },
  'f5r6c.tab.chat': { en: 'Chat rules', es: 'Reglas del chat' },
  'f5r6c.tab.history': { en: 'History', es: 'Historial' },
  'f5r6c.role': { en: 'Role', es: 'Rol' },
  'f5r6c.role.agent': { en: 'Agent', es: 'Agente' },
  'f5r6c.role.navigator': { en: 'Navigator', es: 'Navigator' },
  'f5r6c.role.uploader': { en: 'Uploader', es: 'Uploader' },
  'f5r6c.role.supervisor': { en: 'Supervisor', es: 'Supervisor' },
  'f5r6c.role.admin': { en: 'Admin', es: 'Admin' },
  'f5r6c.roleNote': {
    en: '{n} events apply to this role · {bell} on the bell · {urgent} interrupt a call',
    es: '{n} eventos aplican a este rol · {bell} en campana · {urgent} interrumpen en llamada',
  },
  'f5r6c.bellAnnounce.on': {
    en: '{role}: {bell} events on the bell. The bell is drawn in their header.',
    es: '{role}: {bell} eventos en campana. La campana se le dibuja en su header.',
  },
  'f5r6c.bellAnnounce.off': {
    en: '{role}: 0 events on the bell. The bell is not drawn in their header. Turn at least one on and it appears.',
    es: '{role}: 0 eventos en campana. La campana no se le dibuja en su header. Prendé al menos uno para que aparezca.',
  },
  'f5r6c.bellWarn.b': {
    en: 'This role has no bell events at all, so the bell is not drawn for them.',
    es: 'Este rol no tiene ningún evento en campana, así que la campana no se le dibuja.',
  },
  'f5r6c.bellWarn.rest': {
    en: 'That is the rule: an icon that never brings anything teaches people to ignore the ones that do. Turn at least one on and the bell appears in their header.',
    es: 'Es la regla: un ícono que nunca trae nada enseña a ignorar los que sí. Prendé al menos uno y la campana aparece en su header.',
  },
  'f5r6c.col.event': { en: 'Event', es: 'Evento' },
  'f5r6c.col.prio': { en: 'Priority', es: 'Prioridad' },
  'f5r6c.col.remind': { en: 'Reminder', es: 'Recordatorio' },
  'f5r6c.chan.campana': { en: 'Bell', es: 'Campana' },
  'f5r6c.chan.campana.sub': { en: 'in the app', es: 'en la app' },
  'f5r6c.chan.chat': { en: 'Chat', es: 'Chat' },
  'f5r6c.chan.chat.sub': { en: 'system message', es: 'mensaje del sistema' },
  'f5r6c.chan.correo': { en: 'Email', es: 'Correo' },
  'f5r6c.chan.correo.sub': { en: 'deferred', es: 'diferido' },
  'f5r6c.blocked.correo': {
    en: 'Email does not apply: this notice only matters in the moment',
    es: 'Por correo no aplica: es un aviso que sólo sirve en el momento',
  },
  'f5r6c.blocked.chat': { en: 'This event already lives in the chat', es: 'Este evento ya vive en el chat' },
  'f5r6c.tag.nuevo': { en: 'new', es: 'nuevo' },
  'f5r6c.cell.aria': { en: '{event} · {channel} · {role}', es: '{event} · {channel} · {role}' },
  'f5r6c.prio.aria': {
    en: '{event} · priority {value} · {role} · tap to change',
    es: '{event} · prioridad {value} · {role} · tocar para cambiar',
  },
  'f5r6c.remind.aria': {
    en: '{event} · reminder {value} · {role} · tap to change',
    es: '{event} · recordatorio {value} · {role} · tocar para cambiar',
  },
  'f5r6c.prio.0': { en: 'Low', es: 'Baja' },
  'f5r6c.prio.0.note': { en: 'piles up, no sound', es: 'se acumula, no suena' },
  'f5r6c.prio.1': { en: 'Normal', es: 'Normal' },
  'f5r6c.prio.1.note': { en: 'rings on the bell', es: 'suena en la campana' },
  'f5r6c.prio.2': { en: 'Critical', es: 'Crítica' },
  'f5r6c.prio.2.note': { en: 'interrupts a call', es: 'interrumpe una llamada' },
  'f5r6c.remind.0': { en: 'No repeat', es: 'No repite' },
  'f5r6c.remind.0.note': { en: 'notified once', es: 'se avisa una vez' },
  'f5r6c.remind.1': { en: 'At 4 h', es: 'A las 4 h' },
  'f5r6c.remind.1.note': { en: 'one reminder and done', es: 'un recordatorio y listo' },
  'f5r6c.remind.2': { en: 'Every 24 h', es: 'Cada 24 h' },
  'f5r6c.remind.2.note': { en: 'until someone resolves it', es: 'hasta que alguien lo resuelva' },
  'f5r6c.remind.3': { en: 'Every 4 h', es: 'Cada 4 h' },
  'f5r6c.remind.3.note': { en: 'until someone opens it', es: 'hasta que alguien lo abra' },
  'f5r6c.matrixFoot': {
    en: 'Whatever is unchecked is not sent on that channel. Priority and reminder change by tapping the chip: critical is the only one that interrupts a call, and the reminder only repeats while the event stays unresolved. Notices pile up outside the time window: they are never discarded.',
    es: 'Lo que no está marcado no se avisa por ese canal. Prioridad y recordatorio se cambian tocando el chip: la crítica es la única que interrumpe una llamada, y el recordatorio sólo repite mientras el evento siga sin resolverse. Los avisos se acumulan fuera de la franja horaria: nunca se descartan.',
  },
  'f5r6c.matrixPending': {
    en: 'Today the emitters do not read this matrix yet: what you save here takes effect when the reminders/priorities server side is built (declared in the drawing itself).',
    es: 'Hoy los emisores todavía no leen esta matriz: lo guardado acá rige cuando se construya el lado del servidor de recordatorios y prioridades (declarado en el propio dibujo).',
  },
  // — eventos: etiqueta + regla («cada evento dice su regla», dibujo) —
  'f5r6c.ev.safety_concern.label': { en: 'Safety alert on a screening', es: 'Alerta de seguridad en un screening' },
  'f5r6c.ev.safety_concern.rule': { en: 'When it fires on a high-risk answer or the aggregate score', es: 'Cuando dispara por respuesta de alto riesgo o por puntaje agregado' },
  'f5r6c.ev.missed_booking.label': { en: 'Missed appointment', es: 'Cita perdida' },
  'f5r6c.ev.missed_booking.rule': { en: 'The cron marks it when the time passes and nobody logged the outcome', es: 'El cron la marca cuando pasa la hora y nadie registró el resultado' },
  'f5r6c.ev.reassign_requested.label': { en: 'Reassignment request', es: 'Pedido de reasignación' },
  'f5r6c.ev.reassign_requested.rule': { en: 'When someone asks to move a patient to another person', es: 'Cuando alguien pide mover un paciente a otra persona' },
  'f5r6c.ev.attempts_reset.label': { en: 'Attempts reset on your patient', es: 'Intentos reseteados en tu paciente' },
  'f5r6c.ev.attempts_reset.rule': { en: 'When an admin opens a new cycle on a case that was yours', es: 'Cuando un admin abre un ciclo nuevo en un caso que era tuyo' },
  'f5r6c.ev.bug_report_resolved.label': { en: 'Your bug report was resolved', es: 'Tu reporte de bug se resolvió' },
  'f5r6c.ev.bug_report_resolved.rule': { en: 'When someone changes the status of the report you opened', es: 'Cuando alguien cambia el estado del reporte que abriste' },
  'f5r6c.ev.chat_message.label': { en: 'Mention or message in the chat', es: 'Mención o mensaje en el chat' },
  'f5r6c.ev.chat_message.rule': {
    en: 'Each message creates a notification besides the chat’s own counter',
    es: 'Cada mensaje crea una notificación además del contador propio del chat',
  },
  'f5r6c.ev.your_screening_was_flagged.label': { en: 'Screening returned with an error', es: 'Screening devuelto con error' },
  'f5r6c.ev.your_screening_was_flagged.rule': { en: 'When the uploader returns it to the agent who sent it', es: 'Cuando el uploader lo devuelve al agente que lo envió' },
  'f5r6c.ev.screening_flagged_by_uploader.label': { en: 'Screening flagged by an uploader', es: 'Screening marcado con error por un uploader' },
  'f5r6c.ev.screening_flagged_by_uploader.rule': { en: 'When an uploader flags a screening so administration sees it', es: 'Cuando un uploader marca un screening con error, para que administración lo vea' },
  'f5r6c.ev.nav_form_reminder.label': { en: 'FindHelp form left unfinished', es: 'Formulario FindHelp sin completar' },
  'f5r6c.ev.nav_form_reminder.rule': { en: 'When a ledger form goes business days without moving', es: 'Cuando un formulario del ledger lleva días hábiles sin moverse' },
  'f5r6c.ev.findhelp_sla_breach_digest.label': { en: 'FindHelp delays digest', es: 'Digesto de atrasos FindHelp' },
  'f5r6c.ev.findhelp_sla_breach_digest.rule': { en: 'Summary for the opt-in recipients list in Settings when uploads breach the SLA', es: 'Resumen para la lista opt-in de Configuración cuando hay subidas fuera de SLA' },
  'f5r6c.ev.claim_expiring.label': { en: 'Claim about to expire', es: 'Reclamo por vencer' },
  'f5r6c.ev.claim_expiring.rule': { en: 'One minute before the 5 queue minutes are released', es: 'Un minuto antes de que se liberen los 5 minutos de la fila' },
  'f5r6c.ev.appointment_30m.label': { en: 'Appointment in 30 minutes', es: 'Cita en 30 minutos' },
  'f5r6c.ev.appointment_30m.rule': { en: 'Half an hour before a scheduled appointment', es: 'Media hora antes de una cita agendada' },
  'f5r6c.ev.followup_overdue.label': { en: 'Follow-up overdue', es: 'Seguimiento vencido' },
  'f5r6c.ev.followup_overdue.rule': { en: 'The day after the agreed date, with no call logged', es: 'Al día siguiente de la fecha acordada, sin llamada registrada' },
  'f5r6c.ev.preauth_pending.label': { en: 'Pre-authorization awaiting signature', es: 'Preautorización pendiente de firma' },
  'f5r6c.ev.preauth_pending.rule': { en: 'When requested, and while it is neither signed nor denied', es: 'Al pedirla, y mientras no se firme ni se deniegue' },
  'f5r6c.ev.preauth_denied.label': { en: 'Pre-authorization denied', es: 'Preautorización denegada' },
  'f5r6c.ev.preauth_denied.rule': { en: 'When the denial is signed, with the reason written', es: 'Al firmar la denegación, con el motivo escrito' },
  'f5r6c.ev.session_no_tracker.label': { en: 'Session closed with no tracker mark', es: 'Sesión cerrada sin marca de tracker' },
  'f5r6c.ev.session_no_tracker.rule': { en: 'When the reaper closes a session at 480 minutes', es: 'Cuando el reaper cierra una sesión a los 480 minutos' },
  'f5r6c.ev.batch_done.label': { en: 'Patient batch finished', es: 'Lote de pacientes terminado' },
  'f5r6c.ev.batch_done.rule': { en: 'When a batch upload or priority list finishes processing', es: 'Cuando termina de procesarse una carga de lote o lista prioritaria' },
  'f5r6c.ev.bug_blocking.label': { en: 'Blocking bug report', es: 'Reporte de bug que bloquea' },
  'f5r6c.ev.bug_blocking.rule': { en: 'When someone reports a problem and marks “it blocks me”', es: 'Cuando alguien reporta un problema y marca «me bloquea»' },
  // — Horarios y escalado —
  'f5r6c.quiet.k1': { en: 'Window in which the system notifies', es: 'Franja en la que el sistema avisa' },
  'f5r6c.quiet.p1': {
    en: 'Outside this window nothing rings or vibrates: notices pile up and are delivered on sign-in. None is lost.',
    es: 'Fuera de esta franja nada suena ni vibra: los avisos se acumulan y se entregan al abrir sesión. No se pierde ninguno.',
  },
  'f5r6c.quiet.from': { en: 'From', es: 'Desde' },
  'f5r6c.quiet.to': { en: 'Until', es: 'Hasta' },
  'f5r6c.quiet.p2': {
    en: 'The member-call window is 08:00–20:00 and lives in Operations. This one is wider on purpose: the team organizes before and after calling.',
    es: 'La franja de llamadas al miembro es 08:00–20:00 y vive en Operación. Esta es más ancha a propósito: el equipo se organiza antes y después de llamar.',
  },
  'f5r6c.quiet.invalid': {
    en: '“From” must be earlier than “Until” (HH:MM format).',
    es: '«Desde» tiene que ser antes que «Hasta» (formato HH:MM).',
  },
  'f5r6c.days.k': { en: 'Days without notices', es: 'Días sin avisos' },
  'f5r6c.days.p': {
    en: 'Checked = the system does notify that day. Saturday and Sunday off: nobody is on shift, and a weekend notice nobody can act on only teaches people to mute the app.',
    es: 'Marcado = el sistema sí avisa ese día. Sábado y domingo apagados: nadie tiene turno, y un aviso de fin de semana que nadie puede atender sólo enseña a silenciar la app.',
  },
  'f5r6c.day.mon': { en: 'Mon', es: 'Lun' },
  'f5r6c.day.tue': { en: 'Tue', es: 'Mar' },
  'f5r6c.day.wed': { en: 'Wed', es: 'Mié' },
  'f5r6c.day.thu': { en: 'Thu', es: 'Jue' },
  'f5r6c.day.fri': { en: 'Fri', es: 'Vie' },
  'f5r6c.day.sat': { en: 'Sat', es: 'Sáb' },
  'f5r6c.day.sun': { en: 'Sun', es: 'Dom' },
  'f5r6c.dnd.label': { en: 'Silence during a call', es: 'Silenciar durante una llamada' },
  'f5r6c.dnd.note': {
    en: 'With a screening open, only events marked as “cuts through in a call” get through.',
    es: 'Con un screening abierto, sólo pasan los eventos marcados como «atraviesa en llamada».',
  },
  'f5r6c.esc.k': { en: 'Automatic escalation', es: 'Escalado automático' },
  'f5r6c.esc.p': {
    en: 'How long the system waits before raising a notice to the supervisor. Too short and the supervisor gets noise; too long and the work falls silently.',
    es: 'Cuánto espera el sistema antes de subir un aviso al supervisor. Si es demasiado corto, el supervisor recibe ruido; si es demasiado largo, el trabajo se cae en silencio.',
  },
  'f5r6c.esc.claim.label': { en: 'Uploader claim expired', es: 'Reclamo vencido del uploader' },
  'f5r6c.esc.claim.rule': {
    en: 'Minutes after expiring before telling the supervisor the queue got stuck',
    es: 'Minutos después de vencer antes de avisarle al supervisor que la fila quedó trabada',
  },
  'f5r6c.esc.followup.label': { en: 'Follow-up overdue', es: 'Seguimiento vencido' },
  'f5r6c.esc.followup.rule': {
    en: 'Days of delay before it shows in the supervisor’s tray',
    es: 'Días de atraso antes de que aparezca en la bandeja del supervisor',
  },
  'f5r6c.esc.preauth.label': { en: 'Pre-authorization unsigned', es: 'Preautorización sin firmar' },
  'f5r6c.esc.preauth.rule': {
    en: 'Hours before reminding the supervisor something awaits their signature',
    es: 'Horas antes de recordarle al supervisor que hay algo esperando su firma',
  },
  'f5r6c.unit.min': { en: 'min', es: 'min' },
  'f5r6c.unit.days': { en: 'days', es: 'días' },
  'f5r6c.unit.h': { en: 'h', es: 'h' },
  // — Reglas del chat —
  'f5r6c.chat.k': { en: 'Who can write to whom', es: 'Quién puede escribirle a quién' },
  'f5r6c.pair.agentNavigator.label': { en: 'Agent ↔ Navigator', es: 'Agente ↔ Navigator' },
  'f5r6c.pair.agentNavigator.note': {
    en: 'The case moves from one to the other: the most frequent conversation in the system.',
    es: 'El caso pasa de uno a otro: es la conversación más frecuente del sistema.',
  },
  'f5r6c.pair.agentUploader.label': { en: 'Agent ↔ Uploader', es: 'Agente ↔ Uploader' },
  'f5r6c.pair.agentUploader.note': {
    en: 'To resolve a returned screening without another full round trip.',
    es: 'Para resolver un screening devuelto sin que dé otra vuelta completa.',
  },
  'f5r6c.pair.uploaderNavigator.label': { en: 'Uploader ↔ Navigator', es: 'Uploader ↔ Navigator' },
  'f5r6c.pair.uploaderNavigator.note': {
    en: 'Rare, but it happens when an upload affects a case in progress.',
    es: 'Poco frecuente, pero pasa cuando una carga afecta un caso en curso.',
  },
  'f5r6c.pair.anyoneSupervisor.label': { en: 'Anyone → Supervisor', es: 'Cualquiera → Supervisor' },
  'f5r6c.pair.anyoneSupervisor.note': {
    en: 'Nobody is left unable to escalate. It cannot be fully turned off.',
    es: 'Nadie queda sin poder escalar. No se puede apagar del todo.',
  },
  'f5r6c.pair.agentAgent.label': { en: 'Agent ↔ Agent', es: 'Agente ↔ Agente' },
  'f5r6c.pair.agentAgent.note': {
    en: 'On today (Cluster T-7): agents coordinate handoffs and coverage between peers. The drawing proposes turning it off — that flip is the CEO’s.',
    es: 'Hoy prendido (Cluster T-7): los agentes coordinan traspasos y cobertura entre pares. El dibujo propone apagarlo — ese click es del CEO.',
  },
  'f5r6c.priv.k': { en: 'Chat privacy', es: 'Privacidad del chat' },
  'f5r6c.priv.always': { en: 'Always', es: 'Siempre' },
  'f5r6c.priv.audited.label': { en: 'The chat is internal and audited', es: 'El chat es interno y auditado' },
  'f5r6c.priv.audited.note': {
    en: 'Every message keeps author, time and thread. It is never deleted or edited.',
    es: 'Cada mensaje guarda autor, hora e hilo. No se borra ni se edita.',
  },
  'f5r6c.priv.nomirror.label': { en: 'Not mirrored to FindHelp', es: 'No se espeja a FindHelp' },
  'f5r6c.priv.nomirror.note': {
    en: 'The record is written in the case note, not here.',
    es: 'El expediente se escribe en la nota del caso, no acá.',
  },
  'f5r6c.priv.nofiles.label': { en: 'No file attachments', es: 'Sin archivos adjuntos' },
  'f5r6c.priv.nofiles.note': {
    en: 'A Pathway screenshot is patient information. A case reference is attached instead.',
    es: 'Una captura de Pathway es información del paciente. Se adjunta una referencia de caso.',
  },
  'f5r6c.priv.foot': {
    en: 'These three are not editable: they come from compliance, not product. They are shown so nobody digs for them in the database.',
    es: 'Estas tres no se editan: vienen de cumplimiento, no de producto. Se muestran para que nadie las busque en la base.',
  },
  'f5r6c.ret.k': { en: 'Message retention', es: 'Retención de mensajes' },
  'f5r6c.ret.aria': { en: 'Message retention days', es: 'Días de retención de mensajes' },
  'f5r6c.ret.years': { en: '{y} years', es: '{y} años' },
  'f5r6c.ret.days': { en: '{d} days', es: '{d} días' },
  'f5r6c.ret.warn': {
    en: 'Careful: under one year, threads linked to cases are deleted before the case. Whoever opens that case in the future will not be able to read why things were decided the way they were.',
    es: 'Atención: con menos de un año, los hilos vinculados a casos se borran antes que el caso. Quien abra ese caso en el futuro no va a poder leer por qué se decidió lo que se decidió.',
  },
  'f5r6c.ret.ok': {
    en: 'Threads linked to a case live at least as long as the case. The chat is not a clinical record, but it is the trail of operational decisions.',
    es: 'Los hilos vinculados a un caso viven al menos tanto como el caso. El chat no es expediente clínico, pero sí es el rastro de las decisiones operativas.',
  },
  'f5r6c.ret.pending': {
    en: 'Today no sweep deletes messages: this number governs when that sweep exists (needs server).',
    es: 'Hoy ningún barrido borra mensajes: este número rige cuando ese barrido exista (necesita server).',
  },
  // — Historial —
  'f5r6c.hist.k': { en: 'Changes in this group', es: 'Cambios en este grupo' },
  'f5r6c.hist.foot': {
    en: 'Every change notifies the team through the bell: if the system stops notifying something, people must find out through the system, not by noticing.',
    es: 'Cada cambio avisa al equipo por la campana: si el sistema deja de avisar algo, la gente tiene que enterarse por el sistema, no por darse cuenta.',
  },
  'f5r6c.hist.empty': { en: 'No changes saved in this group yet.', es: 'Todavía no hay cambios guardados en este grupo.' },
  'f5r6c.hist.errTitle': { en: 'Could not load the history', es: 'No se pudo cargar el historial' },
  'f5r6c.hist.errBody': { en: 'The list did not arrive. Try again.', es: 'La lista no llegó. Probá de nuevo.' },
  'f5r6c.hist.retry': { en: 'Retry', es: 'Reintentar' },
  'f5r6c.key.notify_matrix': { en: 'Events & channels matrix', es: 'Matriz de eventos y canales' },
  'f5r6c.key.notify_quiet_start': { en: 'Notice window — from', es: 'Franja de avisos — desde' },
  'f5r6c.key.notify_quiet_end': { en: 'Notice window — until', es: 'Franja de avisos — hasta' },
  'f5r6c.key.notify_days': { en: 'Days with notices', es: 'Días con avisos' },
  'f5r6c.key.notify_dnd_in_call': { en: 'Silence during a call', es: 'Silenciar durante una llamada' },
  'f5r6c.key.notify_esc_claim_expired_min': { en: 'Escalation: claim expired', es: 'Escalado: reclamo vencido' },
  'f5r6c.key.notify_esc_followup_overdue_days': { en: 'Escalation: follow-up overdue', es: 'Escalado: seguimiento vencido' },
  'f5r6c.key.notify_esc_preauth_unsigned_hours': { en: 'Escalation: pre-auth unsigned', es: 'Escalado: preautorización sin firmar' },
  'f5r6c.key.chat_rules': { en: 'Chat rules', es: 'Reglas del chat' },
  'f5r6c.key.chat_retention_days': { en: 'Message retention', es: 'Retención de mensajes' },
  'f5r6c.err.title': { en: 'Could not load the group', es: 'No se pudo cargar el grupo' },
  'f5r6c.err.body': { en: 'The settings did not arrive. Try again.', es: 'La configuración no llegó. Probá de nuevo.' },
  // — campana (aviso de cambio de configuración, tipo notify_settings_changed) —
  'f5r6c.bell.title': { en: 'What the system notifies changed', es: 'Cambió qué avisa el sistema' },
  'f5r6c.bell.bodyAdmin': {
    en: '“Chat & notifications” was updated by {name}. Open it to see what changed.',
    es: 'Se actualizó «Chat y notificaciones» ({name}). Abrila para ver qué cambió.',
  },
  'f5r6c.bell.body': {
    en: 'What the system notifies you about, and through which channel, was updated by {name}.',
    es: 'Se actualizó qué te avisa el sistema y por dónde ({name}).',
  },
  // F5-R6b — Chat v2 «Mensajería del equipo» (dibujo del CEO, 2026-08-29).
  // El castellano es verbatim del .dc.html; el inglés es su traducción.
  'f5r6b.title': { en: 'Team messaging', es: 'Mensajería del equipo' },
  'f5r6b.close': { en: 'Close the chat', es: 'Cerrar el chat' },
  'f5r6b.openChat': { en: 'Open the chat', es: 'Abrir el chat' },
  'f5r6b.myStatus': { en: 'My status', es: 'Mi estado' },
  'f5r6b.presence.free': { en: 'Available', es: 'Disponible' },
  'f5r6b.presence.call': { en: 'On a call', es: 'En llamada' },
  'f5r6b.presence.off': { en: 'Off shift', es: 'Fuera de turno' },
  'f5r6b.list.title': { en: 'Chat', es: 'Chat' },
  'f5r6b.list.new': { en: 'New conversation', es: 'Nueva conversación' },
  'f5r6b.list.search': { en: 'Search the chat', es: 'Buscar en el chat' },
  'f5r6b.list.searchAria': { en: 'Search conversations and messages', es: 'Buscar conversaciones y mensajes' },
  'f5r6b.filter.all': { en: 'All', es: 'Todo' },
  'f5r6b.filter.cases': { en: 'Cases', es: 'Casos' },
  'f5r6b.filter.unread': { en: 'Unread', es: 'Sin leer' },
  'f5r6b.group.needsReply': { en: 'Needs a reply', es: 'Exige respuesta' },
  'f5r6b.group.cases': { en: 'Cases', es: 'Casos' },
  'f5r6b.group.team': { en: 'Team', es: 'Equipo' },
  'f5r6b.tag.case': { en: 'Case', es: 'Caso' },
  'f5r6b.tag.group': { en: 'Group', es: 'Grupo' },
  'f5r6b.tag.escalated': { en: 'Escalated', es: 'Escalado' },
  'f5r6b.tag.muted': { en: 'Muted', es: 'Silenciada' },
  'f5r6b.thread.people': { en: '{n} people', es: '{n} personas' },
  'f5r6b.thread.mute': { en: 'Mute this conversation', es: 'Silenciar esta conversación' },
  'f5r6b.thread.unmute': { en: 'Unmute this conversation', es: 'Dejar de silenciar esta conversación' },
  'f5r6b.thread.viewCase': { en: 'View the linked case', es: 'Ver el caso vinculado' },
  'f5r6b.day.today': { en: 'Today', es: 'Hoy' },
  'f5r6b.day.yesterday': { en: 'Yesterday', es: 'Ayer' },
  'f5r6b.sys.joined': {
    en: '{name} joined the conversation · added by {by}',
    es: '{name} se sumó a la conversación · lo agregó {by}',
  },
  'f5r6b.msg.seen': { en: 'Seen {time}', es: 'Visto {time}' },
  'f5r6b.msg.sent': { en: 'Sent', es: 'Enviado' },
  'f5r6b.msg.seenTitle': { en: '{name} saw it at {time}', es: '{name} lo vio a las {time}' },
  'f5r6b.msg.seenManyTitle': { en: 'Seen by {n} people', es: 'Lo vieron {n} personas' },
  'f5r6b.msg.sentTitle': {
    en: 'Sent at {time} · not seen yet',
    es: 'Enviado a las {time} · todavía no lo vio',
  },
  'f5r6b.msg.quickReact': { en: 'React with {emoji}', es: 'Reaccionar con {emoji}' },
  'f5r6b.msg.reactSum': {
    en: '{n} reacted with {emoji} · add yours',
    es: '{n} reaccionaron con {emoji} · sumar la tuya',
  },
  'f5r6b.msg.you': { en: 'You', es: 'Vos' },
  'f5r6b.dnd.title': { en: 'You are on a call.', es: 'Estás en llamada.' },
  'f5r6b.dnd.body': {
    en: 'Messages will not ring until you close the screening; you will see them piled up when you leave.',
    es: 'No te van a sonar mensajes hasta que cierres el screening; los vas a ver acumulados al salir.',
  },
  'f5r6b.tray.recent': { en: 'Your most used', es: 'Los que más usás' },
  'f5r6b.tray.cat.caras': { en: 'Faces and gestures', es: 'Caras y gestos' },
  'f5r6b.tray.cat.manos': { en: 'Hands and signs', es: 'Manos y señas' },
  'f5r6b.tray.cat.trabajo': { en: 'Work and cases', es: 'Trabajo y casos' },
  'f5r6b.tray.cat.salud': { en: 'Health and home', es: 'Salud y hogar' },
  'f5r6b.tray.cat.animo': { en: 'Cheer', es: 'Ánimo' },
  'f5r6b.tray.cat.tiempo': { en: 'Weather and places', es: 'Tiempo y lugar' },
  'f5r6b.tray.insert': { en: 'Insert {emoji}', es: 'Insertar {emoji}' },
  'f5r6b.composer.placeholder': {
    en: 'Write a message… use @ to mention someone · Enter sends, Shift+Enter breaks the line',
    es: 'Escribí un mensaje… usá @ para mencionar a alguien · Enter envía, Shift+Enter baja de línea',
  },
  'f5r6b.composer.aria': { en: 'Write a message', es: 'Escribir un mensaje' },
  'f5r6b.composer.send': { en: 'Send message', es: 'Enviar mensaje' },
  'f5r6b.composer.emoji': { en: 'Emoji', es: 'Emoji' },
  'f5r6b.composer.linkCase': { en: 'Link a case', es: 'Vincular un caso' },
  'f5r6b.composer.mention': { en: 'Mention', es: 'Mencionar' },
  'f5r6b.composer.escalate': { en: 'Escalate', es: 'Escalar' },
  'f5r6b.composer.rule': {
    en: 'Internal and audited · the record goes to FindHelp',
    es: 'Interno y auditado · el expediente va a FindHelp',
  },
  'f5r6b.composer.sendFailed': {
    en: 'It did not go out. What you wrote is still in the box — try again.',
    es: 'No salió. Lo que escribiste sigue en el cuadro — reintentá.',
  },
  'f5r6b.mention.title': { en: 'Mention someone', es: 'Mencionar a alguien' },
  'f5r6b.ctx.linked': { en: 'Linked case', es: 'Caso vinculado' },
  'f5r6b.ctx.change': { en: 'Change', es: 'Cambiar' },
  'f5r6b.ctx.open': { en: 'Open the case', es: 'Abrir el caso' },
  'f5r6b.ctx.isCase': { en: 'Is this about a case?', es: '¿Es sobre un caso?' },
  'f5r6b.ctx.linkBody': {
    en: 'Link it and the thread is tied to that member: the context shows up here and the conversation is visible from the case.',
    es: 'Vinculalo y el hilo queda atado a ese miembro: el contexto aparece acá y desde el caso se ve la conversación.',
  },
  'f5r6b.ctx.searchPlaceholder': { en: 'Search by name or CIN', es: 'Buscar por nombre o CIN' },
  'f5r6b.ctx.searchAria': {
    en: 'Search a case to link to the thread',
    es: 'Buscar un caso para vincular al hilo',
  },
  'f5r6b.ctx.searchHint': {
    en: 'Type at least 3 letters of the name, or the CIN.',
    es: 'Escribí al menos 3 letras del nombre, o el CIN.',
  },
  'f5r6b.ctx.scope.uploader': { en: 'Only cases you uploaded', es: 'Sólo casos que cargaste vos' },
  'f5r6b.ctx.scope.agent': { en: 'Only cases you called', es: 'Sólo casos que llamaste vos' },
  'f5r6b.ctx.scope.navigator': { en: 'Only cases you navigate', es: 'Sólo casos que navegás vos' },
  'f5r6b.ctx.scope.supervisor': { en: 'Only your team’s cases', es: 'Sólo casos de tu equipo' },
  'f5r6b.ctx.scope.admin': { en: 'All program cases', es: 'Todos los casos del programa' },
  'f5r6b.ctx.searchAll': { en: 'Search all', es: 'Buscar en todos' },
  'f5r6b.ctx.backToMine': { en: 'Back to mine', es: 'Volver a los míos' },
  'f5r6b.ctx.recent': { en: 'Your most recent cases', es: 'Tus casos más recientes' },
  'f5r6b.ctx.match': { en: '{n} match', es: '{n} coincidencia' },
  'f5r6b.ctx.matches': { en: '{n} matches', es: '{n} coincidencias' },
  'f5r6b.ctx.other': { en: 'someone else’s', es: 'de otro' },
  'f5r6b.ctx.noResults': {
    en: 'No case of yours matches. Only the cases that belong to you are searched: if it is someone else’s, ask them to open the thread.',
    es: 'Ningún caso tuyo coincide. Sólo se buscan los casos que te tocan a vos: si es de otra persona, pedile que abra el hilo ella.',
  },
  'f5r6b.people.title': { en: '{n} in the conversation', es: '{n} en la conversación' },
  'f5r6b.people.add': { en: 'Add', es: 'Agregar' },
  'f5r6b.people.addAria': {
    en: 'Add someone to this conversation',
    es: 'Agregar a alguien a esta conversación',
  },
  'f5r6b.people.deactivated': { en: 'account deactivated', es: 'cuenta desactivada' },
  'f5r6b.privacy.two': {
    en: 'Private between the two of you. If you add someone, they will see messages from that moment on, not the earlier ones.',
    es: 'Privado entre ustedes dos. Si agregás a alguien, va a ver los mensajes desde ese momento, no los anteriores.',
  },
  'f5r6b.privacy.many': {
    en: 'No longer private between two. Whoever joined sees messages from when they came in; what came before stays between you.',
    es: 'Ya no es privado entre dos. Quien entró ve los mensajes desde que se sumó; lo anterior queda entre ustedes.',
  },
  'f5r6b.esc.kicker': { en: 'Escalation', es: 'Escalado' },
  'f5r6b.esc.body': {
    en: 'Turn this thread into a request with an owner and a date. The chat does not replace a preauthorization or a closure: it opens them.',
    es: 'Convertí este hilo en un pedido con dueño y fecha. El chat no reemplaza una preautorización ni un cierre: los abre.',
  },
  'f5r6b.esc.button': { en: 'Escalate to supervision', es: 'Escalar a supervisión' },
  'f5r6b.esc.owner': { en: 'Owner', es: 'Dueño' },
  'f5r6b.esc.due': { en: 'Due date', es: 'Fecha límite' },
  'f5r6b.esc.note': { en: 'What you need', es: 'Qué necesitás' },
  'f5r6b.esc.submit': { en: 'Escalate', es: 'Escalar' },
  'f5r6b.esc.subjectPrefix': { en: 'Escalated', es: 'Escalado' },
  'f5r6b.esc.msgDue': { en: 'due {date}', es: 'vence {date}' },
  'f5r6b.empty.title': { en: 'No messages yet', es: 'Todavía no tenés mensajes' },
  'f5r6b.empty.body': {
    en: 'When someone writes to you, the conversation lives here.',
    es: 'Cuando alguien te escriba, la conversación vive acá.',
  },
  'f5r6b.empty.cta': { en: 'Write to someone', es: 'Escribirle a alguien' },
  'f5r6b.pick.title': { en: 'Pick a conversation', es: 'Elegí una conversación' },
  'f5r6b.pick.body': {
    en: 'The thread opens here, with its case beside it.',
    es: 'El hilo se abre acá, con su caso al lado.',
  },
  'f5r6b.err.title': { en: 'Could not load the chat', es: 'No se pudo cargar el chat' },
  'f5r6b.err.body': {
    en: 'Nothing was lost: what you write stays in the box until it goes out. Try again.',
    es: 'No se perdió nada: lo que escribís queda en el cuadro hasta que sale. Reintentá.',
  },
  'f5r6b.err.retry': { en: 'Try again', es: 'Reintentar' },
  'f5r6b.new.title': { en: 'New conversation', es: 'Nueva conversación' },
  'f5r6b.new.who': { en: 'Who with', es: 'Con quién' },
  'f5r6b.new.filter': { en: 'Filter by name', es: 'Filtrar por nombre' },
  'f5r6b.new.firstMsg': { en: 'First message (optional)', es: 'Primer mensaje (opcional)' },
  'f5r6b.new.start': { en: 'Start the conversation', es: 'Empezar la conversación' },
  'f5r6b.new.cancel': { en: 'Cancel', es: 'Cancelar' },
  'f5r6b.new.max': { en: 'Max {n}', es: 'Máx {n}' },
  'f5r6b.new.fallbackSubject': { en: 'Conversation', es: 'Conversación' },
  'f5r6b.role.agent': { en: 'Agent', es: 'Agente' },
  'f5r6b.role.supervisor': { en: 'Supervisor', es: 'Supervisor' },
  'f5r6b.role.admin': { en: 'Admin', es: 'Admin' },
  'f5r6b.role.uploader': { en: 'Uploader', es: 'Uploader' },
  'f5r6b.role.navigator': { en: 'Navigator', es: 'Navigator' },
  'f5r6b.caseRef.line': { en: '🔗 Case · {name}', es: '🔗 Caso · {name}' },

  // F5-R8b — «Mi rango»: la escalera como escalera (dibujo del CEO
  // «Rango - Tarjeta de inicio», 2026-08-29). ES verbatim del dibujo;
  // EN es su traducción. Cero montos: escalones, conteos y días.
  'f5r8b.title': { en: 'My rank', es: 'Mi rango' },
  'f5r8b.kicker': { en: 'My rank · {m}', es: 'Mi rango · {m}' },
  'f5r8b.unit.screenings': { en: 'screenings', es: 'screenings' },
  'f5r8b.unit.cargas': { en: 'uploads', es: 'cargas' },
  'f5r8b.verEscalera': { en: 'See my ladder', es: 'Ver mi escalera' },
  'f5r8b.faltanPara': { en: '{n} to go for {tier}', es: 'Faltan {n} para {tier}' },
  'f5r8b.tope': { en: 'Top of the ladder', es: 'Tope de la escalera' },
  'f5r8b.cerrar': { en: 'Close', es: 'Cerrar' },
  'f5r8b.cerrarAria': {
    en: 'Close the ladder and go back to the compact card',
    es: 'Cerrar la escalera y volver a la tarjeta compacta',
  },
  'f5r8b.caminoA': { en: 'Path to {tier}', es: 'Camino a {tier}' },
  'f5r8b.escalonDe': { en: 'Your step this {m}', es: 'Tu escalón de {m}' },
  'f5r8b.sinEscalon': { en: 'No step yet this month', es: 'Todavía sin escalón este mes' },
  'f5r8b.counter.agent': { en: 'screenings in {m}', es: 'screenings de {m}' },
  'f5r8b.counter.uploader': { en: 'uploads in {m}', es: 'cargas de {m}' },
  'f5r8b.acaEstas': { en: 'You are here', es: 'Acá estás' },
  'f5r8b.detail.next': {
    en: 'You need {need} more for {tier} — that is {th} {unit} in the month. {d} days left: {perDay} per day.',
    es: 'Te faltan {need} para {tier}, que son {th} {unit} en el mes. Quedan {d} días: {perDay} por día.',
  },
  'f5r8b.detail.top': {
    en: 'You are at {tier}, the top of the ladder. {d} days of {m} left and the step can no longer be lost.',
    es: 'Estás en {tier}, el tope de la escalera. Quedan {d} días de {m} y el escalón ya no se puede perder.',
  },
  'f5r8b.foot.avg': { en: 'Average per active day', es: 'Promedio por día activo' },
  'f5r8b.foot.perDay': { en: 'Per day to reach {tier}', es: 'Por día para llegar a {tier}' },
  'f5r8b.foot.best': { en: 'Your best month', es: 'Tu mejor mes' },
  'f5r8b.foot.daysLeft': { en: 'Days left in the month', es: 'Días que quedan del mes' },
  'f5r8b.disc1': { en: 'These levels are ', es: 'Estos niveles son ' },
  'f5r8b.discBold': { en: 'motivational', es: 'motivacionales' },
  'f5r8b.disc2': {
    en: ' and do not affect your pay. They are here so you know how your month is going.',
    es: ' y no afectan tu pago. Están para que sepas cómo viene tu mes.',
  },
  'f5r8b.aria.reached': { en: '{tier}, {th} {unit}, reached', es: '{tier}, {th} {unit}, alcanzado' },
  'f5r8b.aria.reachedNoTh': { en: '{tier}, reached', es: '{tier}, alcanzado' },
  'f5r8b.aria.faltan': { en: '{tier}, {th} {unit}, {n} to go', es: '{tier}, {th} {unit}, faltan {n}' },
  'f5r8b.aria.pending': { en: '{tier}, not reached yet', es: '{tier}, todavía no alcanzado' },
  'f5r8b.aria.mini': {
    en: 'My rank: {tier}, {total} {unit} in {m}. Open my ladder.',
    es: 'Mi rango: {tier}, {total} {unit} en {m}. Abrir mi escalera.',
  },

  // ===== F5-R8a — «Agente v2 — Header» (dibujo del CEO): header del agente
  // con Inicio + 4 pestañas. El castellano es verbatim del dibujo; el inglés
  // es su traducción. =====
  'agentF5.r8a.brandAria': { en: 'Go to Home', es: 'Ir a Inicio' },
  'agentF5.r8a.brandTitle': { en: 'Home', es: 'Inicio' },
  'agentF5.r8a.tabInicio': { en: 'Home', es: 'Inicio' },
  'agentF5.r8a.tabQueue': { en: 'My queue', es: 'Mi cola' },
  'agentF5.r8a.tabAgenda': { en: 'My agenda', es: 'Mi agenda' },
  'agentF5.r8a.tabNumbers': { en: 'My numbers', es: 'Mis números' },
  'agentF5.r8a.searchPlaceholder': {
    en: 'Full Medicaid ID or full phone',
    es: 'ID de Medicaid o teléfono completo',
  },
  'agentF5.r8a.searchAria': {
    en: 'Search by full Medicaid ID or full phone number',
    es: 'Buscar por ID de Medicaid completo o teléfono completo',
  },
  'agentF5.r8a.callChip': {
    en: 'On a call with {name} — go back',
    es: 'En llamada con {name} — volver',
  },
  'agentF5.r8a.callChipTitle': {
    en: 'Open call with {name} — back to the screening',
    es: 'Llamada abierta con {name} — volver al screening',
  },
  // (la campana trae su propio tooltip — notifications.title, ya «Notificaciones»)
  'agentF5.r8a.chat': { en: 'Chat', es: 'Chat' },
  'agentF5.r8a.report': { en: 'Report a problem', es: 'Reportar un problema' },
  'agentF5.r8a.homeSub': {
    en: 'Your rank, what needs action today, and access to your queue.',
    es: 'Tu rango, lo que exige acción hoy y el acceso a la cola.',
  },
  'agentF5.r8a.accessKicker': { en: 'Quick access', es: 'Accesos' },
  'agentF5.r8a.accessQueueDesc': {
    en: 'Take the next one and work your calls.',
    es: 'Tomar el siguiente y trabajar tus llamadas.',
  },
  'agentF5.r8a.accessAgendaDesc': {
    en: 'Your appointments for today and the week.',
    es: 'Tus citas de hoy y de la semana.',
  },
  'agentF5.r8a.accessNumbersDesc': {
    en: 'Your day, your month and your tier.',
    es: 'Tu día, tu mes y tu escalón.',
  },
  'agentF5.r8a.accessSettings': { en: 'My profile & settings', es: 'Mi perfil y ajustes' },
  'agentF5.r8a.accessSettingsDesc': {
    en: 'Language, password and preferences.',
    es: 'Idioma, contraseña y preferencias.',
  },
  'agentF5.r8a.agendaSub': {
    en: '{n} appointments today · {w} this week.',
    es: '{n} citas hoy · {w} esta semana.',
  },
  'agentF5.r8a.numbersSub': {
    en: 'Your day, your month and your tier.',
    es: 'Tu día, tu mes y tu escalón.',
  },

  // F5-R8c — «Progreso del agente» (dibujo del CEO, Agentes v2 · 2026-08-29).
  // La ficha que abre una fila de la tabla Admin 03: reemplaza el ProgressModal
  // admin. ES canónico verbatim del dibujo; EN derivado.
  'f5r8c.back': { en: 'All agents', es: 'Todos los agentes' },
  'f5r8c.role': { en: 'Agent', es: 'Agente' },
  'f5r8c.seeScreenings': { en: 'See their screenings', es: 'Ver sus screenings' },
  'f5r8c.export': { en: 'Export their card', es: 'Exportar su ficha' },
  'f5r8c.exportDiag': {
    en: 'There is no endpoint for exporting an individual card yet — the button stays disabled until it exists (NEEDS-SERVER).',
    es: 'Todavía no hay endpoint de export de la ficha individual — el botón queda deshabilitado hasta que exista (NECESITA-SERVER).',
  },
  'f5r8c.err.title': { en: 'The card did not load', es: 'La ficha no cargó' },
  'f5r8c.err.body': {
    en: 'This person’s progress could not be read. Nothing is lost — try again.',
    es: 'No se pudo leer el progreso de esta persona. No se pierde nada — reintentá.',
  },
  'f5r8c.err.retry': { en: 'Try again', es: 'Reintentar' },
  'f5r8c.virgin': {
    en: 'No screenings sent yet this month',
    es: 'Todavía no envió ninguno este mes',
  },
  // -- tarjeta Hoy
  'f5r8c.hoy.kicker': { en: 'Today', es: 'Hoy' },
  'f5r8c.hoy.of': { en: 'of {n}', es: 'de {n}' },
  'f5r8c.hoy.line': {
    en: 'At {c} of {m} screenings for the day. {r} to go to hit the goal.',
    es: 'Va {c} de {m} screenings del día. Le faltan {r} para cumplir la meta.',
  },
  'f5r8c.hoy.lineDone': {
    en: 'At {c} of {m} screenings for the day. Today’s goal is already met.',
    es: 'Va {c} de {m} screenings del día. Ya cumplió la meta de hoy.',
  },
  'f5r8c.hoy.noteA': { en: 'The daily goal comes from ', es: 'La meta diaria sale de ' },
  'f5r8c.hoy.noteCfg': { en: 'Settings › Goals and levels', es: 'Configuración › Metas y niveles' },
  'f5r8c.hoy.noteB': { en: ' (key ', es: ' (clave ' },
  'f5r8c.hoy.noteC': {
    en: '), the same source as the ladder. It is not edited from this screen.',
    es: '), la misma fuente que la escalera. No se edita desde esta pantalla.',
  },
  'f5r8c.pend.lead': {
    en: '{n} household screenings are waiting for validation.',
    es: '{n} screenings de hogar esperan validación.',
  },
  'f5r8c.pend.leadOne': {
    en: 'One household screening is waiting for validation.',
    es: 'Un screening de hogar espera validación.',
  },
  'f5r8c.pend.body': {
    en: 'They count toward their month only once the uploader submits them to FindHelp. Until then they are not in the {today} nor in the month total.',
    es: 'Cuentan para su mes recién cuando el uploader los sube a FindHelp. Hasta entonces no están en el {today} ni en el total del mes.',
  },
  // -- tarjeta Su tramo en la escalera
  'f5r8c.esc.kicker': { en: 'Their stretch of the ladder', es: 'Su tramo en la escalera' },
  'f5r8c.esc.of': { en: 'screenings in {month}', es: 'screenings de {month}' },
  'f5r8c.esc.here': { en: 'Right here', es: 'Acá está' },
  'f5r8c.esc.ariaReached': {
    en: '{name}, {th} screenings in the month, reached',
    es: '{name}, {th} screenings en el mes, alcanzado',
  },
  'f5r8c.esc.ariaMissing': {
    en: '{name}, {th} screenings in the month, {n} to go',
    es: '{name}, {th} screenings en el mes, faltan {n}',
  },
  'f5r8c.esc.note': {
    en: 'They are at {cur}. {n} to go for {next}, which is {th} screenings in the month. The level closes on the last day of {month}.',
    es: 'Está en {cur}. Le faltan {n} para {next}, que son {th} screenings en el mes. El nivel cierra el último día de {month}.',
  },
  'f5r8c.esc.noteTop': {
    en: 'They are at {cur}, the top of the ladder. The level closes on the last day of {month}.',
    es: 'Está en {cur}, el tope de la escalera. El nivel cierra el último día de {month}.',
  },
  'f5r8c.esc.noLadder': {
    en: 'The current ladder did not arrive from the server — without thresholds no made-up stretch is drawn.',
    es: 'La escalera vigente no llegó del servidor — sin umbrales no se dibuja un tramo inventado.',
  },
  // -- tarjeta Su volumen en el tiempo
  'f5r8c.vol.kicker': { en: 'Their volume over time', es: 'Su volumen en el tiempo' },
  'f5r8c.vol.gran.semana': { en: 'Week', es: 'Semana' },
  'f5r8c.vol.gran.mes': { en: 'Month', es: 'Mes' },
  'f5r8c.vol.gran.anio': { en: 'Year', es: 'Año' },
  'f5r8c.vol.note.semana': {
    en: 'Last 12 weeks · one bar per week',
    es: 'Últimas 12 semanas · una barra por semana',
  },
  'f5r8c.vol.note.mes': {
    en: 'Last 12 months · one bar per month',
    es: 'Últimos 12 meses · una barra por mes',
  },
  'f5r8c.vol.note.anio': { en: '{y0} – {y1} · one bar per year', es: '{y0} – {y1} · una barra por año' },
  'f5r8c.vol.firstDay': { en: 'First recorded day', es: 'Primer día registrado' },
  'f5r8c.vol.needsServer': {
    en: 'This view needs the server series (extended endpoint F5-R8c).',
    es: 'Esta vista necesita la serie del servidor (endpoint extendido F5-R8c).',
  },
  'f5r8c.vol.barTitle': { en: '{tick}: {v} screenings', es: '{tick}: {v} screenings' },
  'f5r8c.vol.goalNote': {
    en: 'The goal line for this stretch is {g} screenings per {unit}: they beat it in {k} of {n}.',
    es: 'La línea de meta de este tramo es {g} screenings por {unit}: superó la meta en {k} de {n}.',
  },
  'f5r8c.vol.stat.total': { en: 'Chart total', es: 'Total del gráfico' },
  'f5r8c.vol.stat.avg': { en: 'Average per {unitActive}', es: 'Promedio por {unitActive}' },
  'f5r8c.vol.stat.best': { en: 'Their best {unit}', es: 'Su mejor {unit}' },
  'f5r8c.vol.stat.inactive': { en: '{plural} with no activity', es: '{plural} sin actividad' },
  'f5r8c.vol.unit.semana': { en: 'week', es: 'semana' },
  'f5r8c.vol.unit.mes': { en: 'month', es: 'mes' },
  'f5r8c.vol.unit.anio': { en: 'year', es: 'año' },
  'f5r8c.vol.unitActive.semana': { en: 'active week', es: 'semana activa' },
  'f5r8c.vol.unitActive.mes': { en: 'active month', es: 'mes activo' },
  'f5r8c.vol.unitActive.anio': { en: 'active year', es: 'año activo' },
  'f5r8c.vol.plural.semana': { en: 'weeks', es: 'semanas' },
  'f5r8c.vol.plural.mes': { en: 'months', es: 'meses' },
  'f5r8c.vol.plural.anio': { en: 'years', es: 'años' },
  // -- tarjeta Constancia
  'f5r8c.con.kicker': { en: 'Consistency', es: 'Constancia' },
  'f5r8c.con.streakLabel': {
    en: 'straight days reaching {m}',
    es: 'días seguidos llegando a {m}',
  },
  'f5r8c.con.activeLabel': { en: 'active days in {month}', es: 'días activos en {month}' },
  'f5r8c.con.noteEarned': {
    en: 'At {min} straight days they earn the Consistency Diamond; they already have it.',
    es: 'A los {min} días seguidos se gana el Diamante de Constancia; ya lo tiene.',
  },
  'f5r8c.con.noteLocked': {
    en: 'At {min} straight days they earn the Consistency Diamond; they are at {d}.',
    es: 'A los {min} días seguidos se gana el Diamante de Constancia; lleva {d}.',
  },
  'f5r8c.con.rule': {
    en: 'The streak breaks on the first working day below the daily goal — weekends do not break it.',
    es: 'La racha se corta el primer día laborable que no llega a la meta diaria — no los fines de semana.',
  },
  'f5r8c.con.cut': {
    en: 'The streak broke on {date}: that day did not reach the daily goal.',
    es: 'La racha se cortó el {date}: ese día no llegó a la meta diaria.',
  },
  // -- tarjeta Logros
  'f5r8c.log.kicker': { en: 'Achievements', es: 'Logros' },
  'f5r8c.log.count': { en: '{k} of {m}', es: '{k} de {m}' },
  'f5r8c.log.none': {
    en: 'No achievements to list yet — they arrive with their first screenings.',
    es: 'Todavía sin logros para listar — llegan con sus primeros screenings.',
  },
  'f5r8c.ach.reached': {
    en: 'Reached in {month} · {th} screenings in one month',
    es: 'Alcanzado en {month} · {th} screenings en un mes',
  },
  'f5r8c.ach.locked': {
    en: '{n} to go: it takes {th} screenings in one month',
    es: 'Le faltan {n}: son {th} screenings en un mes',
  },
  'f5r8c.ach.constanciaName': { en: 'Consistency Diamond', es: 'Diamante de Constancia' },
  'f5r8c.ach.constanciaNote': {
    en: '{min} straight days at the daily goal · at {d}',
    es: '{min} días seguidos llegando a la meta diaria · lleva {d}',
  },
  'f5r8c.ach.bestName': { en: 'Their best month', es: 'Su mejor mes' },
  'f5r8c.ach.bestNote': { en: '{n} screenings · {month} {year}', es: '{n} screenings · {month} de {year}' },
  'f5r8c.ach.bestLocked': {
    en: 'It arrives with their first screening of the month.',
    es: 'Llega con su primer screening del mes.',
  },

  // ===== F5-R9u — uploader: marca/título→inicio, «Volver» en Mi perfil,
  // campana de vuelta (F-12 firmada por el CEO 2026-08-30) y el renderer del
  // digest SLA que caía al título genérico. =====
  'f5r9u.up.homeAria': { en: 'Go to Home', es: 'Ir al inicio' },
  'f5r9u.up.back': { en: 'Back', es: 'Volver' },
  'notifications.type.findhelpSlaDigest.title': {
    en: 'FindHelp SLA digest (>24h)',
    es: 'Resumen SLA FindHelp (>24h)',
  },
  'notifications.type.findhelpSlaDigest.body': {
    en: '{n} uploads overdue more than 24h',
    es: '{n} cargas atrasadas más de 24h',
  },
  // E-35 (revisión adversarial, frente 2, M1): los dos avisos nuevos
  // llegaban a la campana sin título ni texto. El cuerpo viene del servidor
  // ya armado y sin PHI (solo conteos y el nombre de la compañera).
  'notifications.type.navVigiaAlert.title': {
    en: 'Navigation watch',
    es: 'Vigía de navegación',
  },
  'notifications.type.navRebalanceCeded.title': {
    en: 'Cases moved to a colleague',
    es: 'Casos pasados a una compañera',
  },
  // ── F5-R9g · «Mi agenda» del agente vestida con la UI del navigator ──
  // Copy de los estados del chasis (States.tsx): el vacío describe la
  // situación normal; el error dice qué se pierde y ofrece UN botón.
  'agentF5.r9g.emptyBody': {
    en: 'When a booking is scheduled it will show up here with its date and time.',
    es: 'Cuando se agende una cita va a aparecer acá con su fecha y hora.',
  },
  'agentF5.r9g.searchEmptyTitle': { en: 'No matches', es: 'Sin coincidencias' },
  'agentF5.r9g.errorTitle': { en: 'Your bookings did not load', es: 'Sus citas no cargaron' },
  'agentF5.r9g.errorBody': {
    en: 'The bookings are safe on the server — this screen just could not fetch them, so the list may be out of date.',
    es: 'Las citas están a salvo en el servidor — esta pantalla no pudo traerlas, así que la lista puede estar desactualizada.',
  },
  'agentF5.r9g.retry': { en: 'Retry', es: 'Reintentar' },

  // ===== F5-R11 — el header del agente entra en pantallas chicas (caminata
  // del CEO en 14" a media pantalla): menú «⋮» + lupa + escarabajo. =====
  'agentF5.r11.menu': { en: 'More options', es: 'Más opciones' },
  'agentF5.r11.searchToggle': { en: 'Search for a patient', es: 'Buscar un paciente' },
  'agentF5.r11.language': { en: 'Language', es: 'Idioma' },

  // ===== F5-R12 — el click-para-llamar vuelve (caminata del CEO: «ya se
  // quejaron… antes lo tenían y ahora no»). Enmienda de E-25 y del contrato
  // de F5-T7. =====
  'agentF5.r12.callAria': {
    en: 'Call this number ({kind})',
    es: 'Llamar a este número ({kind})',
  },

  // ===== F5-P3 — las cuatro vistas de la piscina de navegación (firma CEO
  // 2026-08-30). La piscina es UNA sola (el buzón con scope 'pool'); estas
  // cuatro la parten, y la transición entre ellas la escribe el uploader al
  // procesar el screening — no hay casos que mover. =====
  // E-36: «buzón», el mismo nombre que la parte de abajo («Del buzón», «Todo
  // el buzón», «N en el buzón»). Con «piscina» acá y «buzón» abajo, el MISMO
  // conjunto volvía a tener dos nombres en la misma pantalla (P3d).
  'f5p3.pool.kicker': { en: 'The pool, in four views', es: 'El buzón, en cuatro vistas' },
  'f5p3.pool.total': { en: '{n} in the pool', es: '{n} en el buzón' },
  'f5p3.pool.intro': {
    en: 'One pool, four views. A case moves from one to the next on its own, the '
      + 'moment the uploader processes its screening — nothing is moved by hand. '
      + 'The four counts add up to the pool.',
    es: 'Un solo buzón, cuatro vistas. Un caso pasa de una a la otra solo, apenas '
      + 'el uploader procesa su screening — no se mueve nada a mano. Los cuatro '
      + 'conteos suman el buzón.',
  },
  'f5p3.pool.view.pool_awaiting_verification': {
    en: 'Awaiting verification', es: 'Esperando verificación',
  },
  'f5p3.pool.view.pool_ready_to_deal': { en: 'Ready to deal', es: 'Listos para repartir' },
  'f5p3.pool.view.pool_no_medicaid': { en: 'No Medicaid', es: 'Sin Medicaid' },
  'f5p3.pool.view.pool_negatives': { en: 'Negatives', es: 'Negativos' },
  'f5p3.pool.noDeal': {
    en: 'Not dealt out: the uploader has not processed these screenings yet, so '
      + 'their Medicaid is not confirmed.',
    es: 'No se reparte: el uploader todavía no procesó estos screenings, así que su '
      + 'Medicaid no está confirmado.',
  },
  'f5p3.pool.noDealNote': {
    en: '«Awaiting verification» is a counter, not a batch: dealing a case whose '
      + 'Medicaid nobody confirmed is work that cannot be billed. Deal from «Ready '
      + 'to deal».',
    es: '«Esperando verificación» es un contador, no una tanda: repartir un caso cuyo '
      + 'Medicaid nadie confirmó es trabajo que no se puede facturar. Se reparte '
      + 'desde «Listos para repartir».',
  },
  'f5p3.pool.err': {
    en: 'The pool counts could not be loaded.',
    es: 'No se pudieron cargar los conteos del buzón.',
  },
  // E-36: ya no son cinco (sale la opción de todos los abiertos); no se cuenta
  // para que el texto no vuelva a mentir si la lista cambia.
  'f5p3.re.fromView': {
    en: 'Dealing from the pool view «{name}» — none of the options below applies.',
    es: 'Repartiendo desde la vista «{name}» del buzón — no aplica ninguna de las '
      + 'opciones de abajo.',
  },

  // ===== F5-P2 — Reasignación de navegación CON ORIGEN (encargo CEO
  // 2026-08-30: «que yo desde admin pueda reasignar de un agente a otro en
  // Reassignment, en el control de navegación… que pueda seleccionar pasar
  // todos o una cantidad X, como máximo lo que tenga disponible, excepto los
  // bookings como pasa con los agentes»). ES canónico; EN derivado. =====
  'f5p2.re.mode': { en: 'Where the cases come from', es: 'De dónde salen los casos' },
  // E-36: las pestañas dicen DE DÓNDE salen los casos. Por dentro siguen los
  // mismos dos modos ('filter' = el buzón, 'origin' = una persona).
  'f5p2.re.modeFilter': { en: 'From the pool', es: 'Del buzón' },
  'f5p2.re.modeOrigin': { en: 'From a person', es: 'De una persona' },
  'f5p2.re.source': { en: 'Source (who hands them over)', es: 'Origen (quién los entrega)' },
  'f5p2.re.sourceHint': {
    en: 'Deactivated accounts are listed here as long as they still hold open cases: emptying them is exactly what this is for.',
    es: 'Las cuentas dadas de baja aparecen acá mientras todavía tengan casos abiertos: vaciarlas es justamente para lo que sirve esto.',
  },
  'f5p2.re.sourceEmpty': {
    en: 'No navigator has open cases to hand over.',
    es: 'Ningún navegador tiene casos abiertos para entregar.',
  },
  'f5p2.re.counting': { en: 'Counting what can move…', es: 'Contando lo que se puede mover…' },
  'f5p2.re.countErr': {
    en: 'Could not count what this navigator has.',
    es: 'No se pudo contar lo que tiene este navegador.',
  },
  'f5p2.re.available': {
    en: '{n} of {m} open cases can move',
    es: '{n} de {m} casos abiertos se pueden mover',
  },
  'f5p2.re.members': { en: '{n} members behind them', es: '{n} miembros detrás de esos casos' },
  'f5p2.re.wholeMember': {
    en: 'A member moves whole or not at all: one case held back keeps all of theirs.',
    es: 'Un miembro pasa entero o no pasa: un solo caso retenido deja quietos todos los suyos.',
  },
  'f5p2.re.fromLine': {
    en: 'From {name}: {n} open cases under this filter',
    es: 'De {name}: {n} casos abiertos con este filtro',
  },
  'f5p2.re.why.source': { en: 'Pick the source navigator.', es: 'Elegí el navegador de origen.' },
  'f5p2.re.why.empty': {
    en: 'This navigator has nothing left to hand over.',
    es: 'Este navegador no tiene nada para entregar.',
  },
  'f5p2.re.needReason': {
    en: 'Write the reason first (at least 3 characters).',
    es: 'Escribí primero el motivo (mínimo 3 caracteres).',
  },
  // Las tres exclusiones, cada una con su razón — espejo de los saltados con
  // motivo del módulo de agentes (ReassignSkipReason).
  'f5p2.skip.NAV_APPOINTMENT_SCHEDULED': {
    en: '{n} stay put: they have a navigation appointment booked (it stays with whoever booked it).',
    es: '{n} se quedan: tienen cita de navegación agendada (la cita se queda con quien la agendó).',
  },
  'f5p2.skip.FOLLOWUP_ACCEPTED_PENDING': {
    en: '{n} stay put: an accepted follow-up has not come due yet.',
    es: '{n} se quedan: tienen un seguimiento aceptado que todavía no vence.',
  },
  'f5p2.skip.OPEN_SESSION': {
    en: '{n} stay put: a work session is open right now.',
    es: '{n} se quedan: tienen una sesión de trabajo abierta ahora mismo.',
  },
  // COLA-AGENTE A4 — el miedo que reportaron por correo era a «bloquear el
  // numero» con el quinto intento. No se bloquea: el paciente sale de la
  // cola. Decirlo en la fila saca el miedo del medio.
  'cola.rowLeft': {
    en: '{n} left',
    es: 'le quedan {n}',
  },
  'f5p2.skip.MEMBER_SPLIT_BY_FILTER': {
    en: '{n} stay put: the member has another open case this view does not '
      + 'cover, and a member always moves whole. Switch the view or move them '
      + 'from “Everything this person has”.',
    es: '{n} se quedan: el miembro tiene otro caso abierto que esta vista no '
      + 'alcanza, y un miembro se mueve entero o no se mueve. Cambiá de vista '
      + 'o pasalos desde «Todo lo de esta persona».',
  },
  'f5p2.all.cta': { en: 'Move ALL ({n}) to destination', es: 'Pasar TODOS ({n}) al destino' },
  'f5p2.all.progress': { en: 'Moving… {done} of {total}', es: 'Pasando… {done} de {total}' },
  'f5p2.all.confirmT': {
    en: 'You are about to move {n} cases away from {name}',
    es: 'Vas a pasar {n} casos que hoy son de {name}',
  },
  // E-36 · la confirmación depende del origen. La de antes («los que tienen
  // cita… jamás se mueven») era falsa para las bajas: desde E-35d una cuenta
  // de baja entrega todo, con citas y sesiones (`reassignMember`).
  'e36.all.confirmBActive': {
    en: 'Everything movable goes to the destination, in batches of 200, re-counting what is left on every pass. From an active person only cases nobody has contacted move: anything already worked (contact, appointment, accepted follow-up, open session) stays with them. This runs now.',
    es: 'Todo lo movible pasa al destino, por tandas de 200, recontando lo que queda en cada vuelta. De una persona activa solo pasan los que nadie ha contactado: lo ya trabajado (contacto, cita, seguimiento aceptado, sesión abierta) se queda con ella. Corre ahora.',
  },
  'e36.all.confirmBInactive': {
    en: 'Everything movable goes to the destination, in batches of 200, re-counting what is left on every pass. The account is deactivated, so everything moves: worked cases too, with their appointments and sessions. This runs now.',
    es: 'Todo lo movible pasa al destino, por tandas de 200, recontando lo que queda en cada vuelta. La cuenta está de baja, así que se mueve todo: también lo trabajado, con sus citas y sesiones. Corre ahora.',
  },
  'f5p2.all.confirmGo': { en: 'Yes, move the {n}', es: 'Sí, pasar los {n}' },
  'f5p2.all.confirmNo': { en: 'Cancel', es: 'Cancelar' },
  'f5p2.all.done': {
    en: '{n} cases moved. The source has nothing movable left.',
    es: '{n} casos pasados. Al origen no le queda nada movible.',
  },
  'f5p2.all.partial': {
    en: '{n} moved; {resto} remain (they became non-movable mid-run).',
    es: '{n} pasados; quedan {resto} (se volvieron no-movibles en el medio).',
  },

  // ===== E-30 (CEO, 2026-09-11) — la fila secundaria del caso =====
  // Enmienda acotada del acta E-06: NO vuelve el bloque «Cuando cuelgues» ni
  // el par Pausar/Reanudar. Vuelve UNA salida con condición propia, dicha
  // como acción suelta y subordinada a «Qué hacer ahora».
  //
  // PRIMERA ENMIENDA (CEO, 2026-09-12): eran tres. «Retomar el reloj» y su
  // toast se borraron con el botón — no se agrega ningún botón extra, y el
  // retorno tras un cierre por inactividad entra por el click en «Abrir
  // FindHelp», que ya existía.
  //
  // SEGUNDA ENMIENDA (CEO, 2026-09-12, caminando staging): eran dos. «Guardar
  // sesión» se fue con su clave —*«no era necesario honestamente… el de
  // agendar está bien»*—: cerrar el tramo lo ofrece «Qué hacer ahora» cuando
  // toca, y la fila lo duplicaba con otra etiqueta.
  //
  // Las claves borradas se nombran acá en castellano A PROPÓSITO, sin el
  // identificador punteado: el `sourcesContent` de este archivo viaja en el
  // sourcemap del bundle, y una guarda de promote que busque el identificador
  // completo daría ABORT sobre un artefacto sano (auditoría C6).
  'navigator.case.secondary.schedule': { en: 'Schedule appointment', es: 'Agendar cita' },
  // §1.4 de la Mejora E: elegido el caso, queda como chip y se puede cambiar.
  'navigator.appt.changeCase': { en: 'Change', es: 'Cambiar' },

  // T2-A ===== (#158 A, Edd 23-oct) — el aviso interino de hogar en la
  // pre-llamada, con el texto del boceto elegido. {date}, {agent} y {family}
  // se pintan en negrita (PreCallPage › AvisoHogar).
  't2a.aviso.cuerpo': {
    en: 'This phone belongs to a household screened on {date} by {agent}, with {family}.',
    es: 'Este teléfono es de un hogar screeneado el {date} por {agent}, con {family}.',
  },
  't2a.aviso.familia1': { en: '{count} family member', es: '{count} familiar' },
  't2a.aviso.familiaN': { en: '{count} family members', es: '{count} familiares' },
  't2a.aviso.ti': { en: 'you', es: 'ti' },
  't2a.aviso.alguien': { en: 'another agent', es: 'otro agente' },
  // T2-A · Fable §9.1: el screening abierto («Reanudar») y el 409 del padre
  // con familiares enviados.
  't2a.reanudar.titulo': {
    en: 'You have an open screening with this person.',
    es: 'Tienes un screening abierto con esta persona.',
  },
  't2a.reanudar.sinSubs': {
    en: 'You can pick it up where you left off.',
    es: 'Puedes retomarlo donde lo dejaste.',
  },
  't2a.reanudar.conSubs': {
    en: 'It already has submitted family members: finish it before going back to the queue.',
    es: 'Ya tiene familiares enviados: termínalo antes de volver a la cola.',
  },
  't2a.reanudar.boton': { en: 'Resume the screening', es: 'Reanudar el screening' },
  't2a.reanudar.retenidoT': {
    en: 'This screening cannot go back to the queue',
    es: 'Este screening no puede volver a la cola',
  },
  't2a.reanudar.retenidoB': {
    en: 'It already has submitted family members. Resume it and finish it.',
    es: 'Ya tiene familiares enviados. Reanúdalo y termínalo.',
  },
  // T2-A fin =====
  // T2-F · #146 (Edd, 23-oct-2026: «Exigir la EA») — el texto del boceto que
  // eligió, verbatim: el cierre «no consintió» se hace desde la EA.
  'navigator.close.needEaNo': {
    en: 'To close as “did not consent”, open the EA and mark the no there.',
    es: 'Para cerrar como «no consintió», abre la EA y marca ahí el no.',
  },
  // T2-E — «Mi agenda» (#133/#130, Edd 23-oct): «Completada», «Hechas hoy» y
  // la nota de la cita siempre a la vista (opción B).
  't2e.agenda.complete': { en: 'Completed', es: 'Completada' },
  't2e.agenda.completedChip': { en: 'Completed', es: 'Completada' },
  't2e.agenda.completeKicker': { en: 'Complete appointment', es: 'Completar la cita' },
  't2e.agenda.completeTitle': { en: 'Did this appointment happen?', es: '¿La cita ocurrió?' },
  't2e.agenda.completeBody': {
    en: 'It is marked as completed and moves to “Done today”. It no longer counts as overdue. This does not change the clock or anything billed.',
    es: 'Se marca como completada y pasa a «Hechas hoy». Deja de contar como vencida. No cambia el reloj ni nada de lo facturado.',
  },
  't2e.agenda.completeConfirm': { en: 'Mark as completed', es: 'Marcar completada' },
  't2e.agenda.completed': { en: 'Appointment completed.', es: 'Cita completada.' },
  't2e.agenda.notDue': {
    en: 'An appointment can be marked completed only on its day or after it.',
    es: 'Una cita se marca completada desde su día, no antes.',
  },
  't2e.agenda.followupTitle': { en: 'This appointment belongs to a follow-up', es: 'Esta cita es de un seguimiento' },
  't2e.agenda.followupBody': {
    en: 'Complete the follow-up in the case: when you complete it, the appointment is marked completed on its own.',
    es: 'Completa el seguimiento en el caso: al completarlo, la cita se marca sola como completada.',
  },
  't2e.agenda.doneKicker': { en: 'Done today', es: 'Hechas hoy' },
  't2e.agenda.noteLabel': { en: 'Appointment note:', es: 'Nota de la cita:' },
  't2e.agenda.noteMore': { en: 'See all', es: 'Ver toda' },
  't2e.agenda.noteLess': { en: 'See less', es: 'Ver menos' },
  // T2-G (#151a) — perfil del paciente: «Ver respuestas» en los screenings
  // propios enviados, y «Cancelado» para los borrados (antes «En progreso»).
  'clientProfile.viewAnswers': { en: 'View answers', es: 'Ver respuestas' },
  'clientProfile.cancelled': { en: 'Cancelled', es: 'Cancelado' },

  // CL-W — la pantalla propia del closer de negativos (Edd, 23/24-oct-2026;
  // §11 y §11.1 del plan; CONTRATO-CLOSER §3). Sin reenvío a screener: Edd
  // «ya definí que no se envían de nuevo a un screener». Bloque propio al
  // final, con marcador, para que el merge con los bloques de T2 sea trivial.
  'closer.docTitle': { en: 'Close cases — Pathway', es: 'Cerrar casos — Pathway' },
  'closer.nav.close': { en: 'Close cases', es: 'Cerrar casos' },
  'closer.retry': { en: 'Retry', es: 'Reintentar' },
  'closer.gate.errorTitle': { en: 'Could not load your screen', es: 'No pude cargar tu pantalla' },
  'closer.gate.errorBody': {
    en: 'The server did not answer which screen is yours. Nothing was changed. Try again in a moment.',
    es: 'El servidor no respondió cuál es tu pantalla. No se cambió nada. Vuelve a intentarlo en un momento.',
  },
  'closer.sum.aria': { en: 'Your numbers', es: 'Tus números' },
  'closer.sum.porCerrar': { en: 'To close', es: 'Por cerrar' },
  'closer.sum.cerradosHoy': { en: 'Closed today', es: 'Cerrados hoy' },
  'closer.sum.reenviadosHoy': { en: 'Forwarded today', es: 'Reenviados hoy' },
  'closer.sum.noAparecen': { en: 'Not in FindHelp', es: 'No aparecen' },
  'closer.sum.conTrabajo': {
    en: 'With navigation work: {n}. They come at the end of the queue, to finish in their case or forward to a navigator.',
    es: 'Con trabajo de navegación: {n}. Llegan al final de la cola, para terminarlos en su caso o reenviarlos a una navegadora.',
  },
  'closer.sum.error': { en: 'Could not load the numbers; they refresh on their own.', es: 'No pude cargar los números; se refrescan solos.' },
  'closer.case.kicker': { en: 'Next case · oldest screening first', es: 'Siguiente caso · el screening más antiguo primero' },
  'closer.case.left': { en: '{n} ready', es: 'Quedan {n} listos' },
  'closer.case.dob': { en: 'Date of birth', es: 'Fecha de nacimiento' },
  'closer.case.medicaidId': { en: 'Medicaid ID', es: 'Medicaid ID' },
  'closer.case.negative': { en: 'Negative · no needs', es: 'Negativo · sin necesidades' },
  'closer.case.screeningOn': { en: 'Screening of {date}', es: 'Screening del {date}' },
  'closer.case.by': { en: 'by {name}', es: 'por {name}' },
  'closer.case.household0': { en: 'no family members', es: 'sin familiares' },
  'closer.case.household1': { en: '1 family member', es: '1 familiar' },
  'closer.case.householdN': { en: '{n} family members', es: '{n} familiares' },
  'closer.case.uploadedOn': { en: 'uploaded {date}', es: 'subido el {date}' },
  'closer.case.flaggedOn': {
    en: 'Marked «not in FindHelp» on {date}: waiting for the admin. If it shows up now, you can close it.',
    es: 'Marcado «no aparece en FindHelp» el {date}: lo revisa el admin. Si ya aparece, puedes cerrarlo.',
  },
  'closer.case.hasWork': {
    en: 'This case has navigation work: open it to finish it, or forward it to a navigator.',
    es: 'Este caso tiene trabajo de navegación: ábrelo para terminarlo o reenvíalo a una navegadora.',
  },
  'closer.openCase': { en: 'Open the case', es: 'Abrir el caso' },
  'closer.action.copy': { en: 'Copy', es: 'Copiar' },
  'closer.action.copied': { en: 'Copied', es: 'Copiado' },
  'closer.action.openFindhelp': { en: 'Open FindHelp', es: 'Abrir FindHelp' },
  'closer.action.findhelpMissing': { en: 'FindHelp link not set up (admin)', es: 'Falta el enlace de FindHelp (admin)' },
  'closer.action.close': { en: 'Closed in FindHelp · next', es: 'Cerrado en FindHelp · siguiente' },
  'closer.action.closing': { en: 'Closing…', es: 'Cerrando…' },
  'closer.action.notFound': { en: 'Not in FindHelp', es: 'No aparece en FindHelp' },
  'closer.action.notFoundAlready': { en: 'Already marked: the admin reviews it', es: 'Ya marcado: lo revisa el admin' },
  'closer.action.saving': { en: 'Saving…', es: 'Guardando…' },
  'closer.action.forward': { en: 'Forward…', es: 'Reenviar…' },
  'closer.kbd.ctrlEnter': { en: 'Ctrl+Enter', es: 'Ctrl+Enter' },
  'closer.help.open': { en: 'Keyboard shortcuts (?)', es: 'Atajos de teclado (?)' },
  'closer.help.title': { en: 'Keyboard shortcuts', es: 'Atajos de teclado' },
  'closer.help.copy': { en: 'Copy the Medicaid ID', es: 'Copiar el Medicaid ID' },
  'closer.help.note': {
    en: 'Holding a key down does nothing extra: each case needs its own press. Shortcuts wait a moment after a new case appears.',
    es: 'Mantener una tecla apretada no hace nada de más: cada caso pide su propio toque. Los atajos esperan un momento cuando llega un caso nuevo.',
  },
  'closer.help.close': { en: 'Got it', es: 'Entendido' },
  'closer.notFound.title': { en: 'Not in FindHelp?', es: '¿No aparece en FindHelp?' },
  'closer.notFound.body': {
    en: 'It goes to the admin audit list. The case stays in your queue, but «next» will not serve it again.',
    es: 'Va a la lista de auditoría del admin. El caso sigue en tu cola, pero el «siguiente» no te lo vuelve a servir.',
  },
  'closer.notFound.confirm': { en: 'Yes, mark it', es: 'Sí, marcarlo' },
  'closer.empty.title': { en: 'No cases to close', es: 'No hay casos por cerrar' },
  'closer.empty.waitingAdmin': { en: '{n} waiting for the admin review', es: '{n} esperan revisión del admin' },
  'closer.nextError.title': { en: 'Could not load the next case', es: 'No pude cargar el siguiente caso' },
  'closer.nextError.body': { en: 'Nothing was changed. Try again.', es: 'No se cambió nada. Vuelve a intentarlo.' },
  'closer.notice.notOwner': {
    en: 'That case is no longer yours (it was reassigned). Here is the next one.',
    es: 'Ese caso ya no es tuyo (lo reasignaron). Aquí va el siguiente.',
  },
  'closer.notice.alreadyClosed': { en: 'That case was already closed. Here is the next one.', es: 'Ese caso ya estaba cerrado. Aquí va el siguiente.' },
  'closer.notice.hasWork': {
    en: 'That case has navigation work, so it is not closed with one click. Here is the next one.',
    es: 'Ese caso tiene trabajo de navegación y no se cierra con un clic. Aquí va el siguiente.',
  },
  'closer.live.closedNext': { en: 'Closed. Next: {name}', es: 'Cerrado. Siguiente: {name}' },
  'closer.live.closedNone': { en: 'Closed. No more cases.', es: 'Cerrado. No hay más casos.' },
  'closer.live.flaggedNext': { en: 'Marked. Next: {name}', es: 'Marcado. Siguiente: {name}' },
  'closer.live.flaggedNone': { en: 'Marked. No more cases.', es: 'Marcado. No hay más casos.' },
  'closer.live.forwardedNext': { en: 'Forwarded to {to}. Next: {name}', es: 'Reenviado a {to}. Siguiente: {name}' },
  'closer.live.forwardedNone': { en: 'Forwarded to {to}. No more cases.', es: 'Reenviado a {to}. No hay más casos.' },
  'closer.live.copied': { en: 'Copied', es: 'Copiado' },
  'closer.live.copySelected': { en: 'Selected: press Ctrl+C to copy', es: 'Seleccionado: aprieta Ctrl+C para copiar' },
  'closer.forward.kicker': { en: 'Forward', es: 'Reenviar' },
  'closer.forward.title': { en: 'Forward to a navigator', es: 'Reenviar a una navegadora' },
  'closer.forward.subtitle': {
    en: 'The patient moves with everything to a full navigator, and shows up in her «Not yet contacted».',
    es: 'El paciente pasa con todo a una navegadora full y le aparece en «Sin contactar».',
  },
  'closer.forward.to': { en: 'To', es: 'A quién' },
  'closer.forward.leastLoaded': { en: 'The least loaded', es: 'La menos cargada' },
  'closer.forward.leastLoadedNamed': { en: 'The least loaded ({name}, {n} cases)', es: 'La menos cargada ({name}, {n} casos)' },
  'closer.forward.targetOption': { en: '{name} · {n} cases', es: '{name} · {n} casos' },
  'closer.forward.targetsError': {
    en: 'Could not load the list; «the least loaded» still works.',
    es: 'No pude cargar la lista; «la menos cargada» sigue funcionando.',
  },
  'closer.forward.reason': { en: 'Reason (required)', es: 'Motivo (obligatorio)' },
  'closer.forward.reasonPick': { en: 'Choose a reason…', es: 'Elige un motivo…' },
  'closer.forward.noteRequired': { en: 'Note (required with «other», 10+ characters)', es: 'Nota (obligatoria con «otro», 10 caracteres o más)' },
  'closer.forward.noteOptional': { en: 'Note (optional)', es: 'Nota (opcional)' },
  'closer.forward.missingReason': { en: 'Missing: the reason.', es: 'Falta: el motivo.' },
  'closer.forward.missingNote': { en: 'Missing: the note ({n} more characters).', es: 'Falta: la nota ({n} caracteres más).' },
  'closer.forward.send': { en: 'Forward', es: 'Reenviar' },
  'closer.forward.sending': { en: 'Forwarding…', es: 'Reenviando…' },
  'closer.forward.cancel': { en: 'Cancel', es: 'Cancelar' },
  'closer.forward.failed': { en: 'Could not forward. Nothing was changed: try again.', es: 'No se pudo reenviar. No se cambió nada: vuelve a intentarlo.' },
  'closer.reason.posible_necesidad': { en: 'Possible need', es: 'Posible necesidad' },
  'closer.reason.paciente_pide_ayuda': { en: 'The patient asks for help', es: 'El paciente pide ayuda' },
  'closer.reason.otro': { en: 'Other', es: 'Otro' },
  'closer.err.CASE_HAS_UNCLAIMED_BILLABLE_SESSION': {
    en: 'This case has a billable session not yet declared: open it and finish it before forwarding.',
    es: 'Este caso tiene una sesión facturable sin declarar: ábrelo y termínala antes de reenviar.',
  },
  'closer.err.NO_ACTIVE_NAVIGATOR': {
    en: 'There is no active navigator to receive it. Let the admin know.',
    es: 'No hay ninguna navegadora activa para recibirlo. Avísale al admin.',
  },
  'closer.err.FORWARD_NOTE_REQUIRED': {
    en: 'With «other», the note is required (at least 10 characters).',
    es: 'Con «otro», la nota es obligatoria (al menos 10 caracteres).',
  },
  'closer.err.FORWARD_REASON_REQUIRED': { en: 'Choose a reason from the list.', es: 'Elige un motivo de la lista.' },
  'closer.err.TARGET_GONE': {
    en: 'That person can no longer receive cases. Choose another, or leave «the least loaded».',
    es: 'Esa persona ya no puede recibir casos. Elige otra o deja «la menos cargada».',
  },
  'closer.err.CASE_HAS_POSITIVE_NEED': {
    en: 'This case has a positive need: it is not closed as negative. Forward it to a navigator.',
    es: 'Este caso tiene una necesidad positiva: no se cierra como negativo. Reenvíalo a una navegadora.',
  },
  'closer.err.NEGATIVE_MARK_REQUIRES_SCREENING': {
    en: 'This case has no screening to anchor the FindHelp mark. Report it with the bug button.',
    es: 'Este caso no tiene screening para anclar la marca de FindHelp. Repórtalo con el botón de errores.',
  },
  'closer.err.NOT_A_CLOSER': { en: 'Your user is no longer a closer.', es: 'Tu usuario ya no es closer.' },
  'closer.err.generic': {
    en: 'Could not save. You are still on the same case: nothing advanced.',
    es: 'No se pudo guardar. Sigues en el mismo caso: no se avanzó nada.',
  },
  // T2-C · #161 B1: «Salir sin enviar» con un padre que ya tiene familiares
  // enviados (409 PARENT_HAS_SUBMITTED_SUBS): no se cancela, se queda.
  't2c.salir.retenidoT': {
    en: 'This screening cannot be cancelled',
    es: 'Este screening no se puede cancelar',
  },
  't2c.salir.retenidoB': {
    en: 'It already has submitted family members. Finish it and submit it.',
    es: 'Ya tiene familiares enviados. Termínalo y envíalo.',
  },
  // T2-C fin =====
  // CL-W2 — el admin y la franja del closer (Edd, 23/24-oct-2026; §11 y §11.1
  // del plan; CONTRATO-CLOSER §3). Dos superficies: la lista de auditoría «No
  // aparece en FindHelp» (admin y supervisor) y la franja ámbar de la
  // navegadora que RECIBE un caso de un closer. Sin reenvío a screener: Edd
  // «ya definí que no se envían de nuevo a un screener». Bloque propio al
  // final, con marcador, para que el merge con los de T2 y CL-W sea trivial.
  'adminNav.tab.closerNotFound': { en: 'Not in FindHelp', es: 'No aparecen en FindHelp' },
  'adminNav.closerNotFound.title': { en: 'Not in FindHelp ({n})', es: 'No aparecen en FindHelp ({n})' },
  'adminNav.closerNotFound.sub': {
    en: 'Negative cases the closer could not find in FindHelp, oldest first. They stay in the closer\'s queue; when you resolve one, it goes back to their cases to close.',
    es: 'Casos negativos que el closer no encontró en FindHelp, el más antiguo primero. Siguen en su cola; al resolver uno, vuelve a sus casos por cerrar.',
  },
  'adminNav.closerNotFound.listAria': { en: 'Cases not in FindHelp', es: 'Casos que no aparecen en FindHelp' },
  'adminNav.closerNotFound.medicaidId': { en: 'Medicaid ID', es: 'Medicaid ID' },
  'adminNav.closerNotFound.screening': { en: 'Screening of {date} · by {name}', es: 'Screening del {date} · por {name}' },
  'adminNav.closerNotFound.uploaded': { en: 'Uploaded to FindHelp on {date} by {name}', es: 'Subido a FindHelp el {date} por {name}' },
  'adminNav.closerNotFound.flagged': { en: 'Marked «not in FindHelp» on {date} by {name}', es: 'Marcado «no aparece» el {date} por {name}' },
  'adminNav.closerNotFound.owner': { en: 'In the queue of {name}', es: 'En la cola de {name}' },
  'adminNav.closerNotFound.resolve': { en: 'Resolve…', es: 'Resolver…' },
  'adminNav.closerNotFound.howResolved': { en: 'How was it resolved?', es: '¿Cómo se resolvió?' },
  'adminNav.closerNotFound.res.aparece_ahora': { en: 'It shows up in FindHelp now', es: 'Ya aparece en FindHelp' },
  'adminNav.closerNotFound.res.resubido': { en: 'It was uploaded again', es: 'Se volvió a subir' },
  'adminNav.closerNotFound.res.otro': { en: 'Other', es: 'Otro' },
  'adminNav.closerNotFound.resHint': {
    en: 'The case goes back to the cases to close of {name}.',
    es: 'El caso vuelve a los casos por cerrar de {name}.',
  },
  'adminNav.closerNotFound.missing': { en: 'Missing: how it was resolved.', es: 'Falta: cómo se resolvió.' },
  'adminNav.closerNotFound.confirm': { en: 'Resolve', es: 'Resolver' },
  'adminNav.closerNotFound.cancel': { en: 'Cancel', es: 'Cancelar' },
  'adminNav.closerNotFound.done': {
    en: 'Resolved: the case is back in the closer\'s queue.',
    es: 'Resuelto: el caso volvió a la cola del closer.',
  },
  'adminNav.closerNotFound.gone': {
    en: 'This case is no longer waiting (it was closed or someone resolved it). The list was refreshed.',
    es: 'Este caso ya no espera revisión (se cerró o alguien lo resolvió). La lista se actualizó.',
  },
  'adminNav.closerNotFound.failed': {
    en: 'Could not resolve it. Nothing changed; try again.',
    es: 'No se pudo resolver. No cambió nada; vuelve a intentarlo.',
  },
  'adminNav.closerNotFound.emptyTitle': { en: 'Nothing to review', es: 'Nada que revisar' },
  'adminNav.closerNotFound.emptyBody': {
    en: 'No closer has marked a case «not in FindHelp».',
    es: 'Ningún closer marcó un caso «no aparece en FindHelp».',
  },
  'adminNav.closerNotFound.errorTitle': { en: 'Could not load the list', es: 'No pude cargar la lista' },
  'adminNav.closerNotFound.errorBody': {
    en: 'Until it loads, these cases cannot be reviewed; they stay in the closer\'s queue.',
    es: 'Mientras no cargue, estos casos no se pueden revisar; siguen en la cola del closer.',
  },
  'adminNav.closerNotFound.retry': { en: 'Retry', es: 'Reintentar' },
  'navigator.closerForward.line': { en: 'Forwarded by {name} on {date}', es: 'Reenviado por {name} el {date}' },
  'navigator.closerForward.someone': { en: 'a closer', es: 'un closer' },
  'navigator.closerForward.reason.posible_necesidad': { en: 'Possible need', es: 'Posible necesidad' },
  'navigator.closerForward.reason.paciente_pide_ayuda': { en: 'The patient asks for help', es: 'El paciente pide ayuda' },
  'navigator.closerForward.reason.otro': { en: 'Other reason', es: 'Otro motivo' },
  'navigator.closerForward.note': { en: 'Closer\'s note:', es: 'Nota del closer:' },
  'navigator.closerForward.why': {
    en: 'The screening was negative; the closer passed it to navigation for you to review.',
    es: 'El screening fue negativo; el closer lo pasó a navegación para que lo revises.',
  },
  'navigator.closerForward.loadFailed': {
    en: 'Could not load who forwarded this case to you.',
    es: 'No pude cargar quién te reenvió este caso.',
  },
  'navigator.closerForward.retry': { en: 'Retry', es: 'Reintentar' },

  // T2-B2 ===== (#158 B, Edd 23-oct: «Elegir "es esta persona"») — el
  // recuadro «¿Es alguna de estas personas?» del paso de hogar
  // (HouseholdMatchModal) y el estado terminal «ya screeneado».
  'householdStep.statusBadge.already_screened': { en: 'Already screened', es: 'Ya screeneado' },
  'screeningSummary.memberStateAlreadyScreened': {
    en: 'Already screened: closed without a new screening',
    es: 'Ya screeneado: se cerró sin screening nuevo',
  },
  't2b2.badge.yaScreeneado': { en: 'Already screened on {date}', es: 'Ya screeneado el {date}' },
  't2b2.box.titulo': { en: 'Is it one of these people?', es: '¿Es alguna de estas personas?' },
  't2b2.box.sub': {
    en: 'They are on the Pathway list with the same phone as this household.',
    es: 'Están en la lista de Pathway con el mismo teléfono que este hogar.',
  },
  't2b2.box.subCin': {
    en: 'This person is already on the Pathway list with the Medicaid ID you entered.',
    es: 'Ya está en la lista de Pathway con el Medicaid ID que capturaste.',
  },
  't2b2.box.noLeer': {
    en: "Don't read these details to the person on the call.",
    es: 'No le leas estos datos a quien está en la llamada.',
  },
  't2b2.box.buscando': { en: 'Searching the list…', es: 'Buscando en la lista…' },
  't2b2.box.nadie': {
    en: 'No one on the list shares this phone.',
    es: 'No hay nadie en la lista con el mismo teléfono.',
  },
  't2b2.box.errorCarga': {
    en: "Couldn't search the list. You can screen as usual.",
    es: 'No se pudo buscar en la lista. Puedes screenear como siempre.',
  },
  't2b2.box.cerrar': { en: 'Close', es: 'Cerrar' },
  't2b2.box.esEsta': { en: 'This is the person', es: 'Es esta persona' },
  't2b2.box.ninguna': { en: "None, it's someone else", es: 'Ninguna, es otra persona' },
  't2b2.box.elegida': { en: 'Chosen', es: 'Elegida' },
  't2b2.cand.nacioAnio': { en: 'Born in {year}', es: 'Nació en {year}' },
  't2b2.cand.nacioFecha': { en: 'Born on {date}', es: 'Nació el {date}' },
  't2b2.cand.coincide': { en: 'matches', es: 'coincide' },
  't2b2.cand.medicaid': { en: 'Medicaid …{last4}', es: 'Medicaid …{last4}' },
  't2b2.cola.mine': { en: 'In your queue', es: 'En tu cola' },
  't2b2.cola.other_agent': { en: "In another agent's queue", es: 'En la cola de otra agente' },
  't2b2.cola.other_agent_busy': {
    en: 'Another agent has it open right now',
    es: 'Otra agente la tiene abierta ahora',
  },
  't2b2.cola.none': { en: 'Not assigned', es: 'Sin asignar' },
  't2b2.intentos.0': { en: 'not contacted', es: 'sin contactar' },
  't2b2.intentos.1': { en: '1 attempt', es: '1 intento' },
  't2b2.intentos.n': { en: '{n} attempts', es: '{n} intentos' },
  't2b2.cand.yaScreeneada': {
    en: 'Already screened on {date}: if this is the person, it closes without a new screening.',
    es: 'Ya screeneada el {date}: si es esta persona, se cierra sin screening nuevo.',
  },
  't2b2.bloq.ineligible': { en: 'Not eligible', es: 'No elegible' },
  't2b2.bloq.outside_bronx': { en: 'Outside the Bronx', es: 'Fuera del Bronx' },
  't2b2.bloq.ayuda': {
    en: 'If this is the person, decline the family member with the reason.',
    es: 'Si es esta persona, declina al familiar con el motivo.',
  },
  't2b2.cand.linked': {
    en: 'Already a family member in another household',
    es: 'Ya es familiar de otro hogar',
  },
  't2b2.cand.debil': {
    en: "Neither the name nor the date of birth match",
    es: 'No coinciden ni el nombre ni la fecha de nacimiento',
  },
  't2b2.confirm.soloFecha': {
    en: 'Only the date of birth matches, not the name. Is it the same person?',
    es: 'Solo coincide la fecha de nacimiento, no el nombre. ¿Es la misma persona?',
  },
  't2b2.confirm.soloNombre': {
    en: 'Only the name matches, not the date of birth. Is it the same person?',
    es: 'Solo coincide el nombre, no la fecha de nacimiento. ¿Es la misma persona?',
  },
  't2b2.confirm.si': { en: 'Yes, this is the person', es: 'Sí, es esta persona' },
  't2b2.confirm.no': { en: 'No', es: 'No' },
  't2b2.toast.misma': {
    en: 'Done: the screening will use their record from the list.',
    es: 'Listo: al screenearla se usa su ficha de la lista.',
  },
  't2b2.toast.mismaOtra': {
    en: "Done: the screening will use their record, and it leaves the other agent's queue.",
    es: 'Listo: al screenearla se usa su ficha y sale de la cola de la otra agente.',
  },
  't2b2.toast.ya': {
    en: 'Already screened on {date}: closed without a new screening.',
    es: 'Ya estaba screeneada el {date}: queda cerrada sin screening nuevo.',
  },
  't2b2.error.titulo': { en: "Couldn't save the decision", es: 'No se pudo guardar la decisión' },
  't2b2.fila.revisar': { en: 'Review matches', es: 'Revisar coincidencias' },
  't2b2.fila.deLaLista': { en: 'Already on the list', es: 'Ya está en la lista' },
  't2b2.fila.deLaListaTitle': {
    en: 'The screening will use their record from the list, not a new one.',
    es: 'Al screenearla se usa su ficha de la lista, no una nueva.',
  },
  't2b2.fila.decidePrimero': {
    en: 'First decide whether it is one of these people',
    es: 'Primero decide si es alguna de estas personas',
  },
  // T2-B2 fin =====
  // PD · «Dejar para después» (Edd, 25-oct-2026): la salida explícita del
  // padre con familiares enviados, en los avisos 409 de la pre-llamada (T2-A)
  // y del wizard (T2-C).
  'pd.dejar.boton': { en: 'Leave for later', es: 'Dejar para después' },
  'pd.dejar.explica': {
    en: 'The screening stays saved: if the patient calls back, you pick it up; if not, it closes by itself at midnight.',
    es: 'El screening queda guardado: si el paciente vuelve a llamar lo retomas; si no, se cierra solo a medianoche.',
  },
  'pd.dejar.okT': { en: 'Left for later', es: 'Quedó para después' },
  'pd.dejar.okB': {
    en: 'The screening is saved. You can take the next patient.',
    es: 'El screening quedó guardado. Ya puedes tomar el siguiente paciente.',
  },
  'pd.dejar.errorT': { en: "Couldn't leave it for later", es: 'No se pudo dejar para después' },
  'pd.reanudar.errorT': { en: "Couldn't resume the screening", es: 'No se pudo reanudar el screening' },
  // PD fin =====

  // FX ===== (m2, revisión adversarial Fable antes del promote) — el motivo de
  // cancelación `case_closed` (migración 089, T2-E: cerrar el caso cancela sus
  // citas futuras de navegación) tiene su etiqueta en el diccionario de motivos
  // de la agenda. Sin ella, quien traduzca el motivo pintaría la clave cruda.
  'navigator.agenda.cancel.case_closed': { en: 'Case closed', es: 'Caso cerrado' },
  // FX fin =====

} satisfies Record<string, { en: string; es: string }>;

export type StringKey = keyof typeof STRINGS;

export function useT(): (key: StringKey, vars?: Record<string, string | number>) => string {
  const lang = useLanguageStore((s) => s.lang);
  return (key, vars) => {
    const entry = STRINGS[key];
    if (!entry) return key as string;
    let s = entry[lang] ?? entry.en;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        s = s.split('{' + k + '}').join(String(v));
      }
      }

    return s;
  };
}

export function useLang(): Lang {
  return useLanguageStore((s) => s.lang);
}
